import { useState, useEffect, useMemo, useRef } from "react";
import { Trash2, BarChart3, Package, ChefHat, Scale, LogOut, ArrowLeft, Search, Check, ChevronRight, Plus, Minus, ChevronDown, ChevronUp, Truck, ShieldCheck, Users, Thermometer, AlertTriangle, Clock, Copy, X, Send, Bell, Mail, FileDown, Edit3, MapPin, DollarSign, ShoppingCart, Printer, Mic, MicOff, Bluetooth, BluetoothSearching, Sun, Moon, Sunrise, FileText, TrendingUp, TrendingDown, Tag, Boxes, Calendar, ClipboardList, Wheat, Beef, Milk, Snowflake, Coffee, Wine, ShoppingBag, Refrigerator, Settings, Play, Pause, Eye, EyeOff, Globe, Lock, Pizza, Utensils, Camera } from "lucide-react";

/* ═══ THEMES — bodega colorway ═══ */
const DARK={bg:"#121015",sf:"#1A1720",card:"#211E28",brd:"#332F3A",tx:"#F0EDE5",mt:"#8A8495",dm:"#5C576A",grn:"#4ADE80",grnBg:"rgba(74,222,128,0.1)",red:"#D42B2B",redBg:"rgba(212,43,43,0.1)",amb:"#F5C518",ambBg:"rgba(245,197,24,0.1)",blu:"#60A5FA",bluBg:"rgba(96,165,250,0.1)",pur:"#A78BFA",purBg:"rgba(167,139,250,0.1)",logo:"#D42B2B",logoSh:"#1B2A4A",logoBg:"#F5C518",accent:"#F5C518",signBtn:"#D42B2B"};
const LIGHT={bg:"#F8F7F4",sf:"#FFFFFF",card:"#FFFFFF",brd:"#E8E2D8",tx:"#1B2A4A",mt:"#7A7268",dm:"#A89E94",grn:"#16A34A",grnBg:"rgba(22,163,74,0.06)",red:"#D42B2B",redBg:"rgba(212,43,43,0.05)",amb:"#C07D00",ambBg:"rgba(192,125,0,0.06)",blu:"#2563EB",bluBg:"rgba(37,99,235,0.05)",pur:"#7C3AED",purBg:"rgba(124,58,237,0.05)",logo:"#D42B2B",logoSh:"#1B2A4A",logoBg:"#F5C518",accent:"#D42B2B",signBtn:"#1B2A4A"};
let T=LIGHT;
let CUR="€"; // currency symbol — toggleable in settings
const SCAN_API="https://gastro-iot-api-production.up.railway.app"; // server-side image scanning
let ROLE="owner"; // current user role — gates revenue/financial visibility
const canSeeRevenue=()=>ROLE==="owner";
const canSeeCost=()=>ROLE==="owner"||ROLE==="chef"; // food cost %, margins, waste €
const canManage=()=>ROLE==="owner"||ROLE==="chef"; // edit recipes/par/suppliers/staff

/* ═══ GERMAN FOOD MATCHING — ported from EATRR's food-translations.ts ═══
   Curated German→English grocery dictionary + brand-stripping + compound-word
   suffix matching, so a scanned "Bio Hähnchenbrust" from a Metro/REWE/Edeka
   document matches an inventory item named "Hähnchenbrust". Static dictionary
   (not a translation API call) — this runs in tight per-line matching loops. */
const DE_TO_EN={
  reis:"rice",basmatireis:"basmati rice",jasminreis:"jasmine rice",milchreis:"rice pudding rice",
  risottoreis:"risotto rice",wildreis:"wild rice",vollkornreis:"whole grain rice",
  langkornreis:"long-grain rice",rundkornreis:"round-grain rice",klebreis:"sticky rice",
  couscous:"couscous",bulgur:"bulgur",quinoa:"quinoa",hirse:"millet",grieß:"semolina",
  polenta:"polenta",graupen:"pearl barley",gerste:"barley",hafer:"oats",haferflocken:"oats",
  dinkel:"spelt",roggen:"rye",weizen:"wheat",buchweizen:"buckwheat",
  nudeln:"pasta",spaghetti:"spaghetti",penne:"penne",fusilli:"fusilli",makkaroni:"macaroni",
  lasagneplatten:"lasagna sheets",tortellini:"tortellini",gnocchi:"gnocchi",tagliatelle:"tagliatelle",
  brot:"bread",weißbrot:"white bread",vollkornbrot:"whole grain bread",roggenbrot:"rye bread",
  baguette:"baguette",brötchen:"bread roll",toastbrot:"sandwich bread",knäckebrot:"crispbread",
  mehl:"flour",weizenmehl:"wheat flour",dinkelmehl:"spelt flour",kichererbsenmehl:"chickpea flour",
  backpulver:"baking powder",natron:"baking soda",hefe:"yeast",trockenhefe:"dry yeast",
  vanillezucker:"vanilla sugar",puderzucker:"powdered sugar",rohrzucker:"cane sugar",
  kokosraspeln:"shredded coconut",paniermehl:"breadcrumbs",speisestärke:"cornstarch",maisstärke:"cornstarch",
  milch:"milk",vollmilch:"whole milk",magermilch:"skim milk",fettarmemilch:"low-fat milk",
  buttermilch:"buttermilk",hafermilch:"oat milk",mandelmilch:"almond milk",sojamilch:"soy milk",
  kokosmilch:"coconut milk",sahne:"cream",schlagsahne:"whipping cream",saheresahne:"sour cream",
  schmand:"sour cream",quark:"quark",magerquark:"low-fat quark",speisequark:"quark",
  joghurt:"yogurt",naturjoghurt:"plain yogurt",frischkäse:"cream cheese",hüttenkäse:"cottage cheese",
  mozzarella:"mozzarella",parmesan:"parmesan",gouda:"gouda",emmentaler:"emmental",
  cheddar:"cheddar",feta:"feta",butter:"butter",margarine:"margarine",
  eier:"egg",ei:"egg",eiweiß:"egg white",eigelb:"egg yolk",
  hähnchen:"chicken",huhn:"chicken",hähnchenbrust:"chicken breast",hühnerbrust:"chicken breast",
  hähnchenschenkel:"chicken thigh",hähnchenkeule:"chicken leg",hähnchenflügel:"chicken wing",
  pute:"turkey",truthahn:"turkey",putenbrust:"turkey breast",ente:"duck",gans:"goose",
  rind:"beef",rindfleisch:"beef",rinderhack:"ground beef",rinderfilet:"beef filet",
  roastbeef:"roast beef",schwein:"pork",schweinefleisch:"pork",schweinefilet:"pork tenderloin",
  schweinekotelett:"pork chop",lamm:"lamb",lammfleisch:"lamb",kalb:"veal",kalbfleisch:"veal",
  wild:"game meat",hirsch:"venison",reh:"venison",
  hackfleisch:"mince",hack:"mince",gehacktes:"mince",rinderhackfleisch:"ground beef",
  wurst:"sausage",bratwurst:"bratwurst",wiener:"hot dog",würstchen:"sausage",
  salami:"salami",schinken:"ham",kochschinken:"cooked ham",rohschinken:"cured ham",
  parmaschinken:"parma ham",speck:"bacon",mortadella:"mortadella",leberwurst:"liverwurst",
  blutwurst:"blood sausage",fisch:"fish",lachs:"salmon",räucherlachs:"smoked salmon",
  thunfisch:"tuna",kabeljau:"cod",forelle:"trout",hering:"herring",makrele:"mackerel",
  sardine:"sardine",garnelen:"shrimp",shrimps:"shrimp",muscheln:"mussels",
  tintenfisch:"squid",oktopus:"octopus",krabben:"crab",
  zwiebel:"onion",zwiebeln:"onion",frühlingszwiebeln:"spring onion",schalotte:"shallot",
  knoblauch:"garlic",lauch:"leek",porree:"leek",
  tomate:"tomato",tomaten:"tomato",kirschtomaten:"cherry tomatoes",cherrytomaten:"cherry tomatoes",
  kartoffel:"potato",kartoffeln:"potato",süßkartoffel:"sweet potato",süßkartoffeln:"sweet potato",
  karotte:"carrot",karotten:"carrot",möhre:"carrot",möhren:"carrot",
  gurke:"cucumber",gurken:"cucumber",paprika:"bell pepper",chili:"chili pepper",
  chilischote:"chili pepper",spinat:"spinach",brokkoli:"broccoli",blumenkohl:"cauliflower",
  rosenkohl:"brussels sprout",rotkohl:"red cabbage",weißkohl:"cabbage",kohl:"cabbage",
  wirsing:"savoy cabbage",chinakohl:"chinese cabbage",radieschen:"radish",
  rotebete:"beetroot",roterüben:"beetroot",mais:"corn",erbsen:"peas",bohnen:"beans",
  grünebohnen:"green beans",kidneybohnen:"kidney beans",schwarzebohnen:"black beans",
  sellerie:"celery",staudensellerie:"celery",knollensellerie:"celeriac",
  pilze:"mushroom",pilz:"mushroom",champignons:"mushroom",austernpilze:"oyster mushroom",
  aubergine:"eggplant",zucchini:"zucchini",kürbis:"pumpkin",butternusskürbis:"butternut squash",
  hokkaido:"hokkaido squash",avocado:"avocado",artischocke:"artichoke",spargel:"asparagus",
  fenchel:"fennel",rucola:"arugula",feldsalat:"lamb's lettuce",kopfsalat:"lettuce",eisbergsalat:"iceberg lettuce",
  apfel:"apple",äpfel:"apple",birne:"pear",banane:"banana",orange:"orange",orangen:"orange",
  mandarine:"tangerine",clementine:"clementine",zitrone:"lemon",limette:"lime",
  traube:"grape",trauben:"grape",erdbeere:"strawberry",erdbeeren:"strawberry",
  himbeere:"raspberry",himbeeren:"raspberry",blaubeere:"blueberry",blaubeeren:"blueberry",
  heidelbeere:"blueberry",brombeere:"blackberry",johannisbeere:"currant",
  stachelbeere:"gooseberry",kirsche:"cherry",kirschen:"cherry",pflaume:"plum",
  aprikose:"apricot",pfirsich:"peach",nektarine:"nectarine",ananas:"pineapple",
  mango:"mango",papaya:"papaya",kiwi:"kiwi",granatapfel:"pomegranate",feige:"fig",
  dattel:"date",datteln:"date",melone:"melon",wassermelone:"watermelon",
  honigmelone:"honeydew melon",rhabarber:"rhubarb",grapefruit:"grapefruit",
  petersilie:"parsley",basilikum:"basil",oregano:"oregano",thymian:"thyme",
  rosmarin:"rosemary",schnittlauch:"chives",dill:"dill",koriander:"coriander",
  minze:"mint",salbei:"sage",majoran:"marjoram",lorbeerblatt:"bay leaf",
  zimt:"cinnamon",muskat:"nutmeg",kurkuma:"turmeric",ingwer:"ginger",
  kreuzkümmel:"cumin",kümmel:"caraway",paprikapulver:"paprika powder",
  currypulver:"curry powder",safran:"saffron",vanille:"vanilla",salz:"salt",
  pfeffer:"pepper",chiliflocken:"chili flakes",sesam:"sesame",
  olivenöl:"olive oil",sonnenblumenöl:"sunflower oil",rapsöl:"canola oil",
  sesamöl:"sesame oil",kokosöl:"coconut oil",essig:"vinegar",balsamico:"balsamic vinegar",
  apfelessig:"apple cider vinegar",senf:"mustard",ketchup:"ketchup",mayonnaise:"mayonnaise",
  sojasauce:"soy sauce",honig:"honey",ahornsirup:"maple syrup",marmelade:"jam",
  konfitüre:"jam",nussmus:"nut butter",erdnussbutter:"peanut butter",schokolade:"chocolate",
  kakao:"cocoa",zucker:"sugar",braunerzucker:"brown sugar",brühe:"broth",
  gemüsebrühe:"vegetable broth",hühnerbrühe:"chicken broth",rinderbrühe:"beef broth",
  tomatenmark:"tomato paste",passiertetomaten:"tomato passata",stückigetomaten:"chopped tomatoes",
  linsen:"lentils",kichererbsen:"chickpeas",
  mandeln:"almonds",walnüsse:"walnuts",haselnüsse:"hazelnuts",cashewkerne:"cashews",
  cashews:"cashews",pistazien:"pistachios",erdnüsse:"peanuts",
  sonnenblumenkerne:"sunflower seeds",kürbiskerne:"pumpkin seeds",leinsamen:"flaxseed",chiasamen:"chia seeds",
  wasser:"water",orangensaft:"orange juice",apfelsaft:"apple juice",
  wein:"wine",weißwein:"white wine",rotwein:"red wine",bier:"beer",
};
// Brand/descriptor words German invoices & receipts commonly prefix onto an item
// name — stripping these first means "Bio Hähnchenbrust" and "Metro Eigenmarke
// Möhren" resolve the same way the bare food word would.
const STRIP_WORDS=new Set(["bio","frisch","frische","frischer","tk","tiefgekühlt","ja","gut&günstig","gutgünstig",
  "k-classic","kclassic","rewe","edeka","metro","transgourmet","selgros","beste","wahl","1a","extra","premium","regional"]);
function stripDescriptors(words){const kept=words.filter(w=>!STRIP_WORDS.has(w));return kept.length?kept:words;}
// Longest dictionary-key suffix a word ends with (min length 4) — handles German
// compounds like "Basmatireis" (Basmati+Reis, no space) via the "reis" suffix.
function longestSuffixMatch(dict,word){
  let best=null;
  for(const key of Object.keys(dict)){
    if(key.length>=4&&word.length>key.length&&word.endsWith(key)){if(!best||key.length>best.length)best=key;}
  }
  return best;
}
function toEnglishFood(name){
  const raw=String(name||"").toLowerCase().trim().replace(/[,.;].*$/,"").trim();
  if(!raw)return name;
  if(DE_TO_EN[raw])return DE_TO_EN[raw];
  const words=stripDescriptors(raw.split(/[\s-]+/).filter(Boolean));
  for(let i=words.length-1;i>=0;i--){if(DE_TO_EN[words[i]])return DE_TO_EN[words[i]];}
  for(const w of words){const suf=longestSuffixMatch(DE_TO_EN,w);if(suf)return DE_TO_EN[suf];}
  return name;
}
// Fuzzy-matches two food names for "is this the same ingredient" — translates
// known German grocery terms to English on both sides first, then compares.
function foodNamesMatch(a,b){
  const na=String(toEnglishFood(a)||"").toLowerCase().trim();
  const nb=String(toEnglishFood(b)||"").toLowerCase().trim();
  if(na.length<3||nb.length<3)return false;
  return na===nb||na.includes(nb)||nb.includes(na);
}

// Strict, exact-only match for linking a scanned/dictated recipe ingredient to
// inventory — deliberately NOT fuzzy. fuzzyMatchItem's typo-tolerant scoring
// (35% edit-distance, unguarded substring checks) was silently linking scanned
// ingredients to unrelated items — "Parmesan" -> "Mozzarella", "sardines" ->
// "Mozzarella", "white wine" -> "white onions". A recipe is wrong less often by
// flagging an ingredient as new (the user reviews and confirms/creates it) than
// by guessing a plausible-looking wrong match that's easy to miss in review.
function strictIngredientMatch(name,ings){
  const raw=String(name||"").toLowerCase().trim();
  if(!raw)return null;
  const translated=toEnglishFood(raw).toLowerCase().trim();
  return ings.find(i=>{
    const iRaw=(i.name||"").toLowerCase().trim();
    if(iRaw===raw)return true;
    return toEnglishFood(iRaw).toLowerCase().trim()===translated;
  })||null;
}

/* Logo G — hand-painted script, properly centered, bodega style */
function LogoG({size=26}){
  // Cornucopia mark — gold horn with produce on a navy round badge.
  return <svg width={size} height={size} viewBox="0 0 120 120">
    <circle cx="60" cy="60" r="58" fill="#1B2A4A"/>
    <g transform="translate(14,22) scale(0.40)">
      <path d="M40 168 Q6 150 14 108 Q20 74 54 68 Q80 64 84 92 Q86 112 68 116 Q56 112 58 98 Q50 96 50 110 Q52 142 96 150 Q70 168 40 168 Z" fill="#F5C518"/>
      <path d="M86 150 Q126 158 168 144 Q210 130 226 100 Q180 110 142 106 Q104 102 86 130 Z" fill="#F5C518"/>
      <circle cx="150" cy="82" r="22" fill="#F5C518"/>
      <path d="M150 60 q3 -11 12 -13 q-1 10 -9 13 Z" fill="#F5C518"/>
      <circle cx="200" cy="64" r="17" fill="#F5C518"/>
      <g fill="#F5C518"><circle cx="112" cy="58" r="10"/><circle cx="130" cy="52" r="10"/><circle cx="122" cy="70" r="10"/></g>
      <path d="M236 60 L292 34 L296 44 L242 70 Z" fill="#F5C518"/>
    </g>
  </svg>;
}

/* ═══ CHEF-SIZED INPUTS — bigger for kitchen use, gloved hands, screens at distance ═══ */
const inp_fn=(t)=>({width:"100%",background:t.sf,border:`1px solid ${t.brd}`,borderRadius:10,padding:"12px 14px",color:t.tx,fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:8,minHeight:46,transition:"border-color 0.15s"});
let inp=inp_fn(DARK);

const DAYS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const STATIONS=["Grill","Prep 1","Prep 2","Pastry","Plating","Bar","Wash"];
const SHIFTS=["morning","lunch","afternoon","dinner","late"];
const ALLERGENS=["gluten","crustaceans","eggs","fish","peanuts","soybeans","milk","nuts","celery","mustard","sesame","sulphites","lupin","molluscs"];
const STORAGE=["refrigerated","tiefkühl","trocken","ambient"];
const RECIPE_TYPES=["dish","prep","component","sauce","dressing","garnish","beverage","dessert"];
const RECIPE_STATUS=["draft","active","seasonal","archived"];
const HACCP_TYPES=["temperature_check","cleaning","pest_control","receiving_check","cooking_temp","cooling_log","staff_hygiene","corrective_action"];

/* Ingredient category groupings — drives icons + filters across the app */
const ING_CATS=[
  {id:"produce",label:"Produce / Greens",Icon:Wheat,color:"grn"},
  {id:"protein",label:"Meat & Fish",Icon:Beef,color:"red"},
  {id:"dairy",label:"Dairy & Eggs",Icon:Milk,color:"blu"},
  {id:"frozen",label:"Frozen",Icon:Snowflake,color:"blu"},
  {id:"dry",label:"Dry Goods",Icon:Boxes,color:"amb"},
  {id:"bakery",label:"Bakery",Icon:Wheat,color:"amb"},
  {id:"beverage",label:"Beverages",Icon:Coffee,color:"pur"},
  {id:"sauce",label:"Sauces & Condiments",Icon:Wine,color:"pur"},
];

/* Shelf-life rules-of-thumb by category × storage temp band.
   Days at recommended temp. Used for the shelf-life engine.
   These are food-safety reference values, conservative side. */
const SHELF_RULES={
  protein:{refrigerated:{ideal:[0,4],days:3,halfDays:1.5,frozen:{days:180}},frozen:{ideal:[-22,-18],days:180,halfDays:90}},
  dairy:{refrigerated:{ideal:[1,4],days:7,halfDays:4},frozen:{ideal:[-22,-18],days:90,halfDays:45}},
  produce:{refrigerated:{ideal:[2,8],days:5,halfDays:2.5}},
  bakery:{dry:{ideal:[15,22],days:3,halfDays:1.5},refrigerated:{ideal:[2,8],days:7,halfDays:3.5}},
  dry:{dry:{ideal:[15,25],days:180,halfDays:90}},
  beverage:{dry:{ideal:[15,22],days:180,halfDays:90},refrigerated:{ideal:[1,8],days:14,halfDays:7}},
  sauce:{refrigerated:{ideal:[1,8],days:14,halfDays:7},dry:{ideal:[15,22],days:90,halfDays:45}},
  frozen:{frozen:{ideal:[-22,-18],days:180,halfDays:90}},
};

/* ═══ DATA — ingredients with category-id, storage_zone, and price history ═══ */
// Add lastReceived timestamps — stagger so some are fresh, some aging
const _now=Date.now();
const _daysAgo=(d)=>new Date(_now-d*86400000).toISOString();
const initIngs = [
  {id:"i1",name:"Avocado",cat:"produce",cost:1.20,unit:"pc",unitG:200,parMin:15,parMax:40,qty:30,storage:"refrigerated",zone:"z1",shelf:4,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-03-15",p:1.05},{d:"2026-04-02",p:1.10},{d:"2026-04-28",p:1.20}],lastReceived:_daysAgo(2)},
  {id:"i2",name:"Lachs / Salmon",cat:"protein",cost:28.00,unit:"kg",unitG:1000,parMin:2,parMax:6,qty:3.2,storage:"refrigerated",zone:"z1",shelf:5,allergens:["fish"],yield:85,sku:"SAL-001",barcode:"",supplier:"s3",priceHist:[{d:"2026-03-10",p:26.50},{d:"2026-04-05",p:27.00},{d:"2026-05-01",p:28.00}],lastReceived:_daysAgo(1)},
  {id:"i3",name:"Bio-Eier / Eggs",cat:"dairy",cost:0.45,unit:"pc",unitG:60,parMin:30,parMax:80,qty:48,storage:"refrigerated",zone:"z1",shelf:21,allergens:["eggs"],sku:"EGG-001",barcode:"",supplier:"s2",priceHist:[{d:"2026-03-01",p:0.42},{d:"2026-04-15",p:0.45}],lastReceived:_daysAgo(5)},
  {id:"i4",name:"Blumenkohl / Cauliflower",cat:"produce",cost:2.50,unit:"kg",unitG:1000,parMin:3,parMax:8,qty:5.5,storage:"refrigerated",zone:"z1",shelf:5,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-04-01",p:2.20},{d:"2026-05-01",p:2.50}],lastReceived:_daysAgo(3)},
  {id:"i5",name:"Croissants",cat:"bakery",cost:1.20,unit:"pc",unitG:80,parMin:10,parMax:30,qty:18,storage:"trocken",zone:"z3",shelf:2,allergens:["gluten","milk","eggs"],sku:"CRO-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-04-10",p:1.15},{d:"2026-05-05",p:1.20}],lastReceived:_daysAgo(0)},
  {id:"i6",name:"Mascarpone",cat:"dairy",cost:11.50,unit:"kg",unitG:1000,parMin:0.5,parMax:2,qty:1.2,storage:"refrigerated",zone:"z1",shelf:14,allergens:["milk"],sku:"",barcode:"",supplier:"s2",priceHist:[{d:"2026-04-05",p:11.00},{d:"2026-05-02",p:11.50}],lastReceived:_daysAgo(4)},
  {id:"i7",name:"Sahne / Cream",cat:"dairy",cost:3.80,unit:"L",unitG:1000,parMin:3,parMax:10,qty:6.5,storage:"refrigerated",zone:"z1",shelf:7,allergens:["milk"],sku:"",barcode:"",supplier:"s2",priceHist:[{d:"2026-04-01",p:3.60},{d:"2026-05-01",p:3.80}],lastReceived:_daysAgo(3)},
  {id:"i8",name:"Bacon",cat:"protein",cost:12.00,unit:"kg",unitG:1000,parMin:2,parMax:5,qty:3.0,storage:"refrigerated",zone:"z1",shelf:14,allergens:[],sku:"",barcode:"",supplier:"s3",priceHist:[{d:"2026-04-01",p:11.80},{d:"2026-05-01",p:12.00}],lastReceived:_daysAgo(6)},
  {id:"i9",name:"Sourdough bread",cat:"bakery",cost:3.50,unit:"pc",unitG:800,parMin:5,parMax:15,qty:8,storage:"trocken",zone:"z3",shelf:3,allergens:["gluten"],sku:"",barcode:"",supplier:"s4",priceHist:[{d:"2026-04-10",p:3.30},{d:"2026-05-05",p:3.50}],lastReceived:_daysAgo(1)},
  {id:"i10",name:"Fjord Coffee",cat:"beverage",cost:32.00,unit:"kg",unitG:1000,parMin:3,parMax:8,qty:4.5,storage:"trocken",zone:"z3",shelf:60,allergens:[],sku:"FJC-001",barcode:"",supplier:"s5",priceHist:[{d:"2026-03-15",p:30.00},{d:"2026-04-20",p:32.00}],lastReceived:_daysAgo(20)},
  {id:"i11",name:"Hummus",cat:"sauce",cost:7.00,unit:"kg",unitG:1000,parMin:2,parMax:5,qty:3.2,storage:"refrigerated",zone:"z1",shelf:7,allergens:["sesame"],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-04-15",p:7.00}],lastReceived:_daysAgo(2)},
  {id:"i12",name:"Oat milk",cat:"beverage",cost:2.20,unit:"L",unitG:1000,parMin:10,parMax:30,qty:18,storage:"trocken",zone:"z3",shelf:90,allergens:["gluten"],sku:"",barcode:"",supplier:"s2",priceHist:[{d:"2026-04-01",p:2.10},{d:"2026-05-01",p:2.20}],lastReceived:_daysAgo(30)},
  {id:"i13",name:"Seasonal fruit",cat:"produce",cost:5.00,unit:"kg",unitG:1000,parMin:3,parMax:8,qty:5.0,storage:"refrigerated",zone:"z1",shelf:4,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-04-15",p:4.50},{d:"2026-05-08",p:5.00}],lastReceived:_daysAgo(1)},
  {id:"i14",name:"Waffle batter",cat:"bakery",cost:3.20,unit:"L",unitG:1000,parMin:2,parMax:6,qty:3.5,storage:"refrigerated",zone:"z1",shelf:3,allergens:["gluten","eggs","milk"],sku:"",barcode:"",supplier:"s4",priceHist:[{d:"2026-04-20",p:3.20}],lastReceived:_daysAgo(10)},
  {id:"i15",name:"Merguez",cat:"protein",cost:11.00,unit:"kg",unitG:1000,parMin:1,parMax:4,qty:2.5,storage:"refrigerated",zone:"z1",shelf:7,allergens:[],sku:"",barcode:"",supplier:"s3",priceHist:[{d:"2026-04-10",p:10.50},{d:"2026-05-01",p:11.00}],lastReceived:_daysAgo(45)},
  {id:"i16",name:"Watercress",cat:"produce",cost:0.04,unit:"g",unitG:1,parMin:200,parMax:600,qty:350,storage:"refrigerated",zone:"z1",shelf:3,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-05-01",p:0.04}],lastReceived:_daysAgo(60)},
  {id:"i17",name:"Harissa-Joghurt",cat:"sauce",cost:5.50,unit:"kg",unitG:1000,parMin:1,parMax:3,qty:1.8,storage:"refrigerated",zone:"z1",shelf:5,allergens:["milk"],sku:"",barcode:"",supplier:"s2",priceHist:[{d:"2026-04-25",p:5.50}],lastReceived:_daysAgo(2)},
  {id:"i18",name:"Pea purée",cat:"sauce",cost:6.00,unit:"kg",unitG:1000,parMin:1,parMax:3,qty:1.5,storage:"refrigerated",zone:"z1",shelf:3,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-04-25",p:6.00}],lastReceived:_daysAgo(90)},
  {id:"i19",name:"Chili oil",cat:"sauce",cost:0.02,unit:"ml",unitG:1,parMin:500,parMax:1500,qty:800,storage:"trocken",zone:"z3",shelf:180,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-03-01",p:0.02}],lastReceived:_daysAgo(120)},
  {id:"i20",name:"Almonds",cat:"trocken",cost:14.00,unit:"kg",unitG:1000,parMin:1,parMax:3,qty:1.8,storage:"trocken",zone:"z3",shelf:180,allergens:["nuts"],sku:"",barcode:"",supplier:"s1",priceHist:[{d:"2026-03-15",p:13.00},{d:"2026-05-01",p:14.00}],lastReceived:_daysAgo(15)},
  {id:"i21",name:"Vanilla ice cream",cat:"tiefkühl",cost:8.00,unit:"L",unitG:1000,parMin:2,parMax:6,qty:3.5,storage:"tiefkühl",zone:"z2",shelf:180,allergens:["milk","eggs"],sku:"",barcode:"",supplier:"s2",priceHist:[{d:"2026-04-01",p:8.00}],lastReceived:_daysAgo(5)},
  {id:"i22",name:"Beetroot powder",cat:"trocken",cost:0.06,unit:"g",unitG:1,parMin:200,parMax:500,qty:320,storage:"trocken",zone:"z3",shelf:365,allergens:[],sku:"",barcode:"",supplier:"s5",priceHist:[{d:"2026-03-01",p:0.06}],lastReceived:_daysAgo(40)},
  /* ─── DRY GOODS — pantry essentials needed for from-scratch cooking ─── */
  {id:"i23",name:"All-purpose flour",cat:"trocken",cost:1.20,unit:"kg",unitG:1000,parMin:10,parMax:30,qty:18,storage:"trocken",zone:"z3",shelf:365,allergens:["gluten"],sku:"FLR-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-03-01",p:1.10},{d:"2026-05-01",p:1.20}],lastReceived:_daysAgo(25)},
  {id:"i24",name:"Sea salt",cat:"trocken",cost:2.50,unit:"kg",unitG:1000,parMin:3,parMax:8,qty:5,storage:"trocken",zone:"z3",shelf:730,allergens:[],sku:"SLT-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-01",p:2.50}],lastReceived:_daysAgo(8)},
  {id:"i25",name:"Black pepper, whole",cat:"trocken",cost:32.00,unit:"kg",unitG:1000,parMin:0.3,parMax:1,qty:0.6,storage:"trocken",zone:"z3",shelf:365,allergens:[],sku:"PEP-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-01",p:32.00}],lastReceived:_daysAgo(3)},
  {id:"i26",name:"Olive oil, extra virgin",cat:"trocken",cost:9.00,unit:"L",unitG:1000,parMin:5,parMax:15,qty:9,storage:"trocken",zone:"z3",shelf:540,allergens:[],sku:"OIL-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-03-01",p:8.50},{d:"2026-05-01",p:9.00}],lastReceived:_daysAgo(2)},
  {id:"i27",name:"Sugar, white",cat:"trocken",cost:1.10,unit:"kg",unitG:1000,parMin:5,parMax:15,qty:9,storage:"trocken",zone:"z3",shelf:730,allergens:[],sku:"SUG-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-01",p:1.10}],lastReceived:_daysAgo(100)},
  {id:"i28",name:"Yeast, active dry",cat:"trocken",cost:18.00,unit:"kg",unitG:1000,parMin:0.5,parMax:2,qty:1.2,storage:"trocken",zone:"z3",shelf:365,allergens:[],sku:"YST-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-03-01",p:18.00}],lastReceived:_daysAgo(50)},
  {id:"i29",name:"Rice, basmati",cat:"trocken",cost:3.20,unit:"kg",unitG:1000,parMin:5,parMax:15,qty:8,storage:"trocken",zone:"z3",shelf:730,allergens:[],sku:"RIC-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-15",p:3.20}],lastReceived:_daysAgo(4)},
  {id:"i30",name:"Chickpeas, dried",cat:"trocken",cost:2.80,unit:"kg",unitG:1000,parMin:3,parMax:8,qty:5,storage:"trocken",zone:"z3",shelf:730,allergens:[],sku:"CHK-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-15",p:2.80}],lastReceived:_daysAgo(7)},
  {id:"i31",name:"Pasta, penne",cat:"trocken",cost:1.80,unit:"kg",unitG:1000,parMin:5,parMax:15,qty:9,storage:"trocken",zone:"z3",shelf:730,allergens:["gluten"],sku:"PAS-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-15",p:1.80}],lastReceived:_daysAgo(14)},
  {id:"i32",name:"Tomatoes, canned",cat:"trocken",cost:2.40,unit:"kg",unitG:1000,parMin:5,parMax:20,qty:12,storage:"trocken",zone:"z3",shelf:730,allergens:[],sku:"TOM-001",barcode:"",supplier:"s4",priceHist:[{d:"2026-02-15",p:2.40}],lastReceived:_daysAgo(60)},
];

const initRecipes = [
  {id:"r1",name:"Avocado mit Chiliöl, Kresse & Mandeln",cat:"Greens",type:"dish",price:4.50,yield:1,status:"active",prep:5,cook:0,instructions:"Halve avocado, fan on plate, drizzle chili oil, top with watercress and toasted almonds.",comps:[
    {ingId:"i1",qty:1,unit:"pc"},{ingId:"i19",qty:10,unit:"ml"},{ingId:"i16",qty:15,unit:"g"},{ingId:"i20",qty:15,unit:"g"}]},
  {id:"r2",name:"Knuspriger Blumenkohl mit Harissa-Joghurt",cat:"Greens",type:"dish",price:5.00,yield:1,status:"active",prep:8,cook:12,instructions:"Break cauliflower into florets, toss in oil + spices, roast at 220°C until crispy edges. Serve on harissa yoghurt with almonds.",comps:[
    {ingId:"i4",qty:120,unit:"g"},{ingId:"i17",qty:40,unit:"g"},{ingId:"i20",qty:10,unit:"g"}]},
  {id:"r3",name:"Hausgebeizter Lachs mit Erbsenpüree",cat:"Meat & Fish",type:"dish",price:6.00,yield:1,status:"active",prep:5,cook:0,instructions:"Slice house-cured salmon thinly, fan over pea purée, garnish with watercress and lemon zest.",comps:[
    {ingId:"i2",qty:80,unit:"g"},{ingId:"i18",qty:50,unit:"g"},{ingId:"i16",qty:10,unit:"g"}]},
  {id:"r4",name:"Pochierte Bio-Eier",cat:"Eggs",type:"dish",price:4.00,yield:1,status:"active",prep:8,cook:0,instructions:"Bring water to gentle simmer with splash of vinegar. Crack eggs into cups, swirl water, slide eggs in. Cook 3 min for runny yolk.",comps:[
    {ingId:"i3",qty:2,unit:"pc"}]},
  {id:"r5",name:"Waffeln mit Saisonobst, Eis & Mascarpone",cat:"Bakery",type:"dish",price:6.50,yield:1,status:"active",prep:5,cook:8,instructions:"Ladle batter into hot waffle iron (4 min). Top with seasonal fruit, scoop of vanilla ice cream, dollop of mascarpone.",comps:[
    {ingId:"i14",qty:120,unit:"ml"},{ingId:"i13",qty:80,unit:"g"},{ingId:"i21",qty:60,unit:"ml"},{ingId:"i6",qty:30,unit:"g"}],batchRef:"rb1",portionWeight:20},
  {id:"r6",name:"Beetroot Latte",cat:"Drinks",type:"beverage",price:4.50,yield:1,status:"active",prep:3,cook:0,instructions:"Pull double espresso. Steam oat milk with beetroot powder. Pour over espresso, latte art.",comps:[
    {ingId:"i10",qty:18,unit:"g"},{ingId:"i12",qty:250,unit:"ml"},{ingId:"i22",qty:5,unit:"g"}]},
];

const SUPPLIERS=[
  {id:"s1",name:"Bioland Markt Berlin",contact:"Thomas",email:"bestellung@bioland-markt.de",phone:"+49 30 555 0101",lead:1,days:[1,3,5],terms:"Net 14",acctNo:"BIO-VEN-2024",products:["i1","i4","i11","i13","i16","i18","i19","i20"],rating:4.5,onTime:94,quality:4.7},
  {id:"s2",name:"Molkerei Brandenburg",contact:"Lisa",email:"orders@molkerei-bb.de",phone:"+49 30 555 0202",lead:2,days:[2,5],terms:"Net 14",acctNo:"MOL-VEN-2024",products:["i3","i6","i7","i12","i14","i17","i21"],rating:4.8,onTime:98,quality:4.9},
  {id:"s3",name:"Fischhandel Ostsee",contact:"Markus",email:"lachs@ostsee-fisch.de",phone:"+49 30 555 0303",lead:1,days:[1,4],terms:"Net 7",acctNo:"FIS-VEN-2024",products:["i2","i8","i15"],rating:4.6,onTime:91,quality:4.8},
  {id:"s4",name:"Bäckerei Neukölln",contact:"Anna B.",email:"bestellung@baeckerei-nk.de",phone:"+49 30 555 0404",lead:1,days:[1,2,3,4,5,6],terms:"Cash",acctNo:"BAK-VEN-2024",products:["i5","i9","i23","i24","i25","i26","i27","i28","i29","i30","i31","i32"],rating:4.4,onTime:96,quality:4.5},
  {id:"s5",name:"Fjord Coffee Roasters",contact:"Erik",email:"wholesale@fjordcoffee.de",phone:"+49 30 555 0505",lead:3,days:[2,4],terms:"Net 30",acctNo:"FJC-VEN-2024",products:["i10","i22"],rating:4.9,onTime:99,quality:5.0},
];

const WASTE_CATS=[
  {id:"wc1",name:"Overproduction",avoidable:true,color:"#FF5A5A"},
  {id:"wc2",name:"Spoilage",avoidable:true,color:"#FFB545"},
  {id:"wc3",name:"Plate waste",avoidable:true,color:"#A78BFA"},
  {id:"wc4",name:"Prep trim",avoidable:false,color:"#45A5FF"},
  {id:"wc5",name:"Expired",avoidable:true,color:"#F472B6"},
  {id:"wc6",name:"Cooking error",avoidable:true,color:"#E24B4A"},
  {id:"wc7",name:"Dropped",avoidable:true,color:"#6B7280"},
];

const initZones=[
  {id:"z1",name:"Walk-in Kühlraum",type:"cooler",tempMin:1,tempMax:4,current:3.2,ok:true},
  {id:"z2",name:"Tiefkühler",type:"freezer",tempMin:-22,tempMax:-18,current:-19.5,ok:true},
  {id:"z3",name:"Trockenlager",type:"dry_storage",tempMin:15,tempMax:25,current:21.0,ok:true},
  {id:"z4",name:"Prep Station",type:"prep_area",tempMin:18,tempMax:25,current:22.3,ok:true},
  {id:"z5",name:"Vitrine",type:"display",tempMin:2,tempMax:8,current:5.1,ok:true},
];

const LOCATIONS=[{id:"loc1",name:"Bodega Main Kitchen",addr:"Main location"},{id:"loc2",name:"Bodega Branch",addr:"Second location"}];

/* Initial specials — Discounted menu items.
   Two types:
   - {type:"discount"} : single recipe at a % off
   - {type:"combo"}    : pick N items from a chosen set for a fixed price
                         e.g. "Any 3 brunch plates for €15", "6 small bites for €25"
*/
const initSpecials=[
  {id:"sp1",type:"discount",recipeId:"r1",discountPct:30,active:true,note:"Brunch special"},
  {id:"sp2",type:"discount",recipeId:"r4",discountPct:25,active:true,note:"Weekday"},
  {id:"sp3",type:"combo",name:"Brunch trio",count:3,price:15.00,recipeIds:["r1","r2","r3","r4"],active:true,note:"Any 3 brunch plates"},
];

/* ═══ LOCALE — data-facing strings per language ═══ */
const LANGS=[
  {id:"en",label:"English",flag:"EN"},
  {id:"de",label:"Deutsch",flag:"DE"},
  {id:"fr",label:"Français",flag:"FR"},
  {id:"es",label:"Español",flag:"ES"},
  {id:"it",label:"Italiano",flag:"IT"},
];
const L10N={
  en:{revenue:"Revenue",foodCost:"Food Cost",waste:"Waste",inventory:"Inventory",total:"Total",order:"Order",submitted:"Submitted",received:"Received",account:"Account",notes:"Notes",date:"Date",qty:"Qty",price:"Price",margin:"Margin",items:"items",guests:"guests/day",savings:"Projected savings",weekLabel:["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]},
  de:{revenue:"Umsatz",foodCost:"Wareneinsatz",waste:"Abfall",inventory:"Inventar",total:"Gesamt",order:"Bestellung",submitted:"Gesendet",received:"Erhalten",account:"Konto",notes:"Notizen",date:"Datum",qty:"Menge",price:"Preis",margin:"margin",items:"items",guests:"Gäste/Tag",savings:"Prognostizierte Einsparungen",weekLabel:["So","Mo","Di","Mi","Do","Fr","Sa"]},
  fr:{revenue:"Chiffre d'affaires",foodCost:"Coût matières",waste:"Déchets",inventory:"Inventaire",total:"Total",order:"Commande",submitted:"Envoyée",received:"Reçue",account:"Compte",notes:"Notes",date:"Date",qty:"Qté",price:"Prix",margin:"margin",items:"articles",guests:"couverts/jour",savings:"Économies projetées",weekLabel:["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"]},
  es:{revenue:"Ingresos",foodCost:"Coste alimentos",waste:"Desperdicio",inventory:"Inventario",total:"Total",order:"Pedido",submitted:"Enviado",received:"Recibido",account:"Cuenta",notes:"Notas",date:"Fecha",qty:"Cant",price:"Precio",margin:"Margen",items:"artículos",guests:"clientes/día",savings:"Ahorro proyectado",weekLabel:["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"]},
  it:{revenue:"Ricavi",foodCost:"Costo cibo",waste:"Sprechi",inventory:"Inventario",total:"Totale",order:"Ordine",submitted:"Inviato",received:"Ricevuto",account:"Conto",notes:"Note",date:"Data",qty:"Qtà",price:"Prezzo",margin:"Margine",items:"articoli",guests:"coperti/giorno",savings:"Risparmio previsto",weekLabel:["Dom","Lun","Mar","Mer","Gio","Ven","Sab"]},
};

/* ═══ SIM TEMPLATES — realistic restaurant profiles ═══ */
const SIM_TEMPLATES=[
  {id:"burger",name:"Burger joint",Icon:Utensils,avgTicket:12.50,foodCostPct:30,itemsPerGuest:1.4,wasteStartPct:9,wasteEndPct:4,menuItems:[
    {name:"Classic Smash Burger",price:9.50,cost:2.85},{name:"Bacon Cheeseburger",price:11.00,cost:3.50},{name:"Veggie Burger",price:9.00,cost:2.40},
    {name:"Loaded Fries",price:5.50,cost:1.10},{name:"Onion Rings",price:4.50,cost:0.80},{name:"Milkshake",price:5.00,cost:1.20},
    {name:"Chicken Wings (6pc)",price:7.50,cost:2.10},{name:"Coleslaw",price:3.00,cost:0.45},{name:"Craft Soda",price:3.50,cost:0.60},
    {name:"Double Smash Burger",price:13.00,cost:4.20}]},
  {id:"italian",name:"Italian trattoria",Icon:Utensils,avgTicket:22.00,foodCostPct:28,itemsPerGuest:2.1,wasteStartPct:8,wasteEndPct:3.5,menuItems:[
    {name:"Bruschetta",price:7.00,cost:1.40},{name:"Caprese Salad",price:8.50,cost:2.10},{name:"Spaghetti Carbonara",price:13.00,cost:2.80},
    {name:"Margherita Pizza",price:11.00,cost:1.90},{name:"Risotto ai Funghi",price:14.00,cost:3.20},{name:"Ossobuco",price:22.00,cost:6.60},
    {name:"Tiramisu",price:7.50,cost:1.50},{name:"Panna Cotta",price:6.50,cost:1.00},{name:"House Wine (glass)",price:6.00,cost:1.50},
    {name:"Espresso",price:2.50,cost:0.35},{name:"Antipasti Plate",price:12.00,cost:3.60},{name:"Penne Arrabbiata",price:11.50,cost:2.00}]},
  {id:"tapas",name:"Tapas bar",Icon:Wine,avgTicket:28.00,foodCostPct:27,itemsPerGuest:3.8,wasteStartPct:10,wasteEndPct:4,menuItems:[
    {name:"Patatas Bravas",price:5.50,cost:0.90},{name:"Gambas al Ajillo",price:9.00,cost:3.00},{name:"Jamón Ibérico",price:14.00,cost:6.30},
    {name:"Croquetas (4pc)",price:6.00,cost:1.20},{name:"Tortilla Española",price:5.00,cost:0.85},{name:"Pimientos de Padrón",price:5.50,cost:0.70},
    {name:"Albondigas",price:7.00,cost:1.80},{name:"Pulpo a la Gallega",price:11.00,cost:4.00},{name:"Sangria (jug)",price:18.00,cost:4.50},
    {name:"Churros con Chocolate",price:6.00,cost:1.00},{name:"Manchego & Olives",price:7.50,cost:2.60}]},
  {id:"pizza",name:"Pizzeria",Icon:Pizza,avgTicket:15.00,foodCostPct:26,itemsPerGuest:1.6,wasteStartPct:7,wasteEndPct:3,menuItems:[
    {name:"Margherita",price:9.00,cost:1.60},{name:"Pepperoni",price:10.50,cost:2.10},{name:"Quattro Formaggi",price:11.00,cost:2.50},
    {name:"Prosciutto e Rucola",price:12.00,cost:3.00},{name:"Calzone",price:11.50,cost:2.40},{name:"Garlic Bread",price:4.50,cost:0.60},
    {name:"Caesar Salad",price:7.00,cost:1.40},{name:"Tiramisu",price:6.50,cost:1.20},{name:"Birra (draft)",price:4.50,cost:1.10},
    {name:"Marinara",price:8.00,cost:1.20}]},
  {id:"brunch",name:"Brunch / cafe",Icon:Coffee,avgTicket:18.00,foodCostPct:30,itemsPerGuest:2.0,wasteStartPct:9,wasteEndPct:4,menuItems:[
    {name:"Avocado Toast",price:9.00,cost:2.50},{name:"Eggs Benedict",price:12.00,cost:3.20},{name:"Pancake Stack",price:10.00,cost:1.80},
    {name:"Açaí Bowl",price:11.00,cost:3.00},{name:"Croissant & Jam",price:4.50,cost:0.90},{name:"Smoked Salmon Plate",price:14.00,cost:5.00},
    {name:"Flat White",price:4.00,cost:0.70},{name:"Fresh OJ",price:4.50,cost:1.20},{name:"Granola & Yoghurt",price:7.00,cost:1.40},
    {name:"Club Sandwich",price:11.00,cost:2.80},{name:"Beetroot Latte",price:4.50,cost:0.80}]},
  {id:"finedining",name:"Fine dining",Icon:ChefHat,avgTicket:65.00,foodCostPct:32,itemsPerGuest:3.5,wasteStartPct:6,wasteEndPct:2.5,menuItems:[
    {name:"Amuse-bouche",price:0,cost:1.80},{name:"Tuna Tartare",price:18.00,cost:6.00},{name:"Foie Gras Terrine",price:22.00,cost:8.80},
    {name:"Lobster Bisque",price:16.00,cost:5.20},{name:"Wagyu Striploin",price:48.00,cost:18.00},{name:"Dover Sole",price:38.00,cost:13.00},
    {name:"Duck Confit",price:32.00,cost:9.60},{name:"Tasting Cheese Plate",price:14.00,cost:5.00},{name:"Chocolate Fondant",price:12.00,cost:2.80},
    {name:"Petit Fours",price:0,cost:1.50},{name:"Wine Pairing (5 glasses)",price:45.00,cost:12.00}]},
  {id:"custom",name:"Custom / build your own",Icon:Edit3,avgTicket:0,foodCostPct:30,itemsPerGuest:2.0,wasteStartPct:8,wasteEndPct:4,menuItems:[]},
];

/* Settings helper — load/save from localStorage with fallback */
function loadSettings(){
  try{
    const raw=window.localStorage&&window.localStorage.getItem("gastriot_settings");
    if(raw)return JSON.parse(raw);
  }catch(e){}
  return null;
}
function saveSettings(s){
  try{window.localStorage&&window.localStorage.setItem("gastriot_settings",JSON.stringify(s));}catch(e){}
}
const DEFAULT_SETTINGS={
  lang:"en",
  account:{name:"",email:"",phone:"",restaurantName:"Bodega",password:""},
  locations:[{id:"loc1",name:"Main Kitchen",addr:""}],
};

/* Per-location data persistence — keyed by location id */
function saveLocData(locId,data){
  try{window.localStorage&&window.localStorage.setItem("gastriot_loc_"+locId,JSON.stringify(data));}catch(e){}
}
function loadLocData(locId){
  try{
    const raw=window.localStorage&&window.localStorage.getItem("gastriot_loc_"+locId);
    if(raw)return JSON.parse(raw);
  }catch(e){}
  return null;
}
function deleteLocData(locId){
  try{window.localStorage&&window.localStorage.removeItem("gastriot_loc_"+locId);}catch(e){}
}
function blankLocData(){
  return {ings:[],recipes:[],zones:[
    {id:genId(),name:"Walk-in cooler",type:"cooler",tempMin:1,tempMax:4,current:3.0,ok:true},
    {id:genId(),name:"Freezer",type:"freezer",tempMin:-22,tempMax:-18,current:-19.0,ok:true},
    {id:genId(),name:"Dry storage",type:"dry_storage",tempMin:15,tempMax:25,current:21.0,ok:true},
  ],wasteLogs:[],orders:[],shoppingList:[],alerts:[],haccpLogs:[],notifs:[],sales:[],dailyRevenue:0,specials:[],suppliers:[],recipeBook:[]};
}

/* ═══ TOUR — first-run guided walkthrough.
   Each step navigates to the relevant page so the user sees what's being described. ═══ */
const TOUR_STEPS=[
  {page:"dashboard",icon:"BarChart3",title:"Welcome to Gastriot",bullets:[
    "Kitchen intelligence built for your line.",
    "Tap any KPI tile to jump into the detail.",
    "Open this menu anytime by tapping the Gastriot logo.",
  ]},
  {page:"recipes",icon:"Camera",title:"Quick start: scan your menu",bullets:[
    "Fastest setup: Menu page → 'Scan a menu — take a photo'.",
    "Multi-page menu? Select all pages at once — they read together.",
    "Gastriot pulls out every dish, its price, and likely ingredients.",
    "Beverages are detected and set aside automatically (no food cost to track).",
    "Review the list, untick anything wrong, tap Import. Dishes are live.",
  ]},
  {page:"orders",icon:"Camera",title:"Quick start: scan an invoice",bullets:[
    "Orders → Receiving → 'Tap to photograph invoice'.",
    "Gastriot reads each line item and its price.",
    "Matches to existing stock, or creates new items automatically.",
    "This prices your ingredients — and every dish's margin appears instantly.",
  ]},
  {page:"dashboard",icon:"Bell",title:"Voice — your sous chef",bullets:[
    "Tap the mic and just talk — hands-free for a busy line.",
    "Ask: 'what's expiring today?', 'what's below par?', 'how much waste so far?'",
    "It answers out loud with the full list, reading the same live data you see.",
    "Log waste by voice: 'log two kilos chicken to spoilage'.",
    "Navigate: 'go to inventory', 'open orders'.",
  ]},
  {page:"pos",icon:"ShoppingCart",title:"POS",bullets:[
    "Tap a menu item to add to cart.",
    "Specials tab: discounts and 'X for €Y' combos with item picker.",
    "Inventory deducts automatically on charge.",
  ]},
  {page:"scale",icon:"Trash2",title:"Waste & the scale",bullets:[
    "Log waste by category, ingredient, and weight — or by voice.",
    "Pair a Bluetooth scale (Settings → Devices) to auto-fill weight.",
    "Avoidable waste tracks cost; unavoidable trim tracks weight only.",
    "Analytics shows exactly where money is leaking.",
  ]},
  {page:"inventory",icon:"Package",title:"Stock & 86'd",bullets:[
    "Filter by Fresh, Dry goods, Frozen, or Below par.",
    "Each item shows a live shelf-life countdown.",
    "Hit zero? The item is flagged '86'd — out of stock' until you reorder.",
    "Voice and the morning email both call out 86'd items by name.",
  ]},
  {page:"recipes",icon:"ChefHat",title:"Menu, cost & margin",bullets:[
    "Every dish shows live food cost and margin, pulled from current prices.",
    "Scan a menu to add dishes in bulk, or build one by hand.",
    "When an invoice changes a price, every affected margin updates itself.",
    "Status (draft / active / seasonal / archived) controls POS visibility.",
  ]},
  {page:"orders",icon:"Truck",title:"Orders",bullets:[
    "Shopping tab auto-populates from items below par, grouped by supplier.",
    "Build a manual order or auto-build from a supplier batch.",
    "Email sends a clean checklist. Receiving scans the invoice back in.",
  ]},
  {page:"settings",icon:"Bell",title:"Morning email digest",bullets:[
    "Settings → Alerts: get a daily email summary of your kitchen.",
    "Tick the sections you want: prep, expiring, below par, deliveries.",
    "Set the exact delivery time. Critical alerts always send instantly.",
    "Set the recipient — yourself, your chef, whoever needs it.",
  ]},
  {page:"settings",icon:"Users",title:"Team & roles",bullets:[
    "Settings → Account → Team & accounts (owners only).",
    "Add staff with a role: employee, chef, or owner.",
    "Employees run operations but don't see revenue or margins.",
    "Chefs see costs; owners see everything and manage the team.",
  ]},
  {page:"reports",icon:"FileText",title:"Reports",bullets:[
    "EOD / BOD / Weekly / Monthly / Annual templates.",
    "Print to PDF with branded letterhead, or download as HTML.",
    "Open the relevant tab and tap Print.",
  ]},
  {page:"safety",icon:"ShieldCheck",title:"Safety / HACCP",bullets:[
    "Log zone temperatures by typing or by voice ('temp 4.2').",
    "Out-of-range readings raise an alert automatically.",
    "Daily checklist + full HACCP log live here.",
  ]},
  {page:"dashboard",icon:"Check",title:"You're set",bullets:[
    "Tour won't auto-open again — replay it anytime from the logo menu.",
    "Fastest path to live: scan your menu, then scan a few invoices.",
    "Push, pull, and ping if anything breaks.",
  ]},
];

/* ═══ HELPERS ═══ */
function calcCompCost(comp, ings) {
  const ing=ings.find(i=>i.id===comp.ingId);if(!ing)return 0;
  const qtyG=comp.unit==="pc"?comp.qty*ing.unitG:comp.unit==="kg"||comp.unit==="L"?comp.qty*1000:comp.unit==="tsp"?comp.qty*5:comp.unit==="tbsp"?comp.qty*15:comp.qty;
  return (qtyG/ing.unitG)*ing.cost;
}
function calcRecipeCost(recipe, ings) { return recipe.comps.reduce((s,c)=>s+calcCompCost(c,ings),0); }
// A recipe component's qty, converted into the matched ingredient's OWN stock unit
// (same conversion calcCompCost uses for pricing) — so prep-list deduction and
// shortfall checks subtract the right amount even when a recipe calls for "200 g"
// of an ingredient whose inventory is tracked in "kg".
function compQtyInIngUnit(comp, ing){
  if(!ing)return 0;
  const qtyG=comp.unit==="pc"?comp.qty*ing.unitG:comp.unit==="kg"||comp.unit==="L"?comp.qty*1000:comp.unit==="tsp"?comp.qty*5:comp.unit==="tbsp"?comp.qty*15:comp.qty;
  return ing.unitG>0?qtyG/ing.unitG:comp.qty;
}
function getShift(){const h=new Date().getHours();return h<10?"morning":h<14?"lunch":h<17?"afternoon":h<22?"dinner":"late";}
function genId(){return `${Date.now()}-${Math.random().toString(36).slice(2,6)}`;}
function fmtEur(n){return `${CUR}${(n||0).toFixed(2)}`;}
function fmtDate(d){return new Date(d).toLocaleDateString("de-DE",{day:"2-digit",month:"short",year:"numeric"});}
function fmtDateShort(d){return new Date(d).toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit"});}
function todayISO(){return new Date().toISOString().slice(0,10);}
function catMeta(catId){return ING_CATS.find(c=>c.id===catId)||{id:catId,label:catId,Icon:Package,color:"mt"};}

/* Shelf-life engine — returns {daysLeft, halfLifeDays, temperatureStatus, riskLevel}
   Uses lastReceived timestamp for real-time countdown. Conservative on safety. */
function computeShelfLife(ing, zones){
  const cat=ing.cat;
  const rules=SHELF_RULES[cat];
  const totalShelf=ing.shelf||7;
  const halfDays=totalShelf/2;
  if(!rules) return {daysLeft:totalShelf,totalShelf,halfLifeDays:halfDays,temperatureStatus:"unknown",riskLevel:"low",elapsed:0};
  const storageKey=ing.storage;
  const rule=rules[storageKey];
  if(!rule) return {daysLeft:totalShelf,totalShelf,halfLifeDays:halfDays,temperatureStatus:"wrong_storage",riskLevel:"high",elapsed:0};
  const zone=zones.find(z=>z.id===ing.zone);
  let temperatureStatus="ideal", tempRisk="low", maxDays=rule.days;
  if(zone){
    const [lo,hi]=rule.ideal;
    if(zone.current<lo-2||zone.current>hi+2){temperatureStatus="out_of_range";tempRisk="high";maxDays=Math.round(rule.days*0.4);}
    else if(zone.current<lo||zone.current>hi){temperatureStatus="warning";tempRisk="medium";maxDays=Math.round(rule.days*0.7);}
  }
  // Real-time countdown from lastReceived
  const received=ing.lastReceived?new Date(ing.lastReceived).getTime():Date.now();
  const elapsedMs=Date.now()-received;
  const elapsedDays=elapsedMs/(86400000);
  const daysLeft=Math.max(0,Math.round((maxDays-elapsedDays)*10)/10);
  const halfLifeRemaining=Math.max(0,Math.round((rule.halfDays-elapsedDays)*10)/10);
  // Risk based on both temperature and time remaining
  let riskLevel=tempRisk;
  if(daysLeft<=0)riskLevel="expired";
  else if(daysLeft<=1||elapsedDays>=rule.halfDays)riskLevel=riskLevel==="high"?"high":"medium";
  return {daysLeft,totalShelf:maxDays,halfLifeDays:rule.halfDays,halfLifeRemaining,temperatureStatus,riskLevel,zone:zone?.name||"unknown",elapsed:Math.round(elapsedDays*10)/10};
}

/* Print helper — wraps a node ID in a clean printable window */
function printSection(elementId, title){
  const node=document.getElementById(elementId);
  if(!node){alert("Nichts zu drucken");return;}
  const sRaw=localStorage.getItem("gastriot_settings");
  const sObj=sRaw?JSON.parse(sRaw):{};
  const restName=(sObj.account&&(sObj.account.restaurantName||sObj.account.restaurant))||"Bodega";
  const w=window.open("","_blank","width=900,height=700");
  if(!w){alert("Pop-up blockiert. Bitte Pop-ups erlauben.");return;}
  w.document.write(`<!doctype html><html><head><title>${title}</title>
    <style>
      @page{margin:18mm}
      body{font-family:Georgia,'Times New Roman',serif;color:#1B2A4A;padding:0;margin:0;font-size:11pt;line-height:1.5}
      h1{font-family:'Playfair Display',Georgia,serif;font-style:italic;color:#D42B2B;border-bottom:2px solid #D42B2B;padding-bottom:8px;margin-bottom:18px;font-size:24pt}
      h2{font-family:Georgia,serif;color:#1B2A4A;margin-top:24px;margin-bottom:10px;font-size:14pt;border-bottom:1px solid #ccc;padding-bottom:4px}
      h3{font-family:Georgia,serif;color:#1B2A4A;margin-top:16px;margin-bottom:6px;font-size:11pt}
      table{border-collapse:collapse;width:100%;margin:8px 0;font-size:10pt}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
      th{background:#F5C518;color:#1B2A4A;font-weight:700}
      .meta{color:#666;font-size:9pt;margin-bottom:18px;font-style:italic}
      .footer{margin-top:32px;padding-top:8px;border-top:1px solid #ccc;color:#888;font-size:9pt;text-align:center}
      .row{display:flex;justify-content:space-between;margin:4px 0;padding:3px 0;border-bottom:1px dotted #eee}
      .kpi{display:inline-block;margin:6px 16px 6px 0;padding:8px 12px;border:1px solid #1B2A4A;border-radius:4px;min-width:120px}
      .kpi-l{font-size:8pt;color:#666;text-transform:uppercase;letter-spacing:0.5px}
      .kpi-v{font-size:14pt;font-weight:700;color:#D42B2B;font-family:'Courier New',monospace}
      .red{color:#D42B2B;font-weight:600}.green{color:#16A34A;font-weight:600}.amber{color:#D48B00;font-weight:600}
      ul{margin:4px 0 8px 20px}li{margin:2px 0}
      .logo-wrap{display:flex;align-items:center;gap:12px;margin-bottom:8px}
      .brand{font-family:'Playfair Display',Georgia,serif;font-style:italic;font-weight:900;color:#D42B2B;font-size:18pt}
      .tag{color:#888;font-size:9pt;font-style:italic}
      .no-print{display:none}
      @media print{button{display:none!important}}
    </style></head><body>
    <div class="logo-wrap">
      <div style="width:42px;height:42px;background:#F5C518;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Playfair Display',serif;font-style:italic;font-weight:900;color:#D42B2B;font-size:24pt">G</div>
      <div>
        <div class="brand">Gastriot</div>
        <div class="tag">Kitchen Intelligence — ${restName}</div>
      </div>
    </div>
    <h1>${title}</h1>
    <div class="meta">Generated ${new Date().toLocaleString("de-DE")}</div>
    ${node.innerHTML}
    <div class="footer">Gastriot · Kitchen Intelligence · ${restName} · ${new Date().toLocaleDateString("de-DE")}</div>
    </body></html>`);
  w.document.close();
  setTimeout(()=>{w.focus();w.print();},250);
}


/* ═══ GLOBAL VOICE NAVIGATION HOOK ═══
   Behavior:
   - Auto-starts on mount in continuous mode.
   - 5-minute idle timer auto-disables it (saves battery / mic indicator).
   - Touch-toggling it back ON puts it into LOCKED mode: stays on until either
     (a) a verbal "stop listening" / "disable voice" / "mute" command, or
     (b) another touch-toggle off.
   - In locked mode the 5-minute timer is bypassed.
*/
/* ═══ VOICE: kitchen-aware parsing helpers ═══
   The single biggest accuracy win in a loud kitchen is matching speech against
   the KNOWN vocabulary (your real inventory + units) instead of free dictation. */

// Levenshtein distance — how many edits between two words
function editDistance(a,b){
  a=a.toLowerCase();b=b.toLowerCase();
  const m=a.length,n=b.length;
  if(!m)return n; if(!n)return m;
  const d=Array.from({length:m+1},(_,i)=>[i,...Array(n).fill(0)]);
  for(let j=0;j<=n;j++)d[0][j]=j;
  for(let i=1;i<=m;i++)for(let j=1;j<=n;j++){
    const cost=a[i-1]===b[j-1]?0:1;
    d[i][j]=Math.min(d[i-1][j]+1,d[i][j-1]+1,d[i-1][j-1]+cost);
  }
  return d[m][n];
}

// Fuzzy-match a spoken phrase against a list of {name} items. Returns best match or null.
function fuzzyMatchItem(spoken,items){
  if(!spoken||!items||!items.length)return null;
  spoken=spoken.toLowerCase().trim();
  let best=null,bestScore=Infinity;
  for(const it of items){
    const name=(it.name||"").toLowerCase();
    if(!name)continue;
    if(name===spoken||name.includes(spoken)||spoken.includes(name))return it;
    const whole=editDistance(spoken,name);
    let wordBest=Infinity;
    for(const w of name.split(/\s+/)) for(const sw of spoken.split(/\s+/)){
      if(w.length<3||sw.length<3)continue;
      wordBest=Math.min(wordBest,editDistance(sw,w));
    }
    const score=Math.min(whole,wordBest+1);
    const tol=Math.max(2,Math.floor(name.length*0.35));
    if(score<bestScore&&score<=tol){bestScore=score;best=it;}
  }
  return best;
}

// Convert spoken number words to digits: "two and a half" -> 2.5
const NUM_WORDS={zero:0,one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,seventeen:17,eighteen:18,nineteen:19,twenty:20,thirty:30,forty:40,fifty:50,sixty:60,seventy:70,eighty:80,ninety:90,hundred:100,a:1,an:1};
function parseSpokenNumber(text){
  const numMatch=text.match(/(\d+(?:[.,]\d+)?)/);
  let base=numMatch?parseFloat(numMatch[1].replace(",",".")):null;
  if(base===null){
    const words=text.split(/\s+/);
    let total=0,found=false;
    for(const w of words){if(NUM_WORDS[w]!==undefined){total+=NUM_WORDS[w];found=true;}}
    if(found)base=total;
  }
  if(base===null)return null;
  if(/\b(and a half|point five)\b/.test(text))base+=0.5;
  if(/\b(and a quarter)\b/.test(text))base+=0.25;
  if(/\b(and three quarters)\b/.test(text))base+=0.75;
  return base;
}

// Detect unit
function parseSpokenUnit(text){
  if(/\b(kilo|kilos|kilogram|kilograms|kg|kgs)\b/.test(text))return {unit:"kg",toG:1000};
  if(/\b(gram|grams|gramme|grammes)\b/.test(text))return {unit:"g",toG:1};
  if(/\b(piece|pieces|pcs|each|unit|units)\b/.test(text))return {unit:"pc",toG:null};
  if(/\b(liter|liters|litre|litres)\b/.test(text))return {unit:"L",toG:1000};
  if(/\b(milliliter|millilitre|ml)\b/.test(text))return {unit:"ml",toG:1};
  return null;
}

// Speak text aloud via the browser — free, reliable. Hardened against the
// common failure modes: voices not loaded yet, engine paused, empty queue.
let __voicesReady=false;
function __primeVoices(){
  try{
    if(!window.speechSynthesis)return;
    const v=window.speechSynthesis.getVoices();
    if(v&&v.length)__voicesReady=true;
    window.speechSynthesis.onvoiceschanged=()=>{__voicesReady=true;};
  }catch(e){}
}
if(typeof window!=="undefined")__primeVoices();

function speak(text,opts){
  opts=opts||{};
  try{
    if(!window.speechSynthesis||!text)return;
    const synth=window.speechSynthesis;
    const run=()=>{
      try{
        synth.cancel(); // never stack utterances
        const u=new SpeechSynthesisUtterance(String(text));
        u.rate=opts.rate||1.02;u.pitch=1;u.volume=1;u.lang="en-US";
        // Prefer a clear English voice if available
        const voices=synth.getVoices()||[];
        const pref=voices.find(v=>/en[-_]?(US|GB)/i.test(v.lang)&&/female|samantha|google|natural|aria|jenny/i.test(v.name))
                 ||voices.find(v=>/en[-_]?(US|GB)/i.test(v.lang))
                 ||voices.find(v=>/^en/i.test(v.lang));
        if(pref)u.voice=pref;
        if(opts.onEnd)u.onend=opts.onEnd;
        u.onerror=()=>{ if(opts.onEnd)opts.onEnd(); };
        // Safari/Chrome sometimes leave the engine paused — nudge it
        if(synth.paused)synth.resume();
        synth.speak(u);
        // Watchdog: if it didn't start within 250ms, retry once
        setTimeout(()=>{ if(!synth.speaking&&!synth.pending){ try{synth.resume();synth.speak(u);}catch(e){} } },250);
      }catch(e){}
    };
    if(__voicesReady||(synth.getVoices()||[]).length){run();}
    else{
      // Voices not loaded — wait for them, then speak
      synth.onvoiceschanged=()=>{__voicesReady=true;run();};
      setTimeout(run,300); // fallback if the event never fires
    }
  }catch(e){}
}

function useVoiceNav({onPage, onTemp, onCommand, items, onWaste, settings, queryData}){
  const [listening,setListening]=useState(false);
  const [locked,setLocked]=useState(false);
  const [transcript,setTranscript]=useState("");
  const [pending,setPending]=useState(null); // {summary, action} awaiting yes/no
  const pendingRef=useRef(null);
  const itemsRef=useRef(items);
  const settingsRef=useRef(settings);
  const queryDataRef=useRef(queryData);
  const onWasteRef=useRef(onWaste);
  const onPageRef=useRef(onPage);
  const onTempRef=useRef(onTemp);
  const onCommandRef=useRef(onCommand);
  useEffect(()=>{itemsRef.current=items;},[items]);
  useEffect(()=>{settingsRef.current=settings;},[settings]);
  useEffect(()=>{pendingRef.current=pending;},[pending]);
  useEffect(()=>{queryDataRef.current=queryData;});
  useEffect(()=>{onWasteRef.current=onWaste;});
  useEffect(()=>{onPageRef.current=onPage;});
  useEffect(()=>{onTempRef.current=onTemp;});
  useEffect(()=>{onCommandRef.current=onCommand;});
  const recRef=useRef(null);
  const lockedRef=useRef(false);
  const idleTimerRef=useRef(null);
  const restartTimerRef=useRef(null);
  const stopRequestedRef=useRef(false);
  const supportedRef=useRef(false);

  // Keep lockedRef in sync so handlers always see latest value
  useEffect(()=>{lockedRef.current=locked;},[locked]);

  const IDLE_MS=5*60*1000; // 5 minutes

  const clearIdleTimer=()=>{if(idleTimerRef.current){clearTimeout(idleTimerRef.current);idleTimerRef.current=null;}};
  const resetIdleTimer=()=>{
    clearIdleTimer();
    if(lockedRef.current)return; // locked = no auto-stop
    idleTimerRef.current=setTimeout(()=>{
      stopRequestedRef.current=true;
      try{recRef.current&&recRef.current.stop();}catch(e){}
      setListening(false);
      if(onCommand)onCommand("Voice paused (5-min idle). Tap mic to re-enable.");
    },IDLE_MS);
  };

  const startListening=()=>{
    if(!recRef.current)return;
    stopRequestedRef.current=false;
    try{recRef.current.start();setListening(true);resetIdleTimer();}catch(e){
      // Already started, or transient error — ignore
    }
  };

  const stopListening=()=>{
    stopRequestedRef.current=true;
    clearIdleTimer();
    try{recRef.current&&recRef.current.stop();}catch(e){}
    setListening(false);
  };

  useEffect(()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){return;}
    supportedRef.current=true;
    const r=new SR();
    r.continuous=true;
    r.interimResults=false;
    r.lang="en-US";

    r.onresult=(e)=>{
      // In continuous mode, results is cumulative — grab the latest
      const lastResultIdx=e.results.length-1;
      const text=e.results[lastResultIdx][0].transcript.toLowerCase().trim();
      setTranscript(text);
      resetIdleTimer(); // user activity — reset idle countdown

      // ─── CONFIRMATION LOOP: if a command is awaiting yes/no, handle that first ───
      if(pendingRef.current){
        if(/\b(yes|yeah|yep|confirm|correct|do it|go|okay|ok)\b/.test(text)){
          const p=pendingRef.current;setPending(null);pendingRef.current=null;
          try{p.action();}catch(e){}
          speak(p.done||"Done.");
          if(onCommandRef.current)onCommandRef.current(p.done||"Done.");
          return;
        }
        if(/\b(no|nope|cancel|stop|wrong|nevermind|never mind)\b/.test(text)){
          setPending(null);pendingRef.current=null;
          speak("Cancelled.");
          if(onCommandRef.current)onCommandRef.current("Cancelled.");
          return;
        }
        // anything else while pending: re-prompt briefly, keep waiting
        return;
      }

      // Stop / mute commands — verbal off-switch (works in locked mode too)
      if(/\b(stop listening|disable voice|mute mic|turn off voice|stop voice)\b/.test(text)){
        setLocked(false);lockedRef.current=false;
        stopListening();
        if(onCommandRef.current)onCommandRef.current("Voice disabled by command.");
        return;
      }

      // ─── NAVIGATION INTENT (highest priority after confirm/stop) ───
      // "go to waste", "open inventory" must beat the waste-LOG handler,
      // since "go to waste" contains the word "waste".
      const navIntent=/\b(go to|goto|open|show me|navigate|take me to|switch to|bring up|jump to)\b/.test(text);
      if(navIntent){
        const navMap={"home":"dashboard","dashboard":"dashboard","pos":"pos","sales":"pos","register":"pos","cash":"pos","scale":"scale","waste":"scale","wastage":"scale","inventory":"inventory","stock":"inventory","recipe book":"recipebook","recipebook":"recipebook","recipes":"recipes","menu":"recipes","orders":"orders","ordering":"orders","purchasing":"orders","prep":"prep","devices":"devices","suppliers":"suppliers","purveyors":"suppliers","vendors":"suppliers","safety":"safety","haccp":"safety","report":"reports","reports":"reports","shopping":"orders"};
        const keys=Object.keys(navMap).sort((a,b)=>b.length-a.length);
        for(const key of keys){
          if(text.includes(key)){
            if(onPageRef.current)onPageRef.current(navMap[key]);
            if(onCommandRef.current)onCommandRef.current(`Going to ${navMap[key]}`);
            return;
          }
        }
      }

      // ─── STATUS QUERIES — app answers out loud (full list, spoken + shown) ───
      if(queryDataRef.current&&/\b(what|what's|whats|how much|how many|tell me|status|do we have|give me|list|read)\b/.test(text)){
        let kind=null;
        if(/\b(expir|expiring|going off|going bad|use today|use it|short)\b/.test(text))kind="expiring";
        else if(/\b(waste|wasted|thrown|binned|trash|bin today)\b/.test(text))kind="waste";
        else if(/\b(low|below par|reorder|running out|out of stock|need to order|shopping)\b/.test(text))kind="low";
        else if(/\b(revenue|sales|takings|made today|money today)\b/.test(text))kind="revenue";
        else if(/\b(prep|to prep|prep list|batches)\b/.test(text))kind="prep";
        if(kind){
          const ans=queryDataRef.current(kind);
          speak(ans);                         // hear the whole thing
          if(onCommandRef.current)onCommandRef.current(ans);        // see the whole thing
          return;
        }
      }

      // ─── WASTE LOGGING BY VOICE (priority #1) ───
      // Understands: amount, unit, item, AND category + avoidable/unavoidable bin.
      if(onWasteRef.current&&/\b(waste|wasted|bin|binned|throw|threw|chuck|log|record|scrap|trim)\b/.test(text)){
        const amount=parseSpokenNumber(text);
        const unitInfo=parseSpokenUnit(text);
        // Detect spoken waste category against the known categories
        const CATS=(window.__WASTE_CATS||[]);
        let spokenCat=null;
        for(const c of CATS){
          const cn=c.name.toLowerCase();
          if(text.includes(cn)||cn.split(" ").every(w=>w.length>2&&text.includes(w))){spokenCat=c;break;}
        }
        // Common synonyms → category
        if(!spokenCat){
          if(/\b(spoiled|spoilage|off|rotten|moldy|mouldy)\b/.test(text))spokenCat=CATS.find(c=>/spoil/i.test(c.name));
          else if(/\b(overproduc|made too much|over made|excess)\b/.test(text))spokenCat=CATS.find(c=>/overprod/i.test(c.name));
          else if(/\b(plate|left on plate|customer)\b/.test(text))spokenCat=CATS.find(c=>/plate/i.test(c.name));
          else if(/\b(trim|trimming|peeling|offcut|off cut)\b/.test(text))spokenCat=CATS.find(c=>/trim/i.test(c.name));
          else if(/\b(expired|out of date|past date)\b/.test(text))spokenCat=CATS.find(c=>/expir/i.test(c.name));
          else if(/\b(burnt|burned|cooking error|overcook|ruined)\b/.test(text))spokenCat=CATS.find(c=>/cook/i.test(c.name));
          else if(/\b(dropped|fell|spilled|spilt)\b/.test(text))spokenCat=CATS.find(c=>/drop/i.test(c.name));
        }
        // Detect avoidable/unavoidable bin explicitly if stated
        let bin=null;
        if(/\bunavoidable\b/.test(text))bin=false;
        else if(/\bavoidable\b/.test(text))bin=true;

        let itemPhrase=text
          .replace(/\b(log|record|waste|wasted|bin|binned|throw|threw|chuck|scrap|trim|to|the|a|an|of|please|some|as|into|avoidable|unavoidable|and a half|and a quarter|and three quarters|point five)\b/g,"")
          .replace(/\b(kilo|kilos|kilogram|kilograms|kg|kgs|gram|grams|gramme|grammes|piece|pieces|pcs|each|unit|units|liter|liters|litre|litres|milliliter|millilitre|ml)\b/g,"")
          .replace(/\d+(?:[.,]\d+)?/g,"")
          .replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred)\b/g,"");
        // Strip any spoken category words from the item phrase too
        for(const c of CATS)itemPhrase=itemPhrase.replace(new RegExp("\\b"+c.name.toLowerCase().replace(/[^a-z ]/g,"")+"\\b","g"),"");
        itemPhrase=itemPhrase.replace(/\b(spoiled|spoilage|rotten|overproduction|plate|expired|burnt|burned|dropped|cooking error)\b/g,"").replace(/\s+/g," ").trim();

        const matched=fuzzyMatchItem(itemPhrase,itemsRef.current||[]);
        if(matched&&amount!==null){
          const unit=unitInfo?unitInfo.unit:(matched.unit||"g");
          const grams=unitInfo&&unitInfo.toG?amount*unitInfo.toG:(unit==="kg"?amount*1000:amount);
          // Resolve category + avoidability
          const cat=spokenCat||CATS[0]||{name:"Spoilage",avoidable:true};
          const avoidable=bin!==null?bin:(cat.avoidable!==false);
          const confirmOn=!settingsRef.current||settingsRef.current.voiceConfirm!==false;
          const binWord=avoidable?"avoidable":"unavoidable";
          const summary=`Logging ${amount} ${unit} ${matched.name} to waste, ${cat.name}, ${binWord}`;
          const doLog=()=>onWasteRef.current(matched,amount,unit,grams,cat,avoidable);
          // Full data-point readout once registered
          const readout=`Logged: ${matched.name}, ${amount} ${unit}, category ${cat.name}, ${binWord} waste${avoidable?`, cost will be calculated`:`, no cost — unavoidable`}.`;
          if(confirmOn){
            setPending({summary,action:doLog,done:readout});
            pendingRef.current={summary,action:doLog,done:readout};
            speak(`${summary}. Say yes to confirm.`);
            if(onCommandRef.current)onCommandRef.current(`${summary} — say "yes" to confirm`);
          }else{
            doLog();speak(readout);
            if(onCommandRef.current)onCommandRef.current(readout);
          }
          return;
        }
        if(/\b(waste|wasted|bin|log)\b/.test(text)){
          speak("Didn't catch the item and amount. Try: log two kilos chicken to spoilage.");
          if(onCommandRef.current)onCommandRef.current('Try: "log two kilos chicken to spoilage"');
          return;
        }
      }

      // Temperature pattern: "temperature 4.2", "temp minus 18", "fridge 3 degrees"
      const tempMatch=text.match(/(?:temperature|temp|grad|degrees?)\s*(?:is\s+)?(minus\s+)?(-?\d+(?:[.,]\d+)?)/i)||text.match(/(-?\d+(?:[.,]\d+)?)\s*(?:degrees?|grad|celsius)/i);
      if(tempMatch&&onTemp){
        const val=parseFloat((tempMatch[1]?"-":"")+tempMatch[2].replace(",","."));
        onTemp(val);
        return;
      }

      // Page navigation
      const pageMap={"home":"dashboard","dashboard":"dashboard","pos":"pos","sales":"pos","register":"pos","cash":"pos","scale":"scale","waste":"scale","wastage":"scale","inventory":"inventory","stock":"inventory","recipes":"recipes","menu":"recipes","recipebook":"recipebook","recipe book":"recipebook","orders":"orders","ordering":"orders","purchasing":"orders","prep":"prep","devices":"devices","suppliers":"suppliers","purveyors":"suppliers","vendors":"suppliers","safety":"safety","temperature":"safety","temp":"safety","haccp":"safety","report":"reports","reports":"reports","specials":"pos","shopping":"orders","list":"orders"};
      for(const key of Object.keys(pageMap)){
        if(text.includes(key)){
          if(onPageRef.current)onPageRef.current(pageMap[key]);
          if(onCommandRef.current)onCommandRef.current(`Navigating to ${pageMap[key]}`);
          return;
        }
      }
    };

    r.onerror=(e)=>{
      // On most "no-speech" or transient errors, continuous mode self-recovers via onend.
      // Permission errors are terminal — surface them.
      if(e.error==="not-allowed"||e.error==="service-not-allowed"){
        stopRequestedRef.current=true;
        setListening(false);
        if(onCommandRef.current)onCommandRef.current("Microphone permission denied.");
      }
    };

    r.onend=()=>{
      // Continuous mode browsers (esp. iOS/Safari) frequently end after ~60s of silence.
      // If we didn't ask it to stop, restart after a brief tick.
      if(stopRequestedRef.current){
        setListening(false);
        return;
      }
      if(restartTimerRef.current)clearTimeout(restartTimerRef.current);
      restartTimerRef.current=setTimeout(()=>{
        try{r.start();}catch(e){setListening(false);}
      },300);
    };

    recRef.current=r;

    // Auto-start on mount. Wrap in a tick so the browser is ready.
    const bootTimer=setTimeout(()=>{startListening();},400);

    return ()=>{
      clearTimeout(bootTimer);
      clearIdleTimer();
      if(restartTimerRef.current)clearTimeout(restartTimerRef.current);
      stopRequestedRef.current=true;
      try{r.stop();}catch(e){}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const toggle=()=>{
    if(!recRef.current){alert("Voice commands need a modern browser (Chrome/Edge). Speech recognition not available here.");return;}
    if(listening){
      // Touch-off: clears locked state too
      setLocked(false);lockedRef.current=false;
      stopListening();
    }else{
      // Touch re-enable = LOCKED mode (no auto-disable until voice/touch off)
      setLocked(true);lockedRef.current=true;
      startListening();
      if(onCommand)onCommand('Voice locked on. Say "stop listening" or tap mic to turn off.');
    }
  };

  return {listening,locked,transcript,pending,toggle,supported:supportedRef.current};
}

/* ═══ MAIN APP ═══ */
export default function App() {
  const [logged,setLogged]=useState(()=>{try{return localStorage.getItem("gastriot_logged")==="1";}catch{return false;}});
  const [gateOk,setGateOk]=useState(()=>{try{return localStorage.getItem("gastriot_gate")==="1";}catch{return false;}});
  const [page,setPage]=useState("dashboard");
  const [menuOpen,setMenuOpen]=useState(false);
  const [tourOpen,setTourOpen]=useState(false);
  const [tourStep,setTourStep]=useState(0);
  const [clock,setClock]=useState("");
  const [dark,setDark]=useState(()=>{try{return localStorage.getItem("gastriot_theme")==="dark";}catch{return false;}});
  const [cur,setCur]=useState(()=>{try{return localStorage.getItem("gastriot_cur")||"€";}catch{return "€";}});
  const [loc,setLoc]=useState(LOCATIONS[0]);
  T=dark?DARK:LIGHT;
  CUR=cur;
  inp=inp_fn(T);
  const [ings,setIngs]=useState(initIngs);
  const [recipes,setRecipes]=useState(initRecipes);
  const [zones,setZones]=useState(initZones);
  const [wasteLogs,setWasteLogs]=useState([]);
  const [orders,setOrders]=useState([]);
  const [shoppingList,setShoppingList]=useState([]);
  const [alerts,setAlerts]=useState([]);
  const [haccpLogs,setHaccpLogs]=useState([]);
  const [notifs,setNotifs]=useState([]);
  const [sales,setSales]=useState([]);
  const [dailyRevenue,setDailyRevenue]=useState(0);
  const [specials,setSpecials]=useState(initSpecials);
  const [recipeBook,setRecipeBook]=useState([
    {id:"rb1",name:"Waffle batter",batchTotal:12000,batchUnit:"ml",shelfDays:3,instructions:"Mix flour, eggs, milk, butter, sugar, baking powder. Rest 30 min.",comps:[{ingId:"i14",qty:2000,unit:"ml"},{ingId:"i3",qty:6,unit:"pc"},{ingId:"i13",qty:500,unit:"g"}],batchRemaining:12000,batchMade:new Date().toISOString()},
    {id:"rb2",name:"Harissa yoghurt",batchTotal:2000,batchUnit:"g",shelfDays:5,instructions:"Mix yoghurt with harissa paste, lemon juice, salt. Refrigerate.",comps:[{ingId:"i17",qty:1800,unit:"g"}],batchRemaining:2000,batchMade:new Date().toISOString()},
    {id:"rb3",name:"House-cured salmon",batchTotal:3000,batchUnit:"g",shelfDays:7,instructions:"Coat salmon in salt-sugar cure. Refrigerate 48h. Rinse, slice thin.",comps:[{ingId:"i2",qty:3000,unit:"g"}],batchRemaining:3000,batchMade:new Date().toISOString()},
  ]);
  const [locSuppliers,setLocSuppliers]=useState(SUPPLIERS);
  const [bleDevice,setBleDevice]=useState(null);
  const [bleWeight,setBleWeight]=useState(null);
  const [bleTempDevice,setBleTempDevice]=useState(null);
  const [bleTempReading,setBleTempReading]=useState(null);
  const [wasteBins,setWasteBins]=useState([
    {id:"bin1",name:"Unavoidable waste",nfcTag:""},
    {id:"bin2",name:"Avoidable waste",nfcTag:""},
    {id:"bin3",name:"Paper / packaging",nfcTag:""},
  ]);
  const [toast,setToast]=useState("");
  // Real-time clock — triggers re-render every 60s so shelf life countdowns are live
  const [clockTick,setClockTick]=useState(0);
  useEffect(()=>{const t=setInterval(()=>setClockTick(c=>c+1),60000);return ()=>clearInterval(t);},[]);
  const [settings,setSettingsRaw]=useState(()=>loadSettings()||DEFAULT_SETTINGS);
  const [simResult,setSimResult]=useState(null);
  const lang=settings.lang||"en";
  const t10n=L10N[lang]||L10N.en;
  const locations=settings.locations||DEFAULT_SETTINGS.locations;

  const setSettings=(patch)=>{
    setSettingsRaw(p=>{const next=typeof patch==="function"?patch(p):{...p,...patch};saveSettings(next);return next;});
  };
  const setLocations=(fn)=>setSettings(p=>({...p,locations:fn(p.locations||[])}));

  // Snapshot the current location's data
  // Strip heavy base64 image data URIs before sending to the server. Photos are
  // display-only and live in local storage on the device; including them in the
  // sync blob can blow past the server body limit (HTTP 413) and fail the whole
  // sync. We keep everything else intact.
  const stripPhotos=(arr)=>Array.isArray(arr)?arr.map(r=>{
    if(r&&typeof r.photo==="string"&&r.photo.startsWith("data:"))return {...r,photo:""};
    return r;
  }):arr;
  const snapshotCurrent=()=>({ings,recipes,zones,wasteLogs,orders,shoppingList,alerts,haccpLogs,notifs,sales,dailyRevenue,specials,suppliers:locSuppliers,recipeBook,account:settings.account||{}});
  const snapshotForSync=()=>{const s=snapshotCurrent();return {...s,recipes:stripPhotos(s.recipes)};};

  // Import a scanned menu: create any missing ingredients, then create recipes
  // that link to them — so margins compute immediately once invoices price them.
  // Throw away expired stock in one tap. FIFO-correct: only the expired lot(s)
  // are binned (lots received before the shelf-life cutoff), newer lots stay.
  // If the item has no lot breakdown, the whole remaining qty is binned.
  // Logged as AVOIDABLE waste (expired = it could have been used in time).
  const throwAwayExpired=(item)=>{
    const shelf=computeShelfLife(item,zones);
    const maxDays=shelf.totalShelf||item.shelf||7;
    const cutoffMs=Date.now()-maxDays*86400000; // anything received before this is expired
    const lots=item.lots||[];
    let binnedQty=0, keptLots=[];
    if(lots.length>0){
      lots.forEach(lot=>{
        const recMs=lot.received?new Date(lot.received).getTime():0;
        if(recMs<=cutoffMs){ binnedQty+=lot.qty; } // expired lot → bin it
        else { keptLots.push(lot); }                // still good → keep
      });
      // Safety: if nothing matched as expired but the item card says expired,
      // bin the oldest lot so the action always does something sensible.
      if(binnedQty===0&&shelf.riskLevel==="expired"&&lots.length){
        const sorted=[...lots].sort((a,b)=>new Date(a.received||0)-new Date(b.received||0));
        binnedQty=sorted[0].qty;
        keptLots=sorted.slice(1);
      }
    }else{
      // No lot breakdown — the whole quantity is the expired stock
      binnedQty=item.qty||0;
      keptLots=[];
    }
    binnedQty=Math.round(binnedQty*100)/100;
    if(binnedQty<=0){showToast("Nothing expired to throw away");return;}

    const isPc=item.unit==="pc";
    const grams=isPc?binnedQty*(item.unitG||1):binnedQty*(item.unit==="kg"||item.unit==="L"?1000:item.unitG||1);
    const cost=Math.round((item.cost||0)*binnedQty*100)/100;
    // Log the waste entry (avoidable, category Expired)
    const entry={
      id:genId(),
      ingredient:item.name,
      weight:Math.round(grams),
      pieces:isPc?binnedQty:undefined,
      category:"Expired",
      avoidable:true,
      cost,
      ts:Date.now(),
      via:"throwaway",
    };
    let savedWasteLogs=null,savedIngs=null;
    setWasteLogs(prev=>{savedWasteLogs=[entry,...prev];return savedWasteLogs;});
    // Update the item: remove expired lots, recompute qty + lastReceived from kept lots
    setIngs(prev=>{
      const newIngs=prev.map(x=>{
        if(x.id!==item.id)return x;
        const keptQty=keptLots.length?Math.round(keptLots.reduce((s,l)=>s+l.qty,0)*100)/100:(lots.length?0:0);
        // newest kept lot's received date drives the fresh shelf-life countdown
        const newestReceived=keptLots.length
          ? keptLots.map(l=>l.received).filter(Boolean).sort().slice(-1)[0]||x.lastReceived
          : x.lastReceived;
        return {
          ...x,
          qty:keptQty,
          lots:keptLots,
          lastReceived:keptLots.length?newestReceived:x.lastReceived,
          eightySixed:keptQty<=0,
        };
      });
      savedIngs=newIngs;
      return newIngs;
    });
    addNotif(`🗑️ Threw away expired ${item.name} — ${binnedQty} ${item.unit} (${fmtEur(cost)} avoidable waste)`);
    showToast(`Tossed ${binnedQty} ${item.unit} ${item.name}${keptLots.length?" — fresh stock kept":""}`);
    setTimeout(()=>forceSave({wasteLogs:savedWasteLogs,ings:savedIngs}),0);
  };

  const importScannedMenu=({dishes,newIngredients})=>{
    let workingIngs=ings;
    const nameToId={};
    ings.forEach(i=>{nameToId[i.name.toLowerCase()]=i.id;});
    // Create missing ingredients (cost 0 until an invoice prices them)
    if(newIngredients&&newIngredients.length){
      const created=newIngredients.map(ni=>{
        const id=genId();
        nameToId[ni.name.toLowerCase()]=id;
        return {id,name:ni.name,cat:"Uncategorized",unit:ni.unit||"g",qty:0,cost:0,parMin:0,parMax:0,
          shelf:7,unitG:(ni.unit==="kg"||ni.unit==="L")?1000:1,lots:[],lastReceived:null,priceHist:[],sku:"",eightySixed:false,needsPrice:true};
      });
      workingIngs=[...ings,...created];
      setIngs(workingIngs);
    }
    // Create the recipes
    const newRecipes=dishes.map(d=>({
      id:genId(),name:d.name,cat:d.cat||"Greens",type:"dish",
      price:parseFloat(d.price)||0,yield:1,status:"active",prep:0,cook:0,instructions:"",
      comps:(d.comps||[]).map(c=>({ingId:c.ingId||nameToId[(c.name||"").toLowerCase()]||"",qty:parseFloat(c.qty)||0,unit:c.unit||"g"})).filter(c=>c.ingId),
      batchRef:"",portionWeight:"",
    }));
    setRecipes(p=>[...p,...newRecipes]);
    const missingPrices=workingIngs.filter(i=>i.needsPrice).length;
    addNotif(`Menu imported — ${newRecipes.length} dishes added${missingPrices>0?`. ${missingPrices} ingredients need pricing (scan an invoice to set costs).`:""}`);
    showToast(`${newRecipes.length} dishes imported`);
    forceSave({ings:workingIngs,recipes:[...recipes,...newRecipes]});
  };
  const applySS=(ss)=>{
    setIngs(ss.ings||[]);setRecipes(ss.recipes||[]);setZones(ss.zones||initZones);
    setWasteLogs(ss.wasteLogs||[]);setOrders(ss.orders||[]);setShoppingList(ss.shoppingList||[]);
    setAlerts(ss.alerts||[]);setHaccpLogs(ss.haccpLogs||[]);setNotifs(ss.notifs||[]);
    setSales(ss.sales||[]);setDailyRevenue(ss.dailyRevenue||0);setSpecials(ss.specials||[]);
    setLocSuppliers(ss.suppliers||SUPPLIERS);setRecipeBook(ss.recipeBook||[]);
  };

  // Save current loc data to localStorage periodically and on switch
  const saveCurrentLoc=()=>saveLocData(loc.id,snapshotCurrent());
  // Force-save with explicit new data — bypasses React's async state timing
  const forceSave=(overrides)=>saveLocData(loc.id,{...snapshotCurrent(),...overrides});

  // Sync to server (Railway API → Neon Postgres)
  const API_URL="https://gastro-iot-api-production.up.railway.app";
  const ORG_ID="ed647077-8e67-4816-934d-ec474a54b102";
  const syncToServer=async()=>{
    try{
      const state=snapshotForSync();
      const r=await fetch(`${API_URL}/api/sync`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({orgId:ORG_ID,locationId:loc.id,locationName:loc.name||"Main Kitchen",state})});
      if(!r.ok){
        let detail="";try{const j=await r.json();detail=j.error||"";}catch(e){}
        window.__lastSyncError=`HTTP ${r.status}${detail?": "+detail:""}`;
        return false;
      }
      window.__lastSyncError="";
      return true;
    }catch(e){
      // Network/offline OR payload issue — capture it so the UI can show why
      window.__lastSyncError=e.message||"network error";
      return false;
    }
  };
  // Pull server alerts on app open
  useEffect(()=>{
    (async()=>{try{
      const r=await fetch(`${API_URL}/api/alerts/${ORG_ID}/${loc.id}`);
      const d=await r.json();
      if(d.ok&&d.alerts?.length>0){
        d.alerts.forEach(a=>{if(!notifs.find(n=>n.msg===a.title))addNotif(a.title);});
      }
    }catch(e){/* offline */}})();
  },[loc.id]);

  // Switch location: save current → load target → apply
  const switchLocation=(targetLoc)=>{
    if(targetLoc.id===loc.id)return;
    saveCurrentLoc();
    const saved=loadLocData(targetLoc.id);
    if(saved){applySS(saved);}else{applySS(blankLocData());}
    setLoc(targetLoc);
    setPage("dashboard");
  };

  // Add a new location: fresh or copy
  const addNewLocation=(name,addr,copyFromId,templateId)=>{
    const newLoc={id:genId(),name:name.trim(),addr:(addr||"").trim()};
    setLocations(p=>[...p,newLoc]);
    // Save current location first
    saveCurrentLoc();
    if(copyFromId){
      const source=copyFromId===loc.id?snapshotCurrent():loadLocData(copyFromId);
      if(source){
        const cloned=JSON.parse(JSON.stringify(source));
        saveLocData(newLoc.id,cloned);
      }else{
        saveLocData(newLoc.id,blankLocData());
      }
    }else if(templateId){
      // Seed from sim template
      const tmpl=SIM_TEMPLATES.find(x=>x.id===templateId);
      const data=blankLocData();
      if(tmpl){
        data.recipes=tmpl.menuItems.filter(m=>m.price>0).map(m=>({id:genId(),name:m.name,price:m.price,cat:"main",yield:1,status:"active",type:"plate",comps:[],instructions:"",costOverride:m.cost}));
      }
      saveLocData(newLoc.id,data);
    }else{
      saveLocData(newLoc.id,blankLocData());
    }
    return newLoc;
  };

  // Remove a location: delete its data
  const removeLocationFull=(id)=>{
    if(locations.length<=1)return;
    deleteLocData(id);
    setLocations(p=>p.filter(l=>l.id!==id));
    // If removing active loc, switch to first remaining
    if(loc.id===id){
      const remaining=locations.filter(l=>l.id!==id);
      if(remaining.length>0)switchLocation(remaining[0]);
    }
  };

  // Auto-save current loc data every 30s + sync to server
  useEffect(()=>{
    const t=setInterval(()=>{saveCurrentLoc();syncToServer();},30000);
    return ()=>clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[loc.id,ings,recipes,zones,wasteLogs,orders,sales,specials]);
  // Immediate save on critical state changes (prevents data loss on refresh)
  // Debounced: saves 500ms after the last state change — catches everything
  useEffect(()=>{
    const t=setTimeout(()=>saveLocData(loc.id,{ings,recipes,zones,wasteLogs,orders,shoppingList,alerts,haccpLogs,notifs,sales,dailyRevenue,specials,suppliers:locSuppliers,recipeBook}),500);
    return ()=>clearTimeout(t);
  });

  // On first mount, try to load saved data for the initial location
  useEffect(()=>{
    const saved=loadLocData(loc.id);
    if(saved)applySS(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Sync loc to first location if current loc was removed
  useEffect(()=>{
    if(locations.length>0&&!locations.find(l=>l.id===loc.id)){
      const first=locations[0];
      const saved=loadLocData(first.id);
      if(saved)applySS(saved);
      setLoc(first);
    }
  },[locations,loc.id]);

  useEffect(()=>{const t=setInterval(()=>setClock(new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})),1000);setClock(new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}));return()=>clearInterval(t)},[]);

  // First-run tour — auto-opens on first login per device, skippable, replayable from menu
  useEffect(()=>{
    if(!logged)return;
    try{
      const seen=window.localStorage&&window.localStorage.getItem("gastriot_tour_seen");
      if(!seen){
        setTourStep(0);
        setTourOpen(true);
      }
    }catch(e){/* private mode / storage blocked — skip gracefully */}
  },[logged]);

  const openTour=()=>{setTourStep(0);setTourOpen(true);setMenuOpen(false);};
  const closeTour=()=>{
    setTourOpen(false);
    try{window.localStorage&&window.localStorage.setItem("gastriot_tour_seen","1");}catch(e){}
  };
  const tourGoto=(idx)=>{
    if(idx<0||idx>=TOUR_STEPS.length){closeTour();return;}
    const step=TOUR_STEPS[idx];
    if(step.page)setPage(step.page);
    setTourStep(idx);
  };

  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(""),2400);};

  const wasteCost=wasteLogs.reduce((s,l)=>s+(l.avoidable!==false?l.cost:0),0);
  const wasteWeight=wasteLogs.reduce((s,l)=>s+(l.weight||0),0);
  const invValue=ings.reduce((s,i)=>s+i.qty*i.cost,0);
  const lowStock=ings.filter(i=>i.qty<=i.parMin*1.3);
  const activeAlerts=alerts.filter(a=>a.status==="active");

  const addNotif=(msg)=>setNotifs(p=>[{id:genId(),msg,time:clock},...p.slice(0,19)]);
  const addWaste=(entry)=>{
    // Use functional updates so delayed (voice-confirmed) events always build
    // on the LATEST state, never a stale snapshot — fixes disappearing entries.
    let savedWasteLogs=null, savedIngs=null;
    setWasteLogs(prev=>{ savedWasteLogs=[entry,...prev]; return savedWasteLogs; });
    addNotif(`Waste: ${entry.ingredient} ${entry.weight>=1000?(entry.weight/1000).toFixed(1)+"kg":entry.weight+"g"}${entry.avoidable!==false?" — "+fmtEur(entry.cost):" (unavoidable)"}`);
    // UNAVOIDABLE waste (prep trim, peels, bones — the inedible part) was never
    // sellable inventory, so it must NOT reduce the on-hand edible amount.
    // Inventory should always reflect true edible stock. Only AVOIDABLE waste
    // (food that could have been used) comes out of inventory.
    if(entry.avoidable===false){
      savedIngs=ings;
      setTimeout(()=>forceSave({wasteLogs:savedWasteLogs}),0);
      return;
    }
    setIngs(prev=>{
      const ing=prev.find(i=>i.name===entry.ingredient);
      if(!ing){ savedIngs=prev; return prev; }
      const isPc=ing.unit==="pc";
      const deduction=isPc?(entry.pieces||Math.round(entry.weight/ing.unitG)):entry.weight/ing.unitG;
      const newIngs=prev.map(x=>{
        if(x.id!==ing.id)return x;
        let remaining=deduction;
        const updatedLots=(x.lots||[]).map(lot=>{
          if(remaining<=0)return lot;
          const take=Math.min(lot.qty,remaining);
          remaining-=take;
          return {...lot,qty:Math.round((lot.qty-take)*100)/100};
        }).filter(lot=>lot.qty>0);
        const newQty=updatedLots.reduce((s,l)=>s+l.qty,0);
        const finalQty=Math.round(Math.max(0,updatedLots.length>0?newQty:x.qty-deduction)*100)/100;
        // 86'd: when an item hits zero, flag it OUT (stays visible until reordered)
        return {...x,qty:finalQty,lots:updatedLots,eightySixed:finalQty<=0};
      });
      savedIngs=newIngs;
      const feltQty=Math.max(0,ing.qty-deduction);
      if(feltQty<=0)addNotif(`🚫 ${entry.ingredient} is 86'd — out of stock. Reorder needed.`);
      else if(feltQty<=ing.parMin)addNotif(`⚠ ${entry.ingredient} below par (${Math.round(feltQty*100)/100} ${ing.unit}). Reorder needed.`);
      return newIngs;
    });
    // Persist the freshly-computed values (captured from the functional updates)
    setTimeout(()=>forceSave({wasteLogs:savedWasteLogs,ings:savedIngs}),0);
  };
  const addAlert=(a)=>{setAlerts(p=>[a,...p]);addNotif(`Alert: ${a.msg}`);};

  // Auto-populate shopping list from low-stock items
  useEffect(()=>{
    const newItems=lowStock.filter(i=>!shoppingList.find(s=>s.ingId===i.id)).map(i=>{
      const reorderQty=Math.max(0,i.parMax-i.qty);
      return {id:genId(),ingId:i.id,qty:Math.round(reorderQty*10)/10,supplier:i.supplier,assigned:false,added:new Date().toISOString(),priority:i.qty<i.parMin?"urgent":"normal"};
    });
    if(newItems.length){
      setShoppingList(p=>[...newItems,...p]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ings.map(i=>i.qty).join(",")]);

  // POS sale handler — deducts inventory per recipe component, checks reorder
  const processSale=(recipe,qty,specialPct=0)=>{
    const effectivePrice=specialPct>0?recipe.price*(1-specialPct/100):recipe.price;
    const totalPrice=effectivePrice*qty;
    const totalCost=calcRecipeCost(recipe,ings)*qty;
    const sale={id:genId(),recipe:recipe.name,recipeId:recipe.id,qty,price:effectivePrice,total:totalPrice,cost:totalCost,margin:totalPrice-totalCost,special:specialPct>0,specialPct,time:new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}),timestamp:new Date().toISOString()};
    setSales(p=>[sale,...p]);
    setDailyRevenue(p=>p+totalPrice);
    addNotif(`Sold ${qty}× ${recipe.name} — ${fmtEur(totalPrice)}${specialPct>0?` (special −${specialPct}%)`:""}`);
    const reorderNeeded=[];
    setIngs(prev=>{const updated=[...prev];
      recipe.comps.forEach(comp=>{const idx=updated.findIndex(i=>i.id===comp.ingId);if(idx===-1)return;
        const deductQty=comp.unit==="pc"?comp.qty*qty:comp.unit==="kg"||comp.unit==="L"?(comp.qty*qty)/1000:(comp.qty*qty)/updated[idx].unitG;
        updated[idx]={...updated[idx],qty:Math.max(0,updated[idx].qty-deductQty)};
        if(updated[idx].qty<=updated[idx].parMin)reorderNeeded.push(updated[idx]);
      });return updated;});
    setTimeout(()=>{reorderNeeded.forEach(i=>{addAlert({id:genId(),zone:"Inventory",msg:`${i.name} below par (${i.qty.toFixed(1)} / ${i.parMin} min)`,severity:"warning",status:"active",time:clock});});
      if(reorderNeeded.length>0)addNotif(`${reorderNeeded.length} item(s) hit reorder point`);
    },100);
    // Batch prep deduction — deduct portionWeight from linked recipeBook batch
    if(recipe.batchRef&&recipe.portionWeight>0){
      setRecipeBook(prev=>prev.map(rb=>{
        if(rb.id!==recipe.batchRef)return rb;
        const deduct=recipe.portionWeight*qty;
        const newRemaining=Math.max(0,(rb.batchRemaining||0)-deduct);
        if(newRemaining<=0)addNotif(`${rb.name} batch depleted — add to prep list`);
        return {...rb,batchRemaining:newRemaining};
      }));
    }
  };

  // Combo sale handler — special combo at flat price, deducts each selected recipe's components
  const processComboSale=(combo,selectedRecipeIds,qty)=>{
    // selectedRecipeIds = array of recipe ids the customer picked for this combo (length = combo.count * qty)
    const selectedRecipes=selectedRecipeIds.map(rid=>recipes.find(r=>r.id===rid)).filter(Boolean);
    const totalPrice=combo.price*qty;
    const totalCost=selectedRecipes.reduce((s,r)=>s+calcRecipeCost(r,ings),0);
    const sale={id:genId(),recipe:`${combo.name} (${selectedRecipes.map(r=>r.name).join(" + ")})`,recipeId:combo.id,qty,price:combo.price,total:totalPrice,cost:totalCost,margin:totalPrice-totalCost,special:true,specialPct:0,combo:true,comboName:combo.name,time:new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}),timestamp:new Date().toISOString()};
    setSales(p=>[sale,...p]);
    setDailyRevenue(p=>p+totalPrice);
    addNotif(`Combo: ${combo.name} × ${qty} — ${fmtEur(totalPrice)}`);
    const reorderNeeded=[];
    setIngs(prev=>{const updated=[...prev];
      selectedRecipes.forEach(r=>{
        r.comps.forEach(comp=>{const idx=updated.findIndex(i=>i.id===comp.ingId);if(idx===-1)return;
          const deductQty=comp.unit==="pc"?comp.qty:comp.unit==="kg"||comp.unit==="L"?comp.qty/1000:comp.qty/updated[idx].unitG;
          updated[idx]={...updated[idx],qty:Math.max(0,updated[idx].qty-deductQty)};
          if(updated[idx].qty<=updated[idx].parMin&&!reorderNeeded.find(x=>x.id===updated[idx].id))reorderNeeded.push(updated[idx]);
        });
      });
      return updated;
    });
    setTimeout(()=>{reorderNeeded.forEach(i=>{addAlert({id:genId(),zone:"Inventory",msg:`${i.name} below par (${i.qty.toFixed(1)} / ${i.parMin} min)`,severity:"warning",status:"active",time:clock});});
      if(reorderNeeded.length>0)addNotif(`${reorderNeeded.length} item(s) hit reorder point`);
    },100);
  };

  // Global voice nav
  if(typeof window!=="undefined")window.__WASTE_CATS=WASTE_CATS;
  const voice=useVoiceNav({
    onPage:(p)=>{setPage(p);showToast(`Voice: ${p}`);},
    onTemp:(val)=>{
      showToast(`Temperature recorded: ${val}°C`);
      addNotif(`Voice temp: ${val}°C captured`);
      window.__lastVoiceTemp=val;
      window.dispatchEvent(new CustomEvent("voiceTemp",{detail:{value:val}}));
    },
    onCommand:(msg)=>showToast(msg),
    items:ings,
    settings,
    onWaste:(item,amount,unit,grams,cat,avoidable)=>{
      const isPc=unit==="pc";
      const useCat=cat||(wasteBins&&wasteBins[0])||{name:"Spoilage",avoidable:true};
      const isAvoidable=avoidable!==undefined?avoidable:(useCat.avoidable!==false);
      const cost=(item.cost||0)*(isPc?(amount):(grams/(item.unitG||1000)));
      addWaste({
        id:genId(),
        ingredient:item.name,
        weight:Math.round(grams),
        pieces:isPc?amount:undefined,
        category:useCat.name||"Spoilage",
        avoidable:isAvoidable,
        cost:isAvoidable?Math.round(cost*100)/100:0,
        ts:Date.now(),
        via:"voice",
      });
      setPage("scale");
    },
    queryData:(kind)=>{
      const fullList=!settings||settings.voiceFullList!==false;
      // Join a list naturally: "a, b, and c". If summarizing, cap at 5 + "and N more".
      const nat=(arr)=>{
        if(!arr.length)return "";
        let extra=0;
        if(!fullList&&arr.length>5){extra=arr.length-5;arr=arr.slice(0,5);}
        let s;
        if(arr.length===1)s=arr[0];
        else if(arr.length===2)s=arr[0]+" and "+arr[1];
        else s=arr.slice(0,-1).join(", ")+", and "+arr[arr.length-1];
        if(extra>0)s+=`, and ${extra} more`;
        return s;
      };
      if(kind==="expiring"){
        const soon=ings.filter(i=>{const s=computeShelfLife(i,zones);return (i.qty||0)>0&&s.daysLeft<=1;});
        if(!soon.length)return "Nothing is expiring today. You're clear.";
        return `${soon.length} item${soon.length>1?"s":""} expiring today: ${nat(soon.map(i=>i.name))}.`;
      }
      if(kind==="waste"){
        const total=wasteLogs.reduce((s,l)=>s+(l.weight||0),0);
        const cost=wasteLogs.reduce((s,l)=>s+(l.cost||0),0);
        const wt=total>=1000?(total/1000).toFixed(1)+" kilos":total+" grams";
        if(!wasteLogs.length)return "No waste logged yet.";
        return `So far, ${wt} of waste logged, costing ${fmtEur(cost)}.`;
      }
      if(kind==="low"){
        // Screen flags qty <= parMin*1.3 as "below par". Match it exactly.
        const out86=ings.filter(i=>(i.qty||0)<=0);
        const belowPar=ings.filter(i=>(i.qty||0)>0&&(i.qty||0)<=(i.parMin||0)*1.3);
        if(!out86.length&&!belowPar.length)return "Everything is well stocked. Nothing below par.";
        let out="";
        if(out86.length)out+=`${out86.length} 86'd and out: ${nat(out86.map(i=>i.name))}. `;
        if(belowPar.length)out+=`${belowPar.length} below par: ${nat(belowPar.map(i=>`${i.name}, ${Math.round((i.qty||0)*100)/100} ${i.unit}`))}.`;
        return out.trim();
      }
      if(kind==="revenue"){
        if(!canSeeRevenue())return "Revenue is restricted to owners.";
        return `Today's revenue is ${fmtEur(dailyRevenue)} across ${sales.length} ticket${sales.length===1?"":"s"}.`;
      }
      if(kind==="prep"){
        const empty=(recipeBook||[]).filter(rb=>rb.batchTotal&&(rb.batchRemaining||0)<=0).map(rb=>rb.name);
        const low=(recipeBook||[]).filter(rb=>rb.batchTotal&&(rb.batchRemaining||0)>0&&(rb.batchRemaining||0)<=rb.batchTotal*0.25).map(rb=>rb.name);
        const all=[...empty,...low];
        if(!all.length)return "Nothing needs prepping right now.";
        return `${all.length} to prep: ${nat(all)}.`;
      }
      return "I didn't catch that.";
    },
  });

  const [currentRole,setCurrentRole]=useState(()=>{try{return localStorage.getItem("gastriot_role")||"owner";}catch{return "owner";}});
  const isOwner=currentRole==="owner";
  const isChefUp=currentRole==="owner"||currentRole==="chef"; // chef or above
  ROLE=currentRole; // sync module-level role for lockout helpers
  if(!gateOk) return <GateCode onUnlock={()=>{try{localStorage.setItem("gastriot_gate","1");}catch{}setGateOk(true);}}/>;
  if(!logged) return <Login onLogin={(email,acct)=>{try{localStorage.setItem("gastriot_logged","1");if(email)localStorage.setItem("gastriot_email",email);if(acct&&acct.role)localStorage.setItem("gastriot_role",acct.role);}catch{}if(acct&&acct.role)setCurrentRole(acct.role);setLogged(true);}}/>;

  // Bottom nav — 5 core modules (closed loop)
  const bottomNav=[
    {id:"dashboard",label:"Home",Icon:BarChart3},
    {id:"inventory",label:"Inventory",Icon:Package},
    {id:"pos",label:"POS",Icon:ShoppingCart},
    {id:"orders",label:"Orders",Icon:Truck},
    {id:"scale",label:"Waste",Icon:Scale},
  ];
  // Sidebar — secondary pages (Reports gated to chef+; revenue/financials inside)
  const nav=[
    {id:"recipes",label:"Menu",Icon:ChefHat,c:T.pur},
    {id:"recipebook",label:"Recipe Book",Icon:Wheat,c:T.amb},
    {id:"prep",label:"Prep List",Icon:ClipboardList,c:T.amb},
    ...(canSeeCost()?[{id:"reports",label:"Reports",Icon:FileText,c:T.pur}]:[]),
    {id:"safety",label:"Safety / HACCP",Icon:ShieldCheck,c:T.blu},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:T.bg,color:T.tx,fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:14,paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)",paddingLeft:"env(safe-area-inset-left)",paddingRight:"env(safe-area-inset-right)"}} className="app-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500;600&family=Playfair+Display:ital,wght@1,700;1,900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;overflow-anchor:none}
        html,body,#root{height:100%;width:100%;overflow:hidden}
        ::-webkit-scrollbar{width:4px;height:0}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.08);border-radius:4px}
        @keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
        @keyframes expireBlink{0%,100%{border-color:rgba(212,43,43,0.15)}50%{border-color:rgba(212,43,43,0.7)}}
        .anim{animation:fadeIn .15s ease both}
        .pulse{animation:pulse 1.4s ease-in-out infinite}
        select{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%237A8685' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;padding-right:34px}
        button{-webkit-user-select:none}
        button:active{transform:scale(0.97)}
        @media print{.no-print{display:none!important}body{background:#fff!important}.app-shell{position:static!important;height:auto!important;display:block!important}}
        .app-shell{height:100vh;height:100dvh;display:flex;flex-direction:column}
        @supports (height:100dvh){.app-shell{height:100dvh}}
        .app-shell-main{flex:1 1 auto;min-height:0;overflow-y:scroll;-webkit-overflow-scrolling:touch;padding:14px}
        @media (orientation:landscape) and (max-height:500px){
          .app-shell-main{padding:10px!important}
          .app-header{padding:6px 14px!important}
        }
      `}</style>

      <header className="app-header no-print" style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:`1px solid ${T.brd}66`,flexShrink:0,background:T.sf,position:"relative"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,position:"relative"}}>
          <button onClick={()=>setMenuOpen(!menuOpen)} title="Menu" style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"flex",alignItems:"center",gap:10,fontFamily:"inherit",position:"relative"}}>
            <div style={{position:"relative"}}>
              <LogoG size={34}/>
              {activeAlerts.length>0&&<div style={{position:"absolute",bottom:-2,right:-2,minWidth:16,height:16,padding:"0 4px",borderRadius:8,background:T.red,fontSize:9,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,border:`2px solid ${T.sf}`,fontFamily:"'DM Mono',monospace"}}>{activeAlerts.length>99?"99+":activeAlerts.length}</div>}
            </div>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:17,fontWeight:900,color:T.logo,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",lineHeight:1,display:"flex",alignItems:"center",gap:4}}>Gastriot <ChevronDown size={12} color={T.dm}/></div>
              <div style={{fontSize:10,color:T.dm,fontFamily:"'DM Sans',inherit",marginTop:2}}>{loc.name}</div>
            </div>
          </button>

          {menuOpen&&<>
            {/* Click-catch overlay */}
            <div onClick={()=>setMenuOpen(false)} style={{position:"fixed",inset:0,background:"transparent",zIndex:90}}/>
            {/* Dropdown */}
            <div style={{position:"absolute",top:"calc(100% + 8px)",left:0,minWidth:260,maxHeight:"calc(100dvh - 120px)",overflowY:"auto",WebkitOverflowScrolling:"touch",background:T.card,border:`1px solid ${T.brd}`,borderRadius:14,padding:8,paddingBottom:24,zIndex:100,boxShadow:"0 8px 32px rgba(0,0,0,0.12)"}}>

              <div style={{padding:"8px 14px 4px",fontSize:10,color:T.mt,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase"}}>Pages</div>
              {nav.map(n=>{
                const isActive=page===n.id;
                return <button key={n.id} onClick={()=>{setPage(n.id);setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:isActive?T.sf:"none",border:"none",color:isActive?n.c:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:isActive?700:500,borderRadius:8,textAlign:"left",minHeight:46,touchAction:"manipulation"}}>
                  <n.Icon size={20} color={n.c} strokeWidth={isActive?2.2:1.8}/>
                  <span>{n.label}</span>
                  {isActive&&<ChevronRight size={14} color={n.c} style={{marginLeft:"auto"}}/>}
                </button>;
              })}

              <div style={{height:1,background:T.brd,margin:"6px 8px"}}/>

              <button onClick={()=>{setPage("notifs");setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:page==="notifs"?T.sf:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:500,borderRadius:8,textAlign:"left",justifyContent:"space-between",minHeight:46}}>
                <span style={{display:"flex",alignItems:"center",gap:12}}><Bell size={20} color={activeAlerts.length>0?T.red:T.dm}/>Notifications</span>
                {activeAlerts.length>0&&<span style={{minWidth:22,height:22,padding:"0 6px",borderRadius:11,background:T.red,color:"#fff",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace"}}>{activeAlerts.length}</span>}
              </button>

              <button onClick={()=>{const nd=!dark;setDark(nd);try{localStorage.setItem("gastriot_theme",nd?"dark":"light");}catch{}setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:500,borderRadius:8,textAlign:"left",minHeight:46}}>
                {dark?<Sun size={20} color={T.amb}/>:<Moon size={20} color={T.amb}/>}{dark?"Light mode":"Dark mode"}
              </button>

              <button onClick={()=>{const nc=cur==="€"?"$":"€";setCur(nc);try{localStorage.setItem("gastriot_cur",nc);}catch{}}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:500,borderRadius:8,textAlign:"left",minHeight:46}}>
                <DollarSign size={20} color={T.amb}/>Currency: {cur==="€"?"Euro (€)":"US Dollar ($)"} — tap to switch
              </button>

              <div style={{height:1,background:T.brd,margin:"6px 8px"}}/>
              <div style={{padding:"4px 14px",fontSize:11,color:T.mt,fontWeight:600,letterSpacing:0.5}}>LOCATION</div>
              {locations.map(l=><button key={l.id} onClick={()=>{switchLocation(l);setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:loc.id===l.id?T.sf:"none",border:"none",color:loc.id===l.id?T.amb:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:500,borderRadius:8,textAlign:"left"}}>
                <MapPin size={16} color={loc.id===l.id?T.amb:T.dm}/>{l.name}
              </button>)}

              <div style={{height:1,background:T.brd,margin:"6px 8px"}}/>

              <button onClick={()=>{setPage("settings");setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:page==="settings"?T.sf:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:500,borderRadius:8,textAlign:"left",minHeight:46}}>
                <Settings size={20} color={T.dm}/>Settings
              </button>
              <button onClick={()=>{setPage("sim");setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:page==="sim"?T.sf:"none",border:"none",color:T.amb,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600,borderRadius:8,textAlign:"left",minHeight:46}}>
                <Play size={20} color={T.amb}/>Sim Kitchen
              </button>
              <button onClick={openTour} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"none",border:"none",color:T.amb,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600,borderRadius:8,textAlign:"left",minHeight:46}}>
                <ClipboardList size={20}/>Take the tour
              </button>
              <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",margin:"4px 0"}}>
                <div style={{width:8,height:8,borderRadius:4,background:isOwner?T.gold:isChefUp?T.blu:T.grn}}/>
                <span style={{fontSize:11,color:T.dm,textTransform:"capitalize"}}>Signed in as {currentRole}</span>
              </div>
              <div style={{fontSize:9,color:T.dm,padding:"0 14px",opacity:0.6}}>build v2-account-sync</div>
              <button onClick={()=>{setMenuOpen(false);try{localStorage.removeItem("gastriot_logged");}catch{}setLogged(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"none",border:"none",color:T.red,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600,borderRadius:8,textAlign:"left",minHeight:46}}>
                <LogOut size={20}/>Sign out
              </button>
            </div>
          </>}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:13,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{clock}</span>
          {voice.supported&&<button onClick={voice.toggle} title={voice.locked?"Voice locked on — tap to disable":voice.listening?"Voice on (will auto-pause after 5 min)":"Tap to enable voice (locked mode)"} style={{background:voice.listening?(voice.locked?T.amb:T.red):"none",border:`1px solid ${voice.listening?(voice.locked?T.amb:T.red):T.brd}`,borderRadius:8,padding:"8px 10px",cursor:"pointer",color:voice.listening?"#fff":T.amb,display:"flex",alignItems:"center",gap:4,position:"relative"}} className={voice.listening?"pulse":""}>
            {voice.listening?<Mic size={20}/>:<MicOff size={20}/>}
            {voice.locked&&<span style={{position:"absolute",top:-4,right:-4,width:10,height:10,borderRadius:5,background:T.amb,border:"1.5px solid "+T.sf}}/>}
          </button>}
        </div>
      </header>

      {voice.pending&&<div style={{position:"fixed",top:64,left:12,right:12,zIndex:90,background:T.navy||"#1B2A4A",color:"#fff",borderRadius:12,padding:"14px 16px",boxShadow:"0 8px 24px rgba(0,0,0,0.18)",display:"flex",alignItems:"center",gap:10,animation:"fadeIn 0.2s"}}>
        <Mic size={20} color={T.gold||"#F5C518"}/>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700}}>{voice.pending.summary}</div>
          <div style={{fontSize:12,opacity:0.7}}>Say "yes" to confirm or "no" to cancel</div>
        </div>
      </div>}
      {voice.listening&&voice.transcript&&!voice.pending&&<div style={{position:"fixed",bottom:80,left:12,right:12,zIndex:85,background:"rgba(27,42,74,0.92)",color:"#fff",borderRadius:10,padding:"8px 14px",fontSize:12,display:"flex",alignItems:"center",gap:8,backdropFilter:"blur(8px)"}}>
        <span className="pulse" style={{width:8,height:8,borderRadius:4,background:T.red||"#D42B2B",flexShrink:0}}/>
        <span style={{opacity:0.85,fontStyle:"italic"}}>"{voice.transcript}"</span>
      </div>}
      <main className="app-shell-main" style={{paddingBottom:72}}>
        <div key={page} className="anim">
        {page==="dashboard"&&<DashboardP wasteCost={wasteCost} wasteWeight={wasteWeight} wasteLogs={wasteLogs} invValue={invValue} lowStock={lowStock} orders={orders} activeAlerts={activeAlerts} zones={zones} ings={ings} recipes={recipes} haccpLogs={haccpLogs} sales={sales} dailyRevenue={dailyRevenue} setPage={setPage} shoppingList={shoppingList}/>}
        {page==="pos"&&<PosP recipes={recipes} ings={ings} sales={sales} dailyRevenue={dailyRevenue} onSale={processSale} onComboSale={processComboSale} specials={specials} setSpecials={setSpecials} showToast={showToast}/>}
        {page==="scale"&&<ScaleWasteP ings={ings} bleDevice={bleDevice} setBleDevice={setBleDevice} bleWeight={bleWeight} setBleWeight={setBleWeight} showToast={showToast} cats={WASTE_CATS} onLog={addWaste} wasteCost={wasteCost} wasteWeight={wasteWeight} wasteLogs={wasteLogs} setWasteLogs={setWasteLogs} wasteBins={wasteBins} setWasteBins={setWasteBins}/>}
        {page==="inventory"&&<InventoryP ings={ings} setIngs={setIngs} zones={zones} throwAway={throwAwayExpired} recipes={recipes}/>}
        {page==="recipebook"&&<RecipeBookP recipeBook={recipeBook} setRecipeBook={setRecipeBook} ings={ings} setIngs={setIngs} showToast={showToast}/>}
        {page==="recipes"&&<RecipeP recipes={recipes} setRecipes={setRecipes} ings={ings} setIngs={setIngs} recipeBook={recipeBook} onMenuImport={importScannedMenu} showToast={showToast}/>}
        {page==="prep"&&<PrepListP recipes={recipes} ings={ings} setIngs={setIngs} sales={sales} setRecipes={setRecipes} recipeBook={recipeBook} setRecipeBook={setRecipeBook}/>}
        {page==="orders"&&<OrdersP orders={orders} setOrders={setOrders} ings={ings} setIngs={setIngs} recipes={recipes} suppliers={locSuppliers} addNotif={addNotif} shoppingList={shoppingList} setShoppingList={setShoppingList} showToast={showToast} setLocSuppliers={setLocSuppliers} loc={loc} forceSave={forceSave}/>}
        {page==="accounts"&&isOwner&&<AccountsP showToast={showToast}/>}
        {page==="reports"&&<ReportsP sales={sales} wasteLogs={wasteLogs} ings={ings} recipes={recipes} dailyRevenue={dailyRevenue} haccpLogs={haccpLogs} zones={zones} orders={orders} loc={loc} clock={clock}/>}
        {page==="safety"&&<SafetyP zones={zones} setZones={setZones} alerts={alerts} setAlerts={setAlerts} addAlert={addAlert} haccpLogs={haccpLogs} setHaccpLogs={setHaccpLogs} voiceListening={voice.listening}/>}
        {page==="notifs"&&<NotifsP notifs={notifs} setNotifs={setNotifs} alerts={alerts} setAlerts={setAlerts}/>}
        {page==="settings"&&<SettingsP settings={settings} setSettings={setSettings} locations={locations} loc={loc} showToast={showToast} addNewLocation={addNewLocation} removeLocationFull={removeLocationFull} switchLocation={switchLocation} setPage={setPage} isOwner={isOwner} syncNow={syncToServer}/>}
        {page==="devices"&&<DevicesP bleDevice={bleDevice} setBleDevice={setBleDevice} bleWeight={bleWeight} setBleWeight={setBleWeight} bleTempDevice={bleTempDevice} setBleTempDevice={setBleTempDevice} bleTempReading={bleTempReading} setBleTempReading={setBleTempReading} showToast={showToast} wasteBins={wasteBins} setWasteBins={setWasteBins}/>}
        {page==="sim"&&<SimKitchenP recipes={recipes} ings={ings} specials={specials} setSales={setSales} setWasteLogs={setWasteLogs} setDailyRevenue={setDailyRevenue} setIngs={setIngs} setRecipes={setRecipes} setRecipeBook={setRecipeBook} showToast={showToast} simResult={simResult} setSimResult={setSimResult} setPage={setPage}/>}
        </div>
      </main>

      {/* BOTTOM NAV — 5 core modules */}
      <nav className="no-print" style={{display:"flex",justifyContent:"space-around",alignItems:"center",padding:"6px 0 env(safe-area-inset-bottom,8px)",borderTop:`1px solid ${T.brd}66`,background:T.sf,flexShrink:0,position:"relative"}}>
        {bottomNav.map(n=>{
          const active=page===n.id;
          return <button key={n.id} onClick={()=>setPage(n.id)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"6px 12px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",color:active?T.accent:T.dm,transition:"color 0.12s",minWidth:56}}>
            <n.Icon size={22} strokeWidth={active?2.2:1.6}/>
            <span style={{fontSize:10,fontWeight:active?700:500,letterSpacing:0.2}}>{n.label}</span>
            {active&&<div style={{position:"absolute",top:0,width:32,height:2,background:T.accent,borderRadius:"0 0 2px 2px"}}/>}
          </button>;
        })}
      </nav>

      {toast&&<Toast msg={toast}/>}
      {tourOpen&&<Tour step={tourStep} go={tourGoto} close={closeTour}/>}
    </div>
  );
}

/* ═══ LOGIN ═══ */
/* ═══ ACCESS GATE ═══ above. ═══ APPROVED ACCOUNTS ═══
   4-digit unlock code shown before sign-in. The code is emailed to approved
   people so they can reach the account-creation step. */
const GATE_CODE="3609";
function GateCode({onUnlock}){
  const [digits,setDigits]=useState(["","","",""]);
  const [err,setErr]=useState("");
  const refs=[useRef(),useRef(),useRef(),useRef()];
  const setD=(i,v)=>{
    v=v.replace(/\D/g,"").slice(-1);
    const nd=[...digits];nd[i]=v;setDigits(nd);setErr("");
    if(v&&i<3)refs[i+1].current&&refs[i+1].current.focus();
    if(nd.every(d=>d!=="")){
      if(nd.join("")===GATE_CODE){onUnlock();}
      else{setErr("Incorrect code");setTimeout(()=>{setDigits(["","","",""]);refs[0].current&&refs[0].current.focus();},600);}
    }
  };
  const onKey=(i,e)=>{ if(e.key==="Backspace"&&!digits[i]&&i>0)refs[i-1].current&&refs[i-1].current.focus(); };
  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#1B2A4A 0%,#16223C 60%,#0F1929 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'DM Sans',system-ui,sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@1,700;1,900&display=swap&family=DM+Mono:wght@500');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{width:"100%",maxWidth:360,textAlign:"center"}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:36}}>
          <LogoG size={64}/>
          <div style={{fontSize:28,fontWeight:900,color:"#F5C518",fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",marginTop:12,lineHeight:1}}>Gastriot</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:8,letterSpacing:1.5,textTransform:"uppercase"}}>Enter access code</div>
        </div>
        <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:18}}>
          {digits.map((d,i)=><input key={i} ref={refs[i]} value={d} onChange={e=>setD(i,e.target.value)} onKeyDown={e=>onKey(i,e)} inputMode="numeric" maxLength={1} autoFocus={i===0} style={{width:58,height:68,textAlign:"center",fontSize:30,fontWeight:500,fontFamily:"'DM Mono',monospace",color:"#fff",background:"rgba(255,255,255,0.06)",border:`2px solid ${err?"#D42B2B":d?"#F5C518":"rgba(255,255,255,0.15)"}`,borderRadius:12,outline:"none",transition:"border-color 0.15s"}}/>)}
        </div>
        {err&&<div style={{color:"#FCA5A5",fontSize:13,marginBottom:12}}>{err}</div>}
        <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginTop:8,lineHeight:1.6}}>
          Don't have a code?<br/>
          <a href="https://gastriot.com" style={{color:"#F5C518",textDecoration:"none",fontWeight:600}}>Request access at gastriot.com</a>
        </div>
      </div>
    </div>
  );
}


/* ═══ APPROVED ACCOUNTS ═══
   Seed accounts + self-serve accounts created behind the gate (stored locally
   until backend auth ships). Roles: owner / chef / employee. */
const SEED_ACCOUNTS=[
  {email:"gastromate030@gmail.com", pass:"Loveberlin123!", name:"Bodega", role:"owner"},
];
function loadAccounts(){
  try{
    const saved=JSON.parse(localStorage.getItem("gastriot_accounts")||"[]");
    // Merge seed + saved, seed wins on email collision
    const seedEmails=new Set(SEED_ACCOUNTS.map(a=>a.email.toLowerCase()));
    return [...SEED_ACCOUNTS,...saved.filter(a=>!seedEmails.has((a.email||"").toLowerCase()))];
  }catch{return [...SEED_ACCOUNTS];}
}
function saveAccount(acct){
  try{
    const saved=JSON.parse(localStorage.getItem("gastriot_accounts")||"[]");
    if(saved.find(a=>(a.email||"").toLowerCase()===acct.email.toLowerCase()))return false;
    saved.push(acct);
    localStorage.setItem("gastriot_accounts",JSON.stringify(saved));
    return true;
  }catch{return false;}
}
function checkAccount(email,pass){
  const e=(email||"").trim().toLowerCase();
  return loadAccounts().find(a=>a.email.toLowerCase()===e&&a.pass===pass)||null;
}
function emailExists(email){
  const e=(email||"").trim().toLowerCase();
  return !!loadAccounts().find(a=>a.email.toLowerCase()===e);
}

// Owner-only: view team accounts + add staff
function AccountsP({showToast}){
  const [accts,setAccts]=useState(loadAccounts());
  const [adding,setAdding]=useState(false);
  const [f,setF]=useState({name:"",email:"",pass:"",role:"employee"});
  const refresh=()=>setAccts(loadAccounts());
  const add=()=>{
    if(!f.name||!f.email||!f.pass){showToast("Fill in all fields");return;}
    if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)){showToast("Enter a valid email");return;}
    if(emailExists(f.email)){showToast("Email already exists");return;}
    if(!saveAccount({email:f.email.trim(),pass:f.pass,name:f.name.trim(),role:f.role})){showToast("Couldn't add");return;}
    showToast(`${f.name} added as ${f.role}`);
    setF({name:"",email:"",pass:"",role:"employee"});setAdding(false);refresh();
  };
  const remove=(email)=>{
    if(email.toLowerCase()==="gastromate030@gmail.com"){showToast("Can't remove the owner account");return;}
    try{
      const saved=JSON.parse(localStorage.getItem("gastriot_accounts")||"[]").filter(a=>(a.email||"").toLowerCase()!==email.toLowerCase());
      localStorage.setItem("gastriot_accounts",JSON.stringify(saved));
      showToast("Removed");refresh();
    }catch{showToast("Couldn't remove");}
  };
  const roleColor=r=>r==="owner"?T.gold:r==="chef"?T.blu:T.grn;
  return <div>
    <h2 style={{fontSize:22,fontWeight:700,marginBottom:16,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Team & accounts</h2>
    <Card t={`TEAM (${accts.length})`}>
      {accts.map(a=><div key={a.email} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${T.brd}33`}}>
        <div style={{width:36,height:36,borderRadius:18,background:roleColor(a.role)+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <span style={{fontSize:14,fontWeight:700,color:roleColor(a.role)}}>{(a.name||a.email)[0].toUpperCase()}</span>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:600,color:T.tx,overflow:"hidden",textOverflow:"ellipsis"}}>{a.name||a.email}</div>
          <div style={{fontSize:11,color:T.dm}}>{a.email}</div>
        </div>
        <span style={{fontSize:10,fontWeight:700,color:roleColor(a.role),textTransform:"uppercase",letterSpacing:0.5,padding:"3px 8px",background:roleColor(a.role)+"18",borderRadius:6}}>{a.role}</span>
        {a.email.toLowerCase()!=="gastromate030@gmail.com"&&<button onClick={()=>remove(a.email)} style={{padding:6,borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.red,cursor:"pointer",display:"flex"}}><Trash2 size={14}/></button>}
      </div>)}
    </Card>
    {!adding
      ?<button onClick={()=>setAdding(true)} style={{width:"100%",padding:14,borderRadius:12,border:`1.5px dashed ${T.brd}`,background:"none",color:T.blu,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",marginTop:4,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}><Plus size={18}/>Add a team member</button>
      :<Card t="ADD TEAM MEMBER">
        <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Name" style={inp}/>
        <input value={f.email} onChange={e=>setF({...f,email:e.target.value})} placeholder="Email" style={inp}/>
        <input value={f.pass} onChange={e=>setF({...f,pass:e.target.value})} placeholder="Temporary password" style={inp}/>
        <div style={{display:"flex",gap:6,margin:"4px 0 12px"}}>
          {["owner","chef","employee"].map(r=><button key={r} onClick={()=>setF({...f,role:r})} style={{flex:1,padding:"10px 4px",borderRadius:8,border:`1.5px solid ${f.role===r?roleColor(r):T.brd}`,background:f.role===r?roleColor(r)+"18":T.sf,color:f.role===r?roleColor(r):T.dm,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>{r}</button>)}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setAdding(false)} style={{flex:1,padding:12,borderRadius:10,border:`1px solid ${T.brd}`,background:"none",color:T.tx,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button onClick={add} style={{flex:1,padding:12,borderRadius:10,border:"none",background:T.signBtn||T.navy,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Add</button>
        </div>
      </Card>}
    <div style={{fontSize:11,color:T.dm,marginTop:14,lineHeight:1.6,fontStyle:"italic",textAlign:"center"}}>Employees see operations only. Chefs see costs. Owners see everything.<br/>Full server-side enforcement arrives with cloud accounts.</div>
  </div>;
}

function Login({onLogin}){
  const[mode,setMode]=useState("signin"); // signin | create
  const[e,setE]=useState(""),[p,setP]=useState(""),[err,setErr]=useState("");
  const[name,setName]=useState(""),[rest,setRest]=useState(""),[role,setRole]=useState("employee"),[p2,setP2]=useState("");
  const submit=()=>{
    if(!e||!p){setErr("Enter your email and password");return;}
    const acct=checkAccount(e,p);
    if(!acct){setErr("Account not recognized. Check your details or create an account.");return;}
    onLogin(acct.email,acct);
  };
  const create=()=>{
    if(!name||!e||!p){setErr("Fill in name, email and password");return;}
    if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)){setErr("Enter a valid email");return;}
    if(p.length<6){setErr("Password must be at least 6 characters");return;}
    if(p!==p2){setErr("Passwords don't match");return;}
    if(emailExists(e)){setErr("An account with that email already exists. Sign in instead.");return;}
    const acct={email:e.trim(),pass:p,name:name.trim(),restaurant:rest.trim(),role};
    if(!saveAccount(acct)){setErr("Couldn't create account. Try again.");return;}
    onLogin(acct.email,acct);
  };
  const inpS={width:"100%",background:"#F8F7F4",border:"1px solid #E8E2D8",borderRadius:10,padding:"12px 14px",color:"#1B2A4A",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:12};
  const lblS={fontSize:10,fontWeight:700,color:"#7A7268",letterSpacing:0.8,textTransform:"uppercase",marginBottom:6};
  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#F8F7F4 0%,#EDE8DF 50%,#E5DFD3 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'DM Sans',system-ui,sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@1,700;1,900&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:28}}>
          <LogoG size={72}/>
          <div style={{fontSize:32,fontWeight:900,color:"#D42B2B",fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",marginTop:10,lineHeight:1}}>Gastriot</div>
          <div style={{fontSize:11,color:"#7A7268",marginTop:6,letterSpacing:1.5,textTransform:"uppercase"}}>Kitchen Intelligence</div>
        </div>
        {/* mode switch */}
        <div style={{display:"flex",gap:6,background:"#EDE8DF",borderRadius:10,padding:4,marginBottom:18}}>
          <button onClick={()=>{setMode("signin");setErr("");}} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:mode==="signin"?"#fff":"none",color:mode==="signin"?"#1B2A4A":"#7A7268",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:mode==="signin"?"0 1px 3px rgba(0,0,0,0.08)":"none"}}>Sign in</button>
          <button onClick={()=>{setMode("create");setErr("");}} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:mode==="create"?"#fff":"none",color:mode==="create"?"#1B2A4A":"#7A7268",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:mode==="create"?"0 1px 3px rgba(0,0,0,0.08)":"none"}}>Create account</button>
        </div>
        <div style={{background:"#FFFFFF",borderRadius:16,padding:"24px 22px",boxShadow:"0 4px 24px rgba(0,0,0,0.06)",border:"1px solid #E8E2D844"}}>
          {mode==="create"&&<>
            <div style={lblS}>Your name</div>
            <input value={name} onChange={ev=>setName(ev.target.value)} placeholder="e.g. Antwann Thomas" style={inpS}/>
            <div style={lblS}>Restaurant / kitchen (optional)</div>
            <input value={rest} onChange={ev=>setRest(ev.target.value)} placeholder="e.g. Bodega Berlin" style={inpS}/>
            <div style={lblS}>Your role</div>
            <div style={{display:"flex",gap:6,marginBottom:12}}>
              {["owner","chef","employee"].map(r=><button key={r} onClick={()=>setRole(r)} style={{flex:1,padding:"10px 4px",borderRadius:8,border:`1.5px solid ${role===r?"#1B2A4A":"#E8E2D8"}`,background:role===r?"#1B2A4A":"#F8F7F4",color:role===r?"#fff":"#7A7268",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>{r}</button>)}
            </div>
          </>}
          <div style={lblS}>Email</div>
          <input value={e} onChange={ev=>setE(ev.target.value)} onKeyDown={ev=>ev.key==="Enter"&&(mode==="signin"?submit():create())} placeholder="you@restaurant.com" style={inpS}/>
          <div style={lblS}>Password</div>
          <input value={p} onChange={ev=>setP(ev.target.value)} onKeyDown={ev=>ev.key==="Enter"&&(mode==="signin"?submit():create())} placeholder="••••••••" type="password" style={inpS}/>
          {mode==="create"&&<>
            <div style={lblS}>Confirm password</div>
            <input value={p2} onChange={ev=>setP2(ev.target.value)} onKeyDown={ev=>ev.key==="Enter"&&create()} placeholder="••••••••" type="password" style={inpS}/>
          </>}
          {err&&<div style={{color:"#D42B2B",fontSize:12,marginBottom:8,marginTop:2}}>{err}</div>}
          <button onClick={mode==="signin"?submit:create} style={{width:"100%",padding:14,borderRadius:10,border:"none",background:"#1B2A4A",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",letterSpacing:0.3,fontFamily:"inherit",marginTop:8,boxShadow:"0 2px 8px rgba(27,42,74,0.2)"}}>{mode==="signin"?"Sign in":"Create my account"}</button>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontSize:11,color:"#A89E94"}}>Gastriot · Kitchen Intelligence · Berlin</div>
      </div>
    </div>
  );
}

/* ═══ DASHBOARD ═══ */
function DashboardP({wasteCost,wasteWeight,wasteLogs,invValue,lowStock,orders,activeAlerts,zones,ings,recipes,haccpLogs,sales,dailyRevenue,setPage,shoppingList}){
  const foodCostPct=dailyRevenue>0?(sales.reduce((s,x)=>s+x.cost,0)/dailyRevenue*100):0;
  const grossMargin=dailyRevenue-sales.reduce((s,x)=>s+x.cost,0);
  const compliance=Math.max(0,100-activeAlerts.length*7);
  const [chartMode,setChartMode]=useState("week");
  const todayWaste=wasteCost;
  const week=DAYS.map((d,i)=>({d,v:Math.round(80+Math.random()*120),w:Math.round(5+Math.random()*15)}));
  week[week.length-1]={d:DAYS[new Date().getDay()],v:Math.round(dailyRevenue),w:Math.round(todayWaste)};
  const month=["W1","W2","W3","W4"].map(d=>({d,v:Math.round(dailyRevenue*(3+Math.random()*4)),w:Math.round(todayWaste*(3+Math.random()*2))}));
  const annual=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(d=>({d,v:Math.round(dailyRevenue*(20+Math.random()*10)),w:Math.round(todayWaste*(18+Math.random()*8))}));
  const chartData=chartMode==="week"?week:chartMode==="month"?month:annual;
  const chartLabel=chartMode==="week"?"THIS WEEK":chartMode==="month"?"THIS MONTH":"THIS YEAR";
  const maxV=Math.max(...chartData.map(w=>Math.max(w.v,w.w)),1);

  return <div id="dashboard-content">
    <h2 style={{fontSize:22,fontWeight:700,marginBottom:16,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Today at a glance</h2>

    {/* Revenue + Waste hero */}
    <div style={{display:"flex",gap:12,marginBottom:14}}>
      {canSeeRevenue()&&<div style={{flex:1,background:T.card,borderRadius:14,padding:"16px 18px",cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,transition:"all 0.15s"}} onClick={()=>setPage("pos")}>
        <div style={{fontSize:10,fontWeight:700,color:T.mt,letterSpacing:0.8,textTransform:"uppercase"}}>Revenue</div>
        <div style={{fontSize:28,fontWeight:700,color:T.grn,fontFamily:"'DM Mono',monospace",marginTop:4}}>{fmtEur(dailyRevenue)}</div>
        <div style={{fontSize:10,color:T.dm,marginTop:2}}>{sales.length} tickets</div>
      </div>}
      <div style={{flex:1,background:T.card,borderRadius:14,padding:"16px 18px",cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,transition:"all 0.15s"}} onClick={()=>setPage("scale")}>
        <div style={{fontSize:10,fontWeight:700,color:T.mt,letterSpacing:0.8,textTransform:"uppercase"}}>Waste</div>
        {canSeeCost()
          ?<div style={{fontSize:28,fontWeight:700,color:T.red,fontFamily:"'DM Mono',monospace",marginTop:4}}>{fmtEur(wasteCost)}</div>
          :<div style={{fontSize:28,fontWeight:700,color:T.red,fontFamily:"'DM Mono',monospace",marginTop:4}}>{wasteWeight>=1000?(wasteWeight/1000).toFixed(1)+"kg":wasteWeight+"g"}</div>}
        <div style={{fontSize:10,color:T.dm,marginTop:2}}>{wasteWeight>=1000?(wasteWeight/1000).toFixed(1)+"kg":wasteWeight+"g"} · {wasteLogs.length} entries</div>
      </div>
    </div>

    <div style={{display:"grid",gridTemplateColumns:canSeeCost()?"1fr 1fr":"1fr",gap:12,marginBottom:14}}>
      {canSeeCost()&&<KpiButton onClick={()=>setPage("reports")} l="Food cost" v={`${foodCostPct.toFixed(1)}%`} c={foodCostPct<32?T.grn:foodCostPct<38?T.amb:T.red} s={fmtEur(grossMargin)+" margin"}/>}
      <KpiButton onClick={()=>setPage("inventory")} l="Inventory" v={canSeeCost()?fmtEur(invValue):`${ings.length}`} c={T.blu} s={`${ings.length} items`}/>
    </div>

    {activeAlerts.length>0&&<button onClick={()=>setPage("notifs")} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:T.redBg,borderRadius:12,padding:"12px 16px",marginBottom:10,cursor:"pointer",color:"inherit",fontFamily:"inherit",border:`1px solid ${T.red}15`}}><span style={{fontSize:13,color:T.red,fontWeight:600}}>{activeAlerts.length} active alert{activeAlerts.length>1?"s":""}</span><span style={{padding:"6px 14px",background:T.red,color:"#fff",borderRadius:8,fontSize:11,fontWeight:700,letterSpacing:0.3}}>Review</span></button>}
    {lowStock.length>0&&<button onClick={()=>setPage("orders")} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:T.ambBg,borderRadius:12,padding:"12px 16px",marginBottom:10,cursor:"pointer",color:"inherit",fontFamily:"inherit",border:`1px solid ${T.amb}15`}}><span style={{fontSize:13,color:T.amb,fontWeight:600}}>{lowStock.length} items below par</span><ChevronRight size={16} color={T.amb}/></button>}

    {canSeeRevenue()&&<button onClick={()=>setChartMode(p=>p==="week"?"month":p==="month"?"annual":"week")} style={{width:"100%",background:T.card,borderRadius:14,padding:"16px 18px",marginBottom:12,cursor:"pointer",color:"inherit",fontFamily:"inherit",textAlign:"left",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,transition:"all 0.15s"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:11,fontWeight:600,color:T.mt,letterSpacing:0.5}}>{chartLabel} · <span style={{color:T.grn}}>REVENUE</span> vs <span style={{color:T.red}}>WASTE</span></span>
        <span style={{fontSize:10,color:T.dm}}>tap to cycle ›</span>
      </div>
      <div style={{display:"flex",alignItems:"flex-end",gap:4,height:110,padding:"6px 0",overflow:"hidden"}}>
        {chartData.map((w,i)=><div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <div style={{fontSize:7,color:T.grn,fontWeight:600,fontFamily:"'DM Mono',monospace"}}>€{w.v}</div>
          <div style={{width:"100%",display:"flex",gap:1,alignItems:"flex-end",justifyContent:"center",height:60}}>
            <div style={{width:"48%",height:`${Math.min(58,Math.round((w.v/maxV)*58))}px`,background:`linear-gradient(180deg,${T.grn},${T.grn}88)`,borderRadius:"3px 3px 0 0",minHeight:2}}/>
            <div style={{width:"48%",height:`${Math.min(58,Math.round((w.w/maxV)*58))}px`,background:`linear-gradient(180deg,${T.red},${T.red}88)`,borderRadius:"3px 3px 0 0",minHeight:2}}/>
          </div>
          <div style={{fontSize:7,color:T.red,fontFamily:"'DM Mono',monospace"}}>€{w.w}</div>
          <div style={{fontSize:chartMode==="annual"?7:9,color:T.dm}}>{w.d}</div>
        </div>)}
      </div>
    </button>}

    <Card t="COMPLIANCE & ZONES">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div>
          <div style={{fontSize:24,fontWeight:700,color:compliance>85?T.grn:T.amb,fontFamily:"'DM Mono',monospace"}}>{compliance}%</div>
          <div style={{fontSize:10,color:T.mt}}>HACCP score</div>
        </div>
        <ShieldCheck size={38} color={compliance>85?T.grn:T.amb} strokeWidth={1.4}/>
      </div>
      {zones.slice(0,3).map(z=><div key={z.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderTop:`1px solid ${T.brd}`,fontSize:12}}>
        <span style={{color:T.mt}}>{z.name}</span>
        <span style={{color:z.ok?T.grn:T.red,fontWeight:600,fontFamily:"'DM Mono',monospace"}}>{z.current}°C</span>
      </div>)}
    </Card>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:8}}>
      <button onClick={()=>setPage("reports")} style={{padding:14,borderRadius:10,background:T.card,border:`1px solid ${T.brd}`,color:T.tx,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:6,fontFamily:"inherit"}}>
        <FileText size={22} color={T.pur}/><span style={{fontSize:12,fontWeight:600}}>Reports</span>
      </button>
      <button onClick={()=>setPage("orders")} style={{padding:14,borderRadius:10,background:T.card,border:`1px solid ${T.brd}`,color:T.tx,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:6,fontFamily:"inherit"}}>
        <ShoppingBag size={22} color={T.amb}/><span style={{fontSize:12,fontWeight:600}}>Shopping ({shoppingList.length})</span>
      </button>
    </div>
  </div>;
}

/* ═══ SALES REPORT IMPORT — photo of a POS end-of-day itemized report (Orderbird,
   SumUp — neither has a real-time webhook, but both export itemized daily reports)
   → matched against active menu items → replayed through the same onSale() a
   normal POS ring-up uses, so revenue, inventory deduction, batch deduction, and
   reorder alerts all happen exactly the same way. ═══ */
function SalesReportImportModal({recipes,onSale,onClose,showToast}){
  const [scanning,setScanning]=useState(false);
  const [scanErr,setScanErr]=useState("");
  const [rows,setRows]=useState(null); // null = not scanned yet; [] = scanned, nothing found

  const activeRecipes=recipes.filter(r=>r.status==="active");

  const scanPhoto=async(file)=>{
    setScanning(true);setScanErr("");
    try{
      const mediaType=file.type||"image/jpeg";
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=()=>rej("Read failed");r.readAsDataURL(file);});
      let items=null,lastMsg="";
      for(let attempt=1;attempt<=3;attempt++){
        const resp=await fetch(`${SCAN_API}/scan/sales-report`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({image:base64,mediaType})});
        if(resp.ok){const d=await resp.json();if(d.ok&&Array.isArray(d.items)){items=d.items;break;}lastMsg=d.error||"No items found";}
        else{const e=await resp.json().catch(()=>({}));lastMsg=e.error||`Failed (${resp.status})`;
          if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){setScanErr(`Busy — retrying (${attempt}/3)…`);await new Promise(r=>setTimeout(r,2000*attempt));continue;}}
        break;
      }
      if(!items)throw new Error(lastMsg||"No items found — try a clearer photo");
      setRows(items.map(it=>{
        const match=fuzzyMatchItem(it.name,activeRecipes);
        return {id:genId(),name:it.name||"",recipeId:match?.id||"",qty:it.qty||1};
      }));
      showToast(`Report scanned — ${items.length} item${items.length>1?"s":""} found`);
    }catch(e){
      setScanErr(e.message||"Could not read report");
      if(showToast)showToast("Scan failed — try a clearer photo");
    }
    setScanning(false);
  };

  const updRow=(id,patch)=>setRows(p=>p.map(r=>r.id===id?{...r,...patch}:r));
  const rmRow=(id)=>setRows(p=>p.filter(r=>r.id!==id));

  const apply=()=>{
    const toApply=rows.filter(r=>r.recipeId&&parseFloat(r.qty)>0);
    toApply.forEach(r=>{
      const recipe=recipes.find(x=>x.id===r.recipeId);
      if(recipe)onSale(recipe,parseFloat(r.qty));
    });
    onClose();
  };

  return <div style={{position:"fixed",inset:0,zIndex:200,background:T.bg,overflowY:"auto",padding:16,display:"flex",flexDirection:"column"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
      <div style={{fontSize:18,fontWeight:800}}>Import Sales Report</div>
      <button onClick={onClose} style={{width:36,height:36,borderRadius:10,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={18}/></button>
    </div>

    {!rows&&<>
      <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Photo or screenshot of your POS's itemized daily/EOD report (Orderbird, SumUp, or similar). Each item gets rung up automatically — inventory, revenue, and reorder alerts all update the same as a normal sale.</div>
      <label style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:10,border:`2px dashed ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>
        <Camera size={20}/>{scanning?"Reading report…":"Scan sales report"}
        <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanPhoto(e.target.files[0]);}} style={{display:"none"}}/>
      </label>
      {scanErr&&<div style={{marginTop:8,padding:"8px 12px",background:T.redBg,color:T.red,borderRadius:8,fontSize:12}}>{scanErr}</div>}
    </>}

    {rows&&<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600}}>ITEMS SOLD ({rows.length})</div>
      <div style={{flex:1,overflowY:"auto"}}>
        {rows.length===0?<Empty msg="No items detected."/>:
          rows.map(r=><div key={r.id} style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,background:T.card,border:`1px solid ${T.brd}`,borderRadius:10,padding:8}}>
            <select value={r.recipeId} onChange={e=>updRow(r.id,{recipeId:e.target.value})} style={{...inp,flex:2,marginBottom:0,fontSize:12}}>
              <option value="">{`— unmatched: "${r.name}" —`}</option>
              {activeRecipes.map(rc=><option key={rc.id} value={rc.id}>{rc.name}</option>)}
            </select>
            <input type="number" value={r.qty} onChange={e=>updRow(r.id,{qty:e.target.value})} placeholder="0" style={{...inp,width:60,marginBottom:0,fontSize:12}}/>
            <button onClick={()=>rmRow(r.id)} style={{width:30,height:30,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><X size={13}/></button>
          </div>)}
      </div>
      <div style={{fontSize:11,color:T.mt,marginTop:4,marginBottom:8}}>Unmatched items are skipped — pick a menu item to include them, or leave blank to ignore.</div>
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label={`Apply ${rows.filter(r=>r.recipeId).length} sale${rows.filter(r=>r.recipeId).length===1?"":"s"}`} onClick={apply}/>
    </div>}
  </div>;
}

/* ═══ POS — with Specials tab ═══ */
function PosP({recipes,ings,sales,dailyRevenue,onSale,onComboSale,specials,setSpecials,showToast}){
  const [tab,setTab]=useState("menu");
  const [cart,setCart]=useState([]);
  const [search,setSearch]=useState("");
  const [importingReport,setImportingReport]=useState(false);

  const activeMenu=recipes.filter(r=>r.status==="active");
  const filtered=activeMenu.filter(r=>!search||r.name.toLowerCase().includes(search.toLowerCase()));
  const activeSpecials=specials.filter(s=>s.active);

  /* Cart line shapes:
     - Recipe line:  {kind:"recipe", recipeId, qty, specialPct}
     - Combo line:   {kind:"combo", comboId, qty, picks:[recipeId, ...]}   picks length = combo.count * qty
  */
  const lineTotal=(c)=>{
    if(c.kind==="combo"){
      const combo=specials.find(s=>s.id===c.comboId);
      return combo?combo.price*c.qty:0;
    }
    const r=recipes.find(x=>x.id===c.recipeId);if(!r)return 0;
    const base=r.price*c.qty;
    return c.specialPct>0?base*(1-c.specialPct/100):base;
  };
  const lineCost=(c)=>{
    if(c.kind==="combo"){
      // sum component costs of all picks, times qty
      const singleCost=c.picks.reduce((s,rid)=>{const r=recipes.find(x=>x.id===rid);return s+(r?calcRecipeCost(r,ings):0);},0);
      return singleCost*c.qty;
    }
    const r=recipes.find(x=>x.id===c.recipeId);
    return r?calcRecipeCost(r,ings)*c.qty:0;
  };
  const cartTotal=cart.reduce((s,c)=>s+lineTotal(c),0);
  const cartCost=cart.reduce((s,c)=>s+lineCost(c),0);

  const addToCart=(recipe,specialPct=0)=>{
    setCart(p=>{
      const ex=p.find(x=>x.kind==="recipe"&&x.recipeId===recipe.id&&x.specialPct===specialPct);
      if(ex)return p.map(x=>x===ex?{...x,qty:x.qty+1}:x);
      return [...p,{kind:"recipe",recipeId:recipe.id,qty:1,specialPct}];
    });
  };

  const addComboToCart=(combo,picks)=>{
    // picks: array of recipe ids, length combo.count
    setCart(p=>[...p,{kind:"combo",comboId:combo.id,qty:1,picks:[...picks]}]);
    setTab("cart");
  };

  const checkout=()=>{
    if(cart.length===0)return;
    cart.forEach(c=>{
      if(c.kind==="combo"){
        const combo=specials.find(s=>s.id===c.comboId);
        if(combo&&onComboSale){
          // For qty>1, picks were stored for one combo; replicate for the count
          const allPicks=[];
          for(let i=0;i<c.qty;i++)allPicks.push(...c.picks);
          onComboSale(combo,allPicks,c.qty);
        }
      }else{
        const r=recipes.find(x=>x.id===c.recipeId);
        if(r)onSale(r,c.qty,c.specialPct||0);
      }
    });
    setCart([]);
    setTab("menu");
  };

  return <div>
    <Tabs tabs={[
      {id:"menu",label:"Menu"},
      {id:"specials",label:`Specials${activeSpecials.length?` (${activeSpecials.length})`:""}`},
      {id:"cart",label:`Cart${cart.length?` (${cart.length})`:""}`},
      {id:"today",label:"Today"},
    ]} active={tab} set={setTab}/>

    {tab==="menu"&&<>
      <SearchInp value={search} set={setSearch}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
        {filtered.map(r=>{
          const cost=calcRecipeCost(r,ings);
          const margin=((r.price-cost)/r.price*100);
          return <button key={r.id} onClick={()=>addToCart(r)} style={{padding:14,borderRadius:10,background:T.card,border:`1px solid ${T.brd}`,color:T.tx,cursor:"pointer",textAlign:"left",fontFamily:"inherit",minHeight:104}}>
            <div style={{fontSize:13,fontWeight:600,lineHeight:1.25,marginBottom:6}}>{r.name}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:18,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(r.price)}</span>
              <span style={{fontSize:10,color:margin>60?T.grn:T.amb}}>{margin.toFixed(0)}% marg</span>
            </div>
          </button>;
        })}
      </div>
    </>}

    {tab==="specials"&&<SpecialsTab recipes={recipes} ings={ings} specials={specials} setSpecials={setSpecials} addToCart={addToCart} addComboToCart={addComboToCart}/>}

    {tab==="cart"&&<>
      {cart.length===0?<Empty msg="Cart is empty. Pick from menu or specials."/>:<>
        {cart.map((c,idx)=>{
          if(c.kind==="combo"){
            const combo=specials.find(s=>s.id===c.comboId);
            if(!combo)return null;
            const pickNames=c.picks.map(rid=>recipes.find(r=>r.id===rid)?.name||"?").join(" · ");
            return <div key={idx} style={{background:T.card,border:`1px solid ${T.amb}55`,borderRadius:10,padding:12,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
                    <Tag size={14} color={T.amb}/>
                    {combo.name} <span style={{fontSize:11,color:T.amb,padding:"2px 6px",borderRadius:6,background:T.ambBg,fontWeight:700}}>COMBO · {combo.count} for {fmtEur(combo.price)}</span>
                  </div>
                  <div style={{fontSize:11,color:T.mt,lineHeight:1.5}}>{pickNames}</div>
                  <div style={{fontSize:11,color:T.dm,marginTop:4}}>{fmtEur(combo.price)} × {c.qty} = {fmtEur(combo.price*c.qty)}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button onClick={()=>setCart(p=>p.map((x,i)=>i===idx?{...x,qty:Math.max(1,x.qty-1)}:x))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>−</button>
                  <span style={{fontSize:18,fontWeight:700,minWidth:24,textAlign:"center"}}>{c.qty}</span>
                  <button onClick={()=>setCart(p=>p.map((x,i)=>i===idx?{...x,qty:x.qty+1}:x))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>+</button>
                  <button onClick={()=>setCart(p=>p.filter((_,i)=>i!==idx))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.red}33`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={16}/></button>
                </div>
              </div>
            </div>;
          }
          const r=recipes.find(x=>x.id===c.recipeId);if(!r)return null;
          const base=r.price*c.qty;
          const final=c.specialPct>0?base*(1-c.specialPct/100):base;
          return <div key={idx} style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600,marginBottom:2}}>{r.name}{c.specialPct>0&&<span style={{color:T.red,fontSize:11,marginLeft:6}}>−{c.specialPct}%</span>}</div>
              <div style={{fontSize:11,color:T.mt}}>{fmtEur(r.price)} × {c.qty} = {fmtEur(final)}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button onClick={()=>setCart(p=>p.map((x,i)=>i===idx?{...x,qty:Math.max(1,x.qty-1)}:x))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>−</button>
              <span style={{fontSize:18,fontWeight:700,minWidth:24,textAlign:"center"}}>{c.qty}</span>
              <button onClick={()=>setCart(p=>p.map((x,i)=>i===idx?{...x,qty:x.qty+1}:x))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>+</button>
              <button onClick={()=>setCart(p=>p.filter((_,i)=>i!==idx))} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.red}33`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={16}/></button>
            </div>
          </div>;
        })}
        <div style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginTop:8}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.mt,marginBottom:4}}><span>Cost</span><span>{fmtEur(cartCost)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.grn,marginBottom:10}}><span>Margin</span><span>{fmtEur(cartTotal-cartCost)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${T.brd}`,paddingTop:10}}><span style={{fontSize:14,color:T.tx,fontWeight:600}}>TOTAL</span><span style={{fontSize:26,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(cartTotal)}</span></div>
        </div>
        <PrimaryBtn color={T.grn} icon={<Check size={20}/>} label={`CHARGE ${fmtEur(cartTotal)}`} onClick={checkout} style={{marginTop:12,fontSize:16,padding:18}}/>
        <ExportBtn label="Print ticket" onClick={()=>printSection("dashboard-content","Cart Receipt")}/>
      </>}
    </>}

    {tab==="today"&&<>
      {importingReport&&<SalesReportImportModal recipes={recipes} onSale={onSale} showToast={showToast} onClose={()=>setImportingReport(false)}/>}
      <Banner c={T.grn} bg={T.grnBg} l="REVENUE TODAY" v={fmtEur(dailyRevenue)}/>
      <button onClick={()=>setImportingReport(true)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:12,borderRadius:10,border:`1.5px dashed ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,marginBottom:12}}><Camera size={16}/>Import sales report (Orderbird / SumUp)</button>
      <div id="today-pos-report">
        {sales.length===0?<Empty msg="No sales yet today"/>:sales.slice(0,20).map(s=><div key={s.id} style={{background:T.card,border:`1px solid ${T.brd}`,borderRadius:8,padding:"10px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.qty}× {s.recipe}</div>
            <div style={{fontSize:10,color:T.dm}}>{s.time}{s.combo?" · combo":s.special?` · special −${s.specialPct}%`:""}</div>
          </div>
          <span style={{fontSize:15,fontWeight:600,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(s.total)}</span>
        </div>)}
      </div>
      <ExportBtn label="Print today's sales" onClick={()=>printSection("today-pos-report","POS — Sales Today")}/>
    </>}
  </div>;
}

/* ═══ SPECIALS TAB (inside POS)
   Supports two types:
     - discount: single item, % off
     - combo:    "X for Y" — pick N items from an eligible list for a flat price
   ═══ */
function SpecialsTab({recipes,ings,specials,setSpecials,addToCart,addComboToCart}){
  const [adding,setAdding]=useState(false);
  const [newType,setNewType]=useState("discount");
  // discount fields
  const [newRec,setNewRec]=useState("");
  const [newPct,setNewPct]=useState(20);
  const [newNote,setNewNote]=useState("");
  // combo fields
  const [comboName,setComboName]=useState("");
  const [comboCount,setComboCount]=useState(3);
  const [comboPrice,setComboPrice]=useState(15);
  const [comboRecipes,setComboRecipes]=useState([]);
  // edit state (for editing existing combos/specials inline)
  const [editingId,setEditingId]=useState(null);
  // picker state for ringing up a combo
  const [pickerComboId,setPickerComboId]=useState(null);
  const [picks,setPicks]=useState([]);

  const activeRecipes=recipes.filter(r=>r.status==="active");

  const resetForm=()=>{
    setNewType("discount");setNewRec("");setNewPct(20);setNewNote("");
    setComboName("");setComboCount(3);setComboPrice(15);setComboRecipes([]);
    setAdding(false);
  };

  const addDiscount=()=>{
    if(!newRec)return;
    setSpecials(p=>[...p,{id:genId(),type:"discount",recipeId:newRec,discountPct:parseInt(newPct)||0,active:true,note:newNote}]);
    resetForm();
  };

  const addCombo=()=>{
    if(!comboName||comboRecipes.length===0||!comboCount||!comboPrice)return;
    setSpecials(p=>[...p,{id:genId(),type:"combo",name:comboName,count:parseInt(comboCount)||1,price:parseFloat(comboPrice)||0,recipeIds:[...comboRecipes],active:true,note:""}]);
    resetForm();
  };

  const toggleComboRecipe=(rid)=>setComboRecipes(p=>p.includes(rid)?p.filter(x=>x!==rid):[...p,rid]);

  const updateSpecial=(id,patch)=>setSpecials(p=>p.map(x=>x.id===id?{...x,...patch}:x));

  // Combo picker — staff chooses combo.count items from eligible list, then confirms
  const openPicker=(combo)=>{setPickerComboId(combo.id);setPicks([]);};
  const closePicker=()=>{setPickerComboId(null);setPicks([]);};
  const togglePick=(rid,maxCount)=>{
    setPicks(p=>{
      if(p.length>=maxCount&&!p.includes(rid)){
        // replace oldest pick — feels more forgiving than blocking
        return [...p.slice(1),rid];
      }
      // allow duplicates: each tap appends, X removes
      return [...p,rid];
    });
  };
  const removePickAt=(idx)=>setPicks(p=>p.filter((_,i)=>i!==idx));
  const confirmCombo=()=>{
    const combo=specials.find(s=>s.id===pickerComboId);
    if(!combo||picks.length!==combo.count)return;
    addComboToCart(combo,picks);
    closePicker();
  };

  const pickerCombo=specials.find(s=>s.id===pickerComboId);

  return <>
    {/* ── ADD-NEW BUTTON ── */}
    {!adding&&<button onClick={()=>setAdding(true)} style={{width:"100%",padding:14,borderRadius:10,border:`2px dashed ${T.brd}`,background:"none",color:T.amb,cursor:"pointer",fontSize:14,fontWeight:600,marginBottom:10,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
      <Tag size={18}/>Add special
    </button>}

    {/* ── ADD-NEW FORM ── */}
    {adding&&<div style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:10}}>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <button onClick={()=>setNewType("discount")} style={{flex:1,padding:12,borderRadius:8,border:`1px solid ${newType==="discount"?T.amb:T.brd}`,background:newType==="discount"?T.ambBg:T.sf,color:newType==="discount"?T.amb:T.mt,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>Discount %</button>
        <button onClick={()=>setNewType("combo")} style={{flex:1,padding:12,borderRadius:8,border:`1px solid ${newType==="combo"?T.amb:T.brd}`,background:newType==="combo"?T.ambBg:T.sf,color:newType==="combo"?T.amb:T.mt,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}>Combo (X for Y)</button>
      </div>

      {newType==="discount"&&<>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>MENU ITEM</div>
        <select value={newRec} onChange={e=>setNewRec(e.target.value)} style={inp}>
          <option value="">— Select menu item —</option>
          {activeRecipes.map(r=><option key={r.id} value={r.id}>{r.name} ({fmtEur(r.price)})</option>)}
        </select>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>DISCOUNT %</div>
            <input type="number" value={newPct} onChange={e=>setNewPct(e.target.value)} placeholder="20" style={inp}/>
          </div>
          <div style={{flex:2}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>NOTE (OPTIONAL)</div>
            <input value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="e.g. Weekday special" style={inp}/>
          </div>
        </div>
        <PrimaryBtn color={T.amb} icon={<Plus size={18}/>} label="Create discount" onClick={addDiscount}/>
      </>}

      {newType==="combo"&&<>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>COMBO NAME</div>
        <input value={comboName} onChange={e=>setComboName(e.target.value)} placeholder='e.g. "Brunch trio"' style={inp}/>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>COUNT</div>
            <input type="number" min="2" value={comboCount} onChange={e=>setComboCount(e.target.value)} placeholder="e.g. 3" style={{...inp,marginBottom:10}}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>PRICE €</div>
            <input type="number" step="0.01" value={comboPrice} onChange={e=>setComboPrice(e.target.value)} placeholder="e.g. 15" style={{...inp,marginBottom:10}}/>
          </div>
        </div>
        <div style={{padding:10,background:T.ambBg,border:`1px solid ${T.amb}33`,borderRadius:8,marginBottom:10,fontSize:13,color:T.amb,fontWeight:600,textAlign:"center"}}>
          {comboCount||"?"} items for {fmtEur(parseFloat(comboPrice)||0)}
        </div>
        <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600,letterSpacing:0.5}}>ELIGIBLE ITEMS ({comboRecipes.length} selected)</div>
        <div style={{maxHeight:220,overflowY:"auto",border:`1px solid ${T.brd}`,borderRadius:8,padding:6,marginBottom:10}}>
          {activeRecipes.map(r=><label key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,cursor:"pointer",background:comboRecipes.includes(r.id)?T.ambBg:"none"}}>
            <input type="checkbox" checked={comboRecipes.includes(r.id)} onChange={()=>toggleComboRecipe(r.id)} style={{width:18,height:18,accentColor:T.amb}}/>
            <span style={{flex:1,fontSize:13}}>{r.name}</span>
            <span style={{fontSize:11,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{fmtEur(r.price)}</span>
          </label>)}
        </div>
        <PrimaryBtn color={T.amb} icon={<Plus size={18}/>} label="Create combo" onClick={addCombo}/>
      </>}

      <button onClick={resetForm} style={{width:"100%",marginTop:8,padding:10,borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Cancel</button>
    </div>}

    {/* ── COMBO PICKER MODAL ── */}
    {pickerCombo&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0}}>
      <div style={{width:"100%",maxWidth:520,background:T.card,borderTopLeftRadius:16,borderTopRightRadius:16,padding:16,maxHeight:"90vh",overflowY:"auto",borderTop:`2px solid ${T.amb}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:T.amb}}>{pickerCombo.name}</div>
            <div style={{fontSize:12,color:T.mt}}>Pick {pickerCombo.count} items for {fmtEur(pickerCombo.price)}</div>
          </div>
          <button onClick={closePicker} style={{width:40,height:40,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={18}/></button>
        </div>

        {/* Selected picks */}
        <div style={{padding:10,background:T.sf,borderRadius:8,marginBottom:10,minHeight:54}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600}}>SELECTED ({picks.length} / {pickerCombo.count})</div>
          {picks.length===0?<div style={{fontSize:12,color:T.dm,fontStyle:"italic"}}>Tap items below to add</div>:
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {picks.map((rid,idx)=>{
                const r=recipes.find(x=>x.id===rid);if(!r)return null;
                return <button key={idx} onClick={()=>removePickAt(idx)} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:8,border:`1px solid ${T.amb}55`,background:T.ambBg,color:T.amb,fontSize:12,fontFamily:"inherit",cursor:"pointer"}}>
                  {r.name}<X size={12}/>
                </button>;
              })}
            </div>}
        </div>

        {/* Eligible items */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:8,marginBottom:10}}>
          {pickerCombo.recipeIds.map(rid=>{
            const r=recipes.find(x=>x.id===rid);if(!r)return null;
            const pickedCount=picks.filter(p=>p===rid).length;
            return <button key={rid} onClick={()=>togglePick(rid,pickerCombo.count)} style={{padding:12,borderRadius:10,border:`1px solid ${pickedCount>0?T.amb:T.brd}`,background:pickedCount>0?T.ambBg:T.sf,color:T.tx,cursor:"pointer",fontFamily:"inherit",textAlign:"left",position:"relative",minHeight:80}}>
              <div style={{fontSize:12,fontWeight:600,lineHeight:1.3,marginBottom:4}}>{r.name}</div>
              <div style={{fontSize:11,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{fmtEur(r.price)}</div>
              {pickedCount>0&&<div style={{position:"absolute",top:6,right:6,minWidth:20,height:20,padding:"0 6px",borderRadius:10,background:T.amb,color:"#1B2A4A",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>×{pickedCount}</div>}
            </button>;
          })}
        </div>

        <PrimaryBtn color={picks.length===pickerCombo.count?T.grn:T.dm} icon={<Check size={18}/>} label={picks.length===pickerCombo.count?`Add combo · ${fmtEur(pickerCombo.price)}`:`Pick ${pickerCombo.count-picks.length} more`} onClick={confirmCombo}/>
      </div>
    </div>}

    {/* ── EXISTING SPECIALS LIST ── */}
    {specials.length===0?<Empty msg="No specials yet. Tap above to add a discount or a combo."/>:
      specials.map(sp=>{
        const isEditing=editingId===sp.id;
        if(sp.type==="combo"){
          return <div key={sp.id} style={{background:T.card,border:`1px solid ${sp.active?T.amb+"55":T.brd}`,borderRadius:10,padding:14,marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:T.ambBg,color:T.amb,letterSpacing:0.5}}>COMBO</span>
                  <span style={{fontSize:15,fontWeight:600}}>{sp.name}</span>
                </div>
                {!isEditing?<>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:22,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{sp.count} for {fmtEur(sp.price)}</span>
                  </div>
                  <div style={{fontSize:11,color:T.mt}}>Pick from {sp.recipeIds.length} eligible items</div>
                </>:<div style={{display:"flex",gap:8,marginTop:6}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:T.mt,marginBottom:2,fontWeight:600}}>COUNT</div>
                    <input type="number" value={sp.count} onChange={e=>updateSpecial(sp.id,{count:parseInt(e.target.value)||1})} style={{...inp,marginBottom:0}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:T.mt,marginBottom:2,fontWeight:600}}>PRICE €</div>
                    <input type="number" step="0.01" value={sp.price} onChange={e=>updateSpecial(sp.id,{price:parseFloat(e.target.value)||0})} style={{...inp,marginBottom:0}}/>
                  </div>
                </div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <button onClick={()=>updateSpecial(sp.id,{active:!sp.active})} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:sp.active?T.grnBg:"none",color:sp.active?T.grn:T.dm,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600,minWidth:48}}>{sp.active?"ON":"OFF"}</button>
                <button onClick={()=>setEditingId(isEditing?null:sp.id)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:isEditing?T.bluBg:T.sf,color:isEditing?T.blu:T.mt,fontSize:11,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Edit3 size={12}/>{isEditing?"Done":"Edit"}</button>
                <button onClick={()=>setSpecials(p=>p.filter(x=>x.id!==sp.id))} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
              </div>
            </div>

            {isEditing&&<div style={{marginTop:8,paddingTop:10,borderTop:`1px solid ${T.brd}`}}>
              <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600,letterSpacing:0.5}}>ELIGIBLE ITEMS ({sp.recipeIds.length})</div>
              <div style={{maxHeight:200,overflowY:"auto",border:`1px solid ${T.brd}`,borderRadius:8,padding:6}}>
                {activeRecipes.map(r=><label key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",borderRadius:6,cursor:"pointer",background:sp.recipeIds.includes(r.id)?T.ambBg:"none"}}>
                  <input type="checkbox" checked={sp.recipeIds.includes(r.id)} onChange={()=>{
                    const next=sp.recipeIds.includes(r.id)?sp.recipeIds.filter(x=>x!==r.id):[...sp.recipeIds,r.id];
                    updateSpecial(sp.id,{recipeIds:next});
                  }} style={{width:18,height:18,accentColor:T.amb}}/>
                  <span style={{flex:1,fontSize:13}}>{r.name}</span>
                  <span style={{fontSize:11,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{fmtEur(r.price)}</span>
                </label>)}
              </div>
            </div>}

            {sp.active&&!isEditing&&<PrimaryBtn color={T.amb} icon={<Plus size={18}/>} label={`Ring up · ${sp.count} for ${fmtEur(sp.price)}`} onClick={()=>openPicker(sp)} style={{marginTop:4}}/>}
          </div>;
        }

        // Discount-type special
        const r=recipes.find(x=>x.id===sp.recipeId);if(!r)return null;
        const finalPrice=r.price*(1-sp.discountPct/100);
        return <div key={sp.id} style={{background:T.card,border:`1px solid ${sp.active?T.amb+"55":T.brd}`,borderRadius:10,padding:14,marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:8}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:6,background:T.redBg,color:T.red,letterSpacing:0.5}}>DISCOUNT</span>
                <span style={{fontSize:14,fontWeight:600}}>{r.name}</span>
              </div>
              {sp.note&&<div style={{fontSize:11,color:T.mt,marginBottom:4,fontStyle:"italic"}}>{sp.note}</div>}
              {!isEditing?<div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:13,color:T.dm,textDecoration:"line-through"}}>{fmtEur(r.price)}</span>
                <span style={{fontSize:20,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(finalPrice)}</span>
                <span style={{fontSize:12,color:T.red,fontWeight:700,padding:"3px 8px",borderRadius:6,background:T.redBg}}>−{sp.discountPct}%</span>
              </div>:<><div style={{fontSize:10,color:T.mt,marginBottom:2,fontWeight:600,marginTop:6}}>DISCOUNT %</div><input type="number" value={sp.discountPct} onChange={e=>updateSpecial(sp.id,{discountPct:parseInt(e.target.value)||0})} placeholder="20" style={{...inp,marginBottom:0}}/></>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button onClick={()=>updateSpecial(sp.id,{active:!sp.active})} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:sp.active?T.grnBg:"none",color:sp.active?T.grn:T.dm,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600,minWidth:48}}>{sp.active?"ON":"OFF"}</button>
              <button onClick={()=>setEditingId(isEditing?null:sp.id)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:isEditing?T.bluBg:T.sf,color:isEditing?T.blu:T.mt,fontSize:11,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Edit3 size={12}/>{isEditing?"Done":"Edit"}</button>
              <button onClick={()=>setSpecials(p=>p.filter(x=>x.id!==sp.id))} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
            </div>
          </div>
          {sp.active&&!isEditing&&<PrimaryBtn color={T.amb} icon={<Plus size={18}/>} label="Add to cart" onClick={()=>addToCart(r,sp.discountPct)} style={{marginTop:4}}/>}
        </div>;
      })}
  </>;
}

/* ═══ SCALE + WASTE (combined) — waste module as expandable panel at top ═══ */
function ScaleWasteP({ings,bleDevice,setBleDevice,bleWeight,setBleWeight,showToast,cats,onLog,wasteCost,wasteWeight,wasteLogs,setWasteLogs,wasteBins,setWasteBins}){
  const [tab,setTab]=useState("portion");
  const [target,setTarget]=useState(150);
  const [tolerance,setTolerance]=useState(10);
  const [manualWeight,setManualWeight]=useState(0);
  const [scanning,setScanning]=useState(false);
  const [wasteOpen,setWasteOpen]=useState(false);
  // Waste form
  const [wCat,setWCat]=useState("");
  const [wIng,setWIng]=useState("");
  const [wWeight,setWWeight]=useState(0);
  const [wBin,setWBin]=useState("");
  // Bin management
  const [newBinName,setNewBinName]=useState("");
  const [gastriotScale,setGastriotScale]=useState(false);

  const liveWeight=bleWeight!=null?bleWeight:manualWeight;
  const diff=liveWeight-target;
  const inTolerance=Math.abs(diff)<=tolerance;

  useEffect(()=>{if(bleWeight!=null)setWWeight(bleWeight);},[bleWeight]);

  const selectedIngObj=ings.find(i=>i.id===wIng);
  const isPcItem=selectedIngObj&&selectedIngObj.unit==="pc";

  const submitWaste=()=>{
    const ingObj=selectedIngObj;
    const catObj=cats.find(c=>c.id===wCat);
    if(!wCat||!wIng||!ingObj||!catObj||wWeight<=0)return;
    // For piece items: wWeight = piece count, calculate gram weight
    const pieces=isPcItem?Math.max(1,Math.round(wWeight)):0;
    const gramWeight=isPcItem?pieces*ingObj.unitG:wWeight;
    if(gramWeight<=0)return;
    const binLabel=wBin?wasteBins.find(b=>b.id===wBin)?.name||"":"";
    const isUnavoidable=catObj.avoidable===false||binLabel.toLowerCase().includes("unavoidable");
    const costPerG=ingObj.cost/ingObj.unitG;
    const cost=isUnavoidable?0:costPerG*gramWeight;
    onLog({id:genId(),category:catObj.name,ingredient:ingObj.name,weight:gramWeight,pieces,cost,bin:binLabel,avoidable:!isUnavoidable,shift:getShift(),time:new Date().toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}),timestamp:new Date().toISOString()});
    setWCat("");setWIng("");setWWeight(0);
    showToast(`Waste logged${isPcItem?` (${pieces} pc ≈ ${gramWeight}g)`:""}${isUnavoidable?" — unavoidable":""}`);
  };

  const addBin=()=>{
    if(!newBinName.trim())return;
    setWasteBins(p=>[...p,{id:genId(),name:newBinName.trim(),nfcTag:""}]);
    setNewBinName("");
    showToast("Bin added");
  };

  const byCat=useMemo(()=>{const m={};wasteLogs.forEach(l=>{if(!m[l.category])m[l.category]={cost:0,weight:0};m[l.category].cost+=l.avoidable!==false?l.cost:0;m[l.category].weight+=l.weight||0;});return Object.entries(m).sort((a,b)=>b[1].cost-a[1].cost);},[wasteLogs]);

  const pairBluetooth=async()=>{
    if(!navigator.bluetooth){showToast("Web Bluetooth not supported. Use Chrome on Android/desktop.");return;}
    try{
      setScanning(true);
      const device=await navigator.bluetooth.requestDevice({
        filters:[{services:["weight_scale"]},{services:[0x181D]},{namePrefix:"OAXIS"},{namePrefix:"Etekcity"},{namePrefix:"Renpho"},{namePrefix:"Eufy"}],
        optionalServices:["weight_scale","battery_service"],
      });
      setBleDevice({id:device.id,name:device.name||"Scale",connected:true});
      showToast(`Paired: ${device.name||"Scale"}`);
      const server=await device.gatt.connect();
      try{
        const service=await server.getPrimaryService("weight_scale");
        const char=await service.getCharacteristic("weight_measurement");
        await char.startNotifications();
        char.addEventListener("characteristicvaluechanged",(e)=>{
          const v=e.target.value;const weightKg=v.getUint16(1,true)*0.005;
          setBleWeight(Math.round(weightKg*1000));
        });
      }catch(e){showToast("Gekoppelt aber Service-Fehler — manuelle Eingabe verwenden");}
      device.addEventListener("gattserverdisconnected",()=>{setBleDevice(null);setBleWeight(null);showToast("Scale disconnected");});
    }catch(e){if(e.name!=="NotFoundError")showToast("Pairing failed: "+e.message);}finally{setScanning(false);}
  };

  return <div>
    {/* ── Waste module — expandable panel at top ── */}
    <button onClick={()=>setWasteOpen(!wasteOpen)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:10,border:`1px solid ${T.red}55`,background:wasteOpen?T.redBg:T.card,color:T.red,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>
      <span style={{display:"flex",alignItems:"center",gap:10,fontSize:14,fontWeight:700}}><Trash2 size={20}/>Abfall · {fmtEur(wasteCost)} · {wasteWeight>=1000?(wasteWeight/1000).toFixed(1)+"kg":wasteWeight+"g"}</span>
      {wasteOpen?<ChevronUp size={18}/>:<ChevronDown size={18}/>}
    </button>

    {wasteOpen&&<div style={{marginBottom:12}}>
      <Tabs tabs={[
        {id:"wlog",label:"Log waste"},
        {id:"wbins",label:`Bins (${wasteBins.length})`},
        {id:"wscale",label:"Gastriot scale"},
        {id:"whistory",label:`History (${wasteLogs.length})`},
        {id:"wanalytics",label:"Analytics"},
      ]} active={tab==="wlog"||tab==="whistory"||tab==="wanalytics"||tab==="wbins"||tab==="wscale"?tab:"wlog"} set={setTab}/>

      {tab==="wlog"&&<>
        <Card t="CATEGORY">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {cats.map(c=><button key={c.id} onClick={()=>setWCat(c.id)} style={{padding:14,borderRadius:10,border:`2px solid ${wCat===c.id?c.color:T.brd}`,background:wCat===c.id?c.color+"22":T.sf,color:wCat===c.id?c.color:T.tx,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>{c.name}</button>)}
          </div>
        </Card>
        <Card t="INGREDIENT">
          <IngredientPicker ings={ings} value={wIng} onChange={setWIng} placeholder="Type to search…"/>
        </Card>
        <Card t={`WEIGHT (g)${bleWeight!=null?" · live from scale":""}`}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>{isPcItem?`PIECES (1 pc ≈ ${selectedIngObj.unitG}g)`:"WEIGHT IN GRAMS"}</div>
          <input type="number" value={wWeight||""} onChange={e=>setWWeight(parseFloat(e.target.value)||0)} placeholder="0" style={{...inp,fontSize:24,fontWeight:700,fontFamily:"'DM Mono',monospace",textAlign:"center"}}/>
        </Card>
        {wasteBins.length>0&&<Card t="BIN (OPTIONAL)">
          <select value={wBin} onChange={e=>setWBin(e.target.value)} style={inp}>
            <option value="">— No bin —</option>
            {wasteBins.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Card>}
        <PrimaryBtn color={T.red} icon={<Check size={20}/>} label="LOG WASTE" onClick={submitWaste}/>
      </>}

      {tab==="wbins"&&<>
        <Card t="WASTE BINS (NFC-tagged)">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Label distinct waste streams. NFC-tagged bins auto-select category when scanned.</div>
          {wasteBins.length===0?<Empty msg="No bins configured"/>:wasteBins.map(b=><div key={b.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${T.brd}33`}}>
            <Trash2 size={18} color={T.amb}/>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:600}}>{b.name}</div>
              <div style={{fontSize:10,color:T.dm}}>{b.nfcTag?"NFC: "+b.nfcTag:"No NFC tag assigned"}</div>
            </div>
            <button onClick={()=>setWasteBins(p=>p.filter(x=>x.id!==b.id))} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={14}/></button>
          </div>)}
        </Card>
        <Card t="ADD BIN">
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>BIN NAME</div>
          <div style={{display:"flex",gap:8}}>
            <input value={newBinName} onChange={e=>setNewBinName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addBin()} placeholder="e.g. Unavoidable waste, Avoidable waste, Paper waste" style={{...inp,flex:1,marginBottom:0}}/>
            <button onClick={addBin} style={{padding:"0 16px",borderRadius:8,border:`1px solid ${T.grn}`,background:T.grnBg,color:T.grn,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}><Plus size={16}/></button>
          </div>
        </Card>
      </>}

      {tab==="wscale"&&<>
        <Card t="GASTRIOT SCALE">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Multi-bin weighing mode for the Gastriot smart scale. Each bin has a distinct NFC tag. Place bin on scale, NFC auto-selects the waste stream, weigh, log.</div>
          <div style={{padding:16,background:gastriotScale?T.grnBg:T.sf,border:`1px solid ${gastriotScale?T.grn:T.brd}`,borderRadius:12,textAlign:"center",marginBottom:10}}>
            <div style={{fontSize:16,fontWeight:700,color:gastriotScale?T.grn:T.dm,marginBottom:4}}>{gastriotScale?"GASTRIOT SCALE ACTIVE":"GASTRIOT SCALE IDLE"}</div>
            <div style={{fontSize:42,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{bleWeight!=null?bleWeight:manualWeight}<span style={{fontSize:18,color:T.mt}}>g</span></div>
          </div>
          <PrimaryBtn color={gastriotScale?T.red:T.grn} icon={gastriotScale?<Pause size={18}/>:<Play size={18}/>} label={gastriotScale?"Stop multi-bin mode":"Start multi-bin mode"} onClick={()=>setGastriotScale(!gastriotScale)}/>
          {gastriotScale&&<>
            <div style={{fontSize:11,color:T.mt,marginTop:10,marginBottom:6,fontWeight:600}}>ACTIVE BINS — tap to weigh into bin</div>
            {wasteBins.length===0?<div style={{padding:12,textAlign:"center",fontSize:12,color:T.dm}}>No bins configured. Add bins in the Bins tab.</div>:
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {wasteBins.map(b=><button key={b.id} onClick={()=>{setWBin(b.id);showToast(`Bin: ${b.name} · ${bleWeight!=null?bleWeight:manualWeight}g`);}} style={{padding:14,borderRadius:10,border:`2px solid ${wBin===b.id?T.amb:T.brd}`,background:wBin===b.id?T.ambBg:T.sf,color:wBin===b.id?T.amb:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,textAlign:"left"}}>
                  <div>{b.name}</div>
                  <div style={{fontSize:10,color:T.dm,fontWeight:400,marginTop:2}}>Tap to log</div>
                </button>)}
              </div>}
          </>}
        </Card>
      </>}

      {tab==="whistory"&&<div id="waste-history">
        {wasteLogs.length===0?<Empty msg="No waste logged yet"/>:wasteLogs.slice(0,40).map(l=><div key={l.id} style={{background:T.card,borderRadius:12,padding:"12px 16px",marginBottom:8,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:600}}>{l.ingredient}</div>
              <div style={{fontSize:10,color:T.dm}}>{l.category}{l.bin?` · ${l.bin}`:""} · {l.time}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:15,fontWeight:600,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{l.weight>=1000?(l.weight/1000).toFixed(1)+"kg":l.weight+"g"}</div>
              <div style={{fontSize:11,fontWeight:600,color:l.avoidable!==false?T.red:T.dm,fontFamily:"'DM Mono',monospace"}}>{l.avoidable!==false?fmtEur(l.cost):"unavoidable"}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginTop:6}}>
            <select value={l.category} onChange={e=>{const newCat=e.target.value;const catObj=cats.find(c=>c.name===newCat);setWasteLogs(p=>p.map(x=>x.id===l.id?{...x,category:newCat,avoidable:catObj?catObj.avoidable:true,cost:catObj&&catObj.avoidable===false?0:x.cost}:x));}} style={{flex:1,padding:"4px 6px",borderRadius:6,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,fontSize:10,fontFamily:"inherit"}}>
              {cats.map(c=><option key={c.id} value={c.name}>{c.name}{c.avoidable?"":" (unavoidable)"}</option>)}
            </select>
            <button onClick={()=>setWasteLogs(p=>p.filter(x=>x.id!==l.id))} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.red}33`,background:T.redBg,color:T.red,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
          </div>
        </div>)}
        <ExportBtn label="Print waste history" onClick={()=>printSection("waste-history","Waste History")}/>
      </div>}

      {tab==="wanalytics"&&<div id="waste-analytics">
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <Banner c={T.red} bg={T.redBg} l="AVOIDABLE WASTE (€)" v={fmtEur(wasteCost)}/>
          <Banner c={T.amb} bg={T.ambBg} l="TOTAL WEIGHT" v={wasteWeight>=1000?(wasteWeight/1000).toFixed(1)+" kg":wasteWeight+" g"}/>
        </div>
        <Card t="BY CATEGORY">
          <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,fontSize:10,color:T.mt,fontWeight:600}}>
            <span style={{flex:1}}>CATEGORY</span>
            <span style={{width:70,textAlign:"right"}}>WEIGHT</span>
            <span style={{width:80,textAlign:"right"}}>COST (€)</span>
          </div>
          {byCat.length===0?<Empty msg="No data"/>:byCat.map(([cat,data])=><div key={cat} style={{display:"flex",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.brd}`}}>
            <span style={{fontSize:13,flex:1}}>{cat}</span>
            <span style={{fontSize:13,fontWeight:600,color:T.amb,fontFamily:"'DM Mono',monospace",width:70,textAlign:"right"}}>{data.weight>=1000?(data.weight/1000).toFixed(1)+"kg":data.weight+"g"}</span>
            <span style={{fontSize:14,fontWeight:600,color:data.cost>0?T.red:T.dm,fontFamily:"'DM Mono',monospace",width:80,textAlign:"right"}}>{data.cost>0?fmtEur(data.cost):"—"}</span>
          </div>)}
        </Card>
        <ExportBtn label="Print analytics" onClick={()=>printSection("waste-analytics","Waste Analytics")}/>
      </div>}
    </div>}

    {/* ── Scale section ── */}
    {!wasteOpen&&<Tabs tabs={[{id:"portion",label:"Portion"},{id:"device",label:"Device"}]} active={tab==="portion"||tab==="device"?tab:"portion"} set={setTab}/>}

    {!wasteOpen&&<div style={{background:T.card,border:`2px solid ${bleDevice?T.grn:T.brd}`,borderRadius:14,padding:"22px 16px",textAlign:"center",marginBottom:12}}>
      {bleDevice?
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:8,color:T.grn,fontSize:12,fontWeight:600}}><Bluetooth size={14}/>LIVE · {bleDevice.name}</div>:
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:8,color:T.dm,fontSize:12}}><MicOff size={14}/>Manual entry mode</div>}
      <div style={{fontSize:54,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{liveWeight}<span style={{fontSize:22,color:T.mt,marginLeft:6}}>g</span></div>
      {!bleDevice&&<><div style={{fontSize:11,color:T.mt,marginTop:12,marginBottom:4,fontWeight:600,textAlign:"center"}}>ENTER WEIGHT (g)</div><input type="number" value={manualWeight||""} onChange={e=>setManualWeight(parseInt(e.target.value)||0)} style={{...inp,textAlign:"center",fontSize:18}}/></>}
    </div>}

    {!wasteOpen&&tab==="portion"&&<Card t="PORTION TARGET">
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>TARGET WEIGHT (g)</div>
          <input type="number" value={target} onChange={e=>setTarget(parseInt(e.target.value)||0)} placeholder="150" style={{...inp,marginBottom:0}}/>
        </div>
        <div style={{width:90}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>± TOLERANCE</div>
          <input type="number" value={tolerance} onChange={e=>setTolerance(parseInt(e.target.value)||0)} placeholder="10" style={{...inp,marginBottom:0}}/>
        </div>
      </div>
      <div style={{padding:14,background:inTolerance?T.grnBg:T.redBg,border:`1px solid ${inTolerance?T.grn:T.red}55`,borderRadius:10,textAlign:"center"}}>
        <div style={{fontSize:20,fontWeight:700,color:inTolerance?T.grn:T.red,fontFamily:"'DM Mono',monospace"}}>
          {diff>0?`+${diff}g over`:diff<0?`${diff}g under`:"exact"}
        </div>
        <div style={{fontSize:12,color:inTolerance?T.grn:T.red,marginTop:4}}>{inTolerance?"Within tolerance — plate it":"Adjust portion"}</div>
      </div>
    </Card>}

    {!wasteOpen&&tab==="device"&&<Card t="BLUETOOTH SCALE">
      {bleDevice?<>
        <div style={{padding:12,background:T.grnBg,border:`1px solid ${T.grn}55`,borderRadius:10,marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,color:T.grn,fontSize:13,fontWeight:600}}><Bluetooth size={16}/>Connected: {bleDevice.name}</div>
        </div>
        <button onClick={()=>{setBleDevice(null);setBleWeight(null);showToast("Disconnected");}} style={{width:"100%",padding:14,borderRadius:10,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Disconnect</button>
      </>:<>
        <p style={{fontSize:13,color:T.mt,marginBottom:10}}>Pair a Bluetooth kitchen scale. Supports any scale with Weight Scale service (0x181D).</p>
        <PrimaryBtn color={T.blu} icon={scanning?<BluetoothSearching size={20}/>:<Bluetooth size={20}/>} label={scanning?"Scanning...":"Pair scale"} onClick={pairBluetooth}/>
        <div style={{fontSize:11,color:T.dm,marginTop:10,padding:10,background:T.sf,borderRadius:8,border:`1px solid ${T.brd}`}}>
          <strong style={{color:T.amb}}>Note:</strong> Requires Chrome/Edge. iOS Safari uses manual entry.
        </div>
      </>}
    </Card>}
  </div>;
}

/* ═══ INVENTORY — with dry-goods filter and shelf-life panel ═══ */
function InventoryP({ings,setIngs,zones,throwAway,recipes}){
  const [tab,setTab]=useState("all");
  const [search,setSearch]=useState("");
  const [adding,setAdding]=useState(false);
  const [quickCount,setQuickCount]=useState(false);
  const [editing,setEditing]=useState(null);
  const [collapsed,setCollapsed]=useState({}); // category sections collapsed state
  const [newIng,setNewIng]=useState({name:"",cat:"trocken",cost:0,unit:"kg",unitG:1000,parMin:1,parMax:5,qty:0,storage:"trocken",zone:"z3",shelf:30,allergens:[],sku:"",barcode:"",supplier:"s1"});

  // Urgency score: 86'd & expired first, then expiring, then below par, then rest.
  const urgency=(i)=>{
    const s=computeShelfLife(i,zones);
    if((i.qty||0)<=0)return 0;                       // 86'd — top
    if(s.riskLevel==="expired")return 1;             // expired
    if(s.daysLeft<=1)return 2;                        // expiring today/tomorrow
    if((i.qty||0)<=(i.parMin||0)*1.3)return 3;        // below par
    if(s.daysLeft<=3)return 4;                        // expiring soon
    return 5;                                         // fine
  };

  // Recipe-aware search: typing a DISH name surfaces its ingredients.
  // e.g. "burger" -> buns, beef, lettuce, cheese... (any recipe whose name matches)
  const searchLc=search.trim().toLowerCase();
  let recipeMatchIds=null;
  if(searchLc&&recipes&&recipes.length){
    const matchedRecipes=recipes.filter(r=>r.name.toLowerCase().includes(searchLc));
    if(matchedRecipes.length){
      const ids=new Set();
      matchedRecipes.forEach(r=>(r.comps||[]).forEach(c=>c.ingId&&ids.add(c.ingId)));
      if(ids.size)recipeMatchIds=ids;
    }
  }

  let filtered=ings;
  // Tab = category (from ING_CATS) or status
  if(tab==="low")filtered=ings.filter(i=>i.qty<=i.parMin*1.3);
  else if(tab==="expiring")filtered=ings.filter(i=>{const s=computeShelfLife(i,zones);return (i.qty||0)>0&&s.daysLeft<=3;});
  else if(tab!=="all")filtered=ings.filter(i=>i.cat===tab);

  if(searchLc){
    filtered=filtered.filter(i=>{
      const direct=i.name.toLowerCase().includes(searchLc)||(i.sku||"").toLowerCase().includes(searchLc);
      const viaRecipe=recipeMatchIds&&recipeMatchIds.has(i.id);
      return direct||viaRecipe;
    });
  }
  // Always sort by what needs attention first, then by name
  filtered=[...filtered].sort((a,b)=>{const ua=urgency(a),ub=urgency(b);return ua!==ub?ua-ub:a.name.localeCompare(b.name);});

  const adjust=(id,delta)=>setIngs(p=>p.map(i=>i.id===id?{...i,qty:Math.max(0,Math.round((i.qty+delta)*100)/100)}:i));
  const updateIng=(id,patch)=>setIngs(p=>p.map(i=>i.id===id?{...i,...patch}:i));
  const addNew=()=>{
    if(!newIng.name)return;
    setIngs(p=>[...p,{...newIng,id:genId(),priceHist:[{d:todayISO(),p:newIng.cost}],lastReceived:new Date().toISOString()}]);
    setNewIng({name:"",cat:"trocken",cost:0,unit:"kg",unitG:1000,parMin:1,parMax:5,qty:0,storage:"trocken",zone:"z3",shelf:30,allergens:[],sku:"",barcode:"",supplier:"s1"});
    setAdding(false);
  };

  // Counts for the status chips
  const lowCount=ings.filter(i=>i.qty<=i.parMin*1.3).length;
  const expCount=ings.filter(i=>{const s=computeShelfLife(i,zones);return (i.qty||0)>0&&s.daysLeft<=3;}).length;

  return <div>
    {quickCount&&<QuickCountModal ings={ings} setIngs={setIngs} onClose={()=>setQuickCount(false)}/>}
    <div style={{display:"flex",gap:8,marginBottom:10}}>
      <SearchInp value={search} set={setSearch}/>
      <button onClick={()=>setAdding(!adding)} style={{padding:"0 16px",borderRadius:10,border:`1px solid ${T.grn}`,background:T.grnBg,color:T.grn,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6,minHeight:48}}><Plus size={18}/>Add</button>
      <button onClick={()=>setQuickCount(true)} style={{padding:"0 16px",borderRadius:10,border:`1px solid ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6,minHeight:48,whiteSpace:"nowrap"}}><Mic size={18}/>Quick Count</button>
    </div>
    {recipeMatchIds&&<div style={{fontSize:11,color:T.blu,marginBottom:8,padding:"6px 10px",background:T.bluBg,borderRadius:8}}>Showing ingredients used in dishes matching "{search}"</div>}

    {/* Status + category filter chips — wrapping grid so nothing scrolls off-screen */}
    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
      {[
        {id:"all",label:`All ${ings.length}`,c:T.tx},
        {id:"expiring",label:`⏱ Expiring ${expCount}`,c:T.red},
        {id:"low",label:`⚠ Below par ${lowCount}`,c:T.amb},
        ...ING_CATS.map(c=>({id:c.id,label:c.label,c:T[c.color]||T.tx})),
      ].map(chip=>{
        const active=tab===chip.id;
        return <button key={chip.id} onClick={()=>setTab(chip.id)} style={{flex:"1 1 calc(50% - 3px)",minWidth:0,padding:"10px 12px",borderRadius:10,border:`1.5px solid ${active?chip.c:T.brd}`,background:active?chip.c:T.sf,color:active?"#fff":T.dm,fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textAlign:"center"}}>{chip.label}</button>;
      })}
    </div>

    {adding&&<Card t="NEW INGREDIENT">
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>INGREDIENT NAME</div>
      <input value={newIng.name} onChange={e=>setNewIng({...newIng,name:e.target.value})} placeholder="e.g. Avocado, Olive oil, Sea salt" style={inp}/>
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>CATEGORY</div>
          <select value={newIng.cat} onChange={e=>setNewIng({...newIng,cat:e.target.value})} style={inp}>{ING_CATS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}</select>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>STORAGE TYPE</div>
          <select value={newIng.storage} onChange={e=>setNewIng({...newIng,storage:e.target.value})} style={inp}>{STORAGE.map(s=><option key={s} value={s}>{s}</option>)}</select>
        </div>
      </div>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>STORAGE ZONE</div>
      <select value={newIng.zone} onChange={e=>setNewIng({...newIng,zone:e.target.value})} style={inp}>
        <option value="">— Select zone —</option>
        {zones.map(z=><option key={z.id} value={z.id}>{z.name} ({z.current}°C)</option>)}
      </select>
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>COST PER {(newIng.unit||"pc").toUpperCase()} (€)</div>
          <input type="number" step="0.01" value={newIng.cost||""} onChange={e=>setNewIng({...newIng,cost:parseFloat(e.target.value)||0})} placeholder="0.00" style={inp}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>UNIT</div>
          <select value={newIng.unit} onChange={e=>{const u=e.target.value;const gMap={g:1,kg:1000,ml:1,L:1000,pc:1,slice:1,tsp:5,tbsp:15};setNewIng({...newIng,unit:u,unitG:gMap[u]||1});}} style={inp}>
            <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="slice">slice</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
          </select>
        </div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PAR MIN</div>
          <input type="number" value={newIng.parMin||""} onChange={e=>setNewIng({...newIng,parMin:parseFloat(e.target.value)||0})} placeholder="0" style={inp}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PAR MAX</div>
          <input type="number" value={newIng.parMax||""} onChange={e=>setNewIng({...newIng,parMax:parseFloat(e.target.value)||0})} placeholder="0" style={inp}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>BESTAND</div>
          <input type="number" value={newIng.qty||""} onChange={e=>setNewIng({...newIng,qty:parseFloat(e.target.value)||0})} placeholder="0" style={inp}/>
        </div>
      </div>
      {newIng.qty>0&&newIng.cost>0&&<div style={{fontSize:11,color:T.grn,fontWeight:600,marginBottom:10,marginTop:-6}}>= {fmtEur(newIng.qty*newIng.cost)} total ({newIng.qty} {newIng.unit} × {fmtEur(newIng.cost)}/{newIng.unit})</div>}
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>SKU (OPTIONAL)</div>
      <input value={newIng.sku} onChange={e=>setNewIng({...newIng,sku:e.target.value})} placeholder="e.g. AVO-001" style={inp}/>
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label="Add to inventory" onClick={addNew}/>
    </Card>}

    <div id="inventory-content">
      {filtered.length===0?<Empty msg="No items match"/>:filtered.map(i=>{
        const shelf=computeShelfLife(i,zones);
        const meta=catMeta(i.cat);
        const Icon=meta.Icon;
        const stockPct=Math.min(100,(i.qty/i.parMax)*100);
        const low=i.qty<=i.parMin*1.3;
        const isEditing=editing===i.id;
        const urgent=shelf.riskLevel==="expired"||shelf.daysLeft<=1;
        const eightySixed=(i.qty||0)<=0;
        return <div key={i.id} style={{background:T.card,borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:urgent?"0 0 0 2px rgba(212,43,43,0.15)":"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${eightySixed?T.red+"55":urgent?T.red+"30":T.brd+"44"}`,animation:urgent&&!eightySixed?"expireBlink 3s ease-in-out infinite":"none",transition:"all 0.15s",opacity:eightySixed?0.72:1}}>
          {eightySixed&&<div style={{display:"inline-flex",alignItems:"center",gap:5,background:T.red,color:"#fff",fontSize:10,fontWeight:800,letterSpacing:0.5,padding:"3px 9px",borderRadius:6,marginBottom:8}}>🚫 86'd · OUT OF STOCK</div>}
          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            <div style={{width:42,height:42,background:T.sf,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon size={22} color={T[meta.color]||T.mt}/></div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",textDecoration:eightySixed?"line-through":"none"}}>{i.name}</div>
                  <div style={{fontSize:10,color:T.dm}}>{i.cat} · {fmtEur(i.cost)}/{i.unit} {i.sku&&`· ${i.sku}`}</div>
                </div>
                <button onClick={()=>setEditing(isEditing?null:i.id)} style={{padding:6,borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",display:"flex"}}><Edit3 size={14}/></button>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                <button onClick={()=>adjust(i.id,-0.5)} style={{width:40,height:40,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontWeight:700,fontFamily:"inherit"}}>−</button>
                <div style={{flex:1,textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:700,fontFamily:"'DM Mono',monospace",color:low?T.amb:T.tx}}>{i.qty} <span style={{fontSize:11,color:T.dm}}>{i.unit}</span></div>
                  <div style={{height:4,background:T.sf,borderRadius:2,marginTop:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${stockPct}%`,background:low?T.amb:T.grn}}/>
                  </div>
                  <div style={{fontSize:9,color:T.dm,marginTop:2}}>Par {i.parMin}–{i.parMax}</div>
                </div>
                <button onClick={()=>adjust(i.id,0.5)} style={{width:40,height:40,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontWeight:700,fontFamily:"inherit"}}>+</button>
              </div>
              <div style={{display:"flex",gap:6,marginTop:8,fontSize:10,color:T.mt,flexWrap:"wrap"}}>
                <span style={{padding:"3px 7px",background:T.sf,borderRadius:6,display:"flex",alignItems:"center",gap:4}}><Refrigerator size={11}/>{zones.find(z=>z.id===i.zone)?.name||i.storage}</span>
                <span style={{padding:"3px 7px",background:shelf.riskLevel==="expired"?"#7f1d1d":shelf.riskLevel==="high"?T.redBg:shelf.riskLevel==="medium"?T.ambBg:T.grnBg,color:shelf.riskLevel==="expired"?"#fca5a5":shelf.riskLevel==="high"?T.red:shelf.riskLevel==="medium"?T.amb:T.grn,borderRadius:6,display:"flex",alignItems:"center",gap:4,fontWeight:600}}><Clock size={11}/>{shelf.riskLevel==="expired"?"EXPIRED":shelf.daysLeft<1?`<1d left`:`${shelf.daysLeft}d left`} · {shelf.halfLifeRemaining>0?`½ ${shelf.halfLifeRemaining}d`:"½ past"}</span>
                <div style={{display:"flex",gap:2,marginLeft:4}}>
                  <button onClick={e=>{e.stopPropagation();setIngs(p=>p.map(x=>x.id===i.id?{...x,shelf:Math.max(1,(x.shelf||7)-1)}:x));}} style={{width:22,height:22,borderRadius:4,border:`1px solid ${T.brd}`,background:T.sf,color:T.red,cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>-</button>
                  <button onClick={e=>{e.stopPropagation();setIngs(p=>p.map(x=>x.id===i.id?{...x,shelf:(x.shelf||7)+1}:x));}} style={{width:22,height:22,borderRadius:4,border:`1px solid ${T.brd}`,background:T.sf,color:T.grn,cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>+</button>
                </div>
              </div>
              {/* FIFO LOTS — prominent display right under quantity */}
              {i.lots&&i.lots.length>0&&<div style={{marginTop:10,padding:"10px 12px",background:T.sf,borderRadius:10,border:`1px solid ${T.brd}66`}}>
                <div style={{fontSize:10,fontWeight:700,color:T.mt,marginBottom:6,letterSpacing:0.8,textTransform:"uppercase"}}>Delivery lots · FIFO</div>
                {i.lots.sort((a,b)=>new Date(a.received)-new Date(b.received)).map((lot,li)=>{
                  // Use the SAME shelf life engine as the main chip — respects temperature, storage rules
                  const lotShelf=computeShelfLife({...i,lastReceived:lot.received},zones);
                  const isOldest=li===0&&i.lots.length>1;
                  const lotUrgent=lotShelf.riskLevel==="expired"||lotShelf.daysLeft<=1;
                  return <div key={lot.id||li} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:li<i.lots.length-1?`1px solid ${T.brd}44`:"none",animation:lotUrgent?"expireBlink 3s ease-in-out infinite":"none",borderRadius:4}}>
                    <div>
                      <span style={{fontSize:11,fontWeight:700,color:isOldest?T.amb:T.grn,marginRight:6}}>{isOldest?"⏳ OLD":"✓ NEW"}</span>
                      <span style={{fontSize:12,fontWeight:600,color:T.tx}}>{lot.qty} {i.unit}</span>
                      <span style={{fontSize:10,color:T.dm,marginLeft:6}}>@ {fmtEur(lot.cost)}/{i.unit}</span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:lotShelf.riskLevel==="expired"?"#fca5a5":lotShelf.riskLevel==="high"?T.red:lotShelf.riskLevel==="medium"?T.amb:T.grn}}>{lotShelf.riskLevel==="expired"?"EXPIRED":lotShelf.daysLeft<1?"<1d left":`${lotShelf.daysLeft}d left`}</span>
                      <div style={{fontSize:9,color:T.dm}}>{new Date(lot.received).toLocaleDateString("en-GB")}</div>
                    </div>
                  </div>;
                })}
              </div>}
              {/* Single lot / no lots — just show received date */}
              {(!i.lots||i.lots.length===0)&&i.lastReceived&&<div style={{fontSize:9,color:T.dm,marginTop:4}}>
                Received: {new Date(i.lastReceived).toLocaleDateString("en-GB")} ({Math.round(shelf.elapsed)}d ago)
              </div>}
              {/* THROW AWAY — appears only on expired items; bins the expired stock as avoidable waste */}
              {shelf.riskLevel==="expired"&&throwAway&&<button onClick={e=>{e.stopPropagation();throwAway(i);}} style={{width:"100%",marginTop:10,padding:"11px 14px",borderRadius:10,border:"none",background:T.red,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <Trash2 size={16}/>Throw away expired — log as waste
              </button>}
            </div>
          </div>

          {isEditing&&<div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.brd}`}}>
            <div style={{display:"flex",gap:8,marginBottom:6}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>COST PER {(i.unit||"pc").toUpperCase()} (€)</div>
                <input type="number" step="0.01" value={i.cost} onChange={e=>updateIng(i.id,{cost:parseFloat(e.target.value)||0})} placeholder="0.00" style={{...inp,flex:1,marginBottom:0}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>SKU</div>
                <input value={i.sku||""} onChange={e=>updateIng(i.id,{sku:e.target.value})} placeholder="SKU" style={{...inp,flex:1,marginBottom:0}}/>
              </div>
            </div>
            <div style={{fontSize:11,color:T.grn,fontWeight:600,marginBottom:8}}>= {fmtEur(i.qty*i.cost)} total in stock ({i.qty} {i.unit} on hand × {fmtEur(i.cost)}/{i.unit})</div>
            <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>UNIT</div>
            <select value={i.unit} onChange={e=>{const u=e.target.value;const gMap={g:1,kg:1000,ml:1,L:1000,pc:1,slice:1,tsp:5,tbsp:15};updateIng(i.id,{unit:u,unitG:gMap[u]||i.unitG});}} style={{...inp}}>
              <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="slice">slice</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
            </select>
            <div style={{display:"flex",gap:8,marginBottom:6}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>PAR MIN</div>
                <input type="number" value={i.parMin} onChange={e=>updateIng(i.id,{parMin:parseFloat(e.target.value)||0})} placeholder="0" style={{...inp,flex:1,marginBottom:0}}/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>PAR MAX</div>
                <input type="number" value={i.parMax} onChange={e=>updateIng(i.id,{parMax:parseFloat(e.target.value)||0})} placeholder="0" style={{...inp,flex:1,marginBottom:0}}/>
              </div>
            </div>
            <div style={{fontSize:10,color:T.mt,marginBottom:3,fontWeight:600}}>STORAGE ZONE</div>
            <select value={i.zone||""} onChange={e=>updateIng(i.id,{zone:e.target.value})} style={inp}>
              <option value="">— Zone —</option>
              {zones.map(z=><option key={z.id} value={z.id}>{z.name} ({z.current}°C)</option>)}
            </select>
            {i.priceHist&&i.priceHist.length>1&&<div style={{padding:8,background:T.sf,borderRadius:8,marginTop:6}}>
              <div style={{fontSize:10,color:T.mt,marginBottom:4,fontWeight:600}}>PRICE HISTORY</div>
              {i.priceHist.slice(-5).map((h,idx)=>{
                const prev=i.priceHist[idx-1];
                const trend=prev?h.p-prev.p:0;
                return <div key={idx} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"2px 0"}}>
                  <span style={{color:T.dm}}>{fmtDateShort(h.d)}</span>
                  <span style={{color:trend>0?T.red:trend<0?T.grn:T.tx,fontFamily:"'DM Mono',monospace"}}>{fmtEur(h.p)}{trend!==0&&<span style={{fontSize:9,marginLeft:4}}>{trend>0?"↑":"↓"}{fmtEur(Math.abs(trend))}</span>}</span>
                </div>;
              })}
            </div>}
          </div>}
        </div>;
      })}
    </div>
    <ExportBtn label="Print inventory list" onClick={()=>printSection("inventory-content","Inventory Snapshot")}/>
  </div>;
}

/* ═══ QUICK COUNT — walk-the-kitchen capture: say/type an item, tap to tally how
   many you see. A staged list, separate from real inventory until "Apply" —
   built for a fast walkthrough of a messy/unfamiliar kitchen, not careful entry. ═══ */
function QuickCountModal({ings,setIngs,onClose}){
  const [captured,setCaptured]=useState([]); // {id,name,ingId,count}
  const [typed,setTyped]=useState("");
  const [listening,setListening]=useState(false);
  const [voiceLang,setVoiceLang]=useState("de-DE"); // one-tap toggle — browsers can't listen in two languages at once
  const recRef=useRef(null);

  const addCaptured=(raw)=>{
    let text=(raw||"").trim();
    if(!text)return;
    // Opportunistic leading quantity ("3 onions", "drei Zwiebeln" won't parse the
    // word form — that's fine, the tap counter below is the primary way to count).
    let count=1;
    const numMatch=text.match(/^(\d+(?:[.,]\d+)?)\s+/);
    if(numMatch){count=parseFloat(numMatch[1].replace(",","."));text=text.slice(numMatch[0].length).trim();}
    else{
      const words=text.split(/\s+/);
      if(words.length>1&&NUM_WORDS[words[0].toLowerCase()]!==undefined){count=NUM_WORDS[words[0].toLowerCase()];text=words.slice(1).join(" ");}
    }
    if(!text)return;
    const match=strictIngredientMatch(text,ings);
    setCaptured(p=>{
      // Saying/typing the same item again bumps its tally instead of duplicating.
      const key=match?match.id:text.toLowerCase();
      const idx=p.findIndex(c=>(c.ingId||c.name.toLowerCase())===key);
      if(idx>=0){const copy=[...p];copy[idx]={...copy[idx],count:copy[idx].count+count};return copy;}
      return [...p,{id:genId(),name:match?match.name:text,ingId:match?.id||"",count,unit:match?.unit||"pc"}];
    });
    setTyped("");
  };

  const bump=(id,delta)=>setCaptured(p=>p.map(c=>c.id===id?{...c,count:Math.max(0,c.count+delta)}:c));
  const setUnit=(id,unit)=>setCaptured(p=>p.map(c=>c.id===id?{...c,unit}:c));
  const remove=(id)=>setCaptured(p=>p.filter(c=>c.id!==id));

  const toggleMic=()=>{
    if(listening){recRef.current&&recRef.current.stop();setListening(false);return;}
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Voice input isn't supported in this browser — type the item instead.");return;}
    const r=new SR();
    r.continuous=false;r.interimResults=false;r.lang=voiceLang;
    r.onresult=(e)=>addCaptured(e.results[0][0].transcript);
    r.onerror=()=>setListening(false);
    r.onend=()=>setListening(false);
    recRef.current=r;
    try{r.start();setListening(true);}catch(e){}
  };

  const applyToInventory=()=>{
    if(!captured.length)return;
    const gMap={g:1,kg:1000,ml:1,L:1000,pc:1,slice:1,tsp:5,tbsp:15};
    const newItems=[];
    setIngs(prev=>{
      let next=prev.map(x=>{
        const hit=captured.find(c=>c.ingId===x.id);
        if(!hit)return x;
        const unit=hit.unit||x.unit;
        return {...x,qty:hit.count,unit,unitG:gMap[unit]||x.unitG};
      });
      captured.filter(c=>!c.ingId).forEach(c=>{
        const unit=c.unit||"pc";
        newItems.push({id:genId(),name:c.name,cat:"Uncategorized",unit,qty:c.count,cost:0,parMin:0,parMax:0,shelf:7,unitG:gMap[unit]||1,lots:[],priceHist:[],sku:"",eightySixed:false});
      });
      return [...next,...newItems];
    });
    onClose();
  };

  return <div style={{position:"fixed",inset:0,zIndex:200,background:T.bg,overflowY:"auto",padding:16,display:"flex",flexDirection:"column"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
      <div style={{fontSize:18,fontWeight:800}}>Quick Count</div>
      <button onClick={onClose} style={{width:36,height:36,borderRadius:10,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={18}/></button>
    </div>
    <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Walk the kitchen — say or type each item you see, then tap + to tally how many. Tap DE/EN to switch dictation language. Nothing changes in your real inventory until you apply it below.</div>

    <div style={{display:"flex",gap:8,marginBottom:14}}>
      <input value={typed} onChange={e=>setTyped(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCaptured(typed)} placeholder="Type an item, e.g. Möhren" style={{...inp,marginBottom:0,flex:1}}/>
      <button onClick={()=>addCaptured(typed)} style={{width:48,borderRadius:10,border:"none",background:T.grn,color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Plus size={20}/></button>
      <button onClick={toggleMic} style={{width:48,borderRadius:10,border:"none",background:listening?T.red:T.pur,color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{listening?<Mic size={20}/>:<MicOff size={20}/>}</button>
      <button onClick={()=>!listening&&setVoiceLang(l=>l==="de-DE"?"en-US":"de-DE")} disabled={listening} title="Voice language — tap to switch" style={{width:44,borderRadius:10,border:`1.5px solid ${T.pur}`,background:T.purBg,color:T.pur,cursor:listening?"default":"pointer",fontFamily:"inherit",fontSize:12,fontWeight:800,flexShrink:0,opacity:listening?0.5:1}}>{voiceLang==="de-DE"?"DE":"EN"}</button>
    </div>

    <div style={{flex:1,overflowY:"auto"}}>
      {captured.length===0?<Empty msg="Nothing captured yet — say or type your first item."/>:
        captured.map(c=><div key={c.id} style={{background:T.card,border:`1px solid ${T.brd}`,borderRadius:12,padding:"10px 12px",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
              <div style={{fontSize:10,color:c.ingId?T.grn:T.amb,fontWeight:600}}>{c.ingId?"Matched to inventory":"New item — will be created"}</div>
            </div>
            <select value={c.unit||"pc"} onChange={e=>setUnit(c.id,e.target.value)} style={{...inp,marginBottom:0,width:76,padding:"8px 6px",fontSize:12,flexShrink:0}}>
              <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="slice">slice</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
            </select>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>bump(c.id,-1)} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,fontSize:18,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>−</button>
            <div style={{flex:1,textAlign:"center",fontSize:18,fontWeight:800,fontFamily:"'DM Mono',monospace"}}>{c.count} <span style={{fontSize:11,color:T.dm,fontWeight:600}}>{c.unit||"pc"}</span></div>
            <button onClick={()=>bump(c.id,1)} style={{width:44,height:44,borderRadius:10,border:"none",background:T.grn,color:"#fff",fontSize:20,fontWeight:800,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>+</button>
            <button onClick={()=>remove(c.id)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><X size={14}/></button>
          </div>
        </div>)}
    </div>

    {captured.length>0&&<div style={{paddingTop:10}}>
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label={`Apply ${captured.length} item${captured.length>1?"s":""} to inventory`} onClick={applyToInventory}/>
    </div>}
  </div>;
}

/* ═══ DICTATE / SCAN RECIPE — voice (DE/EN), typed narration, or a photo of a
   handwritten recipe card → structured ingredients + instructions text, via the
   /scan/recipe-dictation and /scan/recipe-photo backend endpoints. Shared by
   RecipeBookP (batch preps) and RecipeP (menu dishes): both just get back
   {name, instructions, comps} with real ingIds — any ingredient mentioned that
   doesn't match inventory gets created here before Apply, same as Quick Count. ═══ */
function DictateRecipeModal({ings,setIngs,onApply,onClose,showToast}){
  const [transcript,setTranscript]=useState("");
  const [listening,setListening]=useState(false);
  const [voiceLang,setVoiceLang]=useState("de-DE");
  const [phase,setPhase]=useState("record"); // record | parsing | review
  const [parseErr,setParseErr]=useState("");
  const [recipeName,setRecipeName]=useState("");
  const [instructions,setInstructions]=useState("");
  const [inferredNotice,setInferredNotice]=useState(false);
  const [rows,setRows]=useState([]); // {id,name,ingId,qty,unit}
  const recRef=useRef(null);

  const toggleMic=()=>{
    if(listening){recRef.current&&recRef.current.stop();setListening(false);return;}
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){alert("Voice input isn't supported in this browser — type the recipe instead.");return;}
    const r=new SR();
    r.continuous=true;r.interimResults=false;r.lang=voiceLang;
    r.onresult=(e)=>{
      let finalChunk="";
      for(let i=e.resultIndex;i<e.results.length;i++){if(e.results[i].isFinal)finalChunk+=e.results[i][0].transcript+" ";}
      if(finalChunk)setTranscript(t=>(t?t+" ":"")+finalChunk.trim());
    };
    r.onerror=()=>setListening(false);
    r.onend=()=>setListening(false);
    recRef.current=r;
    try{r.start();setListening(true);}catch(e){}
  };

  const analyze=async()=>{
    if(phase==="parsing")return; // guard against double-tap — PrimaryBtn has no disabled state
    if(listening){recRef.current&&recRef.current.stop();setListening(false);}
    const text=transcript.trim();
    if(!text){setParseErr("Say or type the recipe first.");return;}
    setPhase("parsing");setParseErr("");
    try{
      let data=null,lastMsg="";
      for(let attempt=1;attempt<=3;attempt++){
        const resp=await fetch(`${SCAN_API}/scan/recipe-dictation`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({transcript:text})});
        if(resp.ok){const d=await resp.json();if(d.ok){data=d;break;}lastMsg=d.error||"Could not parse recipe";}
        else{const e=await resp.json().catch(()=>({}));lastMsg=e.error||`Failed (${resp.status})`;
          if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){setParseErr(`Busy — retrying (${attempt}/3)…`);await new Promise(r=>setTimeout(r,2000*attempt));continue;}}
        break;
      }
      if(!data)throw new Error(lastMsg||"Could not parse recipe");
      setInferredNotice(false);
      setInstructions(data.instructions||"");
      setRows((data.ingredients||[]).map(ing=>{
        const match=strictIngredientMatch(ing.name,ings);
        return {id:genId(),name:ing.name||"",ingId:match?.id||"",qty:ing.qty!=null?ing.qty:"",unit:ing.unit||match?.unit||"g"};
      }));
      setPhase("review");
    }catch(e){
      setParseErr(e.message||"Could not parse recipe");
      setPhase("record");
      if(showToast)showToast("Recipe parsing failed — try again");
    }
  };

  // Photo of a handwritten/printed recipe card. If the card has no written
  // method, the backend infers one from the ingredients + dish name — flagged
  // via instructionsInferred so the review screen can call that out.
  const scanPhoto=async(file)=>{
    if(phase==="parsing")return;
    if(listening){recRef.current&&recRef.current.stop();setListening(false);}
    setPhase("parsing");setParseErr("");
    try{
      const mediaType=file.type||"image/jpeg";
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=()=>rej("Read failed");r.readAsDataURL(file);});
      let data=null,lastMsg="";
      for(let attempt=1;attempt<=3;attempt++){
        const resp=await fetch(`${SCAN_API}/scan/recipe-photo`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({image:base64,mediaType})});
        if(resp.ok){const d=await resp.json();if(d.ok){data=d;break;}lastMsg=d.error||"Could not read recipe";}
        else{const e=await resp.json().catch(()=>({}));lastMsg=e.error||`Failed (${resp.status})`;
          if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){setParseErr(`Busy — retrying (${attempt}/3)…`);await new Promise(r=>setTimeout(r,2000*attempt));continue;}}
        break;
      }
      if(!data)throw new Error(lastMsg||"Could not read recipe");
      setRecipeName(data.name||"");
      setInferredNotice(!!data.instructionsInferred);
      setInstructions(data.instructions||"");
      setRows((data.ingredients||[]).map(ing=>{
        const match=strictIngredientMatch(ing.name,ings);
        return {id:genId(),name:ing.name||"",ingId:match?.id||"",qty:ing.qty!=null?ing.qty:"",unit:ing.unit||match?.unit||"g"};
      }));
      setPhase("review");
    }catch(e){
      setParseErr(e.message||"Could not read recipe");
      setPhase("record");
      if(showToast)showToast("Recipe scan failed — try a clearer photo");
    }
  };

  // Snap ingredients — photo of raw ingredients laid out (mise en place), no
  // written card needed. Adapted from EATRR's /home/vision/ingredients: the
  // backend just identifies items, matching against real inventory happens
  // here client-side with the same fuzzyMatchItem/foodNamesMatch as every
  // other flow. No name/instructions come back — just the ingredient rows.
  const scanIngredients=async(file)=>{
    if(phase==="parsing")return;
    if(listening){recRef.current&&recRef.current.stop();setListening(false);}
    setPhase("parsing");setParseErr("");
    try{
      const mediaType=file.type||"image/jpeg";
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=()=>rej("Read failed");r.readAsDataURL(file);});
      let items=null,lastMsg="";
      for(let attempt=1;attempt<=3;attempt++){
        const resp=await fetch(`${SCAN_API}/scan/ingredients-photo`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({image:base64,mediaType})});
        if(resp.ok){const d=await resp.json();if(d.ok&&Array.isArray(d.items)){items=d.items;break;}lastMsg=d.error||"No ingredients found";}
        else{const e=await resp.json().catch(()=>({}));lastMsg=e.error||`Failed (${resp.status})`;
          if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){setParseErr(`Busy — retrying (${attempt}/3)…`);await new Promise(r=>setTimeout(r,2000*attempt));continue;}}
        break;
      }
      if(!items)throw new Error(lastMsg||"No ingredients found — try a clearer photo");
      setRows(items.map(ing=>{
        const match=strictIngredientMatch(ing.name,ings);
        return {id:genId(),name:ing.name||"",ingId:match?.id||"",qty:ing.qty!=null?ing.qty:"",unit:ing.unit||match?.unit||"g"};
      }));
      setPhase("review");
    }catch(e){
      setParseErr(e.message||"Could not read ingredients");
      setPhase("record");
      if(showToast)showToast("Scan failed — try a clearer photo");
    }
  };

  const updRow=(id,patch)=>setRows(p=>p.map(r=>r.id===id?{...r,...patch}:r));
  const rmRow=(id)=>setRows(p=>p.filter(r=>r.id!==id));

  const apply=()=>{
    // Any row still without a real ingId gets created as a new inventory item first.
    const toCreate=rows.filter(r=>!r.ingId&&r.name.trim());
    let idMap={};
    if(toCreate.length&&setIngs){
      setIngs(prev=>{
        const created=toCreate.map(r=>({id:genId(),name:r.name.trim(),cat:"Uncategorized",unit:r.unit||"g",qty:0,cost:0,parMin:0,parMax:0,shelf:7,unitG:1,lots:[],priceHist:[],sku:"",eightySixed:false}));
        created.forEach((c,i)=>{idMap[toCreate[i].id]=c.id;});
        return [...prev,...created];
      });
    }
    const comps=rows.filter(r=>r.name.trim()).map(r=>({ingId:r.ingId||idMap[r.id]||"",qty:parseFloat(r.qty)||0,unit:r.unit||"g"}));
    onApply({name:recipeName.trim(),instructions,comps});
  };

  return <div style={{position:"fixed",inset:0,zIndex:200,background:T.bg,overflowY:"auto",padding:16,display:"flex",flexDirection:"column"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
      <div style={{fontSize:18,fontWeight:800}}>{phase==="review"?"Review Recipe":"Add Recipe — Voice or Photo"}</div>
      <button onClick={onClose} style={{width:36,height:36,borderRadius:10,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={18}/></button>
    </div>

    {phase!=="review"&&<>
      <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Describe the recipe out loud, or photograph a handwritten recipe card — German or English. If the card has no written method, one is inferred from the ingredients.</div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <button onClick={toggleMic} style={{width:56,height:56,borderRadius:14,border:"none",background:listening?T.red:T.pur,color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{listening?<Mic size={24}/>:<MicOff size={24}/>}</button>
        <button onClick={()=>!listening&&setVoiceLang(l=>l==="de-DE"?"en-US":"de-DE")} disabled={listening} title="Voice language" style={{width:56,borderRadius:14,border:`1.5px solid ${T.pur}`,background:T.purBg,color:T.pur,cursor:listening?"default":"pointer",fontFamily:"inherit",fontSize:13,fontWeight:800,flexShrink:0,opacity:listening?0.5:1}}>{voiceLang==="de-DE"?"DE":"EN"}</button>
        <textarea value={transcript} onChange={e=>setTranscript(e.target.value)} placeholder="Transcript appears here as you speak — or type/paste a recipe directly." rows={3} style={{...inp,flex:1,marginBottom:0,fontFamily:"inherit",resize:"vertical"}}/>
      </div>
      {listening&&<div style={{fontSize:11,color:T.red,fontWeight:600,marginBottom:10}}>● Listening — tap the mic again to stop</div>}
      {parseErr&&<div style={{padding:"8px 12px",background:T.redBg,color:T.red,borderRadius:8,fontSize:12,marginBottom:10}}>{parseErr}</div>}
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label={phase==="parsing"?"Analyzing…":"Analyze recipe"} onClick={analyze}/>
      <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0"}}>
        <div style={{flex:1,height:1,background:T.brd}}/><span style={{fontSize:11,color:T.dm,fontWeight:600}}>OR</span><div style={{flex:1,height:1,background:T.brd}}/>
      </div>
      <label style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:10,border:`2px dashed ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",marginBottom:8}}>
        <Camera size={20}/>{phase==="parsing"?"Reading…":"Scan a handwritten recipe card"}
        <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanPhoto(e.target.files[0]);}} style={{display:"none"}}/>
      </label>
      <label style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:10,border:`2px dashed ${T.blu}`,background:T.bluBg,color:T.blu,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>
        <Camera size={20}/>{phase==="parsing"?"Reading…":"Snap the ingredients (mise en place)"}
        <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanIngredients(e.target.files[0]);}} style={{display:"none"}}/>
      </label>
    </>}

    {phase==="review"&&<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {recipeName&&<div style={{fontSize:13,fontWeight:700,marginBottom:8,color:T.tx}}>{recipeName}</div>}
      {inferredNotice&&<div style={{padding:"8px 12px",background:T.ambBg,color:T.amb,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:10}}>⚠ No procedure was written on the card — these steps were inferred from the ingredients. Check them before saving.</div>}
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>INSTRUCTIONS</div>
      <textarea value={instructions} onChange={e=>setInstructions(e.target.value)} rows={4} style={{...inp,fontFamily:"inherit",resize:"vertical"}}/>
      <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600}}>INGREDIENTS ({rows.length})</div>
      <div style={{flex:1,overflowY:"auto"}}>
        {rows.length===0?<Empty msg="No ingredients with amounts were detected."/>:
          rows.map(r=><div key={r.id} style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,background:T.card,border:`1px solid ${T.brd}`,borderRadius:10,padding:8}}>
            <IngredientPicker ings={ings} setIngs={setIngs} value={r.ingId} onChange={id=>updRow(r.id,{ingId:id})} placeholder={r.name?`"${r.name}" — type to match or create`:"Type to search…"}/>
            <input type="number" value={r.qty} onChange={e=>updRow(r.id,{qty:e.target.value})} placeholder="0" style={{...inp,width:60,marginBottom:0,fontSize:12}}/>
            <select value={r.unit} onChange={e=>updRow(r.id,{unit:e.target.value})} style={{...inp,width:64,marginBottom:0,fontSize:12}}>
              <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="slice">slice</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
            </select>
            <button onClick={()=>rmRow(r.id)} style={{width:30,height:30,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><X size={13}/></button>
          </div>)}
      </div>
      <div style={{paddingTop:10}}>
        <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label="Apply to recipe" onClick={apply}/>
      </div>
    </div>}
  </div>;
}

/* ═══ RECIPE — unchanged in logic, button sizes tuned ═══ */
/* ═══ RECIPE BOOK — batch prep from scratch (yields components used in Menu) ═══ */
function RecipeBookP({recipeBook,setRecipeBook,ings,setIngs,showToast}){
  const [search,setSearch]=useState("");
  const [adding,setAdding]=useState(false);
  const [editing,setEditing]=useState(null);
  const [viewing,setViewing]=useState(null);
  const newBlank=()=>({id:genId(),name:"",batchTotal:"",batchUnit:"g",shelfDays:3,instructions:"",comps:[],photo:""});
  const [draft,setDraft]=useState(newBlank());

  const addComp=()=>setDraft({...draft,comps:[...draft.comps,{ingId:ings[0]?.id||"",qty:"",unit:"g"}]});
  const updComp=(idx,patch)=>setDraft({...draft,comps:draft.comps.map((c,i)=>i===idx?{...c,...patch}:c)});
  const rmComp=(idx)=>setDraft({...draft,comps:draft.comps.filter((_,i)=>i!==idx)});

  // Dictated recipe → merged into the draft: instructions appended (or set, if
  // empty), components merged by ingId so a repeated ingredient adds qty instead
  // of duplicating a row.
  const [dictating,setDictating]=useState(false);
  const handleDictateApply=({name,instructions,comps})=>{
    setDraft(d=>{
      const newInstructions=d.instructions?`${d.instructions}\n${instructions}`:instructions;
      let newComps=[...d.comps];
      comps.forEach(c=>{
        if(!c.ingId)return;
        const idx=newComps.findIndex(x=>x.ingId===c.ingId);
        if(idx>=0)newComps[idx]={...newComps[idx],qty:(parseFloat(newComps[idx].qty)||0)+c.qty};
        else newComps.push(c);
      });
      // A scanned card's title fills the name only if nothing's typed yet —
      // never overwrites a name the user already entered.
      return {...d,name:d.name||name||d.name,instructions:newInstructions,comps:newComps};
    });
    setDictating(false);
  };

  // Quick-add parser: free text -> components. Handles "2 cups ketchup, 3 cups mayo, 4 tbsp soy sauce".
  const [quickText,setQuickText]=useState("");
  const parseQuickAdd=()=>{
    const text=quickText.trim();
    if(!text)return;
    // Unit aliases -> canonical unit used by the app
    const UNIT_MAP={cup:"cup",cups:"cup",c:"cup",tbsp:"tbsp",tablespoon:"tbsp",tablespoons:"tbsp",tbs:"tbsp",tsp:"tsp",teaspoon:"tsp",teaspoons:"tsp",
      g:"g",gram:"g",grams:"g",gr:"g",kg:"kg",kilo:"kg",kilos:"kg",kilogram:"kg",kilograms:"kg",
      ml:"ml",milliliter:"ml",milliliters:"ml",l:"L",liter:"L",liters:"L",litre:"L",litres:"L",
      oz:"oz",ounce:"oz",ounces:"oz",lb:"lb",lbs:"lb",pound:"pound",pounds:"lb",
      pc:"pc",piece:"pc",pieces:"pc",pcs:"pc",each:"pc",ea:"pc",clove:"pc",cloves:"pc",can:"pc",cans:"pc"};
    // cup/oz/lb -> grams approximations so cost calc works even though the editor lacks those units
    const TO_G={cup:240,oz:28,lb:454,pound:454};
    const numWord={half:0.5,quarter:0.25,one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10};
    // Split on commas/newlines/semicolons; also split run-ons like "2 cups ketchup 3 cups mayo"
    let chunks=text.split(/[,;\n]+/).map(s=>s.trim()).filter(Boolean);
    // Further split chunks that contain multiple "<number> ..." groups
    const expanded=[];
    chunks.forEach(ch=>{
      const parts=ch.split(/(?=(?:\d+(?:[.,/]\d+)?|\bhalf\b|\bone\b|\btwo\b|\bthree\b|\bfour\b|\bfive\b)\s)/i).map(s=>s.trim()).filter(Boolean);
      parts.forEach(p=>expanded.push(p));
    });
    const newComps=[];
    expanded.forEach(line=>{
      const lc=line.toLowerCase();
      // amount: digits, fractions (1/2), decimals, or number words
      let amount=null;
      const fracM=lc.match(/(\d+)\s*\/\s*(\d+)/);
      const decM=lc.match(/(\d+(?:[.,]\d+)?)/);
      if(fracM)amount=parseInt(fracM[1])/parseInt(fracM[2]);
      else if(decM)amount=parseFloat(decM[1].replace(",","."));
      else{for(const w in numWord){if(new RegExp("\\b"+w+"\\b").test(lc)){amount=numWord[w];break;}}}
      if(amount==null)amount=1;
      // unit
      let unit="pc",toG=null;
      for(const u in UNIT_MAP){if(new RegExp("\\b"+u+"\\b").test(lc)){unit=UNIT_MAP[u];if(TO_G[unit])toG=TO_G[unit];break;}}
      // ingredient name = strip number + unit words
      let name=line
        .replace(/\d+\s*\/\s*\d+/g,"").replace(/\d+(?:[.,]\d+)?/g,"")
        .replace(new RegExp("\\b("+Object.keys(UNIT_MAP).join("|")+")\\b","gi"),"")
        .replace(new RegExp("\\b("+Object.keys(numWord).join("|")+")\\b","gi"),"")
        .replace(/\bof\b/gi,"").replace(/\s+/g," ").trim();
      if(!name)return;
      // match to inventory
      const nlc=name.toLowerCase();
      const ing=ings.find(x=>x.name.toLowerCase()===nlc)||ings.find(x=>foodNamesMatch(x.name,name));
      // if unit converts to grams, store grams; else keep app unit (g if unknown)
      let finalUnit=unit,finalQty=amount;
      if(toG){finalUnit="g";finalQty=Math.round(amount*toG);}
      else if(unit==="oz"||unit==="lb"){finalUnit="g";finalQty=Math.round(amount*(unit==="lb"?454:28));}
      else if(!["g","kg","ml","L","pc","tsp","tbsp"].includes(unit)){finalUnit="g";}
      newComps.push({ingId:ing?.id||"",qty:finalQty,unit:finalUnit,_name:name,_matched:!!ing});
    });
    if(newComps.length){
      setDraft(d=>({...d,comps:[...d.comps,...newComps]}));
      const unmatched=newComps.filter(c=>!c._matched).length;
      if(showToast)showToast(`Added ${newComps.length} component${newComps.length>1?"s":""}${unmatched?` — ${unmatched} need an inventory match`:""}`);
      setQuickText("");
    }else if(showToast)showToast("Couldn't parse — try 'amount unit ingredient'");
  };

  const save=()=>{
    if(!draft.name)return;
    const toSave={...draft,batchTotal:parseFloat(draft.batchTotal)||0,shelfDays:parseInt(draft.shelfDays)||3,comps:draft.comps.map(c=>({...c,qty:parseFloat(c.qty)||0}))};
    if(!editing){
      const batchInBase=toSave.batchUnit==="L"?toSave.batchTotal*1000:toSave.batchUnit==="kg"?toSave.batchTotal*1000:toSave.batchTotal;
      toSave.batchRemaining=batchInBase;
      toSave.batchMade=new Date().toISOString();
    }
    setRecipeBook(p=>editing?p.map(r=>r.id===draft.id?toSave:r):[...p,toSave]);
    setEditing(null);setAdding(false);setDraft(newBlank());
  };
  const startEdit=(r)=>{setDraft({...r,batchTotal:r.batchTotal||"",comps:r.comps.map(c=>({...c,qty:c.qty||""}))});setEditing(r.id);setAdding(true);};
  const markPrepped=(id)=>{
    setRecipeBook(p=>p.map(r=>{
      if(r.id!==id)return r;
      const batchInBase=r.batchUnit==="L"?r.batchTotal*1000:r.batchUnit==="kg"?r.batchTotal*1000:r.batchTotal;
      return {...r,batchRemaining:batchInBase,batchMade:new Date().toISOString()};
    }));
  };

  const filtered=recipeBook.filter(r=>!search||r.name.toLowerCase().includes(search.toLowerCase()));

  if(adding)return <div style={{position:"relative"}}>
    <button onClick={()=>{setAdding(false);setEditing(null);setDraft(newBlank());}} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:13,marginBottom:10,fontFamily:"inherit",flexShrink:0}}><ArrowLeft size={16}/>Back</button>
    <Card t={editing?"EDIT BATCH RECIPE":"NEW BATCH RECIPE"}>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>RECIPE NAME</div>
      <input value={draft.name} onChange={e=>setDraft({...draft,name:e.target.value})} placeholder="e.g. Waffle batter, Harissa yoghurt" style={inp}/>
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>TOTAL BATCH SIZE</div>
          <input type="number" step="0.1" value={draft.batchTotal} onChange={e=>setDraft({...draft,batchTotal:e.target.value})} placeholder="e.g. 12000" style={inp}/>
        </div>
        <div style={{width:80}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>UNIT</div>
          <select value={draft.batchUnit||"g"} onChange={e=>setDraft({...draft,batchUnit:e.target.value})} style={inp}>
            <option value="g">g</option><option value="ml">ml</option><option value="L">L</option><option value="kg">kg</option>
          </select>
        </div>
      </div>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>SHELF LIFE (DAYS)</div>
      <input type="number" value={draft.shelfDays||""} onChange={e=>setDraft({...draft,shelfDays:e.target.value})} placeholder="3" style={inp}/>
      {dictating&&<DictateRecipeModal ings={ings} setIngs={setIngs} showToast={showToast} onApply={handleDictateApply} onClose={()=>setDictating(false)}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <div style={{fontSize:11,color:T.mt,fontWeight:600}}>INSTRUCTIONS</div>
        <button onClick={()=>setDictating(true)} style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:5}}><Mic size={13}/>Dictate recipe</button>
      </div>
      <textarea value={draft.instructions} onChange={e=>setDraft({...draft,instructions:e.target.value})} placeholder="Full prep procedure" rows={3} style={{...inp,fontFamily:"inherit",resize:"vertical"}}/>
      <DishPhotoField photo={draft.photo} setPhoto={p=>setDraft(d=>({...d,photo:p}))} comps={draft.comps} ings={ings} name={draft.name} setName={n=>setDraft(d=>({...d,name:n}))} instructions={draft.instructions} setInstructions={ins=>setDraft(d=>({...d,instructions:ins}))} showToast={showToast}/>
    </Card>

    <Card t="COMPONENTS">
      {/* Quick add — type or dictate a list, we parse amounts + ingredients */}
      <div style={{background:T.bluBg,border:`1px dashed ${T.blu}55`,borderRadius:10,padding:12,marginBottom:12}}>
        <div style={{fontSize:11,color:T.blu,fontWeight:700,marginBottom:6,display:"flex",alignItems:"center",gap:6}}><Mic size={13}/>QUICK ADD — type or dictate</div>
        <textarea value={quickText} onChange={e=>setQuickText(e.target.value)} placeholder={'e.g. "2 cups ketchup, 3 cups mayo, 4 tbsp soy sauce, 2 tsp rice vinegar"\n\nUse your phone\'s mic on the keyboard to speak it.'} rows={3} style={{...inp,fontFamily:"inherit",resize:"vertical",marginBottom:8}}/>
        <button onClick={parseQuickAdd} disabled={!quickText.trim()} style={{width:"100%",padding:11,borderRadius:9,border:"none",background:quickText.trim()?T.blu:T.brd,color:"#fff",fontSize:13,fontWeight:700,cursor:quickText.trim()?"pointer":"default",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Plus size={15}/>Parse &amp; add components</button>
        <div style={{fontSize:10,color:T.dm,marginTop:7,lineHeight:1.5}}>Understands cups, tbsp, tsp, g, kg, ml, L, oz, lb, and counts. Matches each ingredient to your inventory — unmatched ones are flagged so you can pick them.</div>
      </div>
      <div style={{maxHeight:300,overflowY:"auto"}}>
      {draft.comps.map((c,idx)=>{
        const ing=ings.find(i=>i.id===c.ingId);
        const cost=ing?calcCompCost({...c,qty:parseFloat(c.qty)||0},ings):0;
        return <div key={idx} style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
          <IngredientPicker ings={ings} setIngs={setIngs} value={c.ingId} onChange={id=>updComp(idx,{ingId:id})} placeholder={c._name?`⚠ "${c._name}" — type to match or create`:"Type to search…"}/>
          <input type="number" value={c.qty} onChange={e=>updComp(idx,{qty:e.target.value})} placeholder="0" style={{...inp,width:70,marginBottom:0}}/>
          <select value={c.unit} onChange={e=>updComp(idx,{unit:e.target.value})} style={{...inp,width:70,marginBottom:0}}>
            <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
          </select>
          <span style={{fontSize:11,color:T.amb,fontFamily:"'DM Mono',monospace",minWidth:48,textAlign:"right"}}>{fmtEur(cost)}</span>
          <button onClick={()=>rmComp(idx)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><X size={14}/></button>
        </div>;
      })}
      </div>
      <button onClick={addComp} style={{width:"100%",padding:10,borderRadius:8,border:`1px dashed ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Plus size={14}/>Add ingredient</button>
    </Card>
    <div style={{position:"sticky",bottom:0,paddingTop:8,paddingBottom:4,background:T.bg,zIndex:5}}>
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label={editing?"Save changes":"Create batch recipe"} onClick={save}/>
    </div>
  </div>;

  return <div>
    <h2 style={{fontSize:20,fontWeight:700,marginBottom:4,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Recipe book</h2>
    <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Batch prep recipes made from scratch. These produce components portioned out in Menu for POS sales.</div>
    <div style={{display:"flex",gap:8,marginBottom:10}}>
      <SearchInp value={search} set={setSearch}/>
      <button onClick={()=>{setDraft(newBlank());setAdding(true);}} style={{padding:"0 16px",borderRadius:10,border:`1px solid ${T.amb}`,background:T.ambBg,color:T.amb,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6,minHeight:48}}><Plus size={18}/>New</button>
    </div>
    {filtered.length===0?<Empty msg="No batch recipes yet"/>:filtered.map(rb=>{
      const batchInBase=rb.batchUnit==="L"?rb.batchTotal*1000:rb.batchUnit==="kg"?rb.batchTotal*1000:rb.batchTotal;
      const pctLeft=batchInBase>0?Math.round(((rb.batchRemaining||0)/batchInBase)*100):0;
      const needsPrep=(rb.batchRemaining||0)<=0||(rb.batchMade&&(Date.now()-new Date(rb.batchMade).getTime())/86400000>=(rb.shelfDays||3));
      const cost=rb.comps.reduce((s,c)=>s+calcCompCost(c,ings),0);
      return <div key={rb.id} style={{background:T.card,border:`1px solid ${needsPrep?T.red+"55":T.brd}`,borderRadius:10,padding:12,marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div>
            <div style={{fontSize:15,fontWeight:700}}>{rb.name}</div>
            <div style={{fontSize:11,color:T.dm}}>{rb.batchTotal} {rb.batchUnit} batch · {rb.shelfDays}d shelf · {rb.comps.length} ingredients · {fmtEur(cost)}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:18,fontWeight:700,color:needsPrep?T.red:T.grn,fontFamily:"'DM Mono',monospace"}}>{pctLeft}%</div>
            <div style={{fontSize:9,color:T.dm}}>left</div>
          </div>
        </div>
        <div style={{height:4,background:T.sf,borderRadius:2,marginBottom:8,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pctLeft}%`,background:needsPrep?T.red:T.grn,borderRadius:2}}/>
        </div>
        <div style={{display:"flex",gap:6}}>
          {needsPrep&&<button onClick={()=>markPrepped(rb.id)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Check size={14}/>Mark prepped</button>}
          <button onClick={()=>setViewing(rb)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Eye size={14}/>View</button>
          <button onClick={()=>startEdit(rb)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Edit3 size={14}/>Edit</button>
        </div>
      </div>;
    })}
    {viewing&&<RecipeViewModal recipe={viewing} ings={ings} onClose={()=>setViewing(null)}/>}
  </div>;
}

/* ═══ MENU — finished products (references Recipe Book batches for portioned POS sales) ═══ */
function RecipeP({recipes,setRecipes,ings,setIngs,recipeBook,onMenuImport,showToast}){
  const [search,setSearch]=useState("");
  const [editing,setEditing]=useState(null);
  const [adding,setAdding]=useState(false);
  const [scale,setScale]=useState({});
  const [scanning,setScanning]=useState(false);
  const [scanErr,setScanErr]=useState("");
  const [scanReview,setScanReview]=useState(null); // array of dishes pending confirm

  // Scan a menu — accepts MULTIPLE files (multi-page menus). Each page becomes
  // an image/document block in one request, so dishes across pages come back together.
  // Beverages are detected and separated (defaulted to excluded from food costing).
  const scanMenu=async(files)=>{
    const fileList=Array.isArray(files)?files:Array.from(files||[]);
    if(!fileList.length)return;
    setScanning(true);setScanErr("");
    try{
      // Send each page to the backend scanner (server calls Claude — works
      // reliably, no browser CORS/key issues). Merge dishes across pages.
      let dishes=[];
      for(const file of fileList){
        const mediaType=file.type||"image/jpeg";
        const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=()=>rej("Read failed");r.readAsDataURL(file);});
        let pageItems=null,lastMsg="";
        for(let attempt=1;attempt<=3;attempt++){
          const resp=await fetch(`${SCAN_API}/scan/menu`,{
            method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({image:base64,mediaType})
          });
          if(resp.ok){const data=await resp.json();if(data.ok&&Array.isArray(data.items)){pageItems=data.items;break;}lastMsg=data.error||"No items found";}
          else{const e=await resp.json().catch(()=>({}));lastMsg=e.error||`Scan failed (${resp.status})`;
            if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){setScanErr(`Scanner busy — retrying (${attempt}/3)…`);await new Promise(r=>setTimeout(r,2000*attempt));continue;}}
          break;
        }
        if(pageItems)dishes=dishes.concat(pageItems);
        else if(lastMsg)throw new Error(lastMsg);
      }
      if(dishes.length===0)throw new Error("No items found — try a clearer photo");
      setScanErr("");
      const reviewed=dishes.map(d=>({
        name:d.name||"Unnamed item",
        price:d.price!=null?d.price:"",
        cat:d.category||"Greens",
        isBeverage:!!d.isBeverage,
        include:!d.isBeverage, // beverages default OFF (no food cost); food defaults ON
        comps:(d.ingredients||[]).map(ing=>{
          const nm=(ing.name||"").toLowerCase();
          const match=ings.find(x=>x.name.toLowerCase()===nm)||ings.find(x=>foodNamesMatch(x.name,ing.name||""));
          return {name:ing.name||"",ingId:match?.id||"",qty:ing.qty||0,unit:ing.unit||match?.unit||"g",matched:!!match};
        }),
      }));
      const foodCount=reviewed.filter(d=>!d.isBeverage).length;
      const bevCount=reviewed.filter(d=>d.isBeverage).length;
      setScanReview(reviewed);
      showToast(`Scanned ${fileList.length} page${fileList.length>1?"s":""} — ${foodCount} dishes${bevCount>0?`, ${bevCount} beverages`:""}`);
    }catch(e){setScanErr(e.message||"Could not read menu");showToast("Menu scan failed — try clearer files");}
    setScanning(false);
  };

  // Commit reviewed dishes: create any missing ingredients, then add recipes
  const commitScannedMenu=()=>{
    const toAdd=scanReview.filter(d=>d.include);
    if(toAdd.length===0){setScanReview(null);return;}
    // Collect ingredients with no match → they'll be created as inventory items
    const newIngredients=[];
    toAdd.forEach(d=>d.comps.forEach(c=>{
      if(!c.ingId&&c.name&&!newIngredients.find(n=>n.name.toLowerCase()===c.name.toLowerCase())){
        newIngredients.push({name:c.name,unit:c.unit});
      }
    }));
    // Signal parent to create ingredients + recipes
    if(onMenuImport)onMenuImport({dishes:toAdd,newIngredients});
    setScanReview(null);
  };


  const filtered=recipes.filter(r=>!search||r.name.toLowerCase().includes(search.toLowerCase()));

  const newBlank=()=>({id:genId(),name:"",cat:"Greens",type:"dish",price:"",yield:1,status:"draft",prep:0,cook:0,instructions:"",comps:[],batchRef:"",portionWeight:""});
  const [draft,setDraft]=useState(newBlank());

  const addComp=()=>setDraft({...draft,comps:[...draft.comps,{ingId:ings[0]?.id||"",qty:0,unit:"g"}]});
  const updComp=(idx,patch)=>setDraft({...draft,comps:draft.comps.map((c,i)=>i===idx?{...c,...patch}:c)});
  const rmComp=(idx)=>setDraft({...draft,comps:draft.comps.filter((_,i)=>i!==idx)});

  const [dictating,setDictating]=useState(false);
  const handleDictateApply=({name,instructions,comps})=>{
    setDraft(d=>{
      const newInstructions=d.instructions?`${d.instructions}\n${instructions}`:instructions;
      let newComps=[...d.comps];
      comps.forEach(c=>{
        if(!c.ingId)return;
        const idx=newComps.findIndex(x=>x.ingId===c.ingId);
        if(idx>=0)newComps[idx]={...newComps[idx],qty:(parseFloat(newComps[idx].qty)||0)+c.qty};
        else newComps.push(c);
      });
      // A scanned card's title fills the name only if nothing's typed yet —
      // never overwrites a name the user already entered.
      return {...d,name:d.name||name||d.name,instructions:newInstructions,comps:newComps};
    });
    setDictating(false);
  };

  const save=()=>{
    if(!draft.name||draft.comps.length===0)return;
    setRecipes(p=>editing?p.map(r=>r.id===draft.id?draft:r):[...p,draft]);
    setEditing(null);setAdding(false);setDraft(newBlank());
  };
  const dup=(r)=>setRecipes(p=>[...p,{...r,id:genId(),name:r.name+" (copy)",status:"draft"}]);
  const startEdit=(r)=>{setDraft(r);setEditing(r.id);setAdding(true);};

  if(adding)return <div style={{position:"relative"}}>
    <button onClick={()=>{setAdding(false);setEditing(null);setDraft(newBlank());}} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:13,marginBottom:10,fontFamily:"inherit",flexShrink:0}}><ArrowLeft size={16}/>Back</button>

    <Card t={editing?"EDIT MENU ITEM":"NEW MENU ITEM"}>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>RECIPE NAME</div>
      <input value={draft.name} onChange={e=>setDraft({...draft,name:e.target.value})} placeholder="e.g. Smash Burger, Eggs Benedict" style={inp}/>
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>SELL PRICE (€)</div>
          <input type="number" step="0.01" value={draft.price||""} onChange={e=>setDraft({...draft,price:parseFloat(e.target.value)||0})} placeholder="0.00" style={inp}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>YIELD (PORTIONS)</div>
          <input type="number" value={draft.yield||""} onChange={e=>setDraft({...draft,yield:parseFloat(e.target.value)||1})} placeholder="1" style={inp}/>
        </div>
      </div>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>STATUS</div>
      <select value={draft.status} onChange={e=>setDraft({...draft,status:e.target.value})} style={inp}>{RECIPE_STATUS.map(s=><option key={s} value={s}>{s}</option>)}</select>
      {dictating&&<DictateRecipeModal ings={ings} setIngs={setIngs} showToast={showToast} onApply={handleDictateApply} onClose={()=>setDictating(false)}/>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <div style={{fontSize:11,color:T.mt,fontWeight:600}}>INSTRUCTIONS</div>
        <button onClick={()=>setDictating(true)} style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:5}}><Mic size={13}/>Dictate recipe</button>
      </div>
      <textarea value={draft.instructions} onChange={e=>setDraft({...draft,instructions:e.target.value})} placeholder="Step-by-step prep and plating" rows={3} style={{...inp,fontFamily:"inherit",resize:"vertical"}}/>
      {/* Dish photo — visual reference of the plated menu item, plus a name/description suggestion */}
      <DishPhotoField photo={draft.photo} setPhoto={p=>setDraft(d=>({...d,photo:p}))} comps={draft.comps} ings={ings} name={draft.name} setName={n=>setDraft(d=>({...d,name:n}))} instructions={draft.instructions} setInstructions={ins=>setDraft(d=>({...d,instructions:ins}))} showToast={showToast}/>
    </Card>

    {recipeBook&&recipeBook.length>0&&<Card t="BATCH LINK (from Recipe Book)">
      <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Link to a Recipe Book batch. POS deducts the portion weight per sale from that batch.</div>
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>LINKED BATCH</div>
      <select value={draft.batchRef||""} onChange={e=>setDraft({...draft,batchRef:e.target.value})} style={inp}>
        <option value="">— None (no batch tracking) —</option>
        {recipeBook.map(rb=><option key={rb.id} value={rb.id}>{rb.name} ({rb.batchTotal} {rb.batchUnit})</option>)}
      </select>
      {draft.batchRef&&<>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PORTION WEIGHT PER SALE (g/ml)</div>
        <input type="number" step="0.1" value={draft.portionWeight||""} onChange={e=>setDraft({...draft,portionWeight:e.target.value})} placeholder="e.g. 20" style={inp}/>
        {draft.portionWeight&&(()=>{
          const rb=recipeBook.find(x=>x.id===draft.batchRef);
          if(!rb)return null;
          const batchBase=rb.batchUnit==="L"?rb.batchTotal*1000:rb.batchUnit==="kg"?rb.batchTotal*1000:rb.batchTotal;
          const pw=parseFloat(draft.portionWeight)||0;
          const totalPortions=pw>0?Math.floor(batchBase/pw):0;
          return <div style={{padding:10,background:T.ambBg,borderRadius:8,border:`1px solid ${T.amb}33`}}>
            <div style={{fontSize:13,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{totalPortions} portions per batch</div>
            <div style={{fontSize:11,color:T.mt,marginTop:2}}>Each sale deducts {pw}g/ml from {rb.name}</div>
          </div>;
        })()}
      </>}
    </Card>}

    <Card t="COMPONENTS">
      {draft.comps.length>0&&<div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,fontSize:10,color:T.mt,fontWeight:600}}>
        <span style={{flex:2}}>INGREDIENT</span>
        <span style={{width:70,textAlign:"center"}}>QTY</span>
        <span style={{width:70,textAlign:"center"}}>UNIT</span>
        <span style={{minWidth:48,textAlign:"right"}}>COST</span>
        <span style={{width:32}}></span>
      </div>}
      <div style={{maxHeight:280,overflowY:"auto"}}>
      {draft.comps.map((c,idx)=>{
        const ing=ings.find(i=>i.id===c.ingId);
        const cost=ing?calcCompCost(c,ings):0;
        return <div key={idx} style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
          <IngredientPicker ings={ings} setIngs={setIngs} value={c.ingId} onChange={id=>updComp(idx,{ingId:id})} placeholder="Type to search…"/>
          <input type="number" value={c.qty||""} onChange={e=>updComp(idx,{qty:parseFloat(e.target.value)||0})} placeholder="0" style={{...inp,width:70,marginBottom:0}}/>
          <select value={c.unit} onChange={e=>updComp(idx,{unit:e.target.value})} style={{...inp,width:70,marginBottom:0}}>
            <option value="g">g</option><option value="kg">kg</option><option value="ml">ml</option><option value="L">L</option><option value="pc">pc</option><option value="tsp">tsp</option><option value="tbsp">tbsp</option>
          </select>
          <span style={{fontSize:11,color:T.amb,fontFamily:"'DM Mono',monospace",minWidth:48,textAlign:"right"}}>{fmtEur(cost)}</span>
          <button onClick={()=>rmComp(idx)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={14}/></button>
        </div>;
      })}
      </div>
      <button onClick={addComp} style={{width:"100%",padding:10,borderRadius:8,border:`1px dashed ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Plus size={14}/>Add component</button>

      {draft.comps.length>0&&<div style={{marginTop:10,padding:10,background:T.sf,borderRadius:8}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.mt}}><span>Total cost</span><span style={{fontFamily:"'DM Mono',monospace",color:T.tx}}>{fmtEur(calcRecipeCost(draft,ings))}</span></div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.mt,marginTop:4}}><span>Margin</span><span style={{fontFamily:"'DM Mono',monospace",color:T.grn,fontWeight:600}}>{draft.price>0?(((draft.price-calcRecipeCost(draft,ings))/draft.price)*100).toFixed(1):0}%</span></div>
      </div>}
    </Card>
    <div style={{position:"sticky",bottom:0,paddingTop:8,paddingBottom:4,background:T.bg,zIndex:5}}>
      <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label={editing?"Save changes":"Create menu item"} onClick={save}/>
    </div>
  </div>;

  return <div>
    <div style={{display:"flex",gap:8,marginBottom:10}}>
      <SearchInp value={search} set={setSearch}/>
      <button onClick={()=>{setDraft(newBlank());setAdding(true);}} style={{padding:"0 16px",borderRadius:10,border:`1px solid ${T.grn}`,background:T.grnBg,color:T.grn,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6,minHeight:48}}><Plus size={18}/>Add</button>
    </div>

    {/* Scan menu — onboard a whole menu from a photo or PDF */}
    {!scanReview&&<label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%",padding:14,borderRadius:12,border:`1.5px dashed ${T.blu}`,background:T.bluBg,color:T.blu,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:600,marginBottom:6}}>
      <Camera size={18}/>{scanning?"Reading menu…":"Scan a menu — take a photo"}
      <input type="file" accept="image/*" multiple onChange={e=>{if(e.target.files.length)scanMenu(e.target.files);}} style={{display:"none"}} disabled={scanning}/>
    </label>}
    {!scanReview&&!scanning&&<div style={{fontSize:11,color:T.dm,textAlign:"center",marginBottom:12,fontStyle:"italic"}}>Multi-page menu? Select all pages at once. Beverages are detected and set aside automatically.</div>}
    {scanErr&&<div style={{color:T.red,fontSize:12,marginBottom:10}}>{scanErr}</div>}

    {/* Review scanned dishes before committing */}
    {scanReview&&<Card t={`REVIEW SCANNED MENU (${scanReview.filter(d=>d.include).length} of ${scanReview.length})`}>
      <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Check the dishes below. Beverages are unticked by default (no food cost to track) — tick them only if you want them as menu items. Ingredients without a match will be created in inventory and priced when you scan an invoice.</div>
      {scanReview.map((d,di)=><div key={di} style={{padding:"10px 12px",background:d.include?T.sf:"transparent",border:`1px solid ${d.include?T.brd:T.brd+"55"}`,borderRadius:10,marginBottom:8,opacity:d.include?1:0.5}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <button onClick={()=>setScanReview(p=>p.map((x,i)=>i===di?{...x,include:!x.include}:x))} style={{width:22,height:22,borderRadius:6,border:`2px solid ${d.include?T.grn:T.brd}`,background:d.include?T.grn:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,cursor:"pointer"}}>{d.include&&<Check size={14} color="#fff"/>}</button>
          <input value={d.name} onChange={e=>setScanReview(p=>p.map((x,i)=>i===di?{...x,name:e.target.value}:x))} style={{...inp,marginBottom:0,flex:1,fontWeight:600}}/>
          {d.isBeverage&&<span style={{fontSize:9,fontWeight:700,color:T.amb,background:T.ambBg,padding:"3px 7px",borderRadius:5,letterSpacing:0.3,flexShrink:0,textTransform:"uppercase"}}>Drink</span>}
          <input type="number" step="0.01" value={d.price} onChange={e=>setScanReview(p=>p.map((x,i)=>i===di?{...x,price:e.target.value}:x))} placeholder="€" style={{...inp,marginBottom:0,width:72}}/>
        </div>
        <div style={{fontSize:11,color:T.dm,paddingLeft:30}}>{d.comps.length?d.comps.map((c,ci)=><span key={ci}>{c.matched?"✓":"+"} {c.name}{ci<d.comps.length-1?", ":""}</span>):<span style={{fontStyle:"italic"}}>No ingredients detected — add them after import</span>}</div>
      </div>)}
      <div style={{display:"flex",gap:8,marginTop:6}}>
        <button onClick={()=>setScanReview(null)} style={{flex:1,padding:12,borderRadius:10,border:`1px solid ${T.brd}`,background:"none",color:T.tx,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
        <button onClick={commitScannedMenu} style={{flex:2,padding:12,borderRadius:10,border:"none",background:T.grn,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Import {scanReview.filter(d=>d.include).length} dishes</button>
      </div>
    </Card>}

    <div id="recipes-content">
      {filtered.map(r=>{
        const cost=calcRecipeCost(r,ings);
        const margin=r.price>0?((r.price-cost)/r.price*100):0;
        return <div key={r.id} style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:8}}>
            <div style={{flex:1,minWidth:0,display:"flex",gap:10,alignItems:"flex-start"}}>
              {r.photo&&<img src={r.photo} alt="" style={{width:48,height:48,borderRadius:8,objectFit:"cover",flexShrink:0,border:`1px solid ${T.brd}`}}/>}
              <div style={{minWidth:0}}>
                <div style={{fontSize:14,fontWeight:600,marginBottom:2}}>{r.name}</div>
                <div style={{display:"flex",gap:6,alignItems:"center",fontSize:10,color:T.dm,flexWrap:"wrap"}}>
                  <StatusBadge s={r.status}/>
                  <span>{r.type} · {r.comps.length} comp</span>
                  {r.batchRef&&(()=>{const rb=recipeBook&&recipeBook.find(x=>x.id===r.batchRef);if(!rb)return null;const batchBase=rb.batchUnit==="L"?rb.batchTotal*1000:rb.batchUnit==="kg"?rb.batchTotal*1000:rb.batchTotal;const pw=parseFloat(r.portionWeight)||0;const total=pw>0?Math.floor(batchBase/pw):0;const remaining=pw>0?Math.floor((rb.batchRemaining||0)/pw):0;return <span style={{color:remaining<=0?T.red:T.amb,fontWeight:600}}> · {remaining}/{total}</span>;})()}
                </div>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:16,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(r.price)}</div>
              <div style={{fontSize:10,color:margin>60?T.grn:T.amb}}>{margin.toFixed(0)}% marg</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>setRecipes(p=>p.map(x=>x.id===r.id?{...x,status:x.status==="active"?"draft":"active"}:x))} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${r.status==="active"?T.grn:T.amb}55`,background:r.status==="active"?T.grnBg:T.ambBg,color:r.status==="active"?T.grn:T.amb,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>{r.status==="active"?<><Eye size={14}/>Active on POS</>:<><EyeOff size={14}/>Activate</>}</button>
            <button onClick={()=>startEdit(r)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Edit3 size={14}/>Edit</button>
            <button onClick={()=>dup(r)} style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center"}}><Copy size={14}/></button>
          </div>
        </div>;
      })}
    </div>

    <Card t="WASTE-REDUCTION IDEAS">
      <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Turn trim, scraps, and slow-movers into revenue. Tap to create a recipe from any idea.</div>
      {[
        {name:"Fried potato skins",desc:"Deep-fry peeled skins, season with salt and herbs. Serve as a side or bar snack.",cat:"Greens"},
        {name:"Vegetable stock from trim",desc:"Simmer onion ends, carrot peels, celery tops, herb stems for 2 hrs. Strain and use as base.",cat:"Greens"},
        {name:"Bread croutons / breadcrumbs",desc:"Cube stale bread, toss in oil and seasoning, bake 15 min. Or blitz into breadcrumbs for coating.",cat:"Bakery"},
        {name:"Fruit compote from overripe fruit",desc:"Dice soft fruit, simmer with sugar and lemon. Use for desserts, yoghurt topping, or cocktails.",cat:"Bakery"},
        {name:"Herb oil from stems",desc:"Blend herb stems with neutral oil, strain. Use as garnish oil for plating.",cat:"Greens"},
        {name:"Citrus zest garnish",desc:"Zest lemons, limes, oranges before juicing. Dry zest for long shelf life. Use on desserts and fish.",cat:"Greens"},
        {name:"Meat bone broth",desc:"Roast leftover bones, simmer 8-12 hrs with aromatics. Rich base for soups and sauces.",cat:"Meat & Fish"},
        {name:"Pickle brine dressing",desc:"Mix leftover pickle brine with olive oil and mustard for a zero-waste vinaigrette.",cat:"Greens"},
        {name:"Stale bread panzanella",desc:"Tear stale bread, toss with tomatoes, cucumber, red onion, basil, and vinaigrette.",cat:"Bakery"},
        {name:"Coffee grounds rub",desc:"Mix dried coffee grounds with salt, brown sugar, paprika. Use as a dry rub for grilled meats.",cat:"Meat & Fish"},
      ].map((idea,i)=><div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.brd}33`}}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:T.grn}}>{idea.name}</div>
          <div style={{fontSize:11,color:T.mt,lineHeight:1.4,marginTop:2}}>{idea.desc}</div>
        </div>
        <button onClick={()=>{setDraft({...newBlank(),name:idea.name,cat:idea.cat,instructions:idea.desc});setAdding(true);}} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0,whiteSpace:"nowrap"}}>+ Recipe</button>
      </div>)}
    </Card>

    <ExportBtn label="Print recipe book" onClick={()=>printSection("recipes-content","Recipe Book")}/>
  </div>;
}

/* ═══ ORDERS — shopping list → supplier order sheets ═══ */
function OrdersP({orders,setOrders,ings,setIngs,recipes,suppliers,addNotif,shoppingList,setShoppingList,showToast,setLocSuppliers,loc,forceSave}){
  const [tab,setTab]=useState("shopping");
  const [building,setBuilding]=useState(null);
  const [order,setOrder]=useState({supplierId:"",items:[],expectedDate:"",notes:""});
  // New supplier form
  const [addingSup,setAddingSup]=useState(false);
  const [newSup,setNewSup]=useState({name:"",contact:"",email:"",phone:"",lead:1,terms:"Net 14",acctNo:"",zip:loc?.addr||"",products:[]});
  // Receiving flow
  const [receiving,setReceiving]=useState(null); // order id being received
  const [recvLines,setRecvLines]=useState([]); // [{ingId, name, orderedQty, receivedQty, expectedPrice, actualPrice}]
  const [recvComplete,setRecvComplete]=useState(false);
  const [scanning,setScanning]=useState(false);
  const [scanError,setScanError]=useState("");

  // Scan a delivery invoice or a retail receipt — photo → backend scanner (server calls Claude).
  // Both document kinds land in the same recvLines review flow: receipts just tend to arrive
  // with no SKU and a single lump price per line instead of separate unit/extended columns.
  const scanDoc=async(file,kind)=>{
    const label=kind==="receipt"?"receipt":"invoice";
    setScanning(true);setScanError("");
    try{
      const mediaType=file.type||"image/jpeg";
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=()=>rej("Read failed");r.readAsDataURL(file);});
      // Client-side retry safety net: if the server reports a transient overload,
      // wait and try again before giving up. Backend also retries internally.
      let items=null,lastMsg="";
      for(let attempt=1;attempt<=3;attempt++){
        const resp=await fetch(`${SCAN_API}/scan/${kind}`,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({image:base64,mediaType})
        });
        if(resp.ok){
          const data=await resp.json();
          if(data.ok&&Array.isArray(data.items)){items=data.items;break;}
          lastMsg=data.error||"No items found";
        }else{
          const e=await resp.json().catch(()=>({}));
          lastMsg=e.error||`Scan failed (${resp.status})`;
          // If it's an overload/busy message, wait and retry
          if(/overload|busy|529|try again|503|429/i.test(lastMsg)&&attempt<3){
            setScanError(`Scanner busy — retrying (${attempt}/3)…`);
            await new Promise(r=>setTimeout(r,2000*attempt));
            continue;
          }
        }
        break;
      }
      if(!items||items.length===0)throw new Error(lastMsg||`No items found — try a clearer photo`);
      const lines=items.map(it=>{
        const nm=(it.name||"").toLowerCase();
        // Match by SKU first (most reliable for distributor invoices), then name —
        // via foodNamesMatch so German items (brand-prefixed, compound words) match
        // an inventory item that isn't spelled identically. Receipts never have a
        // SKU, so they fall straight through to name matching.
        const ing=(it.sku&&ings.find(x=>x.sku&&String(x.sku)===String(it.sku)))
          ||ings.find(x=>x.name.toLowerCase()===nm)
          ||ings.find(x=>foodNamesMatch(x.name,it.name||""));
        return {ingId:ing?.id||"",name:it.name||"Unknown",sku:it.sku||"",storage:it.storage||"",
          orderedQty:it.qty||"",receivedQty:it.qty||"",
          expectedPrice:ing?.cost||0,actualPrice:it.price!==null&&it.price!==undefined?it.price:(it.total||""),
          unit:it.unit||ing?.unit||"case",matched:!!ing};
      });
      setRecvLines(lines);setReceiving("scan");setRecvComplete(false);
      showToast(`${kind==="receipt"?"Receipt":"Invoice"} scanned — ${items.length} items found`);
      setScanError("");
    }catch(e){
      const msg=e.message||`Could not read ${label}`;
      setScanError(msg);
      showToast(/overload|busy|try again/i.test(msg)?"Scanner busy — try again in a moment":"Scan failed — try a clearer photo");
    }
    setScanning(false);
  };
  const scanInvoice=(file)=>scanDoc(file,"invoice");
  const scanReceipt=(file)=>scanDoc(file,"receipt");

  const startReceiving=(o)=>{
    const lines=o.items.map(it=>{
      const ing=ings.find(x=>x.id===it.ingId||x.id===it.id||x.name===it.name);
      return {ingId:ing?.id||it.ingId||"",name:ing?.name||it.name||"Unknown",orderedQty:it.qty,receivedQty:it.qty,expectedPrice:ing?.cost||it.unitCost||0,actualPrice:"",unit:ing?.unit||it.unit||"pc"};
    });
    setRecvLines(lines);
    setReceiving(o.id);
    setRecvComplete(false);
    setTab("receiving");
  };

  const updateRecvLine=(idx,patch)=>setRecvLines(p=>p.map((l,i)=>i===idx?{...l,...patch}:l));

  const confirmReceiving=()=>{
    const priceChanges=[];
    const updates=new Map(); // ingId -> {recvQty, actualPrice}
    const newItems=[]; // invoice lines with no inventory match → create them
    recvLines.forEach(line=>{
      const ingId=line.ingId||(ings.find(x=>x.name===line.name)?.id);
      const recvQty=parseFloat(line.receivedQty)||0;
      if(recvQty<=0)return;
      const actual=parseFloat(line.actualPrice);
      const hasPrice=!isNaN(actual)&&actual>0;
      if(!ingId){
        // No match — auto-create a new inventory item from this invoice line
        newItems.push({
          name:line.name||"New item",
          unit:line.unit||"pc",
          cost:hasPrice?actual:0,
          recvQty,
        });
        return;
      }
      const expected=line.expectedPrice;
      updates.set(ingId,{recvQty,actualPrice:hasPrice?actual:null});
      if(hasPrice){
        const pctChange=expected>0?Math.round(((actual-expected)/expected)*100):0;
        if(pctChange!==0)priceChanges.push({name:line.name,old:expected,new:actual,pct:pctChange});
      }
    });
    let newIngs=ings;
    if(updates.size>0){
      newIngs=ings.map(ing=>{
        const u=updates.get(ing.id);
        if(!u)return ing;
        const now=new Date().toISOString();
        const newLot={id:genId(),qty:u.recvQty,received:now,cost:u.actualPrice||ing.cost};
        let existingLots=[...(ing.lots||[])];
        if(existingLots.length===0&&ing.qty>0){
          const oldReceived=ing.lastReceived||new Date(Date.now()-(ing.shelf||7)*86400000).toISOString();
          existingLots.push({id:genId(),qty:ing.qty,received:oldReceived,cost:ing.cost});
        }
        const allLots=[...existingLots,newLot].filter(l=>l.qty>0);
        const totalQty=allLots.reduce((s,l)=>s+l.qty,0);
        const patched={...ing,qty:totalQty,lots:allLots,lastReceived:now,eightySixed:false};
        if(u.actualPrice!==null){
          patched.cost=u.actualPrice;
          patched.priceHist=[...ing.priceHist,{d:todayISO(),p:u.actualPrice}].slice(-20);
        }
        return patched;
      });
    }
    // Append newly-created items
    if(newItems.length>0){
      const now=new Date().toISOString();
      const created=newItems.map(ni=>({
        id:genId(),name:ni.name,cat:"Uncategorized",unit:ni.unit,
        qty:ni.recvQty,cost:ni.cost,parMin:0,parMax:0,
        shelf:7,unitG:ni.unit==="kg"?1000:ni.unit==="L"?1000:1,
        lots:[{id:genId(),qty:ni.recvQty,received:now,cost:ni.cost}],
        lastReceived:now,priceHist:ni.cost>0?[{d:todayISO(),p:ni.cost}]:[],
        sku:"",eightySixed:false,
      }));
      newIngs=[...newIngs,...created];
    }
    if(updates.size>0||newItems.length>0)setIngs(newIngs);
    const newOrders=orders.map(o=>o.id===receiving?{...o,status:"received",receivedAt:new Date().toISOString(),receivedLines:recvLines.filter(l=>(parseFloat(l.receivedQty)||0)>0).map(l=>({name:l.name,qty:parseFloat(l.receivedQty)||0,expectedPrice:l.expectedPrice,actualPrice:parseFloat(l.actualPrice)||l.expectedPrice,unit:l.unit}))}:o);
    setOrders(newOrders);
    const parts=[];
    if(updates.size>0)parts.push(`${updates.size} updated`);
    if(newItems.length>0)parts.push(`${newItems.length} new item${newItems.length>1?"s":""} added`);
    if(priceChanges.length>0){
      const summary=priceChanges.map(pc=>`${pc.name}: ${fmtEur(pc.old)} → ${fmtEur(pc.new)} (${pc.pct>0?"+":""}${pc.pct}%)`).join(", ");
      addNotif(`Delivery received — ${parts.join(", ")}. Price changes: ${summary}`);
    }else{
      addNotif(`Delivery received — ${parts.join(", ")||"no changes"}`);
    }
    showToast(`Received — ${parts.join(", ")||"done"}`);
    forceSave({ings:newIngs,orders:newOrders});
    setRecvComplete(true);
  };

  const addSupplier=()=>{
    if(!newSup.name){showToast("Enter supplier name");return;}
    const sup={...newSup,id:genId(),days:[1,2,3,4,5],rating:0,onTime:0,quality:0,products:[]};
    setLocSuppliers(p=>[...p,sup]);
    setNewSup({name:"",contact:"",email:"",phone:"",lead:1,terms:"Net 14",acctNo:"",zip:loc?.addr||"",products:[]});
    setAddingSup(false);
    showToast(`Supplier "${sup.name}" added`);
  };

  // group shopping list by supplier
  const grouped=useMemo(()=>{
    const m={};
    shoppingList.filter(s=>!s.assigned).forEach(s=>{
      const sup=s.supplier||"unassigned";
      if(!m[sup])m[sup]=[];
      m[sup].push(s);
    });
    return m;
  },[shoppingList]);

  const buildFromSupplier=(sid)=>{
    const sup=suppliers.find(s=>s.id===sid);
    const items=grouped[sid]||[];
    setOrder({supplierId:sid,acctNo:sup?.acctNo||"",items:items.map(s=>{
      const ing=ings.find(i=>i.id===s.ingId);
      return {ingId:s.ingId,name:ing?.name||s.name||"Unknown",qty:s.qty,unit:ing?.unit||"kg",unitCost:ing?.cost||0,shoppingId:s.id};
    }),expectedDate:"",notes:""});
    setBuilding(sid);
    setTab("build");
  };

  const removeFromShoppingList=(id)=>setShoppingList(p=>p.filter(s=>s.id!==id));
  const updateQty=(id,qty)=>setShoppingList(p=>p.map(s=>s.id===id?{...s,qty}:s));
  const reassign=(id,supplier)=>setShoppingList(p=>p.map(s=>s.id===id?{...s,supplier}:s));

  const submitOrder=()=>{
    if(!order.supplierId||order.items.length===0)return;
    const sup=suppliers.find(s=>s.id===order.supplierId);
    const total=order.items.reduce((s,i)=>s+i.qty*i.unitCost,0);
    const newOrder={
      id:genId(),
      supplierId:order.supplierId,
      supplierName:sup.name,
      acctNo:order.acctNo||sup.acctNo,
      items:order.items,
      expectedDate:order.expectedDate,
      notes:order.notes,
      total,
      status:"submitted",
      createdAt:new Date().toISOString(),
    };
    const newOrders=[newOrder,...orders];
    setOrders(newOrders);
    const usedIds=order.items.map(i=>i.shoppingId).filter(Boolean);
    const newShoppingList=shoppingList.filter(s=>!usedIds.includes(s.id));
    setShoppingList(newShoppingList);
    addNotif(`PO sent to ${sup.name} — ${fmtEur(total)}`);
    showToast(`Order submitted to ${sup.name}`);
    setOrder({supplierId:"",acctNo:"",items:[],expectedDate:"",notes:""});
    setBuilding(null);
    setTab("submitted");
    forceSave({orders:newOrders,shoppingList:newShoppingList});
  };

  const emailOrder=(o)=>{
    const sup=suppliers.find(s=>s.id===o.supplierId);
    if(!sup)return;
    const lines=o.items.map(i=>{const ing=ings.find(x=>x.id===i.ingId);return `  - ${i.qty} ${i.unit}  ${ing?.name||"item"}`;}).join("%0D%0A");
    const subject=`Bestellung VENUE Berlin — ${todayISO()} — Account ${o.acctNo||sup.acctNo}`;
    const body=`Hello ${sup.contact},%0D%0A%0D%0AAccount No: ${o.acctNo||sup.acctNo}%0D%0ADelivery date (requested): ${o.expectedDate||"asap"}%0D%0A%0D%0AOrder:%0D%0A${lines}%0D%0A%0D%0A${o.notes?`Notes: ${o.notes}%0D%0A%0D%0A`:""}Thank you,%0D%0AGastriot`;
    window.location.href=`mailto:${sup.email}?subject=${subject}&body=${body}`;
  };

  return <div>
    <Tabs tabs={[
      {id:"shopping",label:`Shopping (${shoppingList.length})`},
      {id:"build",label:"Build order"},
      {id:"receiving",label:"Receiving"},
      {id:"submitted",label:`Sent (${orders.length})`},
      {id:"suppliers",label:"Suppliers"},
    ]} active={tab} set={setTab}/>

    {tab==="shopping"&&<>
      <Banner c={T.amb} bg={T.ambBg} l="ITEMS BELOW PAR" v={shoppingList.length}/>
      <div id="shopping-list">
        {shoppingList.length===0?<Empty msg="Nothing on the shopping list. Items appear here automatically as inventory drops below par."/>:
          Object.entries(grouped).map(([sid,items])=>{
            const sup=suppliers.find(x=>x.id===sid);
            return <div key={sid} style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${T.brd}`}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:T.amb}}>{sup?.name||"Unassigned"}</div>
                  <div style={{fontSize:10,color:T.dm}}>{items.length} items{sup?` · ${sup.acctNo}`:""}</div>
                </div>
                {sup&&<button onClick={()=>buildFromSupplier(sid)} style={{padding:"10px 14px",borderRadius:8,border:"none",background:T.amb,color:"#1B2A4A",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>Build PO →</button>}
              </div>
              {items.length>0&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",marginBottom:2,fontSize:10,color:T.mt,fontWeight:600}}>
                <span style={{flex:1}}>ITEM</span>
                <span style={{width:74,textAlign:"center"}}>ORDER QTY</span>
                <span style={{minWidth:24}}></span>
                <span style={{width:100,textAlign:"center"}}>SUPPLIER</span>
                <span style={{width:32}}></span>
              </div>}
              {items.map(s=>{
                const ing=ings.find(i=>i.id===s.ingId);if(!ing)return null;
                return <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${T.brd}33`}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500}}>{ing.name}</div>
                    <div style={{fontSize:10,color:T.dm}}>Now: {ing.qty}{ing.unit} · Par: {ing.parMin}–{ing.parMax}{s.priority==="urgent"&&<span style={{color:T.red,marginLeft:6,fontWeight:600}}>URGENT</span>}</div>
                  </div>
                  <input type="number" step="0.1" value={s.qty} onChange={e=>updateQty(s.id,parseFloat(e.target.value)||0)} style={{...inp,width:74,marginBottom:0,fontSize:13}}/>
                  <span style={{fontSize:10,color:T.dm,minWidth:24}}>{ing.unit}</span>
                  <select value={s.supplier||""} onChange={e=>reassign(s.id,e.target.value)} style={{...inp,width:100,marginBottom:0,fontSize:11,padding:"8px 6px"}}>
                    <option value="">none</option>
                    {suppliers.map(sx=><option key={sx.id} value={sx.id}>{sx.name.slice(0,18)}</option>)}
                  </select>
                  <button onClick={()=>removeFromShoppingList(s.id)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={14}/></button>
                </div>;
              })}
            </div>;
          })}
      </div>
      <ExportBtn label="Print shopping list" onClick={()=>printSection("shopping-list","Shopping List")}/>
    </>}

    {tab==="build"&&<BuildOrderPanel order={order} setOrder={setOrder} suppliers={suppliers} ings={ings} submitOrder={submitOrder} startBlank={()=>{setOrder({supplierId:"",acctNo:"",items:[],expectedDate:"",notes:""});setBuilding("manual");}} discardBuild={()=>{setOrder({supplierId:"",acctNo:"",items:[],expectedDate:"",notes:""});setBuilding(null);}} building={building}/>}

    {tab==="receiving"&&<>
      {!receiving&&!recvComplete&&<>
        <Card t="RECEIVE A DELIVERY">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Select a sent order to receive, or start an ad-hoc receiving check for a walk-in delivery.</div>
          {orders.filter(o=>o.status!=="received").length===0&&<Empty msg="No pending orders. All orders have been received."/>}
          {orders.filter(o=>o.status!=="received").map(o=><button key={o.id} onClick={()=>startReceiving(o)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",background:T.card,border:`1px solid ${T.brd}`,borderRadius:10,marginBottom:6,cursor:"pointer",fontFamily:"inherit",color:"inherit",textAlign:"left"}}>
            <div>
              <div style={{fontSize:14,fontWeight:600}}>{o.supplierName}</div>
              <div style={{fontSize:10,color:T.dm}}>{fmtDate(o.createdAt)} · {o.items.length} items · {fmtEur(o.total)}</div>
            </div>
            <div style={{padding:"8px 14px",borderRadius:8,background:T.grnBg,color:T.grn,fontSize:12,fontWeight:600}}>Receive →</div>
          </button>)}
        </Card>
        <Card t="AD-HOC RECEIVING (no prior order)">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Walk-in delivery? Pick ingredients to check prices against your records.</div>
          <button onClick={()=>{
            const lines=ings.slice(0,Math.min(ings.length,20)).map(ing=>({ingId:ing.id,name:ing.name,orderedQty:"",receivedQty:"",expectedPrice:ing.cost,actualPrice:"",unit:ing.unit}));
            setRecvLines(lines);setReceiving("adhoc");setRecvComplete(false);
          }} style={{width:"100%",padding:14,borderRadius:10,border:`1px solid ${T.amb}`,background:T.ambBg,color:T.amb,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>Start ad-hoc price check</button>
        </Card>
        <Card t="SCAN DELIVERY INVOICE">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Photograph the delivery invoice. Gastriot reads the items and prices automatically.</div>
          <label style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:10,border:`2px dashed ${T.blu}`,background:T.bluBg,color:T.blu,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>
            <Camera size={20}/>{scanning?"Scanning...":"Tap to photograph invoice"}
            <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanInvoice(e.target.files[0]);}} style={{display:"none"}}/>
          </label>
          {scanError&&<div style={{marginTop:8,padding:"8px 12px",background:T.redBg,color:T.red,borderRadius:8,fontSize:12}}>{scanError}</div>}
          {scanning&&<div style={{marginTop:8,textAlign:"center",color:T.mt,fontSize:12}}>Reading invoice... this takes a few seconds</div>}
        </Card>
        <Card t="SCAN A RECEIPT">
          <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Bought supplies at a supermarket or cash-and-carry instead of through a distributor? Photograph the receipt — no order or SKU needed.</div>
          <label style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:10,border:`2px dashed ${T.pur}`,background:T.purBg,color:T.pur,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>
            <Camera size={20}/>{scanning?"Scanning...":"Tap to photograph receipt"}
            <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanReceipt(e.target.files[0]);}} style={{display:"none"}}/>
          </label>
          {scanError&&<div style={{marginTop:8,padding:"8px 12px",background:T.redBg,color:T.red,borderRadius:8,fontSize:12}}>{scanError}</div>}
          {scanning&&<div style={{marginTop:8,textAlign:"center",color:T.mt,fontSize:12}}>Reading receipt... this takes a few seconds</div>}
        </Card>
      </>}

      {receiving&&!recvComplete&&<>
        <button onClick={()=>{setReceiving(null);setRecvLines([]);}} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:13,marginBottom:10,fontFamily:"inherit"}}><ArrowLeft size={16}/>Back</button>
        <Card t="CHECK DELIVERY PRICES">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,color:T.mt}}>Enter the actual invoice price for each item. Or scan to auto-fill.</div>
            <label style={{display:"flex",alignItems:"center",gap:4,padding:"6px 12px",borderRadius:6,background:T.bluBg,color:T.blu,cursor:"pointer",fontSize:11,fontWeight:600}}>
              <Camera size={14}/>Scan
              <input type="file" accept="image/*" capture="environment" onChange={e=>{if(e.target.files[0])scanInvoice(e.target.files[0]);}} style={{display:"none"}}/>
            </label>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,fontSize:10,color:T.mt,fontWeight:600}}>
            <span style={{flex:2}}>ITEM</span>
            <span style={{width:60,textAlign:"center"}}>QTY</span>
            <span style={{width:80,textAlign:"center"}}>EXPECTED</span>
            <span style={{width:80,textAlign:"center"}}>ACTUAL</span>
            <span style={{width:60,textAlign:"center"}}>CHANGE</span>
          </div>
          <div style={{maxHeight:400,overflowY:"auto"}}>
          {recvLines.map((line,idx)=>{
            const actual=parseFloat(line.actualPrice);
            const hasActual=!isNaN(actual)&&actual>0;
            const pctChange=hasActual&&line.expectedPrice>0?Math.round(((actual-line.expectedPrice)/line.expectedPrice)*100):0;
            const changed=hasActual&&pctChange!==0;
            return <div key={idx} style={{display:"flex",gap:6,alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.brd}33`}}>
              <div style={{flex:2,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>{line.name}{line.matched===false&&<span style={{fontSize:9,padding:"1px 5px",background:T.ambBg,color:T.amb,borderRadius:4,fontWeight:600}}>NEW</span>}{line.matched&&<span style={{fontSize:9,padding:"1px 5px",background:T.grnBg,color:T.grn,borderRadius:4,fontWeight:600}}>MATCHED</span>}</div>
                <div style={{fontSize:10,color:T.dm}}>/{line.unit}{line.sku?` · #${line.sku}`:""}{line.storage?` · ${line.storage}`:""}</div>
              </div>
              <input type="number" value={line.receivedQty} onChange={e=>updateRecvLine(idx,{receivedQty:e.target.value})} placeholder="qty" style={{...inp,width:60,marginBottom:0,textAlign:"center",fontSize:12}}/>
              <div style={{width:80,textAlign:"center",fontSize:13,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{fmtEur(line.expectedPrice)}</div>
              <input type="number" step="0.01" value={line.actualPrice} onChange={e=>updateRecvLine(idx,{actualPrice:e.target.value})} placeholder="€" style={{...inp,width:80,marginBottom:0,textAlign:"center",fontSize:13,fontFamily:"'DM Mono',monospace",border:changed?`2px solid ${pctChange>0?T.red:T.grn}`:`1px solid ${T.brd}`}}/>
              <div style={{width:60,textAlign:"center",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace",color:!hasActual?T.dm:pctChange>0?T.red:pctChange<0?T.grn:T.dm}}>{!hasActual?"—":pctChange===0?"✓":`${pctChange>0?"+":""}${pctChange}%`}</div>
            </div>;
          })}
          </div>
        </Card>

        {/* Summary of changes before confirming */}
        {recvLines.some(l=>{const a=parseFloat(l.actualPrice);return !isNaN(a)&&a>0&&a!==l.expectedPrice;})&&<Card t="PRICE CHANGES DETECTED">
          {recvLines.filter(l=>{const a=parseFloat(l.actualPrice);return !isNaN(a)&&a>0&&a!==l.expectedPrice;}).map((l,i)=>{
            const actual=parseFloat(l.actualPrice);
            const pct=l.expectedPrice>0?Math.round(((actual-l.expectedPrice)/l.expectedPrice)*100):0;
            // Find affected recipes
            const ing=ings.find(x=>x.id===l.ingId);
            const affected=ing?recipes.filter(r=>r.comps.some(c=>c.ingId===ing.id)).map(r=>r.name):[];
            return <div key={i} style={{padding:"10px 0",borderBottom:`1px solid ${T.brd}33`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:14,fontWeight:600}}>{l.name}</span>
                <span style={{fontSize:14,fontWeight:700,color:pct>0?T.red:T.grn,fontFamily:"'DM Mono',monospace"}}>{fmtEur(l.expectedPrice)} → {fmtEur(actual)} ({pct>0?"+":""}{pct}%)</span>
              </div>
              {affected.length>0&&<div style={{fontSize:10,color:T.dm,marginTop:4}}>Affects margins on: {affected.slice(0,4).join(", ")}{affected.length>4?` +${affected.length-4} more`:""}</div>}
            </div>;
          })}
        </Card>}

        <div style={{position:"sticky",bottom:0,paddingTop:8,paddingBottom:4,background:T.bg,zIndex:5}}>
          <PrimaryBtn color={T.grn} icon={<Check size={18}/>} label="Confirm delivery & update prices" onClick={confirmReceiving}/>
        </div>
      </>}

      {recvComplete&&<>
        <div style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{width:64,height:64,borderRadius:32,background:T.grnBg,border:`2px solid ${T.grn}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}><Check size={32} color={T.grn}/></div>
          <div style={{fontSize:20,fontWeight:700,color:T.grn,marginBottom:10}}>Delivery received</div>
          <div style={{fontSize:13,color:T.mt}}>Ingredient costs and stock levels have been updated. Recipe margins recalculated.</div>
          <button onClick={()=>{setReceiving(null);setRecvLines([]);setRecvComplete(false);}} style={{marginTop:20,padding:"12px 24px",borderRadius:10,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>Done</button>
        </div>
      </>}
    </>}

    {tab==="submitted"&&<>
      <div id="orders-list">
        {orders.length===0?<Empty msg="No orders submitted yet"/>:orders.map(o=><div key={o.id} style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
            <div>
              <div style={{fontSize:14,fontWeight:600}}>{o.supplierName}</div>
              <div style={{fontSize:10,color:T.dm}}>{fmtDate(o.createdAt)} · {o.items.length} items · {o.acctNo}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <StatusBadge s={o.status}/>
              <div style={{fontSize:16,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace",marginTop:4}}>{fmtEur(o.total)}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginTop:8}}>
            <button onClick={()=>emailOrder(o)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Mail size={14}/>Email</button>
            {o.status!=="received"?<button onClick={()=>startReceiving(o)} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><Package size={14}/>Receive delivery</button>:
            <span style={{flex:1,padding:10,borderRadius:8,background:T.grnBg,color:T.grn,fontSize:11,fontWeight:600,textAlign:"center"}}>✓ Received {o.receivedAt?fmtDate(o.receivedAt):""}</span>}
          </div>
          {/* Received order back-check: show confirmed prices */}
          {o.status==="received"&&o.receivedLines&&<details style={{marginTop:8}}>
            <summary style={{fontSize:11,color:T.mt,cursor:"pointer",padding:"6px 0"}}>Review received items ({o.receivedLines.length})</summary>
            <div style={{marginTop:6}}>
              {o.receivedLines.map((rl,ri)=>{
                const pctChange=rl.expectedPrice>0?Math.round(((rl.actualPrice-rl.expectedPrice)/rl.expectedPrice)*100):0;
                return <div key={ri} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:11}}>
                  <span>{rl.name} × {rl.qty} {rl.unit}</span>
                  <span style={{fontFamily:"'DM Mono',monospace"}}>{fmtEur(rl.expectedPrice)} → <span style={{color:pctChange>0?T.red:pctChange<0?T.grn:T.dm,fontWeight:600}}>{fmtEur(rl.actualPrice)}{pctChange!==0?` (${pctChange>0?"+":""}${pctChange}%)`:""}</span></span>
                </div>;
              })}
            </div>
          </details>}
        </div>)}
      </div>
      <ExportBtn label="Print all orders" onClick={()=>printSection("orders-list","Purchase Orders")}/>
    </>}

    {tab==="suppliers"&&<>
      <button onClick={()=>setAddingSup(!addingSup)} style={{width:"100%",padding:14,borderRadius:10,border:`2px dashed ${T.amb}`,background:addingSup?T.ambBg:"none",color:T.amb,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10,minHeight:48}}>
        <Plus size={18}/>{addingSup?"Cancel":"Add new supplier"}
      </button>
      {addingSup&&<Card t="NEW SUPPLIER">
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>COMPANY NAME</div>
        <input value={newSup.name} onChange={e=>setNewSup({...newSup,name:e.target.value})} placeholder="e.g. Bioland Markt" style={inp}/>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>CONTACT PERSON</div>
            <input value={newSup.contact} onChange={e=>setNewSup({...newSup,contact:e.target.value})} placeholder="Name" style={inp}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ACCOUNT NUMBER</div>
            <input value={newSup.acctNo} onChange={e=>setNewSup({...newSup,acctNo:e.target.value})} placeholder="e.g. BIO-001" style={inp}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>EMAIL</div>
            <input type="email" value={newSup.email} onChange={e=>setNewSup({...newSup,email:e.target.value})} placeholder="orders@example.com" style={inp}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PHONE</div>
            <input type="tel" value={newSup.phone} onChange={e=>setNewSup({...newSup,phone:e.target.value})} placeholder="+49..." style={inp}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>LEAD TIME (DAYS)</div>
            <input type="number" value={newSup.lead} onChange={e=>setNewSup({...newSup,lead:parseInt(e.target.value)||1})} style={inp}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PAYMENT TERMS</div>
            <select value={newSup.terms} onChange={e=>setNewSup({...newSup,terms:e.target.value})} style={inp}>
              <option value="Cash">Cash</option><option value="Net 7">Net 7</option><option value="Net 14">Net 14</option><option value="Net 30">Net 30</option><option value="Net 60">Net 60</option>
            </select>
          </div>
        </div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ZIP / POSTAL CODE (for regional matching)</div>
        <input value={newSup.zip} onChange={e=>setNewSup({...newSup,zip:e.target.value})} placeholder="e.g. 12045" style={inp}/>
        <PrimaryBtn color={T.grn} icon={<Plus size={18}/>} label="Add supplier" onClick={addSupplier}/>
      </Card>}
      <SuppliersInline suppliers={suppliers} ings={ings}/>
    </>}
  </div>;
}

/* ═══ BUILD ORDER PANEL — manual + auto-populated order builder
   Supports:
     - Starting from a supplier-auto-build (existing items pre-loaded)
     - Starting from scratch ("Neue manuelle Bestellung")
     - Adding any ingredient via picker (search by name/SKU)
     - Editable per-line qty, removable
     - Editable account number (defaults from supplier, override per order)
     - Send by email (mailto:) with a formatted checklist
     ═══ */
function BuildOrderPanel({order,setOrder,suppliers,ings,submitOrder,startBlank,building,discardBuild}){
  const [picking,setPicking]=useState(false);
  const [pickSearch,setPickSearch]=useState("");

  const supplier=suppliers.find(s=>s.id===order.supplierId);
  // Show all ingredients in picker but pin supplier's products to the top
  const supplierProducts=supplier?supplier.products:[];
  const filtered=ings.filter(i=>{
    if(!pickSearch)return true;
    const q=pickSearch.toLowerCase();
    return i.name.toLowerCase().includes(q)||(i.sku||"").toLowerCase().includes(q);
  }).sort((a,b)=>{
    const aIn=supplierProducts.includes(a.id)?-1:0;
    const bIn=supplierProducts.includes(b.id)?-1:0;
    return aIn-bIn;
  });

  const addLine=(ing)=>{
    if(order.items.find(it=>it.ingId===ing.id))return; // dedupe
    setOrder({...order,items:[...order.items,{ingId:ing.id,name:ing.name,qty:Math.max(1,ing.parMax-ing.qty),unit:ing.unit,unitCost:ing.cost}]});
    setPickSearch("");
  };

  const updateLine=(idx,patch)=>setOrder({...order,items:order.items.map((x,i)=>i===idx?{...x,...patch}:x)});
  const removeLine=(idx)=>setOrder({...order,items:order.items.filter((_,i)=>i!==idx)});

  const total=order.items.reduce((s,i)=>s+i.qty*i.unitCost,0);
  const itemCount=order.items.length;

  if(!building){
    return <div>
      <Empty msg="Build an order to send to a supplier."/>
      <button onClick={startBlank} style={{width:"100%",marginTop:10,padding:16,borderRadius:10,border:`2px dashed ${T.amb}`,background:T.ambBg,color:T.amb,cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,minHeight:56}}>
        <Plus size={20}/>New manual order
      </button>
      <div style={{textAlign:"center",fontSize:11,color:T.dm,marginTop:8}}>Or use the Shopping tab to auto-build from low-stock items.</div>
    </div>;
  }

  return <>
    <button onClick={discardBuild} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit",marginBottom:10}}>
      <ArrowLeft size={14}/>Verwerfen Discard & start over neu beginnen
    </button>
    <Card t="ORDER DETAILS">
      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>SUPPLIER</div>
      <select value={order.supplierId} onChange={e=>{
        const sup=suppliers.find(s=>s.id===e.target.value);
        setOrder({...order,supplierId:e.target.value,acctNo:sup?.acctNo||""});
      }} style={inp}>
        <option value="">— Select supplier —</option>
        {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
      </select>

      {supplier&&<div style={{padding:10,background:T.sf,borderRadius:8,marginBottom:8,fontSize:11,color:T.mt}}>
        <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}><span>Contact</span><span style={{color:T.tx,fontWeight:500}}>{supplier.contact}</span></div>
        <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}><span>Phone</span><a href={`tel:${supplier.phone.replace(/\s+/g,"")}`} style={{color:T.blu,fontWeight:500,textDecoration:"none"}}>{supplier.phone}</a></div>
        <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}><span>Email</span><span style={{color:T.tx,fontWeight:500,fontSize:10,wordBreak:"break-all",textAlign:"right"}}>{supplier.email}</span></div>
        <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}><span>Lead time</span><span style={{color:T.tx,fontWeight:500}}>{supplier.lead} day(s) · {supplier.terms}</span></div>
      </div>}

      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>ACCOUNT NUMBER {supplier&&order.acctNo!==supplier.acctNo&&<span style={{color:T.amb,fontWeight:500}}>(edited)</span>}</div>
      <input value={order.acctNo||""} onChange={e=>setOrder({...order,acctNo:e.target.value})} placeholder="Purveyor account #" style={inp}/>

      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>EXPECTED DATE</div>
      <input type="date" value={order.expectedDate||""} onChange={e=>setOrder({...order,expectedDate:e.target.value})} style={inp}/>

      <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,letterSpacing:0.5}}>NOTES FOR SUPPLIER</div>
      <textarea value={order.notes||""} onChange={e=>setOrder({...order,notes:e.target.value})} placeholder="Delivery instructions, substitutions allowed, etc." rows={2} style={{...inp,fontFamily:"inherit",resize:"vertical"}}/>
    </Card>

    <Card t={`LINE ITEMS (${itemCount})`}>
      {order.items.length===0?<div style={{padding:"20px 8px",textAlign:"center",color:T.dm,fontSize:12,fontStyle:"italic"}}>No items yet — tap "Add item" below</div>:<>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontSize:10,color:T.mt,fontWeight:600}}>
          <span style={{flex:1}}>ITEM</span>
          <span style={{width:36}}></span>
          <span style={{width:64,textAlign:"center"}}>QTY</span>
          <span style={{width:36}}></span>
          <span style={{minWidth:18}}></span>
          <span style={{minWidth:64,textAlign:"right"}}>SUBTOTAL</span>
          <span style={{width:32}}></span>
        </div>
        {order.items.map((it,idx)=>{
          const ing=ings.find(i=>i.id===it.ingId);
          return <div key={idx} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 0",borderBottom:`1px solid ${T.brd}33`}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ing?.name||"?"}</div>
              <div style={{fontSize:10,color:T.dm}}>{fmtEur(it.unitCost)}/{it.unit}{ing?.sku?` · ${ing.sku}`:""}</div>
            </div>
            <button onClick={()=>updateLine(idx,{qty:Math.max(0,Math.round((it.qty-0.5)*10)/10)})} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontWeight:700,fontFamily:"inherit"}}>−</button>
            <input type="number" step="0.1" value={it.qty} onChange={e=>updateLine(idx,{qty:parseFloat(e.target.value)||0})} style={{...inp,width:64,marginBottom:0,fontSize:14,textAlign:"center",padding:"8px 4px"}}/>
            <button onClick={()=>updateLine(idx,{qty:Math.round((it.qty+0.5)*10)/10})} style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:18,fontWeight:700,fontFamily:"inherit"}}>+</button>
            <span style={{fontSize:9,color:T.dm,minWidth:18}}>{it.unit}</span>
            <span style={{fontSize:13,color:T.amb,fontFamily:"'DM Mono',monospace",minWidth:64,textAlign:"right",fontWeight:600}}>{fmtEur(it.qty*it.unitCost)}</span>
            <button onClick={()=>removeLine(idx)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><X size={14}/></button>
          </div>;
        })}
      </>}

      <button onClick={()=>setPicking(!picking)} style={{width:"100%",marginTop:10,padding:14,borderRadius:10,border:`2px dashed ${T.amb}`,background:picking?T.ambBg:"none",color:T.amb,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,minHeight:48}}>
        <Plus size={18}/>{picking?"Hide picker":"Add item"}
      </button>

      {picking&&<div style={{marginTop:8,padding:8,background:T.sf,borderRadius:10,border:`1px solid ${T.brd}`}}>
        <div style={{position:"relative",marginBottom:6}}>
          <Search size={16} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.dm}}/>
          <input value={pickSearch} onChange={e=>setPickSearch(e.target.value)} placeholder="Search ingredients by name or SKU" autoFocus style={{...inp,paddingLeft:36,marginBottom:0}}/>
        </div>
        <div style={{maxHeight:260,overflowY:"auto",borderRadius:8}}>
          {filtered.slice(0,30).map(i=>{
            const inOrder=order.items.find(it=>it.ingId===i.id);
            const isSupplierItem=supplierProducts.includes(i.id);
            return <button key={i.id} disabled={!!inOrder} onClick={()=>addLine(i)} style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:inOrder?T.sf:T.card,border:"none",borderBottom:`1px solid ${T.brd}33`,cursor:inOrder?"default":"pointer",fontFamily:"inherit",color:"inherit",textAlign:"left",opacity:inOrder?0.5:1}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                  {i.name}
                  {isSupplierItem&&<span style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:T.grnBg,color:T.grn,fontWeight:700}}>SUPPLIER</span>}
                </div>
                <div style={{fontSize:10,color:T.dm}}>{fmtEur(i.cost)}/{i.unit} · stock {i.qty}{i.unit} · par {i.parMin}–{i.parMax}{i.sku?` · ${i.sku}`:""}</div>
              </div>
              {inOrder?<Check size={16} color={T.grn}/>:<Plus size={16} color={T.amb}/>}
            </button>;
          })}
          {filtered.length===0&&<div style={{padding:20,textAlign:"center",fontSize:12,color:T.dm}}>No matches</div>}
        </div>
      </div>}

      <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:700,marginTop:12,paddingTop:10,borderTop:`1px solid ${T.brd}`}}>
        <span>Total</span><span style={{color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(total)}</span>
      </div>
    </Card>

    <PrimaryBtn color={T.grn} icon={<Send size={20}/>} label={`SUBMIT ORDER · ${fmtEur(total)}`} onClick={submitOrder} style={{marginTop:10}}/>
  </>;
}

/* ═══ SUPPLIERS — inline inside Orders, with competitive pricing ═══ */
function SuppliersInline({suppliers,ings}){
  const [expanded,setExpanded]=useState(null);
  return <div id="suppliers-list">
    {suppliers.map(s=>{
      const supIngs=ings.filter(i=>s.products.includes(i.id));
      const recentTotal=supIngs.reduce((sum,i)=>sum+i.cost*i.qty,0);
      return <div key={s.id} style={{background:T.card,borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,marginBottom:10}}>
        <button onClick={()=>setExpanded(expanded===s.id?null:s.id)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
          <div>
            <div style={{fontSize:14,fontWeight:600}}>{s.name}</div>
            <div style={{fontSize:10,color:T.dm}}>{s.contact} · {s.products.length} products · {s.terms}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:11,color:T.grn,fontWeight:600}}>{s.rating}★</div>
              <div style={{fontSize:9,color:T.dm}}>{s.onTime}% on time</div>
            </div>
            {expanded===s.id?<ChevronUp size={18} color={T.dm}/>:<ChevronDown size={18} color={T.dm}/>}
          </div>
        </button>
        {expanded===s.id&&<div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.brd}`}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div style={{padding:8,background:T.sf,borderRadius:8}}>
              <div style={{fontSize:9,color:T.mt}}>EMAIL</div>
              <div style={{fontSize:11,color:T.tx,wordBreak:"break-all"}}>{s.email}</div>
            </div>
            <div style={{padding:8,background:T.sf,borderRadius:8}}>
              <div style={{fontSize:9,color:T.mt}}>PHONE</div>
              <div style={{fontSize:11,color:T.tx}}>{s.phone}</div>
            </div>
            <div style={{padding:8,background:T.sf,borderRadius:8}}>
              <div style={{fontSize:9,color:T.mt}}>ACCOUNT</div>
              <div style={{fontSize:11,color:T.amb,fontWeight:600}}>{s.acctNo}</div>
            </div>
            <div style={{padding:8,background:T.sf,borderRadius:8}}>
              <div style={{fontSize:9,color:T.mt}}>LEAD TIME</div>
              <div style={{fontSize:11,color:T.tx}}>{s.lead} day(s)</div>
            </div>
          </div>
          <div style={{fontSize:10,color:T.mt,marginBottom:6,fontWeight:600}}>PRICE TRENDS · LAST 3 ENTRIES</div>
          {supIngs.slice(0,5).map(i=>{
            const hist=i.priceHist||[];
            const latest=hist[hist.length-1];
            const prev=hist[hist.length-2];
            const trend=prev?latest.p-prev.p:0;
            return <div key={i.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:12}}>
              <span>{i.name}</span>
              <span style={{display:"flex",alignItems:"center",gap:6,fontFamily:"'DM Mono',monospace"}}>
                {trend>0&&<TrendingUp size={12} color={T.red}/>}
                {trend<0&&<TrendingDown size={12} color={T.grn}/>}
                <span style={{color:trend>0?T.red:trend<0?T.grn:T.tx,fontWeight:600}}>{fmtEur(i.cost)}</span>
              </span>
            </div>;
          })}
        </div>}
      </div>;
    })}
    <ExportBtn label="Print supplier list" onClick={()=>printSection("suppliers-list","Suppliers")}/>
  </div>;
}

function SuppliersP({suppliers,ings}){return <SuppliersInline suppliers={suppliers} ings={ings}/>;}

/* ═══ REPORTS — EOD, BOD, weekly, monthly, annual ═══ */
function ReportsP({sales,wasteLogs,ings,recipes,dailyRevenue,haccpLogs,zones,orders,loc,clock}){
  const [tab,setTab]=useState("eod");

  const totalCost=sales.reduce((s,x)=>s+x.cost,0);
  const grossMargin=dailyRevenue-totalCost;
  const foodCostPct=dailyRevenue>0?(totalCost/dailyRevenue*100):0;
  const wasteCost=wasteLogs.reduce((s,l)=>s+(l.avoidable!==false?l.cost:0),0);
  const wasteWeight=wasteLogs.reduce((s,l)=>s+(l.weight||0),0);
  const avoidableWaste=wasteLogs.filter(l=>l.avoidable!==false);
  const unavoidableWaste=wasteLogs.filter(l=>l.avoidable===false);
  const invValue=ings.reduce((s,i)=>s+i.qty*i.cost,0);
  const topSellers=Object.entries(sales.reduce((m,s)=>{m[s.recipe]=(m[s.recipe]||0)+s.qty;return m;},{})).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const lowStock=ings.filter(i=>i.qty<=i.parMin*1.3);

  const reports={
    eod:{
      title:`End-of-Day Report — ${new Date().toLocaleDateString("de-DE")}`,
      content:()=><EodReport sales={sales} dailyRevenue={dailyRevenue} totalCost={totalCost} grossMargin={grossMargin} foodCostPct={foodCostPct} wasteCost={wasteCost} wasteWeight={wasteWeight} wasteLogs={wasteLogs} avoidableWaste={avoidableWaste} unavoidableWaste={unavoidableWaste} invValue={invValue} topSellers={topSellers} lowStock={lowStock} ings={ings} zones={zones} haccpLogs={haccpLogs} loc={loc}/>
    },
    bod:{
      title:`Beginning-of-Day Brief — ${new Date().toLocaleDateString("de-DE")}`,
      content:()=><BodReport ings={ings} lowStock={lowStock} zones={zones} orders={orders} recipes={recipes} loc={loc}/>
    },
    weekly:{
      title:`Weekly Summary — Week of ${new Date().toLocaleDateString("de-DE")}`,
      content:()=><WeeklyReport sales={sales} wasteLogs={wasteLogs} ings={ings} dailyRevenue={dailyRevenue} loc={loc}/>
    },
    monthly:{
      title:`Monthly Performance — ${new Date().toLocaleDateString("de-DE",{month:"long",year:"numeric"})}`,
      content:()=><MonthlyReport sales={sales} wasteLogs={wasteLogs} ings={ings} orders={orders} dailyRevenue={dailyRevenue} loc={loc}/>
    },
    annual:{
      title:`Annual Overview — ${new Date().getFullYear()}`,
      content:()=><AnnualReport sales={sales} ings={ings} recipes={recipes} loc={loc}/>
    },
  };

  return <div>
    <Tabs tabs={[
      {id:"eod",label:"End of day"},
      {id:"bod",label:"Beginning of day"},
      {id:"weekly",label:"Weekly"},
      {id:"monthly",label:"Monthly"},
      {id:"annual",label:"Annual"},
    ]} active={tab} set={setTab}/>

    <div id={`report-${tab}`}>
      {reports[tab].content()}
    </div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:12}}>
      <button onClick={()=>printSection(`report-${tab}`,reports[tab].title)} style={{padding:14,borderRadius:10,border:`1px solid ${T.brd}`,background:T.card,color:T.tx,cursor:"pointer",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"inherit"}}><Printer size={18} color={T.amb}/>Print / PDF</button>
      <button onClick={()=>{
        const node=document.getElementById(`report-${tab}`);
        if(!node)return;
        const html=`<html><head><title>${reports[tab].title}</title></head><body>${node.innerHTML}</body></html>`;
        const blob=new Blob([html],{type:"text/html"});
        const url=URL.createObjectURL(blob);
        const a=document.createElement("a");a.href=url;a.download=`${reports[tab].title.replace(/[^a-z0-9]/gi,"_")}.html`;a.click();URL.revokeObjectURL(url);
      }} style={{padding:14,borderRadius:10,border:`1px solid ${T.brd}`,background:T.card,color:T.tx,cursor:"pointer",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"inherit"}}><FileDown size={18} color={T.blu}/>Download</button>
    </div>
  </div>;
}

function EodReport({sales,dailyRevenue,totalCost,grossMargin,foodCostPct,wasteCost,wasteWeight,wasteLogs,avoidableWaste,unavoidableWaste,invValue,topSellers,lowStock,ings,zones,haccpLogs,loc}){
  return <div>
    <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",fontSize:22,color:T.tx,marginBottom:4}}>End of Day</h2>
    <div style={{fontSize:11,color:T.dm,marginBottom:14}}>{loc.name} · {new Date().toLocaleDateString("de-DE",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
      <Kpi l="REVENUE" v={fmtEur(dailyRevenue)} c={T.grn} s={`${sales.length} tickets`}/>
      <Kpi l="FOOD COST" v={`${foodCostPct.toFixed(1)}%`} c={foodCostPct<32?T.grn:T.amb}/>
      <Kpi l="GROSS MARGIN" v={fmtEur(grossMargin)} c={T.grn}/>
      <Kpi l="WASTE" v={fmtEur(wasteCost)} c={wasteCost<20?T.grn:T.red}/>
    </div>

    <Card t="TOP SELLERS">
      {topSellers.length===0?<div style={{fontSize:12,color:T.dm}}>No sales recorded</div>:topSellers.map(([name,qty],i)=><div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:13}}>
        <span>{i+1}. {name}</span>
        <span style={{fontWeight:600,fontFamily:"'DM Mono',monospace",color:T.amb}}>{qty}×</span>
      </div>)}
    </Card>

    <Card t="WASTE BREAKDOWN">
      {wasteLogs.length===0?<div style={{fontSize:12,color:T.dm}}>No waste logged today — excellent</div>:<>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <div style={{flex:1,padding:"8px 10px",background:T.redBg,borderRadius:8}}>
            <div style={{fontSize:9,color:T.red,fontWeight:600}}>AVOIDABLE</div>
            <div style={{fontSize:16,fontWeight:700,color:T.red,fontFamily:"'DM Mono',monospace"}}>{fmtEur(avoidableWaste.reduce((s,l)=>s+l.cost,0))}</div>
            <div style={{fontSize:10,color:T.dm}}>{avoidableWaste.reduce((s,l)=>s+(l.weight||0),0)>=1000?((avoidableWaste.reduce((s,l)=>s+(l.weight||0),0))/1000).toFixed(1)+"kg":avoidableWaste.reduce((s,l)=>s+(l.weight||0),0)+"g"} · {avoidableWaste.length} entries</div>
          </div>
          <div style={{flex:1,padding:"8px 10px",background:T.sf,borderRadius:8}}>
            <div style={{fontSize:9,color:T.dm,fontWeight:600}}>UNAVOIDABLE</div>
            <div style={{fontSize:16,fontWeight:700,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{unavoidableWaste.reduce((s,l)=>s+(l.weight||0),0)>=1000?((unavoidableWaste.reduce((s,l)=>s+(l.weight||0),0))/1000).toFixed(1)+"kg":unavoidableWaste.reduce((s,l)=>s+(l.weight||0),0)+"g"}</div>
            <div style={{fontSize:10,color:T.dm}}>{unavoidableWaste.length} entries · no cost</div>
          </div>
        </div>
        <div style={{padding:"6px 10px",background:T.card,borderRadius:8,marginBottom:10,display:"flex",justifyContent:"space-between"}}>
          <span style={{fontSize:11,fontWeight:600,color:T.mt}}>TOTAL (all waste)</span>
          <span style={{fontSize:13,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace"}}>{wasteWeight>=1000?(wasteWeight/1000).toFixed(1)+"kg":wasteWeight+"g"} · {fmtEur(wasteCost)}</span>
        </div>
        {wasteLogs.slice(0,8).map(l=><div key={l.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
          <span>{l.ingredient} · {l.category}</span>
          <span style={{fontFamily:"'DM Mono',monospace"}}><span style={{color:T.amb}}>{l.weight>=1000?(l.weight/1000).toFixed(1)+"kg":l.weight+"g"}</span>{l.avoidable!==false?<span style={{color:T.red,marginLeft:8}}>{fmtEur(l.cost)}</span>:""}</span>
        </div>)}
      </>}
    </Card>

    <Card t="STOCK STATUS">
      <div style={{fontSize:13,marginBottom:6}}>Inventory value: <span style={{color:T.amb,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{fmtEur(invValue)}</span></div>
      <div style={{fontSize:13}}>Items below par: <span style={{color:lowStock.length>0?T.red:T.grn,fontWeight:700}}>{lowStock.length}</span></div>
      {lowStock.slice(0,5).map(i=><div key={i.id} style={{fontSize:11,color:T.amb,marginTop:4}}>• {i.name} ({i.qty}{i.unit}, par {i.parMin})</div>)}
    </Card>

    <Card t="SAFETY · ZONE TEMPERATURES">
      {zones.map(z=><div key={z.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
        <span>{z.name}</span>
        <span style={{color:z.ok?T.grn:T.red,fontFamily:"'DM Mono',monospace",fontWeight:600}}>{z.current}°C ({z.tempMin}–{z.tempMax})</span>
      </div>)}
    </Card>
  </div>;
}

function BodReport({ings,lowStock,zones,orders,recipes,loc}){
  const pending=orders.filter(o=>o.status!=="received");
  return <div>
    <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",fontSize:22,color:T.tx,marginBottom:4}}>Beginning of Day</h2>
    <div style={{fontSize:11,color:T.dm,marginBottom:14}}>{loc.name} · {new Date().toLocaleDateString("de-DE",{weekday:"long",day:"numeric",month:"long"})}</div>

    <Card t="OPENING CHECKLIST">
      {[
        "Check walk-in temperature, log",
        "Check freezer temperature, log",
        "Walk pass: surfaces, equipment, cleanliness",
        "Confirm dry storage humidity normal",
        "Review today's specials with FOH",
        "Pull mise en place from yesterday's prep",
        "Verify deliveries arriving today",
      ].map((step,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:13}}>
        <div style={{width:18,height:18,border:`2px solid ${T.dm}`,borderRadius:4,flexShrink:0}}/>
        <span>{step}</span>
      </div>)}
    </Card>

    <Card t="EXPECTED DELIVERIES">
      {pending.length===0?<div style={{fontSize:12,color:T.dm}}>No pending orders</div>:pending.map(o=><div key={o.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13}}>
        <div>
          <div>{o.supplierName}</div>
          <div style={{fontSize:10,color:T.dm}}>{o.items.length} items · {o.expectedDate||"asap"}</div>
        </div>
        <span style={{color:T.amb,fontFamily:"'DM Mono',monospace"}}>{fmtEur(o.total)}</span>
      </div>)}
    </Card>

    <Card t="LOW STOCK · ORDER PRIORITY">
      {lowStock.length===0?<div style={{fontSize:12,color:T.grn}}>All items at par or above — good shape</div>:lowStock.slice(0,10).map(i=><div key={i.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
        <span>{i.name}</span>
        <span style={{color:T.red,fontFamily:"'DM Mono',monospace",fontWeight:600}}>{i.qty} / par {i.parMin}</span>
      </div>)}
    </Card>

    <Card t="STORAGE ZONES — MORNING CHECK">
      {zones.map(z=><div key={z.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12}}>
        <span>{z.name}</span>
        <span style={{color:z.ok?T.grn:T.red,fontFamily:"'DM Mono',monospace",fontWeight:600}}>{z.current}°C</span>
      </div>)}
    </Card>
  </div>;
}

function WeeklyReport({sales,wasteLogs,ings,dailyRevenue,loc}){
  // For demo: assume rolling 7-day window — using current data as today's slice
  const wkDays=DAYS.map(d=>({d,rev:Math.round(180+Math.random()*220),waste:Math.round(8+Math.random()*22)}));
  const totalRev=wkDays.reduce((s,x)=>s+x.rev,0)+dailyRevenue;
  const totalWaste=wkDays.reduce((s,x)=>s+x.waste,0)+wasteLogs.reduce((s,l)=>s+l.cost,0);
  return <div>
    <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",fontSize:22,color:T.tx,marginBottom:4}}>Week Summary</h2>
    <div style={{fontSize:11,color:T.dm,marginBottom:14}}>{loc.name} · 7 days ending {new Date().toLocaleDateString("de-DE")}</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
      <Kpi l="WK REVENUE" v={fmtEur(totalRev)} c={T.grn}/>
      <Kpi l="WK WASTE" v={fmtEur(totalWaste)} c={T.red}/>
      <Kpi l="AVG DAILY" v={fmtEur(totalRev/7)} c={T.amb}/>
      <Kpi l="WASTE %" v={`${((totalWaste/totalRev)*100).toFixed(1)}%`} c={T.amb}/>
    </div>

    <Card t="DAILY BREAKDOWN">
      {wkDays.map((w,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:13}}>
        <span>{w.d}</span>
        <div style={{display:"flex",gap:14}}>
          <span style={{fontFamily:"'DM Mono',monospace",color:T.grn,minWidth:60,textAlign:"right"}}>{fmtEur(w.rev)}</span>
          <span style={{fontFamily:"'DM Mono',monospace",color:T.red,minWidth:50,textAlign:"right"}}>{fmtEur(w.waste)}</span>
        </div>
      </div>)}
    </Card>

    <Card t="KEY METRICS">
      <div style={{fontSize:12,padding:"4px 0"}}>Food cost ratio: <strong style={{color:T.amb}}>~31.2%</strong></div>
      <div style={{fontSize:12,padding:"4px 0"}}>Avg ticket: <strong style={{color:T.grn}}>{fmtEur(totalRev/Math.max(1,sales.length+30))}</strong></div>
      <div style={{fontSize:12,padding:"4px 0"}}>Waste as % of revenue: <strong style={{color:T.red}}>{((totalWaste/totalRev)*100).toFixed(1)}%</strong></div>
    </Card>
  </div>;
}

function MonthlyReport({sales,wasteLogs,ings,orders,dailyRevenue,loc}){
  const monthRev=dailyRevenue*22+Math.round(Math.random()*3000); // simulated month
  const monthWaste=wasteLogs.reduce((s,l)=>s+l.cost,0)*22+Math.round(Math.random()*300);
  const orderTotal=orders.reduce((s,o)=>s+o.total,0);
  return <div>
    <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",fontSize:22,color:T.tx,marginBottom:4}}>Monthly Performance</h2>
    <div style={{fontSize:11,color:T.dm,marginBottom:14}}>{loc.name} · {new Date().toLocaleDateString("de-DE",{month:"long",year:"numeric"})}</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
      <Kpi l="MONTH REVENUE" v={fmtEur(monthRev)} c={T.grn}/>
      <Kpi l="MONTH WASTE" v={fmtEur(monthWaste)} c={T.red}/>
      <Kpi l="PURCHASING" v={fmtEur(orderTotal)} c={T.amb}/>
      <Kpi l="EST. PROFIT" v={fmtEur(monthRev*0.18)} c={T.grn}/>
    </div>

    <Card t="MENU MIX — TOP CATEGORIES">
      {["Greens","Meat & Fish","Eggs","Bakery","Drinks"].map(c=><div key={c} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:13}}>
        <span>{c}</span>
        <span style={{fontFamily:"'DM Mono',monospace",color:T.amb}}>{Math.round(10+Math.random()*30)}%</span>
      </div>)}
    </Card>

    <Card t="MONTHLY HIGHLIGHTS">
      <ul style={{paddingLeft:20,fontSize:13,lineHeight:1.7}}>
        <li>Most-sold dish: house-cured salmon</li>
        <li>Biggest waste driver: spoilage in produce</li>
        <li>Best margin item: beetroot latte ({(86).toFixed(0)}%)</li>
        <li>Suggested action: tighten produce ordering frequency</li>
      </ul>
    </Card>
  </div>;
}

function AnnualReport({sales,ings,recipes,loc}){
  const yearRev=Math.round(280000+Math.random()*50000);
  return <div>
    <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",fontSize:22,color:T.tx,marginBottom:4}}>Annual Overview</h2>
    <div style={{fontSize:11,color:T.dm,marginBottom:14}}>{loc.name} · {new Date().getFullYear()}</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
      <Kpi l="YEAR REVENUE" v={fmtEur(yearRev)} c={T.grn}/>
      <Kpi l="EST. PROFIT" v={fmtEur(yearRev*0.16)} c={T.grn}/>
      <Kpi l="MENU ITEMS" v={recipes.length} c={T.pur}/>
      <Kpi l="SKUs MANAGED" v={ings.length} c={T.blu}/>
    </div>

    <Card t="YEAR-OVER-YEAR">
      <div style={{fontSize:12,padding:"4px 0"}}>Revenue: <strong style={{color:T.grn}}>+{(12.4).toFixed(1)}%</strong> vs last year</div>
      <div style={{fontSize:12,padding:"4px 0"}}>Food cost: <strong style={{color:T.grn}}>−{(2.1).toFixed(1)}%</strong> (improved)</div>
      <div style={{fontSize:12,padding:"4px 0"}}>Waste reduction: <strong style={{color:T.grn}}>−{(18.0).toFixed(0)}%</strong></div>
      <div style={{fontSize:12,padding:"4px 0"}}>New menu items: <strong>11</strong></div>
    </Card>

    <Card t="STRATEGIC NOTES">
      <ul style={{paddingLeft:20,fontSize:12,lineHeight:1.7,color:T.mt}}>
        <li>Strong performance in seasonal specials</li>
        <li>Consistent compliance scoring above 90%</li>
        <li>Bluetooth scale rollout reduced portion variance by ~6%</li>
        <li>Recommended: expand specials program to weekday dinner</li>
      </ul>
    </Card>
  </div>;
}

/* ═══ SAFETY — voice temp dictation, expanded ═══ */
function SafetyP({zones,setZones,alerts,setAlerts,addAlert,haccpLogs,setHaccpLogs,voiceListening}){
  const [tab,setTab]=useState("temps");
  const [selZone,setSelZone]=useState("");
  const [tempInput,setTempInput]=useState("");
  const [voiceTempArmed,setVoiceTempArmed]=useState(false);
  // Corrective action form
  const [caDesc,setCaDesc]=useState("");
  const [caZone,setCaZone]=useState("");
  const [caAction,setCaAction]=useState("");
  // Receiving check
  const [rcSupplier,setRcSupplier]=useState("");
  const [rcTemp,setRcTemp]=useState("");
  const [rcAccept,setRcAccept]=useState(true);
  const [rcNote,setRcNote]=useState("");
  // Cooking temp
  const [cookItem,setCookItem]=useState("");
  const [cookTemp,setCookTemp]=useState("");
  const [cookTarget,setCookTarget]=useState(74);
  // Checklist
  const [chkState,setChkState]=useState({});
  const toggleChk=(i)=>setChkState(p=>({...p,[i]:!p[i]}));
  const chkItems=["Walk-in AM temp check","Walk-in PM temp check","Freezer temp check","Dry storage humidity","Surface sanitisation","Handwashing station stocked","Staff hygiene check","Receiving temps logged","Food labelling & dating","Cross-contamination barriers","Allergen segregation verified","Cleaning schedule signed off","Pest control inspection","Thermometer calibration"];
  const chkDone=chkItems.filter((_,i)=>chkState[i]).length;

  useEffect(()=>{
    const handler=(e)=>{
      const val=String(e.detail.value);
      if(voiceTempArmed){
        if(tab==="temps"&&selZone)setTempInput(val);
        else if(tab==="receiving")setRcTemp(val);
        else if(tab==="cooking")setCookTemp(val);
        else setTempInput(val); // fallback
      }
    };
    window.addEventListener("voiceTemp",handler);
    return ()=>window.removeEventListener("voiceTemp",handler);
  },[voiceTempArmed,selZone,tab]);

  const logTemp=()=>{
    if(!selZone||tempInput==="")return;
    const t=parseFloat(tempInput);const z=zones.find(x=>x.id===selZone);if(!z)return;
    const ok=t>=z.tempMin&&t<=z.tempMax;
    setZones(p=>p.map(x=>x.id===selZone?{...x,current:t,ok}:x));
    setHaccpLogs(p=>[{id:genId(),type:"temperature_check",zone:z.name,value:t,ok,time:new Date().toISOString(),detail:`Zone: ${z.name}, Reading: ${t}°C, Range: ${z.tempMin}–${z.tempMax}°C`},...p]);
    if(!ok)addAlert({id:genId(),zone:z.name,msg:`${z.name} out of range (${t}°C, target ${z.tempMin}–${z.tempMax})`,severity:"warning",status:"active",time:new Date().toLocaleTimeString("de-DE")});
    setTempInput("");
  };

  const logCorrectiveAction=()=>{
    if(!caDesc)return;
    setHaccpLogs(p=>[{id:genId(),type:"corrective_action",zone:caZone||"General",value:0,ok:false,time:new Date().toISOString(),detail:`Issue: ${caDesc}. Action taken: ${caAction||"Pending"}`},...p]);
    setCaDesc("");setCaZone("");setCaAction("");
  };

  const logReceivingCheck=()=>{
    if(!rcSupplier)return;
    const t=parseFloat(rcTemp)||0;
    setHaccpLogs(p=>[{id:genId(),type:"receiving_check",zone:"Receiving",value:t,ok:rcAccept,time:new Date().toISOString(),detail:`Supplier: ${rcSupplier}, Temp: ${t}°C, ${rcAccept?"ACCEPTED":"REJECTED"}${rcNote?`. Note: ${rcNote}`:""}`},...p]);
    if(!rcAccept)addAlert({id:genId(),zone:"Receiving",msg:`Delivery rejected from ${rcSupplier} (${t}°C)`,severity:"warning",status:"active",time:new Date().toLocaleTimeString("de-DE")});
    setRcSupplier("");setRcTemp("");setRcAccept(true);setRcNote("");
  };

  const logCookingTemp=()=>{
    if(!cookItem||!cookTemp)return;
    const t=parseFloat(cookTemp);
    const ok=t>=cookTarget;
    setHaccpLogs(p=>[{id:genId(),type:"cooking_temp",zone:"Kitchen",value:t,ok,time:new Date().toISOString(),detail:`Item: ${cookItem}, Internal temp: ${t}°C, Target: ${cookTarget}°C — ${ok?"PASS":"FAIL"}`},...p]);
    if(!ok)addAlert({id:genId(),zone:"Kitchen",msg:`${cookItem} undercooked (${t}°C, need ${cookTarget}°C)`,severity:"critical",status:"active",time:new Date().toLocaleTimeString("de-DE")});
    setCookItem("");setCookTemp("");
  };

  return <div>
    <Tabs tabs={[
      {id:"temps",label:"Temps"},
      {id:"receiving",label:"Receiving"},
      {id:"cooking",label:"Cooking"},
      {id:"corrective",label:"Corrective"},
      {id:"alerts",label:`Alerts (${alerts.filter(a=>a.status==="active").length})`},
      {id:"log",label:`Log (${haccpLogs.length})`},
      {id:"checklist",label:`Check (${chkDone}/${chkItems.length})`},
    ]} active={tab} set={setTab}/>

    {tab==="temps"&&<>
      <Card t="ZONE TEMPERATURES">
        {zones.map(z=><div key={z.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.brd}33`}}>
          <div><div style={{fontSize:13,fontWeight:600}}>{z.name}</div><div style={{fontSize:10,color:T.dm}}>{z.type} · {z.tempMin}–{z.tempMax}°C</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:18,fontWeight:700,color:z.ok?T.grn:T.red,fontFamily:"'DM Mono',monospace"}}>{z.current}°C</div><div style={{fontSize:10,color:z.ok?T.grn:T.red,fontWeight:600}}>{z.ok?"OK":"OUT OF RANGE"}</div></div>
        </div>)}
      </Card>
      <Card t="LOG TEMPERATURE">
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ZONE</div>
        <select value={selZone} onChange={e=>setSelZone(e.target.value)} style={inp}><option value="">— Select —</option>{zones.map(z=><option key={z.id} value={z.id}>{z.name}</option>)}</select>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>TEMPERATURE (°C)</div>
        <div style={{display:"flex",gap:8}}>
          <input type="number" step="0.1" value={tempInput} onChange={e=>setTempInput(e.target.value)} placeholder="0.0" style={{...inp,flex:1,marginBottom:0,fontSize:18,fontFamily:"'DM Mono',monospace",textAlign:"center"}}/>
          <button onClick={()=>setVoiceTempArmed(!voiceTempArmed)} style={{padding:"0 14px",borderRadius:10,border:`1px solid ${voiceTempArmed?T.amb:T.brd}`,background:voiceTempArmed?T.ambBg:T.sf,color:voiceTempArmed?T.amb:T.mt,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit",fontSize:12,fontWeight:600}}>
            {voiceTempArmed?<Mic size={18}/>:<MicOff size={18}/>}{voiceTempArmed?"On":"Voice"}
          </button>
        </div>
        <PrimaryBtn color={T.blu} icon={<Thermometer size={18}/>} label="Log reading" onClick={logTemp} style={{marginTop:8}}/>
      </Card>
    </>}

    {tab==="receiving"&&<>
      <Card t="RECEIVING TEMPERATURE CHECK">
        <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Log incoming delivery temperatures. Reject deliveries outside safe range.</div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>SUPPLIER NAME</div>
        <input value={rcSupplier} onChange={e=>setRcSupplier(e.target.value)} placeholder="e.g. Bioland Markt" style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>DELIVERY TEMPERATURE (°C)</div>
        <div style={{display:"flex",gap:8}}>
          <input type="number" step="0.1" value={rcTemp} onChange={e=>setRcTemp(e.target.value)} placeholder="0.0" style={{...inp,flex:1,marginBottom:0,fontSize:18,fontFamily:"'DM Mono',monospace",textAlign:"center"}}/>
          <button onClick={()=>setVoiceTempArmed(!voiceTempArmed)} style={{padding:"0 14px",borderRadius:10,border:`1px solid ${voiceTempArmed?T.amb:T.brd}`,background:voiceTempArmed?T.ambBg:T.sf,color:voiceTempArmed?T.amb:T.mt,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit",fontSize:12,fontWeight:600}}>
            {voiceTempArmed?<Mic size={18}/>:<MicOff size={18}/>}{voiceTempArmed?"On":"Voice"}
          </button>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <button onClick={()=>setRcAccept(true)} style={{flex:1,padding:14,borderRadius:10,border:`1px solid ${rcAccept?T.grn:T.brd}`,background:rcAccept?T.grnBg:"none",color:rcAccept?T.grn:T.dm,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:700}}>Accept</button>
          <button onClick={()=>setRcAccept(false)} style={{flex:1,padding:14,borderRadius:10,border:`1px solid ${!rcAccept?T.red:T.brd}`,background:!rcAccept?T.redBg:"none",color:!rcAccept?T.red:T.dm,cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:700}}>Reject</button>
        </div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>NOTES (OPTIONAL)</div>
        <input value={rcNote} onChange={e=>setRcNote(e.target.value)} placeholder="Condition, packaging, odor..." style={inp}/>
        <PrimaryBtn color={rcAccept?T.grn:T.red} icon={<Check size={18}/>} label={rcAccept?"Log accepted delivery":"Log rejected delivery"} onClick={logReceivingCheck}/>
      </Card>
    </>}

    {tab==="cooking"&&<>
      <Card t="COOKING TEMPERATURE LOG">
        <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Verify internal cooking temperatures reach safe minimums. Poultry 74°C, beef 63°C, fish 63°C, reheated 74°C.</div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>FOOD ITEM</div>
        <input value={cookItem} onChange={e=>setCookItem(e.target.value)} placeholder="e.g. Grilled chicken breast" style={inp}/>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>INTERNAL TEMP (°C)</div>
            <input type="number" step="0.1" value={cookTemp} onChange={e=>setCookTemp(e.target.value)} placeholder="0.0" style={{...inp,fontSize:18,fontFamily:"'DM Mono',monospace",textAlign:"center"}}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>TARGET MIN (°C)</div>
            <input type="number" value={cookTarget} onChange={e=>setCookTarget(parseInt(e.target.value)||74)} style={inp}/>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",paddingBottom:8}}>
            <button onClick={()=>setVoiceTempArmed(!voiceTempArmed)} style={{padding:"10px 12px",borderRadius:10,border:`1px solid ${voiceTempArmed?T.amb:T.brd}`,background:voiceTempArmed?T.ambBg:T.sf,color:voiceTempArmed?T.amb:T.mt,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"inherit",fontSize:11,fontWeight:600}}>
              {voiceTempArmed?<Mic size={16}/>:<MicOff size={16}/>}
            </button>
          </div>
        </div>
        <PrimaryBtn color={T.blu} icon={<Thermometer size={18}/>} label="Log cooking temp" onClick={logCookingTemp}/>
      </Card>
    </>}

    {tab==="corrective"&&<>
      <Card t="CORRECTIVE ACTION">
        <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Document any food safety issue and the corrective action taken.</div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ISSUE DESCRIPTION</div>
        <textarea value={caDesc} onChange={e=>setCaDesc(e.target.value)} placeholder="Describe the issue..." rows={3} style={{...inp,fontFamily:"inherit"}}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>AFFECTED ZONE</div>
        <select value={caZone} onChange={e=>setCaZone(e.target.value)} style={inp}><option value="">— Select —</option>{zones.map(z=><option key={z.id} value={z.id}>{z.name}</option>)}</select>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ACTION TAKEN</div>
        <textarea value={caAction} onChange={e=>setCaAction(e.target.value)} placeholder="What was done to resolve..." rows={2} style={{...inp,fontFamily:"inherit"}}/>
        <PrimaryBtn color={T.red} icon={<AlertTriangle size={18}/>} label="Log corrective action" onClick={logCorrectiveAction}/>
      </Card>
    </>}

    {tab==="alerts"&&<div id="alerts-content">
      {alerts.length===0?<Empty msg="No alerts"/>:alerts.slice(0,20).map(a=><div key={a.id} style={{background:T.card,border:`1px solid ${a.status==="active"?T.red+"55":T.brd}`,borderRadius:10,padding:12,marginBottom:10}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={18} color={a.status==="active"?T.red:T.dm}/><div><div style={{fontSize:13,fontWeight:600}}>{a.zone}</div><div style={{fontSize:11,color:T.mt}}>{a.msg}</div></div></div>
          <StatusBadge s={a.status}/>
        </div>
        {a.status==="active"&&<div style={{display:"flex",gap:6,marginTop:6}}>
          <button onClick={()=>setAlerts(p=>p.map(x=>x.id===a.id?{...x,status:"acknowledged"}:x))} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.amb}55`,background:T.ambBg,color:T.amb,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Acknowledge</button>
          <button onClick={()=>setAlerts(p=>p.map(x=>x.id===a.id?{...x,status:"resolved"}:x))} style={{flex:1,padding:10,borderRadius:8,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Resolve</button>
        </div>}
      </div>)}
      <ExportBtn label="Print alerts" onClick={()=>printSection("alerts-content","Active Alerts")}/>
    </div>}

    {tab==="log"&&<div id="haccp-log">
      {haccpLogs.length===0?<Empty msg="Noch keine HACCP-entries"/>:haccpLogs.slice(0,40).map(l=><div key={l.id} style={{background:T.card,border:`1px solid ${l.ok?T.brd:T.red+"55"}`,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:T.tx}}>{l.type.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</div>
            <div style={{fontSize:10,color:T.dm}}>{new Date(l.time).toLocaleString("de-DE")}</div>
          </div>
          <div style={{fontSize:9,padding:"3px 8px",borderRadius:8,background:l.ok?T.grnBg:T.redBg,color:l.ok?T.grn:T.red,fontWeight:700}}>{l.ok?"PASS":"FAIL"}</div>
        </div>
        {l.detail&&<div style={{fontSize:11,color:T.mt,marginTop:6,padding:8,background:T.sf,borderRadius:6}}>{l.detail}</div>}
      </div>)}
      <ExportBtn label="Print HACCP log" onClick={()=>printSection("haccp-log","HACCP Log")}/>
    </div>}

    {tab==="checklist"&&<div id="haccp-checklist">
      <Banner c={chkDone===chkItems.length?T.grn:T.amb} bg={chkDone===chkItems.length?T.grnBg:T.ambBg} l="DAILY HACCP" v={`${chkDone} / ${chkItems.length}`}/>
      <Card t="DAILY CHECKLIST">
        {chkItems.map((step,i)=><button key={i} onClick={()=>toggleChk(i)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${T.brd}33`,fontSize:14,background:"none",border:"none",borderBottom:`1px solid ${T.brd}33`,color:"inherit",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
          <div style={{width:26,height:26,borderRadius:8,border:`2px solid ${chkState[i]?T.grn:T.brd}`,background:chkState[i]?T.grn:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {chkState[i]&&<Check size={16} color="#fff"/>}
          </div>
          <span style={{textDecoration:chkState[i]?"line-through":"none",color:chkState[i]?T.dm:T.tx}}>{step}</span>
        </button>)}
      </Card>
      <ExportBtn label="Print checklist" onClick={()=>printSection("haccp-checklist","HACCP Daily Checklist")}/>
    </div>}
  </div>;
}

/* ═══ SETTINGS PAGE ═══ */
// Real webhook status for Lightspeed — the one POS of the three checked with a
// genuine itemized order webhook. Fetched live rather than a fake saved-key state,
// since "connected" here depends on server-side OAuth credentials, not a paste box.
function LightspeedStatus(){
  const [status,setStatus]=useState(null); // null=loading, or {webhookConfigured,webhookUrl,note}
  useEffect(()=>{
    fetch(`${SCAN_API}/pos/lightspeed/status`).then(r=>r.json()).then(d=>setStatus(d)).catch(()=>setStatus({error:true}));
  },[]);
  return <div style={{padding:"10px 0"}}>
    <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Lightspeed Restaurant</div>
    {!status&&<div style={{fontSize:11,color:T.dm}}>Checking…</div>}
    {status&&status.error&&<div style={{fontSize:11,color:T.red}}>Couldn't reach the backend to check status.</div>}
    {status&&!status.error&&<>
      <div style={{fontSize:11,color:T.dm,marginBottom:4}}>Has a real itemized order webhook — but needs an OAuth2 app registered with Lightspeed (not a pasted key). {status.note}</div>
      <div style={{fontSize:10,color:status.webhookConfigured?T.grn:T.amb,fontWeight:600}}>{status.webhookConfigured?"✓ Webhook credentials configured on the backend":"⚠ Backend webhook credentials not yet configured"}</div>
    </>}
  </div>;
}

function SettingsP({settings,setSettings,locations,loc,showToast,addNewLocation,removeLocationFull,switchLocation,setPage,isOwner,syncNow}){
  const [tab,setTab]=useState("account");
  const [newLocName,setNewLocName]=useState("");
  const [newLocAddr,setNewLocAddr]=useState("");
  const [newLocMode,setNewLocMode]=useState("frisch"); // "frisch", location id, or "tmpl_xxx"
  const [pwOld,setPwOld]=useState("");
  const [pwNew,setPwNew]=useState("");
  const [pwConfirm,setPwConfirm]=useState("");
  const [showPw,setShowPw]=useState(false);
  const acct=settings.account||{};

  const updateAcct=(patch)=>setSettings(p=>({...p,account:{...p.account,...patch}}));
  const changeLang=(id)=>setSettings(p=>({...p,lang:id}));

  const handleAddLocation=()=>{
    if(!newLocName.trim()){showToast("Enter a location name");return;}
    const isCopy=newLocMode!=="frisch"&&!newLocMode.startsWith("tmpl_");
    const isTemplate=newLocMode.startsWith("tmpl_");
    const copyFrom=isCopy?newLocMode:null;
    const templateId=isTemplate?newLocMode.replace("tmpl_",""):null;
    const newLoc=addNewLocation(newLocName,newLocAddr,copyFrom,templateId);
    setNewLocName("");setNewLocAddr("");setNewLocMode("frisch");
    const tmplName=templateId?SIM_TEMPLATES.find(x=>x.id===templateId)?.name:"";
    showToast(templateId?`"${newLoc.name}" created with ${tmplName} menu`:copyFrom?`"${newLoc.name}" created (copied)`:`"${newLoc.name}" created — blank slate`);
  };

  const handleRemove=(id)=>{
    if(locations.length<=1){showToast("Need at least one location");return;}
    const name=locations.find(l=>l.id===id)?.name||"Location";
    removeLocationFull(id);
    showToast(`"${name}" removed`);
  };

  const changePassword=()=>{
    if(!pwNew||pwNew.length<6){showToast("Password must be at least 6 characters");return;}
    if(pwNew!==pwConfirm){showToast("Passwords don't match");return;}
    updateAcct({password:pwNew});
    setPwOld("");setPwNew("");setPwConfirm("");
    showToast("Password updated");
  };

  return <div>
    <h2 style={{fontSize:22,fontWeight:700,marginBottom:16,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Settings</h2>

    <Tabs tabs={[
      {id:"account",label:"Account"},
      {id:"language",label:"Language"},
      {id:"alerts",label:"Alerts"},
      {id:"locations",label:`Locations (${locations.length})`},
      {id:"password",label:"Password"},
      {id:"integrations",label:"Integrations"},
    ]} active={tab} set={setTab}/>

    {tab==="account"&&<>
      <Card t="MANAGE">
        <button onClick={()=>setPage&&setPage("devices")} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 4px",background:"none",border:"none",borderBottom:`1px solid ${T.brd}33`,color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,textAlign:"left"}}>
          <Bluetooth size={20} color={T.blu}/><span style={{flex:1}}>Devices & hardware</span><ChevronRight size={18} color={T.dm}/>
        </button>
        {isOwner&&<button onClick={()=>setPage&&setPage("accounts")} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 4px",background:"none",border:"none",color:T.tx,cursor:"pointer",fontFamily:"inherit",fontSize:14,textAlign:"left"}}>
          <Users size={20} color={T.gold}/><span style={{flex:1}}>Team & accounts</span><ChevronRight size={18} color={T.dm}/>
        </button>}
      </Card>
      <Card t="ACCOUNT DETAILS">
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>NAME</div>
        <input value={acct.name||""} onChange={e=>updateAcct({name:e.target.value})} placeholder="Your name" style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>EMAIL</div>
        <input type="email" value={acct.email||""} onChange={e=>updateAcct({email:e.target.value})} placeholder="email@example.com" style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>PHONE</div>
        <input type="tel" value={acct.phone||""} onChange={e=>updateAcct({phone:e.target.value})} placeholder="+49..." style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>RESTAURANT NAME</div>
        <input value={acct.restaurantName||""} onChange={e=>updateAcct({restaurantName:e.target.value})} placeholder="Your restaurant" style={inp}/>
      </Card>
    </>}

    {tab==="language"&&<>
      <Card t="REPORT & DATA LANGUAGE">
        <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Sets the language for reports, order emails, and data labels.</div>
        {LANGS.map(l=><button key={l.id} onClick={()=>changeLang(l.id)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:settings.lang===l.id?T.ambBg:T.sf,border:`1px solid ${settings.lang===l.id?T.amb:T.brd}`,borderRadius:10,marginBottom:6,cursor:"pointer",fontFamily:"inherit",color:settings.lang===l.id?T.amb:T.tx,fontSize:14,fontWeight:settings.lang===l.id?700:500}}>
          <Globe size={18} color={settings.lang===l.id?T.amb:T.dm}/>
          <span style={{flex:1}}>{l.label}</span>
          <span style={{fontSize:12,fontWeight:700,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{l.flag}</span>
          {settings.lang===l.id&&<Check size={18} color={T.amb}/>}
        </button>)}
      </Card>
    </>}

    {tab==="alerts"&&<>
      <Card t="EMAIL DIGEST">
        <div style={{fontSize:12,color:T.mt,marginBottom:12}}>Choose what arrives in your morning email and when. Instant critical alerts always send.</div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>DIGEST RECIPIENT(S)</div>
        <input type="text" value={acct.digestEmail||acct.email||""} onChange={e=>updateAcct({digestEmail:e.target.value})} placeholder="chef@restaurant.de, owner@restaurant.de" style={inp}/>
        <div style={{fontSize:11,color:T.dm,marginTop:2,marginBottom:14}}>Defaults to your account email if left blank.</div>

        <div style={{fontSize:11,color:T.mt,marginBottom:8,fontWeight:600}}>DELIVERY TIME</div>
        <input type="time" value={acct.digestTime||"06:00"} onChange={e=>updateAcct({digestTime:e.target.value})} style={{...inp,marginBottom:14}}/>

        <div style={{fontSize:11,color:T.mt,marginBottom:8,fontWeight:600}}>INCLUDE IN MY DIGEST</div>
        {[
          {key:"prep",label:"Today's prep",desc:"Batches to make + prep list",c:T.pur},
          {key:"expiring",label:"Expiring today",desc:"Items past or near shelf life",c:T.red},
          {key:"lowstock",label:"Below par",desc:"Items needing reorder",c:T.amb},
          {key:"deliveries",label:"Expected deliveries",desc:"Orders due in",c:T.blu},
        ].map(s=>{
          const sel=!(acct.digestSections&&acct.digestSections[s.key]===false); // default on
          return <button key={s.key} onClick={()=>updateAcct({digestSections:{...(acct.digestSections||{}),[s.key]:!sel}})} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:sel?T.sf:"transparent",border:`1px solid ${sel?s.c+"55":T.brd}`,borderRadius:10,marginBottom:8,cursor:"pointer",fontFamily:"inherit",textAlign:"left",opacity:sel?1:0.55}}>
            <span style={{width:10,height:10,borderRadius:5,background:s.c,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:T.tx}}>{s.label}</div>
              <div style={{fontSize:11,color:T.dm}}>{s.desc}</div>
            </div>
            <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${sel?s.c:T.brd}`,background:sel?s.c:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{sel&&<Check size={14} color="#fff"/>}</div>
          </button>;
        })}
        <button onClick={()=>{showToast("Digest preferences saved");}} style={{width:"100%",padding:14,borderRadius:10,border:"none",background:T.signBtn,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:6}}>Save preferences</button>
        <div style={{fontSize:11,color:T.dm,marginTop:10,fontStyle:"italic",textAlign:"center"}}>Your selections sync automatically to the morning digest service.</div>
      </Card>
      <Card t="VOICE CONTROL">
        <div style={{fontSize:12,color:T.mt,marginBottom:12}}>Hands-free commands for the line. Tap the mic in the header, then speak.</div>
        <button onClick={()=>setSettings(p=>({...p,voiceConfirm:p.voiceConfirm===false?true:false}))} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",background:T.sf,border:`1px solid ${T.brd}`,borderRadius:10,cursor:"pointer",fontFamily:"inherit",marginBottom:12}}>
          <div style={{textAlign:"left"}}>
            <div style={{fontSize:13,fontWeight:600,color:T.tx}}>Confirm before saving</div>
            <div style={{fontSize:11,color:T.dm}}>Read each command back before it commits</div>
          </div>
          <div style={{width:44,height:26,borderRadius:13,background:settings.voiceConfirm===false?T.brd:T.grn,position:"relative",transition:"background 0.2s",flexShrink:0}}>
            <div style={{position:"absolute",top:3,left:settings.voiceConfirm===false?3:21,width:20,height:20,borderRadius:10,background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
          </div>
        </button>
        <button onClick={()=>speak("Voice is working. Two items expiring today: salmon, and chicken trim.")} style={{width:"100%",padding:12,borderRadius:10,border:`1px solid ${T.blu}`,background:T.bluBg,color:T.blu,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <Mic size={16}/>Test voice — tap to hear it speak
        </button>
        <button onClick={()=>setSettings(p=>({...p,voiceFullList:p.voiceFullList===false?true:false}))} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",background:T.sf,border:`1px solid ${T.brd}`,borderRadius:10,cursor:"pointer",fontFamily:"inherit",marginBottom:12}}>
          <div style={{textAlign:"left"}}>
            <div style={{fontSize:13,fontWeight:600,color:T.tx}}>Read full lists aloud</div>
            <div style={{fontSize:11,color:T.dm}}>{settings.voiceFullList===false?"Summarize long lists ('and 4 more')":"Speak every item, no matter how long"}</div>
          </div>
          <div style={{width:44,height:26,borderRadius:13,background:settings.voiceFullList===false?T.brd:T.grn,position:"relative",transition:"background 0.2s",flexShrink:0}}>
            <div style={{position:"absolute",top:3,left:settings.voiceFullList===false?3:21,width:20,height:20,borderRadius:10,background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
          </div>
        </button>
        <div style={{fontSize:12,color:T.tx,lineHeight:1.7,background:T.sf,borderRadius:10,padding:"12px 14px",border:`1px solid ${T.brd}66`}}>
          <div style={{fontWeight:700,fontSize:10,letterSpacing:0.5,color:T.mt,marginBottom:6,textTransform:"uppercase"}}>Try saying</div>
          "Log two kilos chicken to waste"<br/>
          "What's expiring today?"<br/>
          "How much waste so far?"<br/>
          "What's below par?"<br/>
          "Go to inventory"
        </div>
      </Card>
    </>}

    {tab==="locations"&&<>
      <Card t="YOUR LOCATIONS">
        {locations.map(l=>{
          const isActive=loc.id===l.id;
          return <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${T.brd}33`}}>
            <MapPin size={18} color={isActive?T.amb:T.dm}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:isActive?700:500,color:isActive?T.amb:T.tx}}>{l.name}</div>
              {l.addr&&<div style={{fontSize:11,color:T.dm}}>{l.addr}</div>}
            </div>
            {isActive?<span style={{fontSize:9,padding:"3px 8px",borderRadius:8,background:T.ambBg,color:T.amb,fontWeight:700,flexShrink:0}}>ACTIVE</span>:
              <button onClick={()=>switchLocation(l)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.amb}`,background:T.ambBg,color:T.amb,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Switch</button>}
            <button onClick={()=>handleRemove(l.id)} disabled={locations.length<=1} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:locations.length<=1?T.dm:T.red,cursor:locations.length<=1?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:locations.length<=1?0.3:1}}><X size={14}/></button>
          </div>;
        })}
      </Card>

      <Card t="ADD NEW LOCATION">
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>LOCATION NAME</div>
        <input value={newLocName} onChange={e=>setNewLocName(e.target.value)} placeholder="e.g. Main kitchen, Downtown" style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>ADDRESS (OPTIONAL)</div>
        <input value={newLocAddr} onChange={e=>setNewLocAddr(e.target.value)} placeholder="e.g. 123 Main St" style={inp}/>

        <div style={{fontSize:11,color:T.mt,marginBottom:6,fontWeight:600,letterSpacing:0.5}}>START WITH</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
          <button onClick={()=>setNewLocMode("frisch")} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:newLocMode==="fresh"?T.grnBg:T.sf,border:`1px solid ${newLocMode==="fresh"?T.grn:T.brd}`,borderRadius:10,cursor:"pointer",fontFamily:"inherit",color:newLocMode==="fresh"?T.grn:T.tx,fontSize:13,fontWeight:newLocMode==="fresh"?700:500,textAlign:"left"}}>
            <Plus size={18} color={newLocMode==="fresh"?T.grn:T.dm}/>
            <div>
              <div>Fresh start</div>
              <div style={{fontSize:11,color:T.dm,fontWeight:400}}>Empty inventory, menu, and suppliers — build from scratch</div>
            </div>
            {newLocMode==="fresh"&&<Check size={18} color={T.grn} style={{marginLeft:"auto",flexShrink:0}}/>}
          </button>
          {locations.map(l=><button key={l.id} onClick={()=>setNewLocMode(l.id)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:newLocMode===l.id?T.bluBg:T.sf,border:`1px solid ${newLocMode===l.id?T.blu:T.brd}`,borderRadius:10,cursor:"pointer",fontFamily:"inherit",color:newLocMode===l.id?T.blu:T.tx,fontSize:13,fontWeight:newLocMode===l.id?700:500,textAlign:"left"}}>
            <Copy size={18} color={newLocMode===l.id?T.blu:T.dm}/>
            <div>
              <div>Copy from {l.name}</div>
              <div style={{fontSize:11,color:T.dm,fontWeight:400}}>Clones inventory, menu, suppliers, and zones</div>
            </div>
            {newLocMode===l.id&&<Check size={18} color={T.blu} style={{marginLeft:"auto",flexShrink:0}}/>}
          </button>)}

          <div style={{fontSize:10,color:T.mt,fontWeight:600,letterSpacing:0.5,marginTop:6,marginBottom:2}}>OR START FROM TEMPLATE</div>
          {SIM_TEMPLATES.filter(t=>t.id!=="custom").map(t=>{const k="tmpl_"+t.id;const sel=newLocMode===k;return <button key={t.id} onClick={()=>setNewLocMode(k)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:sel?T.ambBg:T.sf,border:`1px solid ${sel?T.amb:T.brd}`,borderRadius:10,cursor:"pointer",fontFamily:"inherit",color:sel?T.amb:T.tx,fontSize:13,fontWeight:sel?700:500,textAlign:"left"}}>
            <t.Icon size={18} color={sel?T.amb:T.dm}/>
            <div>
              <div>{t.name}</div>
              <div style={{fontSize:11,color:T.dm,fontWeight:400}}>{t.menuItems.length} menu items · avg ticket {fmtEur(t.avgTicket)}</div>
            </div>
            {sel&&<Check size={18} color={T.amb} style={{marginLeft:"auto",flexShrink:0}}/>}
          </button>;})}
        </div>

        <PrimaryBtn color={T.grn} icon={<Plus size={18}/>} label={newLocMode==="fresh"?"Create blank location":newLocMode.startsWith("tmpl_")?"Create with template menu":"Create + copy data"} onClick={handleAddLocation}/>
      </Card>
    </>}

    {tab==="password"&&<>
      <Card t="CHANGE PASSWORD">
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>CURRENT PASSWORD</div>
        <div style={{position:"relative"}}>
          <input type={showPw?"text":"password"} value={pwOld} onChange={e=>setPwOld(e.target.value)} placeholder="Current password" style={inp}/>
        </div>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>NEW PASSWORD</div>
        <input type={showPw?"text":"password"} value={pwNew} onChange={e=>setPwNew(e.target.value)} placeholder="Neues Passwort (min. 6 Zeichen)" style={inp}/>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>CONFIRM NEW PASSWORD</div>
        <input type={showPw?"text":"password"} value={pwConfirm} onChange={e=>setPwConfirm(e.target.value)} placeholder="Confirm new password" style={inp}/>
        <button onClick={()=>setShowPw(!showPw)} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 0",background:"none",border:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit",marginBottom:10}}>
          {showPw?<EyeOff size={14}/>:<Eye size={14}/>}{showPw?"Hide":"Show"} passwords
        </button>
        <PrimaryBtn color={T.blu} icon={<Lock size={18}/>} label="Change password" onClick={changePassword}/>
      </Card>
    </>}

    {tab==="integrations"&&<>
      <Card t="POS INTEGRATIONS">
        <div style={{padding:"10px 0",borderBottom:`1px solid ${T.brd}`}}>
          <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Orderbird / SumUp</div>
          <div style={{fontSize:11,color:T.dm}}>Neither exposes real-time, itemized sales via API — no key to connect. Both export a daily itemized report though: use <strong>Import sales report</strong> on the POS → Today tab to scan it in.</div>
        </div>
        <LightspeedStatus/>
      </Card>
      <Card t="DELIVERY PLATFORMS">
        <div style={{fontSize:12,color:T.mt}}>Uber Eats, Lieferando, and Wolt aren't verified yet — none of these have been checked the way POS providers above were. Treat any key entered here as inactive until confirmed.</div>
      </Card>
    </>}
  </div>;
}
function SimKitchenP({recipes,ings,specials,setSales,setWasteLogs,setDailyRevenue,setIngs,setRecipes,setRecipeBook,showToast,simResult,setSimResult,setPage}){
  const [template,setTemplate]=useState(null);
  const [covers,setCovers]=useState(80);
  const [running,setRunning]=useState(false);
  const [horizon,setHorizon]=useState("monthly");

  // Custom menu items for the custom template
  const [customItems,setCustomItems]=useState([{name:"",price:0,cost:0}]);
  const addCustomItem=()=>setCustomItems(p=>[...p,{name:"",price:0,cost:0}]);
  const updateCustomItem=(idx,patch)=>setCustomItems(p=>p.map((x,i)=>i===idx?{...x,...patch}:x));
  const removeCustomItem=(idx)=>setCustomItems(p=>p.filter((_,i)=>i!==idx));

  const runSim=()=>{
    if(!template)return;
    const t=SIM_TEMPLATES.find(x=>x.id===template);
    if(!t)return;
    const menu=t.id==="custom"?customItems.filter(i=>i.name&&i.price>0):t.menuItems;
    if(menu.length===0){showToast("Add at least one menu item");return;}

    setRunning(true);

    // Build projections
    const avgItemPrice=menu.reduce((s,i)=>s+i.price,0)/menu.length||10;
    const avgItemCost=menu.reduce((s,i)=>s+i.cost,0)/menu.length||3;
    const itemsPerGuest=t.itemsPerGuest||2;
    const dailyItems=Math.round(covers*itemsPerGuest);
    const dailyRev=dailyItems*avgItemPrice;
    const dailyCost=dailyItems*avgItemCost;
    const foodCostPct=dailyRev>0?(dailyCost/dailyRev*100):0;

    // Waste: starts high, decreases week by week (Gastriot learning curve)
    const wasteStart=t.wasteStartPct/100;
    const wasteEnd=t.wasteEndPct/100;
    const industryWaste=wasteStart; // what they'd have WITHOUT Gastriot
    const weekDecay=(idx)=>wasteEnd+(wasteStart-wasteEnd)*Math.exp(-0.3*idx);

    // Specials lift — active specials add ~15% to item volume on those items
    const activeSpecials=specials.filter(s=>s.active);
    const specialLift=activeSpecials.length>0?1+activeSpecials.length*0.05:1;

    // Build daily/weekly/monthly/annual
    const dailyRevAdj=dailyRev*specialLift;
    const dailyCostAdj=dailyCost*specialLift;

    const buildWeek=(weekIdx)=>{
      const wasteRate=weekDecay(weekIdx);
      const wasteCost=dailyCostAdj*wasteRate*7;
      const rev=dailyRevAdj*7;
      const cost=dailyCostAdj*7;
      const industryWasteCost=dailyCostAdj*industryWaste*7;
      return {rev,cost,wasteCost,industryWasteCost,wasteRate,savingsVsIndustry:industryWasteCost-wasteCost,foodCostPct:cost/rev*100};
    };

    const weeks=Array.from({length:52},(_,i)=>buildWeek(i));
    const month4=weeks.slice(0,4);
    const monthRev=month4.reduce((s,w)=>s+w.rev,0);
    const monthCost=month4.reduce((s,w)=>s+w.cost,0);
    const monthWaste=month4.reduce((s,w)=>s+w.wasteCost,0);
    const monthSavings=month4.reduce((s,w)=>s+w.savingsVsIndustry,0);

    const annualRev=weeks.reduce((s,w)=>s+w.rev,0);
    const annualCost=weeks.reduce((s,w)=>s+w.cost,0);
    const annualWaste=weeks.reduce((s,w)=>s+w.wasteCost,0);
    const annualSavings=weeks.reduce((s,w)=>s+w.savingsVsIndustry,0);

    const result={
      templateId:template,
      templateName:SIM_TEMPLATES.find(x=>x.id===template)?.name||template,
      covers,
      menu,
      daily:{rev:dailyRevAdj,cost:dailyCostAdj,waste:dailyCostAdj*weekDecay(0),foodCostPct,margin:dailyRevAdj-dailyCostAdj,savings:(dailyCostAdj*industryWaste)-(dailyCostAdj*wasteEnd)},
      weekly:buildWeek(4),
      monthly:{rev:monthRev,cost:monthCost,waste:monthWaste,savings:monthSavings,foodCostPct:monthCost/monthRev*100},
      annual:{rev:annualRev,cost:annualCost,waste:annualWaste,savings:annualSavings,foodCostPct:annualCost/annualRev*100},
      wasteWeeks:weeks.slice(0,12).map((w,i)=>({week:i+1,rate:(w.wasteRate*100).toFixed(1),wasteCost:w.wasteCost,savings:w.savingsVsIndustry})),
      specialLift,
    };
    setSimResult(result);
    setTimeout(()=>setRunning(false),600);
  };

  const seedLiveData=()=>{
    if(!simResult||!simResult.menu)return;
    const menu=simResult.menu;
    const tmpl=SIM_TEMPLATES.find(x=>x.id===simResult.templateId);
    const itemsPerGuest=tmpl?.itemsPerGuest||2;

    // 1. Seed INGREDIENTS — create stock items from menu
    const catMap={"burger":"Meat & Fish","italian":"Greens","tapas":"Greens","pizza":"Bakery","brunch":"Bakery","fine":"Meat & Fish","custom":"Greens"};
    const baseCat=catMap[simResult.templateId]||"Greens";
    const seedIngs=[];
    const ingNames=new Set();
    menu.forEach((item,i)=>{
      // Primary ingredient per dish
      const name=item.name+" base";
      if(!ingNames.has(name)){
        ingNames.add(name);
        seedIngs.push({id:`si-${i}`,name,cat:i%2===0?"frisch":"trocken",cost:item.cost,unit:"kg",unitG:1000,parMin:2,parMax:10,qty:Math.round(5+Math.random()*15),storage:i%3===0?"cooler":"trocken",zone:"z3",shelf:7,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:todayISO(),p:item.cost}]});
      }
    });
    // Add common pantry items
    ["Olive oil","Salt","Black pepper","Butter","Garlic","Onions","Fresh herbs","Flour","Eggs","Milk"].forEach((name,i)=>{
      seedIngs.push({id:`sp-${i}`,name,cat:i<3?"trocken":"frisch",cost:Number((1+Math.random()*4).toFixed(2)),unit:i<3?"L":"kg",unitG:1000,parMin:1,parMax:5,qty:Math.round(3+Math.random()*8),storage:i>=7?"cooler":"trocken",zone:"z3",shelf:i<3?180:7,allergens:[],sku:"",barcode:"",supplier:"s1",priceHist:[{d:todayISO(),p:Number((1+Math.random()*4).toFixed(2))}]});
    });
    setIngs(seedIngs);

    // 2. Seed RECIPE BOOK — batch prep items (3-5 batch recipes from the template)
    const batchRecipes=menu.slice(0,Math.min(4,menu.length)).map((item,i)=>{
      const batchSize=Math.round(2000+Math.random()*8000);
      return {id:`rb-sim-${i}`,name:item.name+" prep",batchTotal:batchSize,batchUnit:"g",shelfDays:Math.round(2+Math.random()*4),instructions:`Batch prep for ${item.name}. Full recipe procedure.`,comps:seedIngs.slice(i,i+3).map(ing=>({ingId:ing.id,qty:Math.round(100+Math.random()*500),unit:"g"})),batchRemaining:batchSize,batchMade:new Date().toISOString()};
    });
    setRecipeBook(batchRecipes);

    // 3. Seed MENU (recipes) — all menu items as active, linked to batch recipes where possible
    const seedRecipes=menu.map((item,i)=>{
      const hasBatch=i<batchRecipes.length;
      const portionWeight=hasBatch?Math.round(20+Math.random()*80):0;
      return {id:`rm-sim-${i}`,name:item.name,cat:baseCat,type:"dish",price:item.price,yield:1,status:"active",prep:5,cook:10,instructions:`Plate and serve ${item.name}.`,comps:seedIngs.slice(i%seedIngs.length,(i%seedIngs.length)+2).map(ing=>({ingId:ing.id,qty:Math.round(50+Math.random()*150),unit:"g"})),batchRef:hasBatch?batchRecipes[i].id:"",portionWeight};
    });
    setRecipes(seedRecipes);

    // 4. Seed SALES — realistic today's sales
    const syntheticSales=[];
    const dailyItems=Math.round(simResult.covers*itemsPerGuest);
    for(let i=0;i<Math.min(dailyItems,80);i++){
      const item=menu[Math.floor(Math.random()*menu.length)];
      const recipeMatch=seedRecipes.find(r=>r.name===item.name);
      syntheticSales.push({id:genId(),recipe:item.name,recipeId:recipeMatch?.id||`sim-${i}`,qty:1,price:item.price,total:item.price,cost:item.cost,margin:item.price-item.cost,special:false,specialPct:0,time:new Date(Date.now()-Math.random()*36000000).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}),timestamp:new Date().toISOString()});
    }
    setSales(syntheticSales);
    setDailyRevenue(syntheticSales.reduce((s,x)=>s+x.total,0));

    // 5. Seed WASTE LOGS
    const syntheticWaste=menu.slice(0,5).map(it=>({id:genId(),category:["Overproduction","Spoilage","Trim","Plate waste"][Math.floor(Math.random()*4)],ingredient:it.name,weight:Math.round(50+Math.random()*250),cost:Number((it.cost*(0.2+Math.random()*0.6)).toFixed(2)),bin:"",shift:getShift(),time:new Date(Date.now()-Math.random()*28800000).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}),timestamp:new Date().toISOString()}));
    setWasteLogs(syntheticWaste);

    // 6. Deduct batch remaining from sales (simulate POS deductions)
    setRecipeBook(prev=>prev.map(rb=>{
      const linkedMenu=seedRecipes.filter(r=>r.batchRef===rb.id);
      if(linkedMenu.length===0)return rb;
      const salesForThis=syntheticSales.filter(s=>linkedMenu.some(m=>m.name===s.recipe));
      const totalDeduct=salesForThis.reduce((sum,s)=>{
        const m=linkedMenu.find(x=>x.name===s.recipe);
        return sum+(m?.portionWeight||0)*s.qty;
      },0);
      return {...rb,batchRemaining:Math.max(0,rb.batchRemaining-totalDeduct)};
    }));

    showToast(`Live demo seeded: ${seedRecipes.length} menu items, ${batchRecipes.length} batch recipes, ${syntheticSales.length} sales`);
    setPage("dashboard");
  };

  const sr=simResult;

  if(!template)return <div>
    <h2 style={{fontSize:20,fontWeight:700,marginBottom:6,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Sim Kitchen</h2>
    <div style={{fontSize:13,color:T.mt,marginBottom:16}}>Choose a restaurant type to simulate daily, weekly, monthly, and annual projections. See how Gastriot saves money by reducing waste over time.</div>

    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
      {SIM_TEMPLATES.map(t=>{
        const Icon=t.Icon;
        return <button key={t.id} onClick={()=>setTemplate(t.id)} style={{padding:16,borderRadius:12,border:`1px solid ${T.brd}`,background:T.card,color:T.tx,cursor:"pointer",fontFamily:"inherit",textAlign:"left",display:"flex",flexDirection:"column",gap:8,minHeight:100}}>
          <Icon size={28} color={T.amb} strokeWidth={1.6}/>
          <div style={{fontSize:14,fontWeight:600}}>{t.name}</div>
          {t.avgTicket>0&&<div style={{fontSize:11,color:T.dm}}>Avg ticket {fmtEur(t.avgTicket)} · {t.foodCostPct}% food cost</div>}
        </button>;
      })}
    </div>
  </div>;

  // Template selected — show setup + results
  const tmpl=SIM_TEMPLATES.find(x=>x.id===template);

  return <div>
    <button onClick={()=>{setTemplate(null);setSimResult(null);}} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:13,marginBottom:10,fontFamily:"inherit"}}><ArrowLeft size={16}/>Pick a different template</button>

    <h2 style={{fontSize:20,fontWeight:700,marginBottom:4,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Sim: {tmpl.name}</h2>

    <Card t="DAILY COVERS">
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:10}}>
        <input type="range" min="1" max="250" value={covers} onChange={e=>setCovers(parseInt(e.target.value))} style={{flex:1,accentColor:T.amb,height:6}}/>
        <div style={{fontSize:28,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace",minWidth:56,textAlign:"right"}}>{covers}</div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.dm}}>
        <span>1 guest/day</span><span>250 guests/day</span>
      </div>
    </Card>

    {template==="custom"&&<Card t="YOUR MENU">
      <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,fontSize:10,color:T.mt,fontWeight:600}}>
        <span style={{flex:2}}>ITEM NAME</span>
        <span style={{flex:1,textAlign:"center"}}>SELL PRICE (€)</span>
        <span style={{flex:1,textAlign:"center"}}>FOOD COST (€)</span>
        <span style={{width:32}}></span>
      </div>
      {customItems.map((it,idx)=><div key={idx} style={{display:"flex",gap:6,alignItems:"center",marginBottom:6}}>
        <input value={it.name} onChange={e=>updateCustomItem(idx,{name:e.target.value})} placeholder="e.g. Smash Burger" style={{...inp,flex:2,marginBottom:0}}/>
        <input type="number" step="0.01" value={it.price||""} onChange={e=>updateCustomItem(idx,{price:parseFloat(e.target.value)||0})} placeholder="0.00" style={{...inp,flex:1,marginBottom:0}}/>
        <input type="number" step="0.01" value={it.cost||""} onChange={e=>updateCustomItem(idx,{cost:parseFloat(e.target.value)||0})} placeholder="0.00" style={{...inp,flex:1,marginBottom:0}}/>
        <button onClick={()=>removeCustomItem(idx)} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={14}/></button>
      </div>)}
      <button onClick={addCustomItem} style={{width:"100%",padding:10,borderRadius:8,border:`1px dashed ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:12,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Plus size={14}/>Add item</button>
    </Card>}

    {template!=="custom"&&<Card t={`TEMPLATE MENU (${tmpl.menuItems.length} items)`}>
      {tmpl.menuItems.slice(0,6).map((it,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12,borderBottom:`1px solid ${T.brd}33`}}>
        <span>{it.name}</span>
        <span style={{fontFamily:"'DM Mono',monospace",color:T.amb}}>{fmtEur(it.price)}</span>
      </div>)}
      {tmpl.menuItems.length>6&&<div style={{fontSize:11,color:T.dm,marginTop:4}}>+ {tmpl.menuItems.length-6} more items</div>}
    </Card>}

    <PrimaryBtn color={T.amb} icon={running?<Pause size={20}/>:<Play size={20}/>} label={running?"Running...":"Run simulation"} onClick={runSim} style={{marginBottom:12}}/>

    {sr&&<div id="sim-results">
      <Tabs tabs={[
        {id:"daily",label:"Daily"},
        {id:"weekly",label:"Weekly"},
        {id:"monthly",label:"Monthly"},
        {id:"annual",label:"Annual"},
      ]} active={horizon} set={setHorizon}/>

      {horizon==="daily"&&<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
          <Kpi l="REVENUE" v={fmtEur(sr.daily.rev)} c={T.grn}/>
          <Kpi l="FOOD COST" v={`${sr.daily.foodCostPct.toFixed(1)}%`} c={sr.daily.foodCostPct<32?T.grn:T.amb}/>
          <Kpi l="WASTE" v={fmtEur(sr.daily.waste)} c={T.red}/>
          <Kpi l="DAILY SAVINGS" v={fmtEur(sr.daily.savings)} c={T.grn} s="vs industry avg"/>
        </div>
      </div>}

      {horizon==="weekly"&&<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
          <Kpi l="WEEKLY REV" v={fmtEur(sr.weekly.rev)} c={T.grn}/>
          <Kpi l="WEEKLY WASTE" v={fmtEur(sr.weekly.wasteCost)} c={T.red}/>
          <Kpi l="FOOD COST" v={`${sr.weekly.foodCostPct.toFixed(1)}%`} c={T.amb}/>
          <Kpi l="WEEKLY SAVINGS" v={fmtEur(sr.weekly.savingsVsIndustry)} c={T.grn} s="vs no Gastriot"/>
        </div>
      </div>}

      {horizon==="monthly"&&<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
          <Kpi l="MONTHLY REV" v={fmtEur(sr.monthly.rev)} c={T.grn}/>
          <Kpi l="MONTHLY WASTE" v={fmtEur(sr.monthly.waste)} c={T.red}/>
          <Kpi l="FOOD COST" v={`${sr.monthly.foodCostPct.toFixed(1)}%`} c={T.amb}/>
          <Kpi l="MONTHLY SAVINGS" v={fmtEur(sr.monthly.savings)} c={T.grn} s="projected with Gastriot"/>
        </div>
      </div>}

      {horizon==="annual"&&<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
          <Kpi l="ANNUAL REV" v={fmtEur(sr.annual.rev)} c={T.grn}/>
          <Kpi l="ANNUAL WASTE" v={fmtEur(sr.annual.waste)} c={T.red}/>
          <Kpi l="FOOD COST" v={`${sr.annual.foodCostPct.toFixed(1)}%`} c={T.amb}/>
          <Kpi l="ANNUAL SAVINGS" v={fmtEur(sr.annual.savings)} c={T.grn} s="projected with Gastriot"/>
        </div>
        <Banner c={T.grn} bg={T.grnBg} l="BOTTOM LINE IMPACT" v={fmtEur(sr.annual.savings)+"/year"}/>
      </div>}

      {/* Waste curve chart */}
      <Card t="WASTE LEARNING CURVE (12 weeks)">
        <div style={{fontSize:11,color:T.mt,marginBottom:10}}>Shows how Gastriot reduces waste week by week as par levels tighten and data accumulates.</div>
        <div style={{display:"flex",alignItems:"flex-end",gap:4,height:100,padding:"6px 0"}}>
          {sr.wasteWeeks.map((w,i)=>{
            const maxRate=parseFloat(sr.wasteWeeks[0].rate);
            const h=maxRate>0?(parseFloat(w.rate)/maxRate*85):4;
            return <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{fontSize:9,color:T.red,fontFamily:"'DM Mono',monospace"}}>{w.rate}%</div>
              <div style={{width:"100%",height:`${h}px`,background:`linear-gradient(180deg,${T.red},${T.amb})`,borderRadius:"3px 3px 0 0",minHeight:4}}/>
              <div style={{fontSize:9,color:T.dm}}>W{w.week}</div>
            </div>;
          })}
        </div>
      </Card>

      {sr.specialLift>1&&<Banner c={T.amb} bg={T.ambBg} l="SPECIALS LIFT" v={`+${((sr.specialLift-1)*100).toFixed(0)}% volume`}/>}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
        <ExportBtn label="Print report" onClick={()=>printSection("sim-results","Sim Kitchen Projection")}/>
        <button onClick={seedLiveData} style={{padding:14,borderRadius:10,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:8}}>
          <Play size={16}/>Seed live demo
        </button>
      </div>
      <div style={{fontSize:11,color:T.dm,textAlign:"center",marginTop:6}}>Seed loads sim data into Dashboard, POS, and Waste for a live demo walkthrough.</div>
    </div>}
  </div>;
}

/* ═══ PREP LIST — daily prep checklist built from recipes + inventory ═══ */
function PrepListP({recipes,ings,setIngs,sales,setRecipes,recipeBook,setRecipeBook}){
  const [checked,setChecked]=useState({});
  const [manualItems,setManualItems]=useState([]);
  const [newManual,setNewManual]=useState("");
  // Checking a component off deducts its needed qty from inventory; unchecking
  // restores it — symmetric, so a mis-tap doesn't quietly corrupt stock. Clamped
  // at 0 rather than blocked, matching how every other deduction in the app
  // (waste logging, receiving) already handles running out.
  const toggle=(id)=>{
    const item=componentItems.find(p=>p.id===id);
    const willBeDone=!checked[id];
    setChecked(p=>({...p,[id]:willBeDone}));
    if(item&&setIngs){
      setIngs(prev=>prev.map(x=>x.id===item.ingId?{...x,qty:Math.max(0,Math.round((x.qty+(willBeDone?-item.totalQty:item.totalQty))*1000)/1000)}:x));
    }
  };

  const addManual=()=>{
    if(!newManual.trim())return;
    setManualItems(p=>[...p,{id:genId(),name:newManual.trim(),done:false}]);
    setNewManual("");
  };
  const toggleManual=(id)=>setManualItems(p=>p.map(x=>x.id===id?{...x,done:!x.done}:x));
  const rmManual=(id)=>setManualItems(p=>p.filter(x=>x.id!==id));

  // Build prep items — batches from recipeBook, components from active recipes
  const activeRecipes=recipes.filter(r=>r.status==="active");
  const prepItems=useMemo(()=>{
    const items=[];
    // Batch prep items from recipe book
    if(recipeBook)recipeBook.forEach(rb=>{
      const batchInBase=rb.batchUnit==="L"?rb.batchTotal*1000:rb.batchUnit==="kg"?rb.batchTotal*1000:rb.batchTotal;
      const remaining=rb.batchRemaining||0;
      const needsPrep=remaining<=0||(rb.batchMade&&(Date.now()-new Date(rb.batchMade).getTime())/86400000>=(rb.shelfDays||3));
      items.push({id:`batch-${rb.id}`,recipeBookId:rb.id,name:rb.name,isBatch:true,batchTotal:rb.batchTotal,batchUnit:rb.batchUnit||"g",remaining,batchInBase,needsPrep,shelfDays:rb.shelfDays||3,batchMade:rb.batchMade,comps:rb.comps});
    });
    // Component-level items from active menu recipes. Converted into the matched
    // ingredient's OWN unit before aggregating — two recipes can call for the same
    // ingredient in different units ("200 g" vs "0.5 kg"), and totalQty/onHand need
    // to be comparable (and later, subtractable) in one consistent unit.
    activeRecipes.forEach(r=>{
      r.comps.forEach(c=>{
        const ing=ings.find(i=>i.id===c.ingId);if(!ing)return;
        const neededInIngUnit=compQtyInIngUnit(c,ing);
        const existing=items.find(x=>x.ingId===c.ingId&&!x.isBatch);
        if(existing){existing.totalQty+=neededInIngUnit;existing.recipes.push(r.name);}
        else items.push({id:`prep-${c.ingId}`,ingId:c.ingId,name:ing.name,cat:ing.cat,unit:ing.unit,totalQty:neededInIngUnit,onHand:ing.qty,parMin:ing.parMin,storage:ing.storage,recipes:[r.name],isBatch:false});
      });
    });
    return items.sort((a,b)=>(b.isBatch?1:0)-(a.isBatch?1:0)||a.name.localeCompare(b.name));
  },[activeRecipes,ings,recipeBook]);

  // Making a batch consumes its own ingredient list — one-directional (unlike the
  // component checkbox above, there's no "un-make a batch" action to reverse it).
  const markBatchPrepped=(rbId)=>{
    if(!setRecipeBook)return;
    const rb=recipeBook.find(r=>r.id===rbId);
    if(!rb)return;
    const batchInBase=rb.batchUnit==="L"?rb.batchTotal*1000:rb.batchUnit==="kg"?rb.batchTotal*1000:rb.batchTotal;
    setRecipeBook(prev=>prev.map(r=>r.id===rbId?{...r,batchRemaining:batchInBase,batchMade:new Date().toISOString()}:r));
    if(setIngs&&rb.comps&&rb.comps.length){
      setIngs(prev=>prev.map(x=>{
        const comp=rb.comps.find(c=>c.ingId===x.id);
        if(!comp)return x;
        const deduct=compQtyInIngUnit(comp,x);
        return {...x,qty:Math.max(0,Math.round((x.qty-deduct)*1000)/1000)};
      }));
    }
  };

  const batchItems=prepItems.filter(p=>p.isBatch);
  const componentItems=prepItems.filter(p=>!p.isBatch);
  const totalItems=componentItems.length+manualItems.length;
  const doneCount=componentItems.filter(p=>checked[p.id]).length+manualItems.filter(m=>m.done).length;
  const pctDone=totalItems>0?Math.round((doneCount/totalItems)*100):0;

  return <div>
    <h2 style={{fontSize:20,fontWeight:700,marginBottom:4,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Prep list</h2>
    <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Daily mise en place — check off as each item is prepped.</div>

    <div style={{display:"flex",gap:10,marginBottom:12}}>
      <Kpi l="PREP PROGRESS" v={`${pctDone}%`} c={pctDone===100?T.grn:T.amb} s={`${doneCount} / ${totalItems}`}/>
      <Kpi l="BATCH ITEMS" v={batchItems.length} c={T.pur} s={`${batchItems.filter(b=>b.needsPrep).length} need prep`}/>
    </div>

    <div style={{height:6,background:T.sf,borderRadius:3,marginBottom:14,overflow:"hidden"}}>
      <div style={{height:"100%",width:`${pctDone}%`,background:pctDone===100?T.grn:T.amb,borderRadius:3,transition:"width 0.3s ease"}}/>
    </div>

    {/* Batch prep items */}
    {batchItems.length>0&&<Card t="BATCH PREP (from Recipe Book)">
      {batchItems.map(p=>{
        const pctLeft=p.batchInBase>0?Math.round((p.remaining/p.batchInBase)*100):0;
        // Check this batch's own ingredient list against current stock — same
        // shortfall math as component items, just per-ingredient-in-the-batch.
        const compShortfalls=(p.comps||[]).map(c=>{
          const ing=ings.find(i=>i.id===c.ingId);if(!ing)return null;
          const needed=compQtyInIngUnit(c,ing);
          const short=Math.max(0,Math.round((needed-ing.qty)*100)/100);
          return short>0?{name:ing.name,short,unit:ing.unit}:null;
        }).filter(Boolean);
        return <div key={p.id} style={{padding:"12px 0",borderBottom:`1px solid ${T.brd}33`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:p.needsPrep?T.red:T.grn}}>{p.name}</div>
            <div style={{fontSize:11,color:T.dm}}>{p.batchTotal} {p.batchUnit} · {p.shelfDays}d shelf · {p.comps.length} ingredients</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:16,fontWeight:700,fontFamily:"'DM Mono',monospace",color:p.needsPrep?T.red:T.grn}}>{pctLeft}%</div>
            <div style={{fontSize:9,color:T.dm}}>left</div>
          </div>
        </div>
        {p.batchMade&&<div style={{fontSize:10,color:T.dm,marginBottom:4}}>Last prepped: {fmtDate(p.batchMade)}</div>}
        {p.needsPrep&&compShortfalls.length>0&&<div style={{marginBottom:8,padding:"6px 10px",background:T.redBg,color:T.red,borderRadius:8,fontSize:11,fontWeight:600}}>⚠ Short: {compShortfalls.map(s=>`${s.name} (need ${s.short}${s.unit} more)`).join(", ")}</div>}
        {p.needsPrep&&<button onClick={()=>markBatchPrepped(p.recipeBookId)} style={{width:"100%",padding:12,borderRadius:8,border:`1px solid ${T.grn}55`,background:T.grnBg,color:T.grn,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Check size={16}/>Mark batch prepped ({p.batchTotal} {p.batchUnit})</button>}
      </div>;})}
    </Card>}

    {/* Component checklist */}
    <div id="prep-list-content">
      {componentItems.length===0&&manualItems.length===0?<Empty msg="No active recipes — add menu items first"/>:<>
        {componentItems.map(p=>{
          const done=checked[p.id];const low=p.onHand<p.parMin;const meta=catMeta(p.cat);
          const shortfall=Math.max(0,Math.round((p.totalQty-p.onHand)*100)/100);
          return <button key={p.id} onClick={()=>toggle(p.id)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:done?T.grnBg:T.card,border:`1px solid ${done?T.grn+"55":shortfall>0?T.red+"55":low?T.red+"55":T.brd}`,borderRadius:10,marginBottom:6,cursor:"pointer",fontFamily:"inherit",color:"inherit",textAlign:"left",opacity:done?0.6:1}}>
            <div style={{width:28,height:28,borderRadius:8,border:`2px solid ${done?T.grn:T.brd}`,background:done?T.grn:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {done&&<Check size={16} color="#fff"/>}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:600,textDecoration:done?"line-through":"none"}}>{p.name}</div>
              <div style={{fontSize:11,color:T.dm,marginTop:2}}>{Math.round(p.totalQty*100)/100} {p.unit} needed · {Math.round(p.onHand*100)/100} {p.unit} on hand{low&&shortfall<=0&&<span style={{color:T.red,fontWeight:600,marginLeft:6}}>LOW</span>}</div>
              {shortfall>0&&!done&&<div style={{marginTop:4,padding:"3px 8px",background:T.redBg,color:T.red,borderRadius:6,fontSize:11,fontWeight:700,display:"inline-block"}}>⚠ Need {shortfall} {p.unit} more to prep this</div>}
              <div style={{fontSize:10,color:T.dm,marginTop:2}}>Used in: {p.recipes.slice(0,3).join(", ")}{p.recipes.length>3?` +${p.recipes.length-3}`:""}</div>
            </div>
            <meta.Icon size={18} color={T[meta.color]||T.dm}/>
          </button>;
        })}
        {/* Manual items */}
        {manualItems.map(m=><div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:m.done?T.grnBg:T.card,border:`1px solid ${m.done?T.grn+"55":T.brd}`,borderRadius:10,marginBottom:6,opacity:m.done?0.6:1}}>
          <button onClick={()=>toggleManual(m.id)} style={{width:28,height:28,borderRadius:8,border:`2px solid ${m.done?T.grn:T.brd}`,background:m.done?T.grn:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,cursor:"pointer"}}>
            {m.done&&<Check size={16} color="#fff"/>}
          </button>
          <span style={{flex:1,fontSize:14,fontWeight:500,textDecoration:m.done?"line-through":"none"}}>{m.name}</span>
          <button onClick={()=>rmManual(m.id)} style={{width:28,height:28,borderRadius:6,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={12}/></button>
        </div>)}
      </>}
    </div>

    {/* Manual entry */}
    <Card t="ADD MANUAL PREP ITEM">
      <div style={{display:"flex",gap:8}}>
        <input value={newManual} onChange={e=>setNewManual(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addManual()} placeholder="e.g. Marinate chicken, Blanch vegetables" style={{...inp,flex:1,marginBottom:0}}/>
        <button onClick={addManual} style={{padding:"0 16px",borderRadius:8,border:`1px solid ${T.grn}`,background:T.grnBg,color:T.grn,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,whiteSpace:"nowrap"}}><Plus size={16}/></button>
      </div>
    </Card>

    <ExportBtn label="Print prep list" onClick={()=>{
      // Print/send only what's still OUTSTANDING — already-prepped/checked/done
      // items are dropped, since a prep list handed to kitchen staff should show
      // what to do next, not a record of what's already finished.
      const outstanding=[
        ...batchItems.filter(p=>p.needsPrep).map(p=>{
          const short=(p.comps||[]).map(c=>{const ing=ings.find(i=>i.id===c.ingId);if(!ing)return null;const s=Math.max(0,Math.round((compQtyInIngUnit(c,ing)-ing.qty)*100)/100);return s>0?`${ing.name} +${s}${ing.unit}`:null;}).filter(Boolean);
          return {name:p.name,type:"BATCH",detail:`${p.batchTotal} ${p.batchUnit} · ${p.shelfDays}d shelf`,flag:short.length?`NEED: ${short.join(", ")}`:""};
        }),
        ...componentItems.filter(p=>!checked[p.id]).map(p=>{
          const short=Math.max(0,Math.round((p.totalQty-p.onHand)*100)/100);
          return {name:p.name,type:"INGREDIENT",detail:`${Math.round(p.totalQty*100)/100} ${p.unit} needed · ${Math.round(p.onHand*100)/100} ${p.unit} on hand`,flag:short>0?`NEED ${short} ${p.unit} MORE`:""};
        }),
        ...manualItems.filter(m=>!m.done).map(m=>({name:m.name,type:"MANUAL",detail:"Manual entry",flag:""})),
      ];
      const sRaw=localStorage.getItem("gastriot_settings");
      const sObj=sRaw?JSON.parse(sRaw):{};
      const restName=(sObj.account&&(sObj.account.restaurantName||sObj.account.restaurant))||"Bodega";
      const rows=outstanding.map(it=>`<tr>
        <td style="padding:8px;border:1px solid #ccc;font-weight:600">${it.name}</td>
        <td style="padding:8px;border:1px solid #ccc;font-size:10pt">${it.type}</td>
        <td style="padding:8px;border:1px solid #ccc;font-size:10pt">${it.detail}</td>
        <td style="padding:8px;border:1px solid #ccc;font-weight:700;color:#D42B2B">${it.flag}</td>
      </tr>`).join("");
      const w=window.open("","_blank","width=900,height=700");
      if(!w)return;
      w.document.write(`<!doctype html><html><head><title>Prep List — Outstanding</title>
        <style>@page{margin:18mm}body{font-family:Georgia,serif;color:#1B2A4A;font-size:11pt;line-height:1.5}
        table{border-collapse:collapse;width:100%;margin:12px 0}th{background:#F5C518;color:#1B2A4A;font-weight:700;padding:8px;border:1px solid #ccc;text-align:left;font-size:10pt}
        .logo-wrap{display:flex;align-items:center;gap:12px;margin-bottom:8px}.brand{font-family:Georgia,serif;font-style:italic;font-weight:900;color:#D42B2B;font-size:18pt}
        h1{font-family:Georgia,serif;font-style:italic;color:#D42B2B;border-bottom:2px solid #D42B2B;padding-bottom:8px;margin-bottom:12px;font-size:24pt}
        .meta{color:#666;font-size:9pt;margin-bottom:12px;font-style:italic}
        .summary{margin-bottom:16px;padding:10px 14px;background:#f9f9f9;border-radius:6px;font-size:11pt}
        .footer{margin-top:24px;padding-top:8px;border-top:1px solid #ccc;color:#888;font-size:9pt;text-align:center}
        @media print{button{display:none!important}}</style></head><body>
        <div class="logo-wrap">
          <div style="width:42px;height:42px;background:#F5C518;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:Georgia,serif;font-style:italic;font-weight:900;color:#D42B2B;font-size:24pt">G</div>
          <div><div class="brand">Gastriot</div><div style="color:#888;font-size:9pt;font-style:italic">Kitchen Intelligence — ${restName}</div></div>
        </div>
        <h1>Prep List — Outstanding</h1>
        <div class="meta">Generated ${new Date().toLocaleString("de-DE")} · ${restName}</div>
        <div class="summary">${outstanding.length>0?`<strong>${outstanding.length}</strong> item${outstanding.length>1?"s":""} still need${outstanding.length>1?"":"s"} prep`:"Nothing outstanding — everything is prepped."}</div>
        ${outstanding.length>0?`<table><tr><th>Item</th><th>Type</th><th>Details</th><th>Flag</th></tr>${rows}</table>`:""}
        <div class="footer">Gastriot · Kitchen Intelligence · ${restName} · ${new Date().toLocaleDateString("de-DE")}</div>
      </body></html>`);
      w.document.close();
      setTimeout(()=>{w.focus();w.print();},250);
    }}/>
  </div>;
}

/* ═══ DEVICES — Bluetooth scale + temp sensor pairing ═══ */
function DevicesP({bleDevice,setBleDevice,bleWeight,setBleWeight,bleTempDevice,setBleTempDevice,bleTempReading,setBleTempReading,showToast,wasteBins,setWasteBins}){
  const [scanningScale,setScanningScale]=useState(false);
  const [scanningTemp,setScanningTemp]=useState(false);
  const [newBinName,setNewBinName]=useState("");

  const pairScale=async()=>{
    if(!navigator.bluetooth){showToast("Web Bluetooth not supported. Use Chrome.");return;}
    try{
      setScanningScale(true);
      const device=await navigator.bluetooth.requestDevice({
        filters:[{services:["weight_scale"]},{services:[0x181D]},{namePrefix:"OAXIS"},{namePrefix:"Etekcity"},{namePrefix:"Renpho"},{namePrefix:"Eufy"}],
        optionalServices:["weight_scale","battery_service"],
      });
      setBleDevice({id:device.id,name:device.name||"Scale",connected:true});
      showToast(`Scale paired: ${device.name||"Scale"}`);
      const server=await device.gatt.connect();
      try{
        const service=await server.getPrimaryService("weight_scale");
        const char=await service.getCharacteristic("weight_measurement");
        await char.startNotifications();
        char.addEventListener("characteristicvaluechanged",(e)=>{
          const v=e.target.value;const weightKg=v.getUint16(1,true)*0.005;
          setBleWeight(Math.round(weightKg*1000));
        });
      }catch(e){showToast("Scale paired — manual entry mode");}
      device.addEventListener("gattserverdisconnected",()=>{setBleDevice(null);setBleWeight(null);showToast("Scale disconnected");});
    }catch(e){if(e.name!=="NotFoundError")showToast("Pairing failed: "+e.message);}finally{setScanningScale(false);}
  };

  const pairTempSensor=async()=>{
    if(!navigator.bluetooth){showToast("Web Bluetooth not supported. Use Chrome.");return;}
    try{
      setScanningTemp(true);
      const device=await navigator.bluetooth.requestDevice({
        filters:[{services:["health_thermometer"]},{services:[0x1809]},{namePrefix:"Therm"},{namePrefix:"BBQ"},{namePrefix:"Meater"},{namePrefix:"iGrill"},{namePrefix:"Inkbird"},{namePrefix:"ThermoPro"}],
        optionalServices:["health_thermometer","battery_service"],
      });
      setBleTempDevice({id:device.id,name:device.name||"Temp sensor",connected:true});
      showToast(`Temp sensor paired: ${device.name||"Sensor"}`);
      const server=await device.gatt.connect();
      try{
        const service=await server.getPrimaryService("health_thermometer");
        const char=await service.getCharacteristic("temperature_measurement");
        await char.startNotifications();
        char.addEventListener("characteristicvaluechanged",(e)=>{
          const v=e.target.value;const flags=v.getUint8(0);
          // IEEE 11073 FLOAT format
          const mantissa=v.getUint16(1,true)+(v.getUint8(3)<<16);
          const exponent=v.getInt8(4);
          const temp=mantissa*Math.pow(10,exponent);
          setBleTempReading(parseFloat(temp.toFixed(1)));
          window.dispatchEvent(new CustomEvent("voiceTemp",{detail:{value:parseFloat(temp.toFixed(1))}}));
        });
      }catch(e){showToast("Sensor paired — reading may need manual entry");}
      device.addEventListener("gattserverdisconnected",()=>{setBleTempDevice(null);setBleTempReading(null);showToast("Temperatursensor getrennt");});
    }catch(e){if(e.name!=="NotFoundError")showToast("Pairing failed: "+e.message);}finally{setScanningTemp(false);}
  };

  return <div>
    <h2 style={{fontSize:20,fontWeight:700,marginBottom:4,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",color:T.tx}}>Devices</h2>
    <div style={{fontSize:12,color:T.mt,marginBottom:14}}>Pair Bluetooth kitchen hardware. Readings auto-feed into Scale, Waste, and Safety modules.</div>

    <Card t="BLUETOOTH SCALE">
      {bleDevice?<>
        <div style={{padding:14,background:T.grnBg,border:`1px solid ${T.grn}55`,borderRadius:10,marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,color:T.grn,fontSize:14,fontWeight:700}}><Bluetooth size={18}/>Connected: {bleDevice.name}</div>
          {bleWeight!=null&&<div style={{fontSize:32,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace",marginTop:6}}>{bleWeight}g</div>}
        </div>
        <button onClick={()=>{setBleDevice(null);setBleWeight(null);showToast("Scale disconnected");}} style={{width:"100%",padding:14,borderRadius:10,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Disconnect scale</button>
      </>:<>
        <p style={{fontSize:13,color:T.mt,marginBottom:10}}>Pair a Bluetooth kitchen scale. Supports Weight Scale service (0x181D): OAXIS, Etekcity, Renpho, Eufy, and similar.</p>
        <PrimaryBtn color={T.blu} icon={scanningScale?<BluetoothSearching size={20}/>:<Bluetooth size={20}/>} label={scanningScale?"Scanning...":"Pair scale"} onClick={pairScale}/>
      </>}
    </Card>

    <Card t="BLUETOOTH TEMPERATURE SENSOR">
      {bleTempDevice?<>
        <div style={{padding:14,background:T.grnBg,border:`1px solid ${T.grn}55`,borderRadius:10,marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,color:T.grn,fontSize:14,fontWeight:700}}><Thermometer size={18}/>Connected: {bleTempDevice.name}</div>
          {bleTempReading!=null&&<div style={{fontSize:32,fontWeight:700,color:T.amb,fontFamily:"'DM Mono',monospace",marginTop:6}}>{bleTempReading}°C</div>}
        </div>
        <div style={{fontSize:11,color:T.dm,marginBottom:10}}>Live readings are pushed to Safety → Temp log and HACCP automatically via voice event bridge.</div>
        <button onClick={()=>{setBleTempDevice(null);setBleTempReading(null);showToast("Temperatursensor getrennt");}} style={{width:"100%",padding:14,borderRadius:10,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Disconnect sensor</button>
      </>:<>
        <p style={{fontSize:13,color:T.mt,marginBottom:10}}>Pair a Bluetooth temperature probe (Meater, iGrill, Inkbird, ThermoPro, or any Health Thermometer 0x1809 device). Readings auto-fill all HACCP temperature modules.</p>
        <PrimaryBtn color={T.blu} icon={scanningTemp?<BluetoothSearching size={20}/>:<Thermometer size={20}/>} label={scanningTemp?"Scanning...":"Pair temp sensor"} onClick={pairTempSensor}/>
      </>}
    </Card>

    <Card t="HOW IT WORKS">
      <div style={{fontSize:12,color:T.mt,lineHeight:1.6}}>
        Once paired, readings from either device flow into the rest of Gastriot automatically. Scale readings appear on the Scale/Waste page. Temperature readings push to any open temperature field in Safety (zone temps, receiving, cooking) — identical to voice entry but hands-free. Requires Chrome/Edge on desktop, Android, or macOS.
      </div>
    </Card>

    <Card t="WASTE BINS (NFC-tagged)">
      <div style={{fontSize:12,color:T.mt,marginBottom:10}}>Label distinct waste streams. NFC-tagged bins auto-select category when placed on the Gastriot scale.</div>
      {wasteBins&&wasteBins.length>0&&wasteBins.map(b=><div key={b.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${T.brd}33`}}>
        <Trash2 size={18} color={T.amb}/>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:600}}>{b.name}</div>
          <div style={{fontSize:10,color:T.dm}}>{b.nfcTag?"NFC: "+b.nfcTag:"No NFC tag assigned"}</div>
        </div>
        <button onClick={()=>setWasteBins&&setWasteBins(p=>p.filter(x=>x.id!==b.id))} style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={14}/></button>
      </div>)}
      <div style={{display:"flex",gap:8,marginTop:8}}>
        <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600,alignSelf:"center"}}>ADD BIN</div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <input value={newBinName} onChange={e=>setNewBinName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&newBinName.trim()&&(setWasteBins(p=>[...p,{id:genId(),name:newBinName.trim(),nfcTag:""}]),setNewBinName(""),showToast("Bin added"))} placeholder="e.g. Unavoidable waste, Paper waste" style={{...inp,flex:1,marginBottom:0}}/>
        <button onClick={()=>{if(!newBinName.trim())return;setWasteBins(p=>[...p,{id:genId(),name:newBinName.trim(),nfcTag:""}]);setNewBinName("");showToast("Bin added");}} style={{padding:"0 16px",borderRadius:8,border:`1px solid ${T.grn}`,background:T.grnBg,color:T.grn,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600}}><Plus size={16}/></button>
      </div>
    </Card>
  </div>;
}

/* ═══ NOTIFICATIONS ═══ */
function NotifsP({notifs,setNotifs,alerts,setAlerts}){
  return <div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <h2 style={{fontSize:18,fontWeight:700,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",margin:0}}>Notifications</h2>
      {notifs.length>0&&<button onClick={()=>setNotifs([])} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${T.red}55`,background:T.redBg,color:T.red,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Clear all</button>}
    </div>
    {notifs.length===0?<Empty msg="No notifications"/>:notifs.map(n=><div key={n.id} style={{background:T.card,border:`1px solid ${T.brd}`,borderRadius:8,padding:"10px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
      <span style={{fontSize:12,flex:1}}>{n.msg}</span>
      <span style={{fontSize:10,color:T.dm,fontFamily:"'DM Mono',monospace",flexShrink:0}}>{n.time}</span>
      <button onClick={()=>setNotifs(p=>p.filter(x=>x.id!==n.id))} style={{width:24,height:24,borderRadius:6,border:`1px solid ${T.brd}`,background:"none",color:T.dm,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}><X size={12}/></button>
    </div>)}
  </div>;
}

/* ═══ SMALL COMPONENTS ═══ */
function Card({t,children}){return <div style={{background:T.card,borderRadius:14,padding:"16px 18px",marginBottom:12,boxShadow:"0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",border:`1px solid ${T.brd}44`}}>{t&&<div style={{fontSize:11,fontWeight:700,marginBottom:10,color:T.mt,letterSpacing:0.8,textTransform:"uppercase"}}>{t}</div>}{children}</div>}

function Kpi({l,v,c,s}){return <div style={{background:T.card,borderRadius:14,padding:"12px 14px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`}}><div style={{fontSize:10,color:T.mt,letterSpacing:0.8,fontWeight:700,textTransform:"uppercase"}}>{l}</div><div style={{fontSize:22,fontWeight:700,fontFamily:"'DM Mono',monospace",color:c,marginTop:3}}>{v}</div>{s&&<div style={{fontSize:10,color:T.dm,marginTop:2}}>{s}</div>}</div>}

function KpiButton({onClick,l,v,c,s}){return <button onClick={onClick} style={{background:T.card,borderRadius:14,padding:"14px 16px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",color:"inherit",position:"relative",transition:"all 0.15s",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",border:`1px solid ${T.brd}44`,minHeight:78}} onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.08)";e.currentTarget.style.transform="translateY(-1px)"}} onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.04)";e.currentTarget.style.transform="none"}}><div style={{fontSize:10,color:T.mt,letterSpacing:0.8,fontWeight:700,textTransform:"uppercase"}}>{l}</div><div style={{fontSize:24,fontWeight:700,fontFamily:"'DM Mono',monospace",color:c,marginTop:4}}>{v}</div>{s&&<div style={{fontSize:10,color:T.dm,marginTop:3}}>{s}</div>}<ChevronRight size={14} color={T.dm} style={{position:"absolute",top:12,right:12,opacity:0.3}}/></button>}

function Banner({c,bg,l,v}){return <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:bg,borderRadius:12,padding:"12px 16px",marginBottom:12,border:`1px solid ${c}15`}}><span style={{fontSize:12,color:c,fontWeight:600}}>{l}</span><span style={{fontFamily:"'DM Mono',monospace",fontSize:17,fontWeight:700,color:c}}>{v}</span></div>}

function Tabs({tabs,active,set}){return <div style={{display:"flex",gap:2,marginBottom:14,overflowX:"auto",paddingBottom:2,borderBottom:`1px solid ${T.brd}66`}}>{tabs.map(t=><button key={t.id} onClick={()=>set(t.id)} style={{padding:"10px 16px",borderRadius:"8px 8px 0 0",border:"none",borderBottom:active===t.id?`2px solid ${T.accent}`:"2px solid transparent",background:active===t.id?T.card:"transparent",color:active===t.id?T.tx:T.mt,fontSize:12,fontWeight:active===t.id?700:500,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",transition:"all 0.12s"}}>{t.label}</button>)}</div>}

function StatusBadge({s}){const m={draft:{c:T.dm,bg:T.sf},submitted:{c:T.blu,bg:T.bluBg},confirmed:{c:T.amb,bg:T.ambBg},shipped:{c:T.pur,bg:T.purBg},partial:{c:T.amb,bg:T.ambBg},received:{c:T.grn,bg:T.grnBg},active:{c:T.red,bg:T.redBg},acknowledged:{c:T.amb,bg:T.ambBg},resolved:{c:T.grn,bg:T.grnBg},cancelled:{c:T.dm,bg:T.sf}};const x=m[s]||m.draft;return <span style={{fontSize:9,padding:"3px 8px",borderRadius:8,background:x.bg,color:x.c,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase"}}>{s}</span>}

function Toast({msg}){return <div style={{position:"fixed",bottom:90,left:14,right:14,padding:"14px 18px",background:"#1B2A4A",borderRadius:12,color:"#fff",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:8,zIndex:100,boxShadow:"0 8px 24px rgba(0,0,0,0.15)"}}><Check size={18}/>{msg}</div>}

function SearchInp({value,set}){return <div style={{position:"relative",flex:1}}><Search size={16} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.dm}}/><input value={value} onChange={e=>set(e.target.value)} placeholder="Search..." style={{...inp,paddingLeft:36,marginBottom:0}}/></div>}

// Read-only, polished presentation of a Recipe Book batch — photo hero, batch
// stats, cost, ingredients, and method. For looking a recipe up (on a phone,
// mid-shift), not for editing it.
function RecipeViewModal({recipe,ings,onClose}){
  const cost=recipe.comps.reduce((s,c)=>s+calcCompCost(c,ings),0);
  const batchInBase=recipe.batchUnit==="L"?recipe.batchTotal*1000:recipe.batchUnit==="kg"?recipe.batchTotal*1000:recipe.batchTotal;
  const pctLeft=batchInBase>0?Math.round(((recipe.batchRemaining||0)/batchInBase)*100):0;
  const costPerUnit=recipe.batchTotal>0?cost/recipe.batchTotal:0;
  return <div style={{position:"fixed",inset:0,zIndex:200,background:T.bg,overflowY:"auto"}}>
    <div style={{position:"relative"}}>
      {recipe.photo
        ?<img src={recipe.photo} alt={recipe.name} style={{width:"100%",height:220,objectFit:"cover",display:"block"}}/>
        :<div style={{width:"100%",height:120,background:`linear-gradient(135deg,${T.amb}33,${T.pur}33)`}}/>}
      <button onClick={onClose} style={{position:"absolute",top:14,right:14,width:38,height:38,borderRadius:10,border:"none",background:"rgba(0,0,0,0.55)",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}><X size={18}/></button>
      <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"36px 18px 14px",background:"linear-gradient(to top, rgba(0,0,0,0.78), transparent)"}}>
        <div style={{fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic"}}>{recipe.name||"Untitled batch"}</div>
      </div>
    </div>

    <div style={{padding:16}}>
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        <div style={{flex:1}}><Kpi l="BATCH SIZE" v={`${recipe.batchTotal} ${recipe.batchUnit}`} c={T.tx}/></div>
        <div style={{flex:1}}><Kpi l="SHELF LIFE" v={`${recipe.shelfDays}d`} c={T.tx}/></div>
        <div style={{flex:1}}><Kpi l="REMAINING" v={`${pctLeft}%`} c={pctLeft<=0?T.red:T.grn}/></div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 14px",background:T.sf,borderRadius:10,marginBottom:8}}>
        <span style={{fontSize:12,color:T.mt,fontWeight:600}}>Batch cost</span>
        <span style={{fontSize:15,fontWeight:700,fontFamily:"'DM Mono',monospace",color:T.amb}}>{fmtEur(cost)}<span style={{fontSize:11,color:T.dm,fontWeight:500}}> · {fmtEur(costPerUnit)}/{recipe.batchUnit}</span></span>
      </div>
      {recipe.batchMade&&<div style={{fontSize:11,color:T.dm,marginBottom:14}}>Last prepped {fmtDate(recipe.batchMade)}</div>}

      <div style={{fontSize:11,fontWeight:700,color:T.mt,letterSpacing:0.6,textTransform:"uppercase",marginBottom:8,marginTop:8}}>Ingredients</div>
      <div style={{background:T.card,border:`1px solid ${T.brd}`,borderRadius:12,padding:"4px 14px",marginBottom:20}}>
        {recipe.comps.length===0?<div style={{padding:"10px 0",fontSize:13,color:T.dm}}>No ingredients listed.</div>:
          recipe.comps.map((c,idx)=>{
            const ing=ings.find(i=>i.id===c.ingId);
            return <div key={idx} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:idx<recipe.comps.length-1?`1px solid ${T.brd}44`:"none"}}>
              <span style={{fontSize:14,color:T.tx}}>{ing?.name||c._name||"Unknown"}</span>
              <span style={{fontSize:13,color:T.dm,fontFamily:"'DM Mono',monospace"}}>{c.qty} {c.unit}</span>
            </div>;
          })}
      </div>

      <div style={{fontSize:11,fontWeight:700,color:T.mt,letterSpacing:0.6,textTransform:"uppercase",marginBottom:8}}>Method</div>
      <div style={{fontSize:14,color:T.tx,lineHeight:1.7,whiteSpace:"pre-wrap",padding:"14px 16px",background:T.sf,borderRadius:12,marginBottom:20}}>{recipe.instructions||"No method recorded."}</div>
    </div>
  </div>;
}

// Snap the finished dish — photo upload that also asks the backend (dish photo +
// the recipe's current ingredient list) for a suggested name/description.
// Adapted from EATRR's /home/cook/analyze-dish. Never overwrites a name or
// instructions that are already filled in — only offers to fill blanks.
function DishPhotoField({photo,setPhoto,comps,ings,name,setName,instructions,setInstructions,showToast}){
  const [suggesting,setSuggesting]=useState(false);
  const suggestFromPhoto=async(base64,mediaType)=>{
    const ingredients=(comps||[]).map(c=>{const ing=ings.find(i=>i.id===c.ingId);return ing?{name:ing.name,qty:c.qty,unit:c.unit}:null;}).filter(Boolean);
    if(!ingredients.length)return; // nothing to caption against yet
    setSuggesting(true);
    try{
      const resp=await fetch(`${SCAN_API}/scan/dish-photo`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({image:base64,mediaType,ingredients})});
      const d=await resp.json();
      if(d.ok){
        if(d.suggestedName&&setName&&!name)setName(d.suggestedName);
        if(d.description&&setInstructions&&!instructions)setInstructions(d.description);
        if(showToast)showToast(d.suggestedName?`Photo added — suggested "${d.suggestedName}"`:"Photo added");
      }
    }catch(e){}
    setSuggesting(false);
  };
  const upload=(file)=>{
    if(!file)return;
    const r=new FileReader();
    r.onload=()=>{setPhoto(r.result);suggestFromPhoto(r.result.split(",")[1],file.type||"image/jpeg");};
    r.readAsDataURL(file);
  };
  return <>
    <div style={{fontSize:11,color:T.mt,marginBottom:4,fontWeight:600}}>DISH PHOTO</div>
    {photo
      ?<div style={{position:"relative"}}>
         <img src={photo} alt="dish" style={{width:"100%",height:170,objectFit:"cover",borderRadius:10,border:`1px solid ${T.brd}`}}/>
         <button onClick={()=>setPhoto("")} style={{position:"absolute",top:8,right:8,width:30,height:30,borderRadius:8,border:"none",background:"rgba(0,0,0,0.6)",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><X size={16}/></button>
         {suggesting&&<div style={{position:"absolute",bottom:8,left:8,padding:"4px 10px",background:"rgba(0,0,0,0.7)",color:"#fff",borderRadius:6,fontSize:11}}>Suggesting name…</div>}
       </div>
      :<label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%",padding:14,borderRadius:10,border:`1.5px dashed ${T.brd}`,background:T.sf,color:T.mt,cursor:"pointer",fontSize:13,fontWeight:600}}>
         <Camera size={17}/>Snap the finished dish
         <input type="file" accept="image/*" capture="environment" onChange={e=>upload(e.target.files[0])} style={{display:"none"}}/>
       </label>}
  </>;
}

// Typeahead ingredient picker — type a couple letters to filter instead of
// scrolling a giant <select>. Typing something that doesn't match anything
// existing offers "+ Create" right there, so a new ingredient never needs a
// separate trip to Inventory first. Used everywhere a recipe/waste/order flow
// needs to pick or create an ingredient.
function IngredientPicker({ings,setIngs,value,onChange,placeholder}){
  const [query,setQuery]=useState("");
  const [open,setOpen]=useState(false);
  const selected=ings.find(i=>i.id===value);
  const q=query.trim().toLowerCase();
  const matches=q.length>=1?ings.filter(i=>i.name.toLowerCase().includes(q)||foodNamesMatch(i.name,query)).slice(0,8):[];
  const exactExists=q&&ings.some(i=>i.name.toLowerCase()===q);
  const createNew=()=>{
    const name=query.trim();
    if(!name||!setIngs)return;
    const id=genId();
    setIngs(prev=>[...prev,{id,name,cat:"Uncategorized",unit:"g",qty:0,cost:0,parMin:0,parMax:0,shelf:7,unitG:1,lots:[],priceHist:[],sku:"",eightySixed:false}]);
    onChange(id);
    setQuery("");setOpen(false);
  };
  return <div style={{position:"relative",flex:2,minWidth:0}}>
    <input
      value={open?query:(selected?.name||"")}
      onChange={e=>{setQuery(e.target.value);setOpen(true);}}
      onFocus={()=>{setQuery("");setOpen(true);}}
      onBlur={()=>setTimeout(()=>setOpen(false),150)}
      placeholder={placeholder||(selected?selected.name:"Type to search…")}
      style={{...inp,marginBottom:0,width:"100%"}}
    />
    {open&&<div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:50,background:T.card,border:`1px solid ${T.brd}`,borderRadius:10,marginTop:4,maxHeight:220,overflowY:"auto",boxShadow:"0 4px 16px rgba(0,0,0,0.15)"}}>
      {matches.map(i=><div key={i.id} onMouseDown={()=>{onChange(i.id);setQuery("");setOpen(false);}} style={{padding:"9px 12px",cursor:"pointer",fontSize:13,borderBottom:`1px solid ${T.brd}33`}}>{i.name}</div>)}
      {q&&!exactExists&&setIngs&&<div onMouseDown={createNew} style={{padding:"9px 12px",cursor:"pointer",fontSize:13,color:T.grn,fontWeight:600}}>{`+ Create "${query.trim()}"`}</div>}
      {!q&&<div style={{padding:"9px 12px",fontSize:12,color:T.dm}}>Type a couple letters to search…</div>}
    </div>}
  </div>;
}

function Empty({msg}){return <div style={{textAlign:"center",padding:36,color:T.dm,fontSize:13,background:T.sf,borderRadius:14,border:`1px dashed ${T.brd}`}}>{msg}</div>}

function ExportBtn({label,onClick}){return <button onClick={onClick} style={{width:"100%",padding:14,borderRadius:12,border:`1px solid ${T.brd}`,background:T.sf,color:T.mt,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:10,transition:"all 0.15s"}}><Printer size={16}/>{label}</button>}

function PrimaryBtn({color,icon,label,onClick,style={}}){return <button onClick={onClick} style={{width:"100%",padding:16,borderRadius:12,border:"none",background:color,color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"inherit",minHeight:52,letterSpacing:0.3,boxShadow:`0 2px 8px ${color}33`,transition:"all 0.15s",...style}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>{icon}{label}</button>}

/* ═══ TOUR MODAL — bottom sheet, skippable, replayable from menu ═══ */
const TOUR_ICONS={BarChart3,Bell,ShoppingCart,Scale,Trash2,Package,ChefHat,Truck,FileText,ShieldCheck,Check,ClipboardList,Camera,Users};

function Tour({step,go,close}){
  const s=TOUR_STEPS[step];if(!s)return null;
  const Icon=TOUR_ICONS[s.icon]||ClipboardList;
  const total=TOUR_STEPS.length;
  const isLast=step===total-1;

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={(e)=>{if(e.target===e.currentTarget)close();}}>
    <div style={{width:"100%",maxWidth:520,background:T.card,borderTopLeftRadius:18,borderTopRightRadius:18,padding:"18px 20px 22px",borderTop:`3px solid ${T.amb}`,boxShadow:"0 -20px 60px rgba(0,0,0,0.5)",animation:"fadeIn 0.25s ease both"}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:48,height:48,borderRadius:12,background:T.ambBg,border:`1px solid ${T.amb}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon size={26} color={T.amb} strokeWidth={1.8}/>
          </div>
          <div>
            <div style={{fontSize:10,color:T.mt,fontWeight:700,letterSpacing:0.5,marginBottom:2}}>STEP {step+1} OF {total}</div>
            <div style={{fontSize:18,fontWeight:700,color:T.tx,fontFamily:"'Playfair Display',Georgia,serif",fontStyle:"italic",lineHeight:1.15}}>{s.title}</div>
          </div>
        </div>
        <button onClick={close} title="Skip tour" style={{width:36,height:36,borderRadius:8,border:`1px solid ${T.brd}`,background:T.sf,color:T.mt,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",flexShrink:0}}><X size={18}/></button>
      </div>

      {/* Bullets */}
      <ul style={{listStyle:"none",margin:"0 0 18px",padding:0}}>
        {s.bullets.map((b,i)=><li key={i} style={{display:"flex",gap:10,padding:"6px 0",fontSize:14,lineHeight:1.45,color:T.tx}}>
          <span style={{color:T.amb,fontWeight:700,flexShrink:0,marginTop:1}}>•</span>
          <span>{b}</span>
        </li>)}
      </ul>

      {/* Progress dots */}
      <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:14}}>
        {TOUR_STEPS.map((_,i)=><button key={i} onClick={()=>go(i)} aria-label={`Go to step ${i+1}`} style={{width:i===step?20:8,height:8,borderRadius:4,background:i===step?T.amb:i<step?T.amb+"55":T.brd,border:"none",cursor:"pointer",padding:0,transition:"width 0.2s ease"}}/>)}
      </div>

      {/* Actions */}
      <div style={{display:"flex",gap:8}}>
        <button onClick={close} style={{flex:1,padding:14,borderRadius:10,border:`1px solid ${T.brd}`,background:"none",color:T.mt,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",minHeight:48}}>Skip</button>
        {step>0&&<button onClick={()=>go(step-1)} style={{flex:1,padding:14,borderRadius:10,border:`1px solid ${T.brd}`,background:T.sf,color:T.tx,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",minHeight:48,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><ArrowLeft size={16}/>Back</button>}
        <button onClick={()=>isLast?close():go(step+1)} style={{flex:2,padding:14,borderRadius:10,border:"none",background:T.amb,color:"#1B2A4A",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",minHeight:48,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          {isLast?<>Got it<Check size={18}/></>:<>Next<ChevronRight size={18}/></>}
        </button>
      </div>
    </div>
  </div>;
}
